import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Card,
  Grid,
  useMediaQuery,
  useTheme,
  Tooltip,
  IconButton,
  Divider,
  Button,
  TextField,
  Chip,
  Tabs,
  Tab,
  InputAdornment,
  Collapse,
  Slider,
  ToggleButton,
  ToggleButtonGroup,
  Paper,
  Checkbox,
  FormControlLabel,
  Radio,
  RadioGroup,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Switch,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CallSplitIcon from '@mui/icons-material/CallSplit';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import CancelIcon from '@mui/icons-material/Cancel';
import RemoveIcon from '@mui/icons-material/Remove';
import AddIcon from '@mui/icons-material/Add';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CloseIcon from '@mui/icons-material/Close';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { useProfile } from '../../context/ProfileContext';
import type { CalculatorProfile } from '../../services/profileService';
import { useCurrentUser } from '../../hooks/useCurrentUser';
import { exportBuyingToPDF, type BuyingScheduleRowPdf, type InvestmentTimelineData, type FundSummaryData, type TimelineBorrower } from '../../utils/buyingCalculatorPdfExport';
import { logPdfExport, logCalculatorInteraction } from '../../services/analytics';
import { useGlobalCalculatorSettings } from '../../context/GlobalCalculatorSettingsContext';
import { calculateAssessableIncome } from '../../utils/haircutFormulas';
import {
  useLinkedCalculator,
  hasPendingHistoryLoad,
  beginProfileSaveBack,
  endProfileSaveBack,
  isProfileSaveBackInProgress,
  type SharedProceedsSeller,
} from '../../context/LinkedCalculatorContext';
import HistoryToolbar, { type NewProfileDraft } from '../../components/HistoryToolbar';
import ExportPdfButton from '../../components/ExportPdfButton';
import TextSizeControl from '../../components/TextSizeControl';
import ProfileBanner from '../../components/ProfileBanner';
import CurrencyTextField from '../../components/CurrencyTextField';
import {
  CalculatorTutorial,
  TutorialLaunchButton,
  useCalculatorTutorial,
  type TutorialStepDef,
} from '../../components/CalculatorTutorial';
import WhatsNewDialog, {
  WhatsNewLaunchButton,
  useWhatsNew,
} from '../../components/WhatsNewDialog';
import { BUYING_WHATS_NEW } from '../../utils/buyingWhatsNew';
import {
  type SharedCalculatorProfile,
} from '../../hooks/useSharedProfileHistory';
import {
  calculateBsd,
  getBsdBreakdown,
  getAbsdRate,
  getJointAbsdRate,
  getEffectiveCitizenshipForAbsd,
  CITIZENSHIP_LABELS,
  PROPERTY_COUNT_OPTIONS,
  FTA_ABSD_INFO_TOOLTIP,
  type CitizenshipType,
  type PropertyCount,
} from '../../../utils/stampDutyRates';
import {
  EC_INCOME_CEILING,
  EC_INCOME_CEILING_NEW_SITES,
  EC_ELIGIBILITY_URL,
  HDB_LOAN_ELIGIBILITY_URL,
  HDB_RESALE_ELIGIBILITY_URL,
  HDB_CONCESSIONARY_INTEREST_RATE,
  assessEcEligibility,
  assessHdbFlatEligibility,
  assessHdbLoanEligibility,
  type BuyerRelationship,
  type EligibilityInput,
} from '../../utils/housingEligibility';

// ─── Types ────────────────────────────────────────────────────────────────────

type PropertyTab = 'private' | 'ec' | 'hdb';
type PrivateSubTab = 'new-launch' | 'resale';
type BuyingCalculatorMode = 'simple' | 'detailed';

// ─── EC / HDB eligibility ─────────────────────────────────────────────────────
// Rules, ceilings and URLs live in utils/housingEligibility (shared with the
// Affordability Calculator).
type BorrowerRelationship = BuyerRelationship;
const RELATIONSHIP_LABELS: Record<BorrowerRelationship, string> = {
  SPOUSE: 'Married Couple',
  INDIVIDUAL: 'Individual',
};


// Agency service fee: commission rate × price, with GST on top.
const AGENCY_FEE_GST = 1.09;
const agencyFeeFromPct = (price: number, pct: number) =>
  Math.round(price * (pct / 100) * AGENCY_FEE_GST);

const IRAS_ABSD_URL = "https://www.iras.gov.sg/taxes/stamp-duty/for-property/buying-or-acquiring-property/additional-buyer's-stamp-duty-(absd)";
const CPF_SUBSEQUENT_PROPERTY_URL = 'https://www.cpf.gov.sg/service/article/how-much-cpf-savings-can-i-use-for-my-property-purchase?utm_source=chatgpt.com#:~:text=Purchasing%20your%20second%20or%20subsequent%20property';

const MONTHLY_INCOME_SLIDER_MAX = 30000;
const OTHER_ANNUAL_INCOME_SLIDER_MAX = 200000;
const EXISTING_DEBT_SLIDER_MAX = 15000;

// ─── CPF Ordinary Account (OA) monthly contribution estimate ──────────────────
// Effective 1 Jan 2026. Used to estimate how much CPF OA a borrower earns each
// month (which can service the home loan), so reserves can be projected assuming
// continuous employment.
//   monthly OA = min(wage, OW ceiling) × total contribution rate × OA allocation ratio
// Rates are editable in the UI — this is only the auto-estimate.
const CPF_OW_CEILING_2026 = 8000; // Ordinary Wage ceiling from 1 Jan 2026

// Total CPF contribution rate (employer + employee) by age, 2026.
function cpfTotalContributionRate(age: number): number {
  if (age <= 55) return 0.37;
  if (age <= 60) return 0.325;
  if (age <= 65) return 0.235;
  if (age <= 70) return 0.165;
  return 0.125;
}

// Share of the total CPF contribution allocated to the Ordinary Account, by age.
// Source: CPF Allocation Rates from 1 January 2026 (Private Sector / Non-Pensionable).
function cpfOaAllocationRatio(age: number): number {
  if (age <= 35) return 0.6217;
  if (age <= 45) return 0.5677;
  if (age <= 50) return 0.5136;
  if (age <= 55) return 0.4055;
  if (age <= 60) return 0.353;
  if (age <= 65) return 0.14;
  if (age <= 70) return 0.0607;
  return 0.08;
}

// Estimate monthly CPF OA inflow from age & monthly (ordinary) wage.
// Self-employed persons make no mandatory OA contributions → 0.
function estimateMonthlyCpfOa(age: number, monthlyWage: number, isSelfEmployed: boolean): number {
  if (isSelfEmployed || monthlyWage <= 0) return 0;
  const w = Math.min(monthlyWage, CPF_OW_CEILING_2026);
  return Math.round(w * cpfTotalContributionRate(age) * cpfOaAllocationRatio(age));
}

interface BorrowerInputs {
  /** Borrower's name — seeded from the loaded profile, and the name used when
   *  saving these inputs as a new profile. */
  name?: string;
  age: number;
  monthlyIncome: number;
  otherAnnualIncome: number;
  isSelfEmployed: boolean;
  existingMonthlyDebt: number;
  // Estimated monthly CPF OA inflow. Undefined → auto-derived from age & income;
  // a concrete number means the user has overridden the estimate.
  cpfOaMonthly?: number;
  /** This borrower's own cash savings — summed into Available Funds → Cash. */
  cash: number;
  /** This borrower's own CPF OA balance — summed into Available Funds → CPF. */
  cpf: number;
}

const DEFAULT_BORROWER: BorrowerInputs = {
  age: 30,
  monthlyIncome: 5000,
  otherAnnualIncome: 0,
  isSelfEmployed: false,
  existingMonthlyDebt: 0,
  cash: 0,
  cpf: 0,
};

/** Tolerates borrowers restored from snapshots saved before per-borrower funds existed. */
function normalizeBorrower(raw: unknown): BorrowerInputs {
  const b = (raw ?? {}) as Partial<BorrowerInputs>;
  const num = (v: unknown): number => (typeof v === 'number' && Number.isFinite(v) ? Math.max(0, Math.round(v)) : 0);
  return {
    ...DEFAULT_BORROWER,
    ...b,
    cash: num(b.cash),
    cpf: num(b.cpf),
  };
}

// Effective monthly CPF OA for a borrower: their override if set, else the estimate.
function getMonthlyCpfOa(b: BorrowerInputs): number {
  return b.cpfOaMonthly ?? estimateMonthlyCpfOa(b.age, b.monthlyIncome, b.isSelfEmployed);
}

interface BuyingInputs {
  purchasePrice: number;
  hasBankLoan: boolean;
  additionalCash: number;
  cpf: number;
  additionalCpf: number;
  interestRate: number;
  tenure: number;
  citizenship: CitizenshipType;
  propertyCount: PropertyCount;
  ftaEligible: boolean;
  legalFee: number;
  spDate: string;
  primary: BorrowerInputs;
  includeAdditionalBorrower: boolean;
  additional: BorrowerInputs;
}

interface ScheduleStage {
  id: string;
  label: string;
  percentage: number;
  timing: string;
  type: 'otp' | 'sp-bsd' | 'sp' | 'construction' | 'top' | 'csc';
}

interface ComputedStage extends ScheduleStage {
  stageIndex: number;
  paymentAmount: number;
  loanPayout: number;
  // Bridging-loan disbursement at this stage (EC DPS only). Disbursed at the TOP
  // milestone, drawn before the bank loan, so it reduces the bank loan, not the
  // buyer's own cash/CPF.
  bridgingPayout: number;
  cumulativeBridging: number;
  buyerCash: number;
  cashUsed: number;
  cpfUsed: number;
  cumulativeCash: number;
  cumulativeCpf: number;
  cumulativeLoan: number;
  // Loan still owed once this stage disburses (drawn so far less principal already
  // repaid) and the months left on the tenure — the installment re-amortises this
  // balance over those months, the way banks reset it at each progressive drawdown.
  outstandingLoan: number;
  remainingTenureMonths: number;
  monthlyInstallment: number;
  estimatedDate: Date | null;
  subLabel: string;
  subAmountLabel: string;
  // Simple-mode funding split (populated by augmentSimpleFunding). The buyer can't tell us
  // their cash/CPF mix, so each stage's buyer-funded amount is split into the portion that
  // MUST be cash (the mandatory 5% min cash — plus a further 5% at S&P under the stretched-
  // tenure 10% regime) and the flexible remainder that may be paid by CPF or cash.
  simpleMandatoryCash?: number;
  simpleFlex?: number;
  cumSimpleMandatoryCash?: number;
  cumSimpleFlex?: number;
}

// ─── New Launch Progressive Stages ────────────────────────────────────────────

const NEW_LAUNCH_STAGES: ScheduleStage[] = [
  { id: 'otp', label: 'OTP (Option Fee 5%)', percentage: 5, timing: 'Today', type: 'otp' },
  { id: 'sp-bsd', label: 'Pay Stamp Duty', percentage: 0, timing: 'Within 14 days of exercise', type: 'sp-bsd' },
  { id: 'sp-15', label: 'Pay (15%)', percentage: 15, timing: 'OTP + 8 weeks', type: 'sp' },
  { id: 'foundation', label: 'Upon Foundation (10%)', percentage: 10, timing: '6 - 9 months', type: 'construction' },
  { id: 'framework', label: 'Upon Concrete Framework (10%)', percentage: 10, timing: '9 - 12 months', type: 'construction' },
  { id: 'brick-wall', label: 'Upon Brick Wall (5%)', percentage: 5, timing: '9 - 12 months', type: 'construction' },
  { id: 'ceiling', label: 'Upon Ceiling (5%)', percentage: 5, timing: '6 - 9 months', type: 'construction' },
  { id: 'interior', label: 'Upon Interior, Electrical Plant / AC (5%)', percentage: 5, timing: '6 - 9 months', type: 'construction' },
  { id: 'roads', label: 'Upon Roads / Drains / Carpark (5%)', percentage: 5, timing: '6 - 9 months', type: 'construction' },
  { id: 'top', label: 'TOP (25%)', percentage: 25, timing: '6 - 9 months', type: 'top' },
  { id: 'csc', label: 'Legal Completion Date (CSC) (15%)', percentage: 15, timing: '12 months', type: 'csc' },
];

const RESALE_STAGES: ScheduleStage[] = [
  { id: 'option-fee', label: 'Option Fee (1%)', percentage: 1, timing: 'Today', type: 'otp' },
  { id: 'exercise-otp', label: 'Exercise OTP (4%)', percentage: 4, timing: 'Within 14 days', type: 'sp' },
  { id: 'balance', label: 'Balance Payment', percentage: 95, timing: '8 - 12 weeks', type: 'csc' },
];

// ─── EC Stages ───────────────────────────────────────────────────────────────
// NPS = Normal Progressive Scheme (same as private new-launch)
// DPS = Deferred Payment Scheme (5% OTP / 15% S&P / 65% TOP / 15% CSC)
const EC_NPS_STAGES: ScheduleStage[] = NEW_LAUNCH_STAGES;

const EC_DPS_STAGES: ScheduleStage[] = [
  { id: 'otp', label: 'OTP (Option Fee 5%)', percentage: 5, timing: 'Today', type: 'otp' },
  { id: 'sp-15', label: 'Pay (15%)', percentage: 15, timing: 'OTP + 9 weeks', type: 'sp' },
  { id: 'top', label: 'Notice of Vacant Possession (TOP) (65%)', percentage: 65, timing: '~30 - 36 months', type: 'top' },
  { id: 'csc', label: 'Certificate of Statutory Completion (CSC) (15%)', percentage: 15, timing: '12 months after TOP', type: 'csc' },
];

// Special Bridging Loan (EC DPS, buyer still owns an HDB flat): banks lend up to
// 90% of the HDB's current valuation, less the outstanding HDB loan and resale levy.
const SBL_VALUATION_PCT = 0.9;
// EC DPS down payment (5% OTP + 15% S&P) — buyer-funded; the bridging loan can't cover it.
const EC_DPS_DOWN_PCT = 0.2;
const SBL_REFERENCE_URL = 'https://propertyreviewsg.com/ec-deferred-bridging-loan-explained/';
// New EC framework (MND, 8 May 2026) — applies to EC sites whose land tender closed on
// or after that date. Not yet modelled: shown as a disabled scheme option.
const EC_NEW_SCHEME_URL = 'https://www.hdb.gov.sg/buying-a-flat/executive-condominiums/eligibility';
// Field explainer dialog content: free-text body, optional comparison table and
// trailing note, plus a reference link.
interface FieldInfo {
  title: string;
  body: string;
  url: string;
  embed?: boolean;
  table?: { headers: string[]; rows: string[][] };
  footnote?: string;
}
const EC_SCHEME_TABLE: FieldInfo['table'] = {
  headers: ['', 'Current scheme', 'New scheme'],
  rows: [
    ['Applies to', 'EC land tenders closed before 8 May 2026 (incl. 5 sites already awarded)', 'EC land tenders closing on or after 8 May 2026'],
    ['Minimum Occupation Period', '5 years', '10 years (resale only to SC / SPR during MOP)'],
    ['Full privatisation', '10 years after TOP', '15 years after TOP'],
    ['Payment scheme', 'NPS or Deferred Payment Scheme (DPS)', 'NPS only — DPS removed'],
    ['First-timer allocation', '70% of units, 1-month priority', '90% of units, 2-year priority'],
    ['Income ceiling', '$16,000', '$18,000 (tenders closing on or after 24 Aug 2026)'],
  ],
};
const EC_NEW_SCHEME_INFO: FieldInfo = {
  title: 'New EC Scheme (not yet available)',
  body: 'Effective date: announced by MND on 8 May 2026. Applies to EC sites whose land tender closes on or after 8 May 2026.',
  table: EC_SCHEME_TABLE,
  footnote: 'This calculator models the current scheme. It will support the new scheme once these projects launch.',
  url: EC_NEW_SCHEME_URL,
};
const EC_SCHEME_COMPARE_INFO: FieldInfo = {
  title: 'EC Scheme: Current vs New',
  body: 'This calculator models the current scheme. The new scheme (MND, 8 May 2026) applies to EC sites whose land tender closes on or after 8 May 2026.',
  table: EC_SCHEME_TABLE,
  url: EC_NEW_SCHEME_URL,
};

// Backward-compat alias
const EC_STAGES: ScheduleStage[] = EC_NPS_STAGES;

// ─── HDB Types & Defaults ────────────────────────────────────────────────────
interface HdbInputs {
  purchasePrice: number;
  valuationPrice: number;
  loanSource: 'hdb' | 'bank' | 'none';
  hdbLoanAmount: number;
  hdbInterestRate: number;
  bankInterestRate: number;
  tenure: number;
  cpfOa: number;
  housingGrant: number;
  cpfResaleGrant: number;
  phgGrant: number;
  legalFee: number;
  valuationFee: number;
  hdbAdminFee: number;
  hpsAmount: number;
  caveatFee: number;
  // Agency service fee, entered either way round: agencyServiceFeePct is the commission
  // rate (before GST) and agencyServiceFee the dollar amount incl. 9% GST. Typing a %
  // re-derives the amount and keeps it tracking the purchase price; typing an amount
  // pins it (hdbAgencyFeeUserSet) and back-derives the % shown.
  agencyServiceFee: number;
  agencyServiceFeePct: number;
  // OTP deposit (HDB resale), paid in cash: Option Fee on grant of the OTP + Exercise Fee on
  // exercising it. Historically $1,000 + $4,000 = $5,000 but negotiable, so both are editable
  // (each ≥ $1). The two together form the "OTP deposit" used across the HDB cash flow.
  optionFee: number;
  exerciseFee: number;
  // Second HDB concessionary loan: HDB requires part of the cash proceeds from the
  // previous flat to be set aside toward the new purchase. This amount is deducted
  // from the buyer's available cash.
  isSecondHdbLoan: boolean;
  secondHdbLoanCashSetAside: number;
  otpDate: string;
}

const DEFAULT_HDB_INPUTS: HdbInputs = {
  purchasePrice: 0,
  valuationPrice: 0,
  loanSource: 'hdb',
  hdbLoanAmount: 0,
  hdbInterestRate: HDB_CONCESSIONARY_INTEREST_RATE,
  bankInterestRate: 2.18,
  tenure: 25,
  cpfOa: 0,
  housingGrant: 0,
  cpfResaleGrant: 0,
  phgGrant: 0,
  legalFee: 3000,
  valuationFee: 120,
  hdbAdminFee: 80,
  hpsAmount: 0,
  caveatFee: 200,
  agencyServiceFee: 0,
  agencyServiceFeePct: 1,
  optionFee: 1000,
  exerciseFee: 4000,
  isSecondHdbLoan: false,
  secondHdbLoanCashSetAside: 0,
  otpDate: new Date().toISOString().split('T')[0],
};

// ─── Second HDB Concessionary Loan: 50% Cash Proceeds Rule ────────────────────
// Before HDB grants a second housing loan, the full CPF refund plus part of the cash
// proceeds from the last flat must be put into the new purchase. The buyer keeps the
// GREATER of $25,000 or 50% of the cash proceeds; the balance is applied to the flat
// and reduces the loan quantum HDB grants. The two limits cross at $50,000 of proceeds.
// Source: https://www.mynicehome.gov.sg/get-started/hdb-loans-guide/
const SECOND_HDB_LOAN_MIN_RETAINED = 25_000;
const SECOND_HDB_LOAN_RETAINED_SHARE = 0.5;

function getSecondHdbLoanProceedsSplit(cashProceeds: number) {
  const proceeds = Math.max(0, Math.round(cashProceeds));
  // Capped at the proceeds themselves so a small sale is fully retained rather than
  // reporting a $25,000 "keep" the seller never received.
  const retained = Math.min(
    proceeds,
    Math.max(SECOND_HDB_LOAN_MIN_RETAINED, Math.round(proceeds * SECOND_HDB_LOAN_RETAINED_SHARE)),
  );
  return { proceeds, retained, mustUse: Math.max(0, proceeds - retained) };
}

const SECOND_HDB_LOAN_INFO = {
  title: '2nd HDB Loan — 50% Cash Proceeds Rule',
  body:
    'Before HDB grants a second concessionary housing loan, the full CPF refund and part of the cash proceeds from your last flat must go towards the new flat.\n\n'
    + 'You keep the GREATER of $25,000 or 50% of the cash proceeds (the deposit already received from your buyer counts inside that figure). The balance is applied to this purchase and reduces the loan HDB grants you.\n\n'
    + 'Cash proceeds = sale price − outstanding loan − CPF refund (with accrued interest) − selling expenses − resale levy, if any.\n\n'
    + 'Examples: $40,000 proceeds → keep $25,000, use $15,000. $100,000 proceeds → keep $50,000, use $50,000. Below $50,000 the $25,000 floor applies; above it the 50% share binds.\n\n'
    + 'Note: the amount you must use is not lost — it goes into the flat. But it is not spendable cash either, so it is excluded from your reserve funds.\n\n'
    + 'This applies only to a SECOND HDB concessionary loan. A bank loan carries no such requirement, and neither does your first HDB loan.',
  url: 'https://www.mynicehome.gov.sg/get-started/hdb-loans-guide/',
};

function getHdbResaleFinancing(
  purchasePrice: number,
  valuationPrice: number,
  loanSource: HdbInputs['loanSource'],
  otpDeposit: number,
  // Bank loan whose tenure runs past 25 yrs / age 65: MAS caps LTV at 55% and raises the
  // minimum cash to 10%. Never applies to an HDB concessionary loan.
  tenureStretched = false,
) {
  const effectiveValuation = valuationPrice > 0 ? valuationPrice : purchasePrice;
  const valuationBase = Math.min(purchasePrice, effectiveValuation);
  const cov = Math.max(0, purchasePrice - effectiveValuation);
  const bankStretched = loanSource === 'bank' && tenureStretched;
  const maxLoan = loanSource === 'none' ? 0 : Math.round(valuationBase * (bankStretched ? 0.55 : 0.75));
  const bankMinCash = loanSource === 'bank' ? Math.round(valuationBase * (bankStretched ? 0.10 : 0.05)) : 0;
  // For resale purchases, the option and exercise fees form part of the cash down
  // payment; do not add them again on top of the bank's 5% minimum cash requirement.
  const mandatoryCash = Math.max(otpDeposit, bankMinCash) + cov;

  return {
    valuationBase,
    cov,
    maxLoan,
    bankMinCash,
    mandatoryCash,
    totalDownPayment: Math.max(0, purchasePrice - maxLoan),
  };
}

// ─── Calculation Helpers ──────────────────────────────────────────────────────

const TDSR_RATIO = 0.55;
// MSR (Mortgage Servicing Ratio) caps monthly property-loan repayment at 30% of
// gross monthly income. It applies ONLY to loans for HDB flats and Executive
// Condominiums (EC) bought from a developer. For these property types the loan is
// capped by the LOWER of the MSR and TDSR limits.
// https://www.mas.gov.sg/regulation/explainers/new-housing-loans/msr-and-tdsr-rules
const MSR_RATIO = 0.30;
const PLEDGED_RECOGNITION = 1.0;
const UNPLEDGED_RECOGNITION = 0.30;
const RECOGNITION_MONTHS = 48;

function fmtInstallment(v: number): string {
  return new Intl.NumberFormat('en-SG', { style: 'currency', currency: 'SGD', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v);
}

function calcMonthlyInstallment(principal: number, annualRate: number, tenureYears: number): number {
  if (principal <= 0 || tenureYears <= 0) return 0;
  const r = annualRate / 100 / 12;
  const n = tenureYears * 12;
  if (r === 0) return principal / n;
  const factor = Math.pow(1 + r, n);
  return principal * (r * factor) / (factor - 1);
}

function calculateMaxLoan(monthlyPayment: number, annualRate: number, tenureYears: number): number {
  if (monthlyPayment <= 0 || tenureYears <= 0) return 0;
  const r = annualRate / 100 / 12;
  const n = tenureYears * 12;
  if (r === 0) return Math.round(monthlyPayment * n);
  return Math.round(monthlyPayment * (1 - Math.pow(1 + r, -n)) / r);
}

function addDays(date: Date, days: number): Date {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
}

/**
 * Gaps between consecutive HDB resale milestones, in calendar days. Each is
 * measured from the milestone before it (valuation and exercise both from the
 * OTP), so overriding one date carries the ones after it and leaves the ones
 * before it alone.
 *
 * Intervals follow HDB's published process:
 *  - Exercise OTP — 21 days from the OTP. The Option Period expires at 4pm on
 *    the 21st day after the OTP is granted.
 *  - Submit resale application — 14 days after exercising, so it can never fall
 *    before the exercise. HDB fixes no interval here (the OTP has both parties
 *    agree one), but the application cannot be submitted until the OTP has been
 *    exercised and the second party must follow within 7 calendar days. 14 days
 *    is also the Selling calculator's default, so both sides of the same
 *    transaction agree.
 *  - HDB acceptance — HDB notifies both parties of acceptance "within 28
 *    working days" of a complete application, which is 38–40 calendar days
 *    depending on where the weekends fall; 40 is the conservative end.
 *  - Completion — "about 8 weeks after HDB's acceptance of the resale
 *    application" (HDB).
 */
const HDB_RESALE_GAP_DAYS = {
  valuation: 1,
  exercise: 21,
  resaleApp: 14,
  acceptance: 40,
  completion: 56,
};

/** Milestones on the HDB resale schedule whose date the user can pin. */
type HdbMilestoneKey = keyof typeof HDB_RESALE_GAP_DAYS;

/** Explicit dates (YYYY-MM-DD) pinned by the user; absent → derived. */
type HdbDateOverrides = Partial<Record<HdbMilestoneKey, string>>;

/**
 * HDB resale schedule dates for an OTP date. A pinned date is used as-is and
 * becomes the anchor for everything downstream of it, so editing a late
 * milestone never moves an earlier one.
 */
function hdbResaleSchedule(otpDate: Date, overrides?: HdbDateOverrides) {
  const pinned = (key: HdbMilestoneKey, derived: Date): Date => {
    const iso = overrides?.[key];
    return iso ? new Date(iso + 'T00:00:00') : derived;
  };
  const valuation = pinned('valuation', addDays(otpDate, HDB_RESALE_GAP_DAYS.valuation));
  const exercise = pinned('exercise', addDays(otpDate, HDB_RESALE_GAP_DAYS.exercise));
  const resaleApp = pinned('resaleApp', addDays(exercise, HDB_RESALE_GAP_DAYS.resaleApp));
  const acceptance = pinned('acceptance', addDays(resaleApp, HDB_RESALE_GAP_DAYS.acceptance));
  const completion = pinned('completion', addDays(acceptance, HDB_RESALE_GAP_DAYS.completion));
  return { otp: otpDate, valuation, exercise, resaleApp, acceptance, completion };
}

function addMonths(date: Date, months: number): Date {
  const d = new Date(date);
  d.setMonth(d.getMonth() + months);
  return d;
}

// Whole calendar months from `from` to `to` (never negative).
function monthsBetween(from: Date, to: Date): number {
  return Math.max(0, (to.getFullYear() - from.getFullYear()) * 12 + (to.getMonth() - from.getMonth()));
}

// The stage at which a progressive loan is fully drawn. Its installment is what the
// buyer pays from then until the loan matures.
function finalLoanStage(stages: ComputedStage[]): ComputedStage | undefined {
  for (let i = stages.length - 1; i >= 0; i--) {
    if (stages[i].loanPayout > 0) return stages[i];
  }
  return undefined;
}

function formatDate(date: Date): string {
  return date.toLocaleDateString('en-SG', { day: 'numeric', month: 'short', year: 'numeric' });
}

// Local YYYY-MM-DD (for <input type="date">). Avoids the UTC day-shift that
// toISOString() causes for timezones ahead of UTC (e.g. Singapore, UTC+8).
function toISODate(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

function computeNewLaunchStages(
  inputs: BuyingInputs,
  stages: ScheduleStage[],
  spDate: Date,
  ltvDecimal: number,
  opts?: { cashDownAlloc?: number; cpfDownAlloc?: number; cashPoolAmt?: number; cpfPoolAmt?: number; minCashAmt?: number; bsdByCpf?: boolean; bsdCpfAmt?: number; bsdCashAmt?: number; bridgingAmount?: number; exerciseDateOverride?: string | null; sp15DateOverride?: string | null; stageDateOverrides?: Record<string, string> },
): ComputedStage[] {
  const price = inputs.purchasePrice;
  const otpAmount = Math.round(price * 0.05);
  const spAmount = Math.round(price * 0.15);
  const bsdAmount = calculateBsd(price);

  // Payment pools: use explicit allocation if provided, else fall back to inputs.
  // Min cash is 5% at the standard 75% LTV but 10% under the stretched-tenure
  // 55% LTV regime — the caller passes the actual amount so the buyer-funded
  // pool matches the Payment Structure (otherwise the loan silently absorbs
  // the difference and disburses beyond the LTV cap).
  const minCash = opts?.minCashAmt ?? Math.round(price * 0.05);
  let cashLeft = opts?.cashPoolAmt ?? (minCash + (opts?.cashDownAlloc ?? inputs.additionalCash));
  let cpfLeft = opts?.cpfPoolAmt ?? (opts?.cpfDownAlloc ?? (inputs.cpf + inputs.additionalCpf));
  // Bridging loan (EC DPS only): short-term financing disbursed at the TOP
  // milestone BEFORE the bank loan, so it reduces the bank loan while leaving the
  // buyer's own cash/CPF untouched.
  let bridgingLeft = opts?.bridgingAmount ?? 0;
  let cumulativeLoan = 0;
  let cumulativeBridging = 0;
  let cumulativeCash = 0;
  let cumulativeCpf = 0;
  // Bank rule for progressive loans: the tenure clock starts at the first disbursement,
  // and every later drawdown resets the installment to repay the OUTSTANDING balance
  // over the REMAINING tenure. Between drawdowns the balance amortises at the
  // installment then in force.
  const tenureMonths = Math.round(inputs.tenure * 12);
  const monthlyRate = inputs.interestRate / 100 / 12;
  let firstDrawDate: Date | null = null;
  let lastDate: Date | null = null;
  let outstanding = 0;
  let installment = 0;
  const result: ComputedStage[] = [];

  // Dates — the Pay Stamp Duty and Pay (15%) milestones default to fixed offsets from
  // the OTP/S&P date, but the user can override each with an explicit date. Stamp duty
  // (BSD/ABSD) is due within 14 days of exercising the S&P (~3 weeks after booking), i.e.
  // ~booking + 38 days — matching the PDF report's "Pay Stamp Duty" milestone.
  const otpDate = new Date(spDate);
  const stampDutyDate = opts?.exerciseDateOverride ? new Date(opts.exerciseDateOverride + 'T00:00:00') : addDays(spDate, 38);
  const sp15Date = opts?.sp15DateOverride ? new Date(opts.sp15DateOverride + 'T00:00:00') : addDays(spDate, 56);
  // Construction milestones are measured from launch (OTP date): the first stage
  // (Foundation) falls ~6 months from launch, matching the investment timeline.
  let constructionBase = new Date(otpDate);

  const drawFromPools = (amount: number, allowLoan: boolean, allowBridging = false) => {
    const fromCash = Math.min(amount, cashLeft);
    cashLeft -= fromCash;
    let need1 = amount - fromCash;
    const fromCpf = Math.min(need1, cpfLeft);
    cpfLeft -= fromCpf;
    need1 -= fromCpf;
    // Bridging loan (EC DPS) disburses only at the TOP milestone, before the bank
    // loan, so it reduces the bank loan rather than the buyer's own cash/CPF.
    const fromBridging = allowBridging ? Math.min(need1, bridgingLeft) : 0;
    bridgingLeft -= fromBridging;
    need1 -= fromBridging;
    const fromLoan = allowLoan ? need1 : 0;
    // If loan not allowed and pools insufficient, residual falls back to cash (buyer must top up)
    const fromCashTopUp = !allowLoan ? need1 : 0;
    return { fromCash: fromCash + fromCashTopUp, fromCpf, fromLoan, fromBridging };
  };

  stages.forEach((stage, i) => {
    let paymentAmount = 0;
    let loanPayout = 0;
    let bridgingPayout = 0;
    let cashUsed = 0;
    let cpfUsed = 0;
    let estimatedDate: Date | null = null;
    let subLabel = '';
    let subAmountLabel = '';

    switch (stage.type) {
      case 'otp': {
        paymentAmount = otpAmount;
        estimatedDate = otpDate;
        // Option fee: practically cash-only, but draw from CPF if buyer has none left.
        const draw = drawFromPools(paymentAmount, false);
        cashUsed = draw.fromCash;
        cpfUsed = draw.fromCpf;
        subLabel = 'Booking Fee';
        subAmountLabel = '';
        break;
      }
      case 'sp-bsd': {
        paymentAmount = bsdAmount;
        estimatedDate = stampDutyDate;
        if (opts?.bsdByCpf) {
          cpfUsed = opts.bsdCpfAmt ?? 0;
          cashUsed = opts.bsdCashAmt ?? bsdAmount;
        } else {
          cashUsed = bsdAmount;
        }
        subLabel = 'Buyer Stamp Duty';
        subAmountLabel = `S$${bsdAmount.toLocaleString('en-SG')}`;
        break;
      }
      case 'sp': {
        paymentAmount = spAmount;
        estimatedDate = sp15Date;
        // 15% downpayment from cash/CPF; bank doesn't disburse yet.
        const draw = drawFromPools(paymentAmount, false);
        cashUsed = draw.fromCash;
        cpfUsed = draw.fromCpf;
        subLabel = 'Cash / CPF';
        subAmountLabel = `S$${spAmount.toLocaleString('en-SG')}`;
        break;
      }
      default: {
        // construction / top / csc
        paymentAmount = Math.round(price * stage.percentage / 100);
        const monthMatch = stage.timing.match(/(\d+)\s*(?:-\s*\d+)?\s*month/i);
        const timingMonths = monthMatch ? parseInt(monthMatch[1], 10) : 12;
        estimatedDate = addMonths(constructionBase, timingMonths);


        // Bridging loan (EC DPS) disburses only at the TOP milestone.
        const draw = drawFromPools(paymentAmount, inputs.hasBankLoan, stage.type === 'top');
        cashUsed = draw.fromCash;
        cpfUsed = draw.fromCpf;
        bridgingPayout = draw.fromBridging;
        cumulativeBridging += bridgingPayout;
        loanPayout = draw.fromLoan;
        cumulativeLoan += loanPayout;
        subLabel = loanPayout > 0 ? 'Payout' : '';
        subAmountLabel = loanPayout > 0 ? `S$${loanPayout.toLocaleString('en-SG')}` : '';
        break;
      }
    }

    const override = opts?.stageDateOverrides?.[stage.id];
    if (override && stage.type !== 'otp') estimatedDate = new Date(override + 'T00:00:00');
    // Resets and changes to the anchor must also preserve chronological order.
    if (estimatedDate && lastDate && estimatedDate < lastDate) estimatedDate = new Date(lastDate);
    if (estimatedDate && !['otp', 'sp-bsd', 'sp'].includes(stage.type)) {
      constructionBase = new Date(estimatedDate);
    }

    cumulativeCash += cashUsed;
    cumulativeCpf += cpfUsed;

    // Repay the outstanding balance month by month up to this stage's date.
    if (outstanding > 0 && lastDate && estimatedDate) {
      for (let m = monthsBetween(lastDate, estimatedDate); m > 0 && outstanding > 0; m--) {
        outstanding = Math.max(0, outstanding * (1 + monthlyRate) - installment);
      }
    }
    if (estimatedDate) lastDate = estimatedDate;
    if (loanPayout > 0) {
      if (!firstDrawDate) firstDrawDate = estimatedDate;
      outstanding += loanPayout;
    }
    const remainingTenureMonths = firstDrawDate
      ? Math.max(1, tenureMonths - (estimatedDate ? monthsBetween(firstDrawDate, estimatedDate) : 0))
      : 0;
    if (loanPayout > 0) {
      installment = calcMonthlyInstallment(outstanding, inputs.interestRate, remainingTenureMonths / 12);
    }

    result.push({
      ...stage,
      stageIndex: i,
      paymentAmount,
      loanPayout,
      bridgingPayout,
      cumulativeBridging,
      buyerCash: cashUsed + cpfUsed,
      cashUsed,
      cpfUsed,
      cumulativeCash,
      cumulativeCpf,
      cumulativeLoan,
      outstandingLoan: Math.round(outstanding),
      remainingTenureMonths,
      monthlyInstallment: installment,
      estimatedDate,
      subLabel,
      subAmountLabel,
    });
  });

  return result;
}

function computeResaleStages(inputs: BuyingInputs, stages: ScheduleStage[], spDate: Date, ltvDecimal: number,
  opts?: { cashDownAlloc?: number; cpfDownAlloc?: number; cashPoolAmt?: number; cpfPoolAmt?: number; minCashAmt?: number }): ComputedStage[] {
  const price = inputs.purchasePrice;
  const totalLoan = Math.round(price * ltvDecimal);

  // Pools (CASH → CPF → LOAN). Min cash follows the LTV regime (5% @ 75%,
  // 10% @ 55% stretched tenure) — passed in by the caller.
  const minCash = opts?.minCashAmt ?? Math.round(price * 0.05);
  let cashLeft = opts?.cashPoolAmt ?? (minCash + (opts?.cashDownAlloc ?? inputs.additionalCash));
  let cpfLeft = opts?.cpfPoolAmt ?? (opts?.cpfDownAlloc ?? (inputs.cpf + inputs.additionalCpf));
  let cumulativeLoan = 0;
  let cumulativeCash = 0;
  let cumulativeCpf = 0;

  const drawFromPools = (amount: number, allowLoan: boolean) => {
    const fromCash = Math.min(amount, cashLeft);
    cashLeft -= fromCash;
    const need1 = amount - fromCash;
    const fromCpf = Math.min(need1, cpfLeft);
    cpfLeft -= fromCpf;
    const fromLoan = allowLoan ? amount - fromCash - fromCpf : 0;
    const fromCashTopUp = !allowLoan ? amount - fromCash - fromCpf : 0;
    return { fromCash: fromCash + fromCashTopUp, fromCpf, fromLoan };
  };

  const result: ComputedStage[] = [];
  stages.forEach((stage, i) => {
    const paymentAmount = Math.round(price * stage.percentage / 100);
    let loanPayout = 0;
    let cashUsed = 0;
    let cpfUsed = 0;
    let estimatedDate: Date | null = null;
    let subLabel = '';
    let subAmountLabel = '';

    if (stage.type === 'otp') {
      estimatedDate = new Date(spDate);
      subLabel = 'Option Fee';
      const draw = drawFromPools(paymentAmount, false);
      cashUsed = draw.fromCash;
      cpfUsed = draw.fromCpf;
    } else if (stage.type === 'sp') {
      estimatedDate = addDays(spDate, 14);
      subLabel = 'Exercise Option';
      const draw = drawFromPools(paymentAmount, false);
      cashUsed = draw.fromCash;
      cpfUsed = draw.fromCpf;
    } else {
      estimatedDate = addDays(spDate, 84);
      subLabel = 'Balance Payment';
      const draw = drawFromPools(paymentAmount, inputs.hasBankLoan);
      cashUsed = draw.fromCash;
      cpfUsed = draw.fromCpf;
      loanPayout = draw.fromLoan;
      cumulativeLoan += loanPayout;
    }

    cumulativeCash += cashUsed;
    cumulativeCpf += cpfUsed;

    result.push({
      ...stage,
      stageIndex: i,
      paymentAmount,
      loanPayout,
      bridgingPayout: 0,
      cumulativeBridging: 0,
      buyerCash: cashUsed + cpfUsed,
      cashUsed,
      cpfUsed,
      cumulativeCash,
      cumulativeCpf,
      cumulativeLoan,
      outstandingLoan: cumulativeLoan,
      remainingTenureMonths: cumulativeLoan > 0 ? Math.round(inputs.tenure * 12) : 0,
      monthlyInstallment: stage.type === 'csc' ? calcMonthlyInstallment(totalLoan, inputs.interestRate, inputs.tenure) : 0,
      estimatedDate,
      subLabel,
      subAmountLabel,
    });
  });

  return result;
}

// ─── Simple-mode labelling ─────────────────────────────────────────────────────
// Simple mode shows a single "Amount" instead of a Cash / CPF split — we don't know the
// buyer's funding mix. Only the mandatory min-cash is labelled "Cash"; everything else is
// "CPF/Cash". These helpers relabel the down-payment milestones and split each stage's
// buyer-funded amount into the must-be-cash portion and the flexible (CPF/Cash) remainder.
function simpleStageLabel(label: string): string {
  if (label.startsWith('OTP (Option Fee 5%)')) return 'Booking Fee (5%)';
  if (label.startsWith('Pay (15%)')) return 'Pay 15%';
  return label;
}

function augmentSimpleFunding(stages: ComputedStage[], price: number, minCashPct: number): ComputedStage[] {
  const otpAmount = Math.round(price * 0.05);
  const mandatoryCash = Math.round(price * (minCashPct / 100));
  let cumMandatory = 0;
  let cumFlex = 0;
  return stages.map((s) => {
    const buyerFunded = s.cashUsed + s.cpfUsed;
    // The 5% booking fee is always mandatory cash; the S&P milestone tops the mandatory
    // cash up to the required minimum (a further 5% under the 55% LTV / 10% regime).
    // Everything else (BSD, construction top-ups) is flexible — payable by CPF or cash.
    let mandatory = 0;
    if (s.type === 'otp') mandatory = Math.min(buyerFunded, mandatoryCash);
    else if (s.type === 'sp') mandatory = Math.min(buyerFunded, Math.max(0, mandatoryCash - otpAmount));
    const flex = Math.max(0, buyerFunded - mandatory);
    cumMandatory += mandatory;
    cumFlex += flex;
    return { ...s, simpleMandatoryCash: mandatory, simpleFlex: flex, cumSimpleMandatoryCash: cumMandatory, cumSimpleFlex: cumFlex };
  });
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function snapToStep(value: number, min: number, max: number, step: number): number {
  const clamped = Math.min(max, Math.max(min, value));
  if (step <= 0) return clamped;
  const n = Math.round((clamped - min) / step);
  let snapped = min + n * step;
  const stepStr = step.toString();
  const dot = stepStr.indexOf('.');
  const decimals = dot === -1 ? 0 : stepStr.length - dot - 1;
  if (decimals > 0) snapped = Math.round(snapped * 10 ** decimals) / 10 ** decimals;
  return Math.min(max, Math.max(min, snapped));
}

function parseFlexibleNumber(s: string): number | null {
  const cleaned = s.replace(/,/g, '').replace(/\s/g, '').trim();
  if (cleaned === '' || cleaned === '-') return null;
  const n = parseFloat(cleaned.replace(/[^0-9.-]/g, ''));
  return Number.isFinite(n) ? n : null;
}

function formatDraftForEditing(v: number, step: number): string {
  if (step < 1 && step > 0) return String(Number(v.toFixed(2)));
  return String(Math.round(v));
}

function formatCurrencyDraftForInput(amount: number): string {
  return new Intl.NumberFormat('en-SG', {
    style: 'currency', currency: 'SGD',
    minimumFractionDigits: 0, maximumFractionDigits: 0,
  }).format(amount);
}

// ─── EditableSliderValueRow ────────────────────────────────────────────────────

const EditableSliderValueRow: React.FC<{
  label: React.ReactNode;
  value: number;
  min: number;
  max: number;
  step: number;
  onCommit: (v: number) => void;
  formatDisplay: (v: number) => string;
  valueColor: string;
  currencyInput?: boolean;
  commitMax?: number;
}> = ({ label, value, min, max, step, onCommit, formatDisplay, valueColor, currencyInput = false, commitMax }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const skipBlurRef = useRef(false);

  const commit = () => {
    if (skipBlurRef.current) { skipBlurRef.current = false; return; }
    const raw = parseFlexibleNumber(draft);
    if (raw !== null) onCommit(snapToStep(raw, min, commitMax ?? max, step));
    setEditing(false);
  };

  const startEdit = () => {
    skipBlurRef.current = false;
    setDraft(currencyInput ? formatCurrencyDraftForInput(Math.round(value)) : formatDraftForEditing(value, step));
    setEditing(true);
  };

  const handleDraftChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (currencyInput) {
      const digits = e.target.value.replace(/\D/g, '');
      if (digits === '') { setDraft(''); return; }
      const n = parseInt(digits, 10);
      if (!Number.isFinite(n)) return;
      setDraft(formatCurrencyDraftForInput(n));
      return;
    }
    setDraft(e.target.value);
  };

  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5, gap: 1, flexWrap: 'wrap' }}>
      <Box sx={{ flex: 1, minWidth: 0 }}>{label}</Box>
      {editing ? (
        <TextField
          size="small"
          value={draft}
          onChange={handleDraftChange}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === 'Enter') { e.preventDefault(); (e.target as HTMLInputElement).blur(); }
            if (e.key === 'Escape') { e.preventDefault(); skipBlurRef.current = true; setEditing(false); }
          }}
          autoFocus
          sx={{ width: currencyInput ? { xs: 150, sm: 180 } : { xs: 130, sm: 150 } }}
          inputProps={{ inputMode: currencyInput ? 'numeric' : 'decimal' }}
        />
      ) : (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0, flexShrink: 0 }}>
          <Typography variant="body2" fontWeight={700} sx={{ color: valueColor }}>{formatDisplay(value)}</Typography>
          <IconButton size="small" onClick={startEdit} sx={{ p: 0.35, ml: 0.25 }}>
            <EditIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>
      )}
    </Box>
  );
};

// ─── TenureInlineEditor ───────────────────────────────────────────────────────
// Loan-tenure row: label on the left, whole-year value + pencil on the right. Out-of-range
// or non-numeric input is rejected with an error line under the row (the field stays
// open) instead of being silently clamped.
const TenureInlineEditor: React.FC<{
  label: React.ReactNode;
  value: number;
  min: number;
  max: number;
  color: string;
  /** Shown when the value is above `max`, e.g. "Max 35 yrs (stretched)". */
  maxMessage: string;
  /** Extra guidance shown under the field while the value is valid. */
  hint?: string;
  onCommit: (v: number) => void;
}> = ({ label, value, min, max, color, maxMessage, hint, onCommit }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const n = Number(draft);
  const error = draft.trim() === '' ? 'Enter a tenure'
    : n < min ? `Min ${min} yrs`
      : n > max ? maxMessage
        : '';
  const commit = () => {
    if (error) return;
    onCommit(n);
    setEditing(false);
  };
  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5 }}>
        <Box sx={{ minWidth: 0, flex: 1 }}>{label}</Box>
        {editing ? (
          <TextField
            size="small"
            value={draft}
            onChange={(e) => setDraft(e.target.value.replace(/[^\d]/g, ''))}
            onKeyDown={(e) => {
              if (e.key === 'Enter') { e.preventDefault(); commit(); }
              if (e.key === 'Escape') { e.preventDefault(); setEditing(false); }
            }}
            onBlur={() => { if (error) setEditing(false); else commit(); }}
            error={!!error}
            autoFocus
            InputProps={{ endAdornment: <Typography sx={{ fontSize: '0.8rem', color: 'text.secondary', ml: 0.5 }}>yrs</Typography> }}
            inputProps={{ inputMode: 'numeric', 'aria-label': 'Loan tenure in years', style: { textAlign: 'right' } }}
            sx={{ width: 104, flexShrink: 0 }}
          />
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
            <Typography sx={{ fontSize: '1rem', fontWeight: 800, color }}>{value} yrs</Typography>
            <IconButton size="small" aria-label="Edit loan tenure" onClick={() => { setDraft(String(value)); setEditing(true); }} sx={{ p: 0.35, ml: 0.25 }}>
              <EditIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>
        )}
      </Box>
      {editing && (
        <Typography variant="caption" role={error ? 'alert' : undefined}
          sx={{ display: 'block', mt: 0.5, textAlign: 'right', color: error ? 'error.main' : 'text.secondary', fontWeight: error ? 600 : 400 }}>
          {error || `Allowed: ${min}–${max} yrs${hint ? ` · ${hint}` : ''} · Enter to save`}
        </Typography>
      )}
    </Box>
  );
};

// ─── IntentGuardedSlider ──────────────────────────────────────────────────────

const MOBILE_SLIDER_INTENT_THRESHOLD_PX = 8;

// Same slider treatment as the Maximizer (AffordabilityCalculator): on touch devices
// the knob stays "dropped" (inactive) until the touch establishes horizontal intent —
// taps and vertical scroll-gestures over the track never move the value, preventing
// accidental changes while scrolling the page. The guard activates on small viewports
// (isMobile) and on any coarse-pointer device, so tablets are covered at every
// orientation/width; only touch-originated changes are gated, so mouse and keyboard
// input always passes through.
const IntentGuardedSlider: React.FC<React.ComponentProps<typeof Slider> & { isMobile: boolean }> = ({
  isMobile,
  onChange,
  sx,
  disabled,
  ...props
}) => {
  const isCoarsePointer = useMediaQuery('(pointer: coarse)');
  const guarded = isMobile || isCoarsePointer;
  const touchStateRef = useRef<{
    startX: number;
    startY: number;
    intent: 'pending' | 'horizontal' | 'vertical';
  } | null>(null);
  const [dragReady, setDragReady] = useState(!guarded);

  useEffect(() => {
    setDragReady(!guarded);
    if (!guarded) {
      touchStateRef.current = null;
    }
  }, [guarded]);

  const resetTouch = () => {
    touchStateRef.current = null;
    if (guarded) setDragReady(false);
  };

  const handleTouchStart: React.TouchEventHandler<HTMLSpanElement> = (event) => {
    if (!guarded || disabled) return;
    const touch = event.touches[0];
    touchStateRef.current = {
      startX: touch.clientX,
      startY: touch.clientY,
      intent: 'pending',
    };
    setDragReady(false);
  };

  const handleTouchMove: React.TouchEventHandler<HTMLSpanElement> = (event) => {
    if (!guarded || disabled) return;
    const state = touchStateRef.current;
    if (!state) return;

    const touch = event.touches[0];
    const dx = Math.abs(touch.clientX - state.startX);
    const dy = Math.abs(touch.clientY - state.startY);

    if (state.intent === 'pending') {
      if (dx < MOBILE_SLIDER_INTENT_THRESHOLD_PX && dy < MOBILE_SLIDER_INTENT_THRESHOLD_PX) return;
      if (dx > dy) {
        state.intent = 'horizontal';
        setDragReady(true);
      } else {
        state.intent = 'vertical';
        setDragReady(false);
      }
      touchStateRef.current = state;
    }
  };

  const handleChange: NonNullable<React.ComponentProps<typeof Slider>['onChange']> = (
    event,
    value,
    activeThumb
  ) => {
    if (!onChange) return;
    if (!guarded) {
      onChange(event, value, activeThumb);
      return;
    }
    const isTouchEvent = typeof event?.type === 'string' && event.type.startsWith('touch');
    if (isTouchEvent && touchStateRef.current?.intent !== 'horizontal') return;
    onChange(event, value, activeThumb);
  };

  return (
    <Slider
      {...props}
      disabled={disabled}
      onChange={handleChange}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={resetTouch}
      onTouchCancel={resetTouch}
      sx={[
        {
          touchAction: guarded ? 'pan-y' : 'auto',
          '& .MuiSlider-thumb': guarded
            ? {
                transition: 'transform 120ms ease',
                transform: dragReady
                  ? 'translate(-50%, -50%) scale(1.05)'
                  : 'translate(-50%, -50%) scale(1)',
              }
            : undefined,
        },
        ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
      ]}
    />
  );
};

// ─── HdbAllocField — draft input capped at available, commits on blur/Enter ───

const HdbAllocField: React.FC<{
  value: number; cap: number; onChange: (v: number) => void;
  highlightColor: string; disabled?: boolean; base?: number;
  /** Overrides the internal cap check — lets the caller flag a shortfall even when cap is $0 (mirrors Private's FRow). */
  exceeds?: boolean;
}> = ({ value, cap, onChange, highlightColor, disabled, base = 0, exceeds: exceedsProp }) => {
  const [amtDraft, setAmtDraft] = React.useState('');
  const [pctDraft, setPctDraft] = React.useState('');
  const [editingAmt, setEditingAmt] = React.useState(false);
  const [editingPct, setEditingPct] = React.useState(false);
  const exceeds = exceedsProp ?? (value > cap && cap > 0);
  const pctVal = base > 0 ? (value / base * 100) : 0;

  const commitAmt = () => {
    const n = parseInt(amtDraft.replace(/[^0-9]/g, ''), 10);
    onChange(isNaN(n) ? 0 : n);
    setEditingAmt(false);
  };
  const commitPct = () => {
    setEditingPct(false);
    const p = parseFloat(pctDraft);
    if (isNaN(p) || base <= 0) return;
    // Untouched draft (tap in / tap out) — the 1-dp % display is lossy, so
    // re-deriving the amount from it would silently shift the value.
    if (pctDraft === (pctVal === 0 ? '' : pctVal.toFixed(1))) return;
    let n = Math.round(base * p / 100);
    // Nearby amounts can read as the same 1-dp % (e.g. $138,400 and $138,500 both
    // show 27.7%). If the typed % matches the cap's displayed %, commit the cap.
    if (cap > 0 && n > cap && p.toFixed(1) === (cap / base * 100).toFixed(1)) n = cap;
    onChange(n);
  };

  const fieldSx = {
    '& .MuiOutlinedInput-root': {
      bgcolor: disabled ? 'action.disabledBackground' : undefined,
      '& fieldset': { borderColor: exceeds ? '#ef4444' : undefined },
      '&.Mui-focused fieldset': { borderColor: exceeds ? '#ef4444' : highlightColor },
    },
  };

  return (
    <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center' }}>
      {/* % input */}
      <TextField
        size="small"
        disabled={disabled}
        value={editingPct ? pctDraft : (pctVal === 0 ? '' : pctVal.toFixed(1))}
        placeholder="0"
        inputProps={{ inputMode: 'decimal' }}
        onFocus={() => { setPctDraft(pctVal === 0 ? '' : pctVal.toFixed(1)); setEditingPct(true); }}
        onChange={(e) => setPctDraft(e.target.value.replace(/[^0-9.]/g, ''))}
        onBlur={commitPct}
        onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
        InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
        sx={{ width: 80, '& input': { textAlign: 'right', py: '6px', fontSize: '0.82rem', fontWeight: 600 }, ...fieldSx }}
      />
      {/* $ input */}
      <TextField
        size="small"
        disabled={disabled}
        value={editingAmt ? amtDraft : (value === 0 ? '' : value.toLocaleString('en-SG'))}
        placeholder="0"
        inputProps={{ inputMode: 'numeric' }}
        onFocus={() => { setAmtDraft(value === 0 ? '' : String(value)); setEditingAmt(true); }}
        onChange={(e) => setAmtDraft(e.target.value.replace(/[^0-9]/g, ''))}
        onBlur={commitAmt}
        onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
        InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
        sx={{ width: 130, '& input': { textAlign: 'right', py: '6px', fontSize: '0.85rem', fontWeight: 700 }, ...fieldSx }}
      />
    </Box>
  );
};

// ─── AgencyFeeField — agency service fee as a % of price or a $ amount ───────

/**
 * Two views of the same fee. Typing a percentage re-derives the amount (and lets it
 * keep tracking the purchase price); typing an amount pins it and back-derives the %.
 * Each field commits on blur/Enter, and an untouched draft commits nothing so tapping
 * through the row never flips which side is driving the fee.
 */
const AgencyFeeField: React.FC<{
  pct: number;
  amount: number;
  highlightColor: string;
  onPctChange: (v: number) => void;
  onAmountChange: (v: number) => void;
}> = ({ pct, amount, highlightColor, onPctChange, onAmountChange }) => {
  const [pctDraft, setPctDraft] = React.useState('');
  const [amtDraft, setAmtDraft] = React.useState('');
  const [editingPct, setEditingPct] = React.useState(false);
  const [editingAmt, setEditingAmt] = React.useState(false);
  // Back-derived percentages are rarely round, so show up to 2 dp without trailing zeros.
  const pctDisplay = pct === 0 ? '' : String(Math.round(pct * 100) / 100);
  const amtDisplay = amount === 0 ? '' : amount.toLocaleString('en-SG');

  const commitPct = () => {
    setEditingPct(false);
    if (pctDraft === pctDisplay) return;
    const p = parseFloat(pctDraft);
    onPctChange(isNaN(p) ? 0 : Math.max(0, p));
  };
  const commitAmt = () => {
    setEditingAmt(false);
    if (amtDraft === amtDisplay) return;
    const n = parseInt(amtDraft.replace(/[^0-9]/g, ''), 10);
    onAmountChange(isNaN(n) ? 0 : n);
  };

  const fieldSx = {
    '& .MuiOutlinedInput-root': { '&.Mui-focused fieldset': { borderColor: highlightColor } },
  };

  return (
    <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center', flexShrink: 0 }}>
      <TextField
        size="small"
        value={editingPct ? pctDraft : pctDisplay}
        placeholder="0"
        inputProps={{ inputMode: 'decimal' }}
        onFocus={() => { setPctDraft(pctDisplay); setEditingPct(true); }}
        onChange={(e) => setPctDraft(e.target.value.replace(/[^0-9.]/g, ''))}
        onBlur={commitPct}
        onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
        InputProps={{ endAdornment: <InputAdornment position="end" sx={{ '& p': { fontSize: '0.75rem' } }}>%</InputAdornment> }}
        sx={{ width: { xs: 68, sm: 78 }, '& input': { textAlign: 'right', fontWeight: 600, px: 0.75 }, ...fieldSx }}
      />
      <TextField
        size="small"
        value={editingAmt ? amtDraft : amtDisplay}
        placeholder="0"
        inputProps={{ inputMode: 'numeric' }}
        onFocus={() => { setAmtDraft(amtDisplay); setEditingAmt(true); }}
        onChange={(e) => setAmtDraft(e.target.value.replace(/[^0-9,]/g, ''))}
        onBlur={commitAmt}
        onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
        InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
        sx={{ width: { xs: 116, sm: 132 }, '& input': { textAlign: 'right', fontWeight: 600 }, ...fieldSx }}
      />
    </Box>
  );
};

// ─── BuyerBorrowerSection ─────────────────────────────────────────────────────

const BuyerBorrowerSection: React.FC<{
  title: string;
  borrower: BorrowerInputs;
  onUpdate: (field: keyof BorrowerInputs, value: number | boolean | string | undefined) => void;
  cardBg: string;
  cardBorder: string;
  highlightColor: string;
  isMobile: boolean;
  fmt: (v: number) => string;
  onRemove?: () => void;
  citizenship?: CitizenshipType;
  ftaEligible?: boolean;
  propertyCount?: PropertyCount;
  onCitizenshipChange?: (v: CitizenshipType) => void;
  onFtaEligibleChange?: (v: boolean) => void;
  onPropertyCountChange?: (v: PropertyCount) => void;
  // EC: no ABSD, so the count tops out at "2nd" (a stored "3rd+" shows as "2nd").
  hideThirdProperty?: boolean;
}> = ({ title, borrower, onUpdate, cardBg, cardBorder, highlightColor, isMobile, fmt, onRemove,
        citizenship, ftaEligible, propertyCount, onCitizenshipChange, onFtaEligibleChange, onPropertyCountChange,
        hideThirdProperty = false }) => {
  const recognisedIncome = Math.round(
    calculateAssessableIncome(String(borrower.monthlyIncome), String(borrower.otherAnnualIncome), borrower.isSelfEmployed ? 1 : 0)
  );
  const otherHaircutted = Math.round(borrower.otherAnnualIncome * 0.7 / 12);

  return (
    <Card variant="outlined" sx={{
      p: 2, bgcolor: cardBg, borderColor: cardBorder, mb: 2,
      borderTop: `2px solid ${highlightColor}`,
      position: 'relative',
      overflow: 'visible',
    }}>
      {onRemove && (
        <IconButton
          size="small"
          onClick={onRemove}
          aria-label="Remove borrower"
          sx={{
            position: 'absolute',
            top: -10,
            right: -10,
            width: 24,
            height: 24,
            bgcolor: 'background.paper',
            border: '1px solid',
            borderColor: 'divider',
            color: 'text.secondary',
            boxShadow: 1,
            transition: 'all 120ms ease',
            '&:hover': {
              color: '#fff',
              bgcolor: 'error.main',
              borderColor: 'error.main',
            },
          }}
        >
          <CloseIcon sx={{ fontSize: 14 }} />
        </IconButton>
      )}
      <Typography variant="subtitle2" fontWeight={800} sx={{ mb: 2, fontSize: '0.82rem', color: 'text.primary', letterSpacing: '0.01em' }}>{title}</Typography>

      {/* Name — shown in the card header and used when saving as a profile */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 2 }}>
        <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.82rem', flexShrink: 0 }}>Name</Typography>
        <TextField
          size="small"
          placeholder="e.g. Adam Tan"
          value={borrower.name ?? ''}
          onChange={(e) => onUpdate('name', e.target.value)}
          sx={{ width: 180, '& .MuiInputBase-input': { fontSize: '0.85rem', textAlign: 'right', py: 0.9 } }}
        />
      </Box>

      <Divider sx={{ mb: 1.5, opacity: 0.5 }} />

      {/* Age */}
      <Box sx={{ mb: 2 }}>
        <EditableSliderValueRow
          label={<Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.82rem', color: 'text.primary' }}>Age</Typography>}
          value={borrower.age} min={21} max={70} step={1}
          onCommit={(v) => onUpdate('age', v)}
          formatDisplay={(v) => `${v} yrs`}
          valueColor={highlightColor}
        />
        <Box sx={{ pl: 1.5, pr: 3 }}>
          <IntentGuardedSlider
            isMobile={isMobile} value={borrower.age} min={21} max={70} step={1}
            onChange={(_, v) => onUpdate('age', v as number)}
            sx={{ color: highlightColor, height: 6 }}
          />
        </Box>
      </Box>

      <Divider sx={{ mb: 1.5, opacity: 0.5 }} />

      {/* Monthly Income */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 1 }}>
        <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.82rem', flexShrink: 0 }}>Monthly Income</Typography>
        <Box sx={{ width: 180 }}><CurrencyTextField value={borrower.monthlyIncome} onChange={(v) => onUpdate('monthlyIncome', v)} highlightColor={highlightColor} /></Box>
      </Box>

      <Divider sx={{ mb: 1, opacity: 0.5 }} />

      {/* Est. monthly CPF (OA) — auto-estimated from age & income, editable */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, mb: 0.5 }}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.82rem' }}>Est. monthly CPF (OA)</Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
            {borrower.cpfOaMonthly === undefined
              ? 'Auto from age & income — editable'
              : (
                <Box component="span"
                  onClick={() => onUpdate('cpfOaMonthly', undefined)}
                  sx={{ cursor: 'pointer', color: highlightColor, textDecoration: 'underline' }}
                >
                  Edited — reset to auto
                </Box>
              )}
          </Typography>
        </Box>
        <Box sx={{ width: 180, flexShrink: 0 }}>
          <CurrencyTextField value={getMonthlyCpfOa(borrower)} onChange={(v) => onUpdate('cpfOaMonthly', v)} highlightColor={highlightColor} />
        </Box>
      </Box>

      <Divider sx={{ mb: 1, opacity: 0.5 }} />

      {/* Other Annual Income */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, mb: 0.5 }}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.82rem' }}>Other Annual Income</Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
            30% haircut — recognised: {fmt(otherHaircutted)}/mo
          </Typography>
        </Box>
        <Box sx={{ width: 180, flexShrink: 0 }}><CurrencyTextField value={borrower.otherAnnualIncome} onChange={(v) => onUpdate('otherAnnualIncome', v)} highlightColor={highlightColor} /></Box>
      </Box>

      <Divider sx={{ mb: 1, opacity: 0.5 }} />

      {/* Self-employed */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 1 }}>
        <FormControlLabel
          sx={{ m: 0 }}
          control={<Checkbox checked={borrower.isSelfEmployed} onChange={(e) => onUpdate('isSelfEmployed', e.target.checked)} size="small" sx={{ p: 0.5 }} />}
          label={<Typography variant="body2" sx={{ fontSize: '0.82rem' }}>Self-employed</Typography>}
        />
        <Typography variant="caption" sx={{ color: 'text.secondary', textAlign: 'right', flexShrink: 0 }}>
          Recognised: {fmt(recognisedIncome)}
        </Typography>
      </Box>

      <Divider sx={{ mb: 1, opacity: 0.5 }} />

      {/* Existing Monthly Debt */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 0.5 }}>
        <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.82rem', flexShrink: 0 }}>Existing Monthly Debt</Typography>
        <Box sx={{ width: 180 }}><CurrencyTextField value={borrower.existingMonthlyDebt} onChange={(v) => onUpdate('existingMonthlyDebt', v)} highlightColor={highlightColor} /></Box>
      </Box>

      <Divider sx={{ mb: 1, mt: 1, opacity: 0.5 }} />

      {/* This borrower's own funds — summed into Available Funds */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, mb: 0.5 }}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.82rem' }}>Cash / Saving</Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>Counts towards Available Funds</Typography>
        </Box>
        <Box sx={{ width: 180, flexShrink: 0 }}><CurrencyTextField value={borrower.cash} onChange={(v) => onUpdate('cash', v)} highlightColor={highlightColor} /></Box>
      </Box>

      <Divider sx={{ mb: 1, opacity: 0.5 }} />

      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, mb: 0.5 }}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.82rem' }}>CPF OA Balance</Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>Counts towards Available Funds</Typography>
        </Box>
        <Box sx={{ width: 180, flexShrink: 0 }}><CurrencyTextField value={borrower.cpf} onChange={(v) => onUpdate('cpf', v)} highlightColor={highlightColor} /></Box>
      </Box>

      {/* Citizenship & Which Property */}
      {onCitizenshipChange && citizenship !== undefined && (
        <>
          <Divider sx={{ mb: 2, mt: 2, opacity: 0.5 }} />
          <Box sx={{ mb: 1.5 }}>
            <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.82rem', color: 'text.primary', mb: 0.75 }}>Citizenship</Typography>
            <RadioGroup
              row
              value={citizenship}
              onChange={(e) => {
                const v = e.target.value as CitizenshipType;
                onCitizenshipChange(v);
                if (v === 'SC' && onFtaEligibleChange) onFtaEligibleChange(false);
              }}
            >
              {(Object.keys(CITIZENSHIP_LABELS) as CitizenshipType[]).map((key) => (
                <FormControlLabel
                  key={key} value={key}
                  control={<Radio size="small" sx={{ color: highlightColor, '&.Mui-checked': { color: highlightColor } }} />}
                  label={<Typography variant="body2">{CITIZENSHIP_LABELS[key]}</Typography>}
                />
              ))}
            </RadioGroup>

            {(citizenship === 'SPR' || citizenship === 'FOREIGNER') && onFtaEligibleChange && ftaEligible !== undefined && (
              <Box sx={{ mt: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                  <Typography variant="body2">FTA ABSD remission?</Typography>
                  <Tooltip
                    title={<Typography variant="body2" component="span" sx={{ display: 'block', maxWidth: 360, whiteSpace: 'pre-line' }}>{FTA_ABSD_INFO_TOOLTIP}</Typography>}
                    arrow placement="top" enterTouchDelay={0}
                  >
                    <IconButton size="small" sx={{ p: 0.25 }}><InfoOutlinedIcon fontSize="small" color="primary" /></IconButton>
                  </Tooltip>
                </Box>
                <RadioGroup row value={ftaEligible ? 'yes' : 'no'} onChange={(e) => onFtaEligibleChange(e.target.value === 'yes')}>
                  <FormControlLabel value="no" control={<Radio size="small" />} label={<Typography variant="body2">No</Typography>} />
                  <FormControlLabel value="yes" control={<Radio size="small" />} label={<Typography variant="body2">Yes</Typography>} />
                </RadioGroup>
              </Box>
            )}
          </Box>

          {onPropertyCountChange && propertyCount !== undefined && (
            <Box sx={{ mb: 0.5 }}>
              <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.82rem', color: 'text.primary', mb: 0.75 }}>Which Property</Typography>
              <RadioGroup row value={hideThirdProperty && propertyCount === '3rd+' ? '2nd' : propertyCount} onChange={(e) => onPropertyCountChange(e.target.value as PropertyCount)}>
                {PROPERTY_COUNT_OPTIONS.filter((opt) => !(hideThirdProperty && opt.value === '3rd+')).map((opt) => (
                  <FormControlLabel
                    key={opt.value} value={opt.value}
                    control={<Radio size="small" sx={{ color: highlightColor, '&.Mui-checked': { color: highlightColor } }} />}
                    label={<Typography variant="body2">{opt.label}</Typography>}
                  />
                ))}
              </RadioGroup>
            </Box>
          )}
        </>
      )}
    </Card>
  );
};

// ─── YesNo Toggle ─────────────────────────────────────────────────────────────

const YesNoToggle: React.FC<{ value: boolean; onChange: (v: boolean) => void; disabled?: boolean }> = ({
  value,
  onChange,
  disabled,
}) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
    <Box
      onClick={() => !disabled && onChange(true)}
      sx={{
        display: 'flex', alignItems: 'center', gap: 0.5, px: 1.25, py: 0.5,
        borderRadius: 2, cursor: disabled ? 'default' : 'pointer',
        bgcolor: value ? '#22c55e' : 'transparent',
        border: '1.5px solid', borderColor: value ? '#22c55e' : 'divider',
        color: value ? '#fff' : 'text.secondary',
        fontWeight: 700, fontSize: '0.78rem',
        transition: 'all 0.15s',
      }}
    >
      <CheckCircleIcon sx={{ fontSize: 14 }} />
      Yes
    </Box>
    <Box
      onClick={() => !disabled && onChange(false)}
      sx={{
        display: 'flex', alignItems: 'center', gap: 0.5, px: 1.25, py: 0.5,
        borderRadius: 2, cursor: disabled ? 'default' : 'pointer',
        bgcolor: !value ? '#ef4444' : 'transparent',
        border: '1.5px solid', borderColor: !value ? '#ef4444' : 'divider',
        color: !value ? '#fff' : 'text.secondary',
        fontWeight: 700, fontSize: '0.78rem',
        transition: 'all 0.15s',
      }}
    >
      <CancelIcon sx={{ fontSize: 14 }} />
      No
    </Box>
  </Box>
);

// ─── Editable Number Field ────────────────────────────────────────────────────

const EditableField: React.FC<{
  label: string;
  value: number;
  onChange: (v: number) => void;
  suffix?: string;
  prefix?: string;
  step?: number;
  min?: number;
  max?: number;
  disabled?: boolean;
  helperText?: string;
}> = ({ label, value, onChange, suffix, prefix, step = 1, min = 0, max = 999999999, disabled, helperText }) => {
  const [draft, setDraft] = useState('');
  const [editing, setEditing] = useState(false);

  const handleFocus = () => {
    setDraft(String(value));
    setEditing(true);
  };

  const handleBlur = () => {
    const n = parseFloat(draft.replace(/,/g, ''));
    if (!isNaN(n)) onChange(Math.min(max, Math.max(min, n)));
    setEditing(false);
  };

  const displayValue = editing ? draft : value.toLocaleString('en-SG');

  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="body2" sx={{ mb: 0.75, color: 'text.secondary', fontWeight: 500 }}>
        {label}
      </Typography>
      <TextField
        size="small"
        fullWidth
        disabled={disabled}
        value={displayValue}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
        InputProps={{
          startAdornment: prefix ? <InputAdornment position="start">{prefix}</InputAdornment> : undefined,
          endAdornment: suffix ? <InputAdornment position="end">{suffix}</InputAdornment> : undefined,
        }}
        helperText={helperText}
        sx={{
          '& .MuiOutlinedInput-root': {
            bgcolor: disabled ? 'action.disabledBackground' : 'background.paper',
          },
        }}
      />
    </Box>
  );
};

// ─── Percent Input (draft-edit) ───────────────────────────────────────────────
// Lets the user type freely (e.g. "20") without the controlled re-format fighting
// each keystroke. Commits on blur / Enter.
const PctInput: React.FC<{
  pct: number;
  onCommit: (newPct: number) => void;
  width?: number;
}> = ({ pct, onCommit, width = 80 }) => {
  const [draft, setDraft] = useState('');
  const [editing, setEditing] = useState(false);

  const displayWhenNotEditing = pct === 0
    ? ''
    : (pct % 1 === 0 ? pct.toFixed(0) : pct.toFixed(1));

  const commit = () => {
    const n = parseFloat(draft);
    onCommit(isNaN(n) ? 0 : Math.max(0, n));
    setEditing(false);
  };

  return (
    <TextField
      size="small"
      value={editing ? draft : displayWhenNotEditing}
      placeholder="0"
      onFocus={() => { setDraft(displayWhenNotEditing); setEditing(true); }}
      onChange={(e) => setDraft(e.target.value.replace(/[^0-9.]/g, ''))}
      onBlur={commit}
      onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
      InputProps={{ endAdornment: <InputAdornment position="end" sx={{ '& p': { fontSize: '0.75rem' } }}>%</InputAdornment> }}
      sx={{ width, '& input': { textAlign: 'right', py: '4px', fontSize: '0.8rem' } }}
    />
  );
};

// ─── Stage Card ───────────────────────────────────────────────────────────────

interface StageCardProps {
  stages: ComputedStage[];
  stageNumbers: number[];
  isMerged: boolean;
  isFirst: boolean;
  isLast: boolean;
  isDragging: boolean;
  isDragOver: boolean;
  showDates: boolean;
  fmt: (v: number) => string;
  onDragStart: () => void;
  onDragOver: (e: React.DragEvent) => void;
  onDrop: () => void;
  onDragEnd: () => void;
  onSplit: () => void;
  // Tap-to-merge with the card below (touch-friendly alternative to drag-and-drop,
  // which doesn't work on iPad). Undefined for the last card.
  onMergeDown?: () => void;
  spDateEditing?: boolean;
  // Inline milestone-date editing. When onDateCommit is set, this card's date shows a
  // pencil that opens a native calendar picker; picking a date commits it immediately.
  // dateValue is the current date (YYYY-MM-DD). onDateReset (when set) reverts an
  // overridden S&P milestone back to its auto-derived date.
  dateValue?: string;
  dateMin?: string;
  dateMax?: string;
  onDateCommit?: (iso: string) => void;
  onDateReset?: () => void;
  spDateStr?: string;
  purchasePrice?: number;
  interestRate?: number;
  tenure?: number;
  absdAmount?: number;
  absdRatePct?: number;
  onExplain?: (info: CalcDialogInfo) => void;
  // Simple mode: relabel milestones (Booking Fee / Pay 15%) and collapse the
  // per-stage breakdown to a single Amount split into Cash (mandatory) / CPF/Cash.
  simpleMode?: boolean;
}

const StageCard: React.FC<StageCardProps> = ({
  stages,
  stageNumbers,
  isMerged,
  isFirst,
  isDragging,
  isDragOver,
  showDates,
  fmt,
  onDragStart,
  onDragOver,
  onDrop,
  onDragEnd,
  onSplit,
  onMergeDown,
  dateValue,
  dateMin,
  dateMax,
  onDateCommit,
  onDateReset,
  spDateStr,
  purchasePrice = 0,
  interestRate = 0,
  tenure = 0,
  absdAmount = 0,
  absdRatePct = 0,
  onExplain,
  simpleMode = false,
}) => {
  const [expanded, setExpanded] = useState(true);
  const primaryStage = stages[0];
  const totalPayment = stages.reduce((sum, s) => sum + s.paymentAmount, 0);
  const lastStage = stages[stages.length - 1];
  // Native date input, transparent over the pencil in the header row below.
  const dateInputRef = useRef<HTMLInputElement>(null);
  // Pointer devices only: open the calendar the moment the input is clicked, so the
  // pencil behaves like a button. iOS must never depend on this — showPicker() is a
  // silent no-op for date inputs there (WebKit bug 261703); WebKit opens its own
  // wheel off the genuine tap on the input instead.
  const openDatePicker = () => {
    const el = dateInputRef.current;
    if (!el || typeof el.showPicker !== 'function') return;
    try { el.showPicker(); } catch { /* no transient activation / unsupported — the tap still opens it */ }
  };

  const ExRow = ({ label, value, color, info }: { label: string; value: string; color?: string; info?: CalcDialogInfo }) => (
    <Box onClick={info && onExplain ? () => onExplain(info) : undefined}
      sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.25, cursor: info && onExplain ? 'pointer' : 'default' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4, minWidth: 0 }}>
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>{label}</Typography>
        {info && onExplain && <InfoOutlinedIcon sx={{ fontSize: 13, color: 'primary.main', flexShrink: 0 }} />}
      </Box>
      <Typography variant="caption" fontWeight={600} sx={{ color: color }}>{value}</Typography>
    </Box>
  );

  const PaymentHeaderRow = ({ value, info }: { value: string; info?: CalcDialogInfo }) => (
    <Box
      onClick={info && onExplain ? () => onExplain(info) : undefined}
      sx={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        mb: 0.5, pb: 0.5, borderBottom: '1px dashed', borderColor: 'divider',
        cursor: info && onExplain ? 'pointer' : 'default',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Typography variant="body2" fontWeight={800} sx={{ color: 'text.primary', fontSize: '0.82rem' }}>
          Payment required
        </Typography>
        {info && onExplain && <InfoOutlinedIcon sx={{ fontSize: 14, color: 'primary.main' }} />}
      </Box>
      <Typography variant="body2" fontWeight={800} sx={{ color: 'text.primary', fontSize: '0.82rem' }}>
        {value}
      </Typography>
    </Box>
  );

  const badgeColor = primaryStage.type === 'otp' ? '#D97706'
    : primaryStage.type === 'sp-bsd' || primaryStage.type === 'sp' ? '#0ea5e9'
    : primaryStage.type === 'top' ? '#8b5cf6'
    : primaryStage.type === 'csc' ? '#10b981'
    : '#6b7280';

  return (
    <Box
      sx={{ position: 'relative' }}
      onDragOver={(e) => { e.preventDefault(); onDragOver(e); }}
      onDrop={(e) => { e.preventDefault(); onDrop(); }}
    >
      {isDragOver && (
        <Box sx={{
          position: 'absolute', top: -2, left: 0, right: 0, height: 4,
          bgcolor: '#D97706', borderRadius: 2, zIndex: 10,
          boxShadow: '0 0 8px #D97706',
        }} />
      )}
      <Paper
        elevation={isDragging ? 6 : 0}
        variant={isDragging ? 'elevation' : 'outlined'}
        sx={{
          mb: 1.5,
          border: '1px solid',
          borderColor: isDragOver ? '#D97706' : isMerged ? 'rgba(217,119,6,0.4)' : 'divider',
          borderRadius: 2,
          overflow: 'hidden',
          opacity: isDragging ? 0.5 : 1,
          transition: 'border-color 0.15s, opacity 0.15s',
          bgcolor: isMerged ? 'rgba(217,119,6,0.04)' : 'background.paper',
        }}
      >
        {/* Stage Header */}
        <Box
          sx={{
            display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 1.5,
            borderBottom: isMerged && expanded ? '1px solid' : 'none',
            borderColor: 'divider',
          }}
        >
          {/* Drag Handle */}
          <Box
            draggable
            onDragStart={onDragStart}
            onDragEnd={onDragEnd}
            sx={{
              cursor: 'grab', color: 'text.disabled', display: 'flex',
              alignItems: 'center', flexShrink: 0,
              '&:hover': { color: 'text.secondary' },
              '&:active': { cursor: 'grabbing' },
            }}
          >
            <DragIndicatorIcon fontSize="small" />
          </Box>

          {/* Split (when merged) + tap-to-merge-down — touch-friendly alternative to drag. */}
          {(isMerged || onMergeDown) && (
            <Box sx={{ display: 'flex', flexDirection: 'column', flexShrink: 0, mr: 0.5 }}>
              {isMerged && (
                <Tooltip title="Split stages">
                  <IconButton onClick={onSplit} sx={{ p: 0.25, color: '#D97706' }} aria-label="Split stages">
                    <CallSplitIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </Tooltip>
              )}
              {onMergeDown && (
                <Tooltip title="Merge with stage below">
                  <IconButton onClick={onMergeDown} sx={{ p: 0.25, color: '#D97706' }} aria-label="Merge with stage below">
                    <KeyboardArrowDownIcon sx={{ fontSize: 20 }} />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          )}

          {/* Stage Numbers */}
          <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
            {stageNumbers.map((n, i) => (
              <Box
                key={i}
                sx={{
                  width: 28, height: 28, borderRadius: '50%',
                  bgcolor: badgeColor, color: '#fff',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '0.75rem', fontWeight: 700, flexShrink: 0,
                }}
              >
                {n}
              </Box>
            ))}
          </Box>

          {/* Title */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body2" fontWeight={700} sx={{ lineHeight: 1.3 }}>
              {isMerged
                ? `${simpleMode ? simpleStageLabel(primaryStage.label) : primaryStage.label} + ${stages.length - 1} more`
                : simpleMode ? simpleStageLabel(primaryStage.label) : primaryStage.label}
            </Typography>
            {showDates && primaryStage.estimatedDate && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  {formatDate(primaryStage.estimatedDate)}
                </Typography>
                {!isMerged && onDateCommit && (
                  <Box sx={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
                    {/* Pencil is decoration only; the input above it takes the tap. */}
                    <IconButton size="small" tabIndex={-1} aria-hidden sx={{ p: 0.25, pointerEvents: 'none' }}>
                      <EditIcon sx={{ fontSize: 13, color: 'text.secondary' }} />
                    </IconButton>
                    {/* The real control: a native date input laid transparently over the
                        pencil, so a finger tap lands on it. On iOS (every browser there is
                        WebKit) the date picker only opens from a genuine tap on the input —
                        showPicker() alone left the pencil dead. Stretched left/up/down for a
                        usable touch target, but never past the right edge so a neighbouring
                        Reset link stays tappable. */}
                    <Box
                      component="input"
                      ref={dateInputRef}
                      type="date"
                      aria-label="Edit date"
                      value={dateValue ?? ''}
                      onClick={openDatePicker}
                      min={dateMin}
                      max={dateMax}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        const iso = e.target.value;
                        if (iso && (!dateMin || iso >= dateMin) && (!dateMax || iso <= dateMax)) onDateCommit(iso);
                      }}
                      sx={{
                        position: 'absolute', top: -8, bottom: -8, left: -8, right: 0,
                        opacity: 0, border: 0, padding: 0, background: 'transparent',
                        zIndex: 1, cursor: 'pointer', WebkitAppearance: 'none',
                        // 16px keeps iOS Safari from zooming the page when the input focuses.
                        fontSize: 16,
                      }}
                    />
                  </Box>
                )}
                {!isMerged && onDateReset && (
                  <Typography
                    component="span"
                    variant="caption"
                    onClick={onDateReset}
                    sx={{ color: '#D97706', cursor: 'pointer', fontWeight: 600, '&:hover': { textDecoration: 'underline' } }}
                  >
                    Reset
                  </Typography>
                )}
              </Box>
            )}
            {!showDates && (
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                {primaryStage.timing}
              </Typography>
            )}
          </Box>

          {/* Total Payment */}
          <Box
            onClick={onExplain ? () => onExplain({
              title: isMerged ? `${primaryStage.label} + more — Total` : `${primaryStage.label} — Total`,
              rows: [
                ...stages.map(s => ({ label: s.label, value: fmt(s.paymentAmount) })),
                ...(stages.length > 1 ? [{ isSeparator: true, label: '', value: '' }, { label: 'Total', value: fmt(totalPayment), isResult: true }] : [{ isSeparator: true, label: '', value: '' }, { label: 'Payment at this milestone', value: fmt(totalPayment), isResult: true }]),
              ],
            }) : undefined}
            sx={{ display: 'flex', alignItems: 'center', gap: 0.4, flexShrink: 0, cursor: onExplain ? 'pointer' : 'default' }}>
            {onExplain && <InfoOutlinedIcon sx={{ fontSize: 14, color: 'primary.main', flexShrink: 0 }} />}
            <Typography variant="body2" fontWeight={700}>{fmt(totalPayment)}</Typography>
          </Box>

          {/* Actions — expand/collapse for merged cards (split moved beside the handle). */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
            {isMerged && (
              <IconButton size="small" onClick={() => setExpanded(!expanded)} sx={{ p: 0.5 }}>
                {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
              </IconButton>
            )}
          </Box>
        </Box>

        {/* Stage Detail Lines */}
        <Collapse in={!isMerged || expanded}>
          <Box sx={{ px: 2, py: 1 }}>
            {stages.map((stage) => (
              <Box key={stage.id} sx={{ mb: isMerged ? 1.5 : 0 }}>
                {isMerged && (
                  <Typography variant="caption" fontWeight={700} sx={{ display: 'block', color: 'text.secondary', mb: 0.5 }}>
                    {simpleMode ? simpleStageLabel(stage.label) : stage.label} — {stage.timing}
                  </Typography>
                )}

                {(() => {
                  const n = tenure * 12;
                  const r = interestRate / 100 / 12;
                  // Installment factor over the months left on the tenure at this stage.
                  const nLeft = stage.remainingTenureMonths || n;
                  const pmtF = r > 0 ? r * Math.pow(1 + r, nLeft) / (Math.pow(1 + r, nLeft) - 1) : 1 / nLeft;
                  // Simple mode: no cash/CPF split is known, so collapse the breakdown to a
                  // single Amount tagged Cash (mandatory min-cash) or CPF/Cash (everything else),
                  // and drop the repetitive "Payment required" header (the card total shows it).
                  if (simpleMode) {
                    const mand = stage.simpleMandatoryCash ?? 0;
                    const flex = stage.simpleFlex ?? 0;
                    return (
                      <>
                        {stage.type === 'sp-bsd' ? (
                          <>
                            <ExRow label="Buyer Stamp Duty (CPF/Cash)" value={fmt(stage.paymentAmount)} />
                            {absdAmount > 0 && (
                              <ExRow label={`Additional Buyer Stamp Duty (${absdRatePct}%) (CPF/Cash)`} value={fmt(absdAmount)} color="error.main" />
                            )}
                          </>
                        ) : (
                          <>
                            {mand > 0 && <ExRow label="Cash" value={fmt(mand)} />}
                            {flex > 0 && <ExRow label="CPF/Cash" value={fmt(flex)} />}
                          </>
                        )}
                        {stage.bridgingPayout > 0 && (
                          <ExRow label="Bridging Loan" value={fmt(stage.bridgingPayout)} color="#7c3aed" />
                        )}
                        {stage.loanPayout > 0 && (
                          <ExRow label="Bank Payout" value={fmt(stage.loanPayout)} color="#0ea5e9" />
                        )}
                        {stage.monthlyInstallment > 0 && (
                          <ExRow label="Monthly Installment" value={fmtInstallment(stage.monthlyInstallment)} color="#D97706" />
                        )}
                      </>
                    );
                  }
                  return (
                    <>
                      {/* Row: Payment required (header for breakdown) */}
                      <PaymentHeaderRow value={fmt(stage.paymentAmount)}
                        info={stage.percentage > 0 ? {
                          title: `${stage.label} — Payment`,
                          rows: [
                            { label: 'Purchase Price', value: fmt(purchasePrice) },
                            { label: `× Stage Percentage`, value: `${stage.percentage}%` },
                            { isSeparator: true, label: '', value: '' },
                            { label: `${fmt(purchasePrice)} × ${stage.percentage}%`, value: fmt(stage.paymentAmount), isResult: true },
                          ],
                        } : {
                          title: `${stage.label} — Payment`,
                          rows: [
                            { label: "BSD (Buyer's Stamp Duty)", value: '' },
                            { label: 'Progressive IRAS rates on purchase price', value: '' },
                            { label: 'Purchase Price', value: fmt(purchasePrice) },
                            { isSeparator: true, label: '', value: '' },
                            { label: 'Total BSD', value: fmt(stage.paymentAmount), isResult: true },
                          ],
                        }}
                      />

                      {/* BSD row */}
                      {stage.type === 'sp-bsd' && (
                        <ExRow label="Buyer Stamp Duty" value={fmt(stage.paymentAmount)}
                          info={{
                            title: "Buyer's Stamp Duty (BSD)",
                            rows: [
                              { label: 'Purchase Price', value: fmt(purchasePrice) },
                              { label: 'Progressive IRAS BSD rates:', value: '' },
                              { label: '  First $180k × 1%', value: fmt(Math.round(Math.min(purchasePrice, 180000) * 0.01)) },
                              { label: '  Next $180k × 2%', value: fmt(Math.round(Math.min(Math.max(purchasePrice - 180000, 0), 180000) * 0.02)) },
                              { label: '  Next $640k × 3%', value: fmt(Math.round(Math.min(Math.max(purchasePrice - 360000, 0), 640000) * 0.03)) },
                              { label: '  Next $500k × 4%', value: fmt(Math.round(Math.min(Math.max(purchasePrice - 1000000, 0), 500000) * 0.04)) },
                              { label: '  Next $1.5m × 5%', value: fmt(Math.round(Math.min(Math.max(purchasePrice - 1500000, 0), 1500000) * 0.05)) },
                              { label: '  Remainder × 6%', value: fmt(Math.round(Math.max(purchasePrice - 3000000, 0) * 0.06)) },
                              { isSeparator: true, label: '', value: '' },
                              { label: 'Total BSD', value: fmt(stage.paymentAmount), isResult: true },
                            ],
                          }}
                        />
                      )}
                      {stage.type === 'sp-bsd' && stage.cpfUsed > 0 && (
                        <ExRow label="  via CPF" value={fmt(stage.cpfUsed)} color="text.secondary" />
                      )}
                      {stage.type === 'sp-bsd' && stage.cashUsed > 0 && (
                        <ExRow label="  via Cash" value={fmt(stage.cashUsed)} color="text.secondary" />
                      )}

                      {/* ABSD row — only on sp-bsd stage when ABSD applies */}
                      {stage.type === 'sp-bsd' && absdAmount > 0 && (
                        <ExRow label={`Additional Buyer Stamp Duty (${absdRatePct}%)`} value={fmt(absdAmount)} color="error.main"
                          info={{
                            title: `Additional Buyer's Stamp Duty (ABSD)`,
                            rows: [
                              { label: 'Purchase Price', value: fmt(purchasePrice) },
                              { label: `ABSD Rate`, value: `${absdRatePct}%` },
                              { isSeparator: true, label: '', value: '' },
                              { label: `${fmt(purchasePrice)} × ${absdRatePct}%`, value: fmt(absdAmount), isResult: true },
                            ],
                          }}
                        />
                      )}

                      {/* Cash row — drawn from cash pool (CASH → CPF → LOAN order) */}
                      {stage.type !== 'sp-bsd' && stage.cashUsed > 0 && (
                        <ExRow label="Cash" value={fmt(stage.cashUsed)}
                          info={{
                            title: `${stage.label} — Cash`,
                            rows: [
                              { label: 'Stage Payment', value: fmt(stage.paymentAmount) },
                              { label: 'Drawn from Cash pool first', value: '' },
                              { isSeparator: true, label: '', value: '' },
                              { label: 'Cash applied', value: fmt(stage.cashUsed), isResult: true },
                            ],
                          }}
                        />
                      )}

                      {/* CPF row — after cash pool exhausted */}
                      {stage.type !== 'sp-bsd' && stage.cpfUsed > 0 && (
                        <ExRow label="CPF" value={fmt(stage.cpfUsed)}
                          info={{
                            title: `${stage.label} — CPF`,
                            rows: [
                              { label: 'Stage Payment', value: fmt(stage.paymentAmount) },
                              { label: '− Cash applied', value: fmt(stage.cashUsed) },
                              { label: 'CPF used after cash exhausted', value: '' },
                              { isSeparator: true, label: '', value: '' },
                              { label: 'CPF applied', value: fmt(stage.cpfUsed), isResult: true },
                            ],
                          }}
                        />
                      )}

                      {/* Bridging Loan Payout — EC DPS only (disbursed at TOP) */}
                      {stage.bridgingPayout > 0 && (
                        <ExRow label="Bridging Loan" value={fmt(stage.bridgingPayout)} color="#7c3aed"
                          info={{
                            title: `${stage.label} — Bridging Loan`,
                            rows: [
                              { label: 'Bridging loan funds this milestone before the bank loan', value: '' },
                              { label: 'Stage Payment', value: fmt(stage.paymentAmount) },
                              ...(stage.buyerCash > 0 ? [{ label: '− Cash / CPF applied', value: fmt(stage.buyerCash) }] : []),
                              { isSeparator: true, label: '', value: '' },
                              { label: 'Bridging applied this stage', value: fmt(stage.bridgingPayout), isResult: true },
                              { label: 'Cumulative bridging', value: fmt(stage.cumulativeBridging) },
                            ],
                          }}
                        />
                      )}

                      {/* Bank Payout */}
                      {stage.loanPayout > 0 && (
                        <ExRow label="Bank Payout" value={fmt(stage.loanPayout)} color="#0ea5e9"
                          info={{
                            title: `${stage.label} — Bank Payout`,
                            rows: [
                              { label: 'Bank disburses loan at this milestone', value: '' },
                              { label: 'Stage Percentage', value: `${stage.percentage}%` },
                              { label: 'Stage Payment', value: fmt(stage.paymentAmount) },
                              ...(stage.buyerCash > 0 ? [{ label: '− Cash / CPF applied', value: fmt(stage.buyerCash) }] : []),
                              ...(stage.bridgingPayout > 0 ? [{ label: '− Bridging applied', value: fmt(stage.bridgingPayout) }] : []),
                              { isSeparator: true, label: '', value: '' },
                              { label: 'Bank Payout this stage', value: fmt(stage.loanPayout), isResult: true },
                              { label: 'Cumulative drawn', value: fmt(stage.cumulativeLoan) },
                            ],
                          }}
                        />
                      )}

                      {/* Monthly Installment */}
                      {stage.monthlyInstallment > 0 && (
                        <ExRow label="Installment" value={fmtInstallment(stage.monthlyInstallment)} color="#D97706"
                          info={{
                            title: `${stage.label} — Monthly Installment`,
                            rows: [
                              { label: 'Loan drawn so far', value: fmt(stage.cumulativeLoan) },
                              { label: 'Outstanding balance (after repayments)', value: fmt(stage.outstandingLoan) },
                              { label: 'Bank Rate', value: `${interestRate.toFixed(2)}%` },
                              { label: 'Remaining tenure', value: `${nLeft} of ${n} mths` },
                              { label: 'Monthly rate r = rate ÷ 12', value: `${(interestRate / 12).toFixed(5)}%` },
                              { isSeparator: true, label: '', value: '' },
                              ...(stage.loanPayout > 0
                                ? [
                                  { label: 'Formula: B × r(1+r)ⁿ / ((1+r)ⁿ−1), n = months left', value: '' },
                                  { label: `${fmt(stage.outstandingLoan)} × ${pmtF.toFixed(6)}`, value: fmtInstallment(stage.monthlyInstallment), isResult: true },
                                ]
                                : [{ label: 'No bank drawdown at this stage — installment unchanged', value: fmtInstallment(stage.monthlyInstallment), isResult: true }]),
                              { note: true, label: 'The loan tenure starts at the first disbursement. At each drawdown the bank resets the installment to repay the outstanding balance over the months left.', value: '' },
                            ],
                          }}
                        />
                      )}
                    </>
                  );
                })()}
              </Box>
            ))}

            {/* Cumulative outlay summary (after last stage in card) */}
            {lastStage && (
              <Box sx={{
                mt: 1, pt: 1, borderTop: '1px dashed', borderColor: 'divider',
              }}>
                {simpleMode ? (
                  <>
                    {(lastStage.cumSimpleMandatoryCash ?? 0) > 0 && (
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.25 }}>
                        <Typography variant="caption" fontWeight={700}>Total outlay (Cash)</Typography>
                        <Typography variant="caption" fontWeight={700}>{fmt(lastStage.cumSimpleMandatoryCash ?? 0)}</Typography>
                      </Box>
                    )}
                    {(lastStage.cumSimpleFlex ?? 0) > 0 && (
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="caption" fontWeight={700}>Total outlay (CPF/Cash)</Typography>
                        <Typography variant="caption" fontWeight={700} sx={{ color: '#D97706' }}>
                          {fmt(lastStage.cumSimpleFlex ?? 0)}
                        </Typography>
                      </Box>
                    )}
                    {lastStage.cumulativeLoan > 0 && (
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.25 }}>
                        <Typography variant="caption" fontWeight={700}>Total Bank Payout</Typography>
                        <Typography variant="caption" fontWeight={700} sx={{ color: '#0ea5e9' }}>
                          {fmt(lastStage.cumulativeLoan)}
                        </Typography>
                      </Box>
                    )}
                  </>
                ) : (
                  <>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.25 }}>
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>Total outlay (Cash)</Typography>
                      <Typography variant="caption" fontWeight={600}>{fmt(lastStage.cumulativeCash)}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.25 }}>
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>Total outlay (CPF)</Typography>
                      <Typography variant="caption" fontWeight={600}>{fmt(lastStage.cumulativeCpf)}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="caption" fontWeight={700}>Total outlay</Typography>
                      <Typography variant="caption" fontWeight={700} sx={{ color: '#D97706' }}>
                        {fmt(lastStage.cumulativeCash + lastStage.cumulativeCpf)}
                      </Typography>
                    </Box>
                    {lastStage.cumulativeLoan > 0 && (
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.25 }}>
                        <Typography variant="caption" fontWeight={700}>Total Bank Payout</Typography>
                        <Typography variant="caption" fontWeight={700} sx={{ color: '#0ea5e9' }}>
                          {fmt(lastStage.cumulativeLoan)}
                        </Typography>
                      </Box>
                    )}
                  </>
                )}
              </Box>
            )}

            {/* Merge summary */}
            {isMerged && (
              <Box
                sx={{
                  mt: 1, pt: 1, borderTop: '1px dashed', borderColor: 'divider',
                  display: 'flex', justifyContent: 'space-between',
                }}
              >
                <Typography variant="caption" fontWeight={700}>Total at this stage</Typography>
                <Typography variant="caption" fontWeight={700} sx={{ color: '#D97706' }}>
                  {fmt(totalPayment)}
                </Typography>
              </Box>
            )}
          </Box>
        </Collapse>
      </Paper>
    </Box>
  );
};

// ─── Progressive Schedule ─────────────────────────────────────────────────────

// The "Show dates in timeline" switch shared by the progressive, private-resale and
// HDB schedules. Its value lives in BuyingCalculator so the exported report can drop
// dates alongside the screen.
const TimelineDatesToggle: React.FC<{ value: boolean; onChange: (v: boolean) => void }> = ({ value, onChange }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
      Show dates in timeline
    </Typography>
    <ToggleButtonGroup
      value={value ? 'yes' : 'no'}
      exclusive
      onChange={(_, v) => { if (v) onChange(v === 'yes'); }}
      size="small"
      sx={{
        '& .MuiToggleButton-root': { px: 2, py: 0.4, fontWeight: 600, fontSize: '0.75rem' },
        '& .Mui-selected': { bgcolor: '#D97706 !important', color: '#fff !important', borderColor: '#D97706 !important' },
      }}
    >
      <ToggleButton value="yes">Yes</ToggleButton>
      <ToggleButton value="no">No</ToggleButton>
    </ToggleButtonGroup>
  </Box>
);

interface ProgressiveScheduleProps {
  computedStages: ComputedStage[];
  fmt: (v: number) => string;
  spDateStr: string;
  onSpDateChange: (date: string) => void;
  // Override (or reset, via null) the Pay Stamp Duty / Pay 15% milestone dates. When a
  // handler is omitted that milestone's date isn't editable (e.g. simple mode).
  onStageDateChange?: (id: string, date: string | null) => void;
  stageDateOverrides?: Record<string, string>;
  onExerciseDateChange?: (date: string | null) => void;
  onSp15DateChange?: (date: string | null) => void;
  // Whether each S&P milestone currently has a manual date override (drives the
  // inline "Reset" affordance).
  exerciseOverridden?: boolean;
  sp15Overridden?: boolean;
  purchasePrice?: number;
  interestRate?: number;
  tenure?: number;
  absdAmount?: number;
  absdRatePct?: number;
  onExplain?: (info: CalcDialogInfo) => void;
  // Timeline date visibility, owned by BuyingCalculator so the report follows the screen.
  showDates: boolean;
  onShowDatesChange: (v: boolean) => void;
  // Simple mode: relabel milestones and collapse each stage card to a single Amount split
  // into Cash (mandatory min-cash) / CPF/Cash (flexible remainder).
  simpleMode?: boolean;
  // Reports the merged milestones as groups of computedStages indices (e.g. [[0..6],[7]]),
  // so the exported report can collapse the same stages the agent merged on screen.
  onGroupsChange?: (groups: number[][]) => void;
}

const ProgressiveSchedule: React.FC<ProgressiveScheduleProps> = ({
  computedStages,
  fmt,
  spDateStr,
  onSpDateChange,
  onStageDateChange,
  stageDateOverrides = {},
  onExerciseDateChange,
  onSp15DateChange,
  exerciseOverridden = false,
  sp15Overridden = false,
  purchasePrice = 0,
  interestRate = 0,
  tenure = 0,
  absdAmount = 0,
  absdRatePct = 0,
  onExplain,
  showDates,
  onShowDatesChange,
  simpleMode = false,
  onGroupsChange,
}) => {
  const [mergeGroups, setMergeGroups] = useState<number[]>(() => computedStages.map((_, i) => i));
  const [draggingIndex, setDraggingIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

  // Every milestone is editable when the generic date handler is supplied.
  const stageDateEditable = (type: ComputedStage['type']): boolean =>
    !!onStageDateChange || type === 'otp' || (type === 'sp-bsd' && !!onExerciseDateChange) || (type === 'sp' && !!onSp15DateChange);

  // Commit a picked date for a milestone to the right handler.
  const commitStageDate = (stage: ComputedStage, iso: string) => {
    const { type } = stage;
    if (type === 'otp') onSpDateChange(iso);
    else if (onStageDateChange) onStageDateChange(stage.id, iso);
    else if (type === 'sp-bsd') onExerciseDateChange?.(iso);
    else if (type === 'sp') onSp15DateChange?.(iso);
  };

  useEffect(() => {
    setMergeGroups(computedStages.map((_, i) => i));
  }, [computedStages.length]);

  // Build display groups
  const groups = useMemo(() => {
    const groupMap = new Map<number, ComputedStage[]>();
    const firstIndexMap = new Map<number, number>();

    // Use only the indices that exist in computedStages to avoid stale state
    // when computedStages length shrinks (e.g. switching EC NPS → DPS).
    const effectiveMergeGroups = mergeGroups.length === computedStages.length
      ? mergeGroups
      : computedStages.map((_, i) => i);

    effectiveMergeGroups.forEach((gid, idx) => {
      const stage = computedStages[idx];
      if (!stage) return;
      if (!groupMap.has(gid)) {
        groupMap.set(gid, []);
        firstIndexMap.set(gid, idx);
      }
      groupMap.get(gid)!.push(stage);
    });

    const seen = new Set<number>();
    const uniqueGroups: number[] = [];
    effectiveMergeGroups.forEach((g) => { if (!seen.has(g)) { seen.add(g); uniqueGroups.push(g); } });
    uniqueGroups.sort((a, b) => (firstIndexMap.get(a) ?? a) - (firstIndexMap.get(b) ?? b));

    return uniqueGroups.map((gid) => ({
      gid,
      stages: groupMap.get(gid)!,
      firstIndex: firstIndexMap.get(gid)!,
    }));
  }, [mergeGroups, computedStages]);

  // Publish the merged milestones upward (as contiguous stage-index groups) so the
  // exported PDF schedule mirrors what is on screen.
  useEffect(() => {
    onGroupsChange?.(groups.map((g) => g.stages.map((_, i) => g.firstIndex + i)));
  }, [groups, onGroupsChange]);

  const handleDragStart = (groupFirstIndex: number) => {
    setDraggingIndex(groupFirstIndex);
  };

  const handleDragEnd = () => {
    setDraggingIndex(null);
    setDragOverIndex(null);
  };

  const handleDrop = (targetGroupFirstIndex: number) => {
    if (draggingIndex === null || targetGroupFirstIndex === draggingIndex) {
      setDraggingIndex(null);
      setDragOverIndex(null);
      return;
    }

    const fromIdx = draggingIndex;
    const toIdx = targetGroupFirstIndex;
    const targetGroup = mergeGroups[toIdx];

    setMergeGroups((prev) => {
      const next = [...prev];
      // Merge all stages from dragged group into target group
      const draggedGroupId = prev[fromIdx];
      for (let i = 0; i < next.length; i++) {
        if (next[i] === draggedGroupId) {
          next[i] = targetGroup;
        }
      }
      // Also merge all stages between toIdx and fromIdx
      const lo = Math.min(fromIdx, toIdx);
      const hi = Math.max(fromIdx, toIdx);
      for (let i = lo; i <= hi; i++) {
        next[i] = targetGroup;
      }
      return next;
    });

    setDraggingIndex(null);
    setDragOverIndex(null);
  };

  const handleSplit = (gid: number) => {
    setMergeGroups((prev) => {
      const next = [...prev];
      for (let i = 0; i < next.length; i++) {
        if (next[i] === gid) {
          next[i] = i;
        }
      }
      return next;
    });
  };

  // Tap-to-merge a display group with its neighbour above/below (touch alternative to
  // drag-and-drop). Both groups' stage indices collapse into one merged milestone.
  const handleMergeAdjacent = (gi: number, dir: 'up' | 'down') => {
    const otherGi = dir === 'up' ? gi - 1 : gi + 1;
    if (otherGi < 0 || otherGi >= groups.length) return;
    const loGroup = groups[Math.min(gi, otherGi)];
    const hiGroup = groups[Math.max(gi, otherGi)];
    const startIdx = loGroup.firstIndex;
    const endIdx = hiGroup.firstIndex + hiGroup.stages.length - 1;
    const targetGid = loGroup.gid;
    setMergeGroups((prev) => {
      const next = [...prev];
      for (let i = startIdx; i <= endIdx; i++) next[i] = targetGid;
      return next;
    });
  };

  const displayStageNumbers = (group: { stages: ComputedStage[]; firstIndex: number }) => {
    return group.stages.map((_, i) => group.firstIndex + i + 1);
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2, gap: 2 }}>
        <Box>
          <Typography variant="h6" fontWeight={700} sx={{ letterSpacing: '0.03em' }}>
            PROGRESSIVE SCHEDULE
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            Drag stages, or tap the ▼ arrow, to merge payment milestones
          </Typography>
        </Box>
        <TimelineDatesToggle value={showDates} onChange={onShowDatesChange} />
      </Box>

      {/* Stages */}
      <Box>
        {groups.map((group, gi) => (
          <StageCard
            key={group.gid}
            stages={group.stages}
            stageNumbers={displayStageNumbers(group)}
            purchasePrice={purchasePrice}
            interestRate={interestRate}
            tenure={tenure}
            absdAmount={absdAmount}
            absdRatePct={absdRatePct}
            onExplain={onExplain}
            simpleMode={simpleMode}
            isMerged={group.stages.length > 1}
            isFirst={gi === 0}
            isLast={gi === groups.length - 1}
            isDragging={draggingIndex === group.firstIndex}
            isDragOver={dragOverIndex === group.firstIndex}
            showDates={showDates}
            fmt={fmt}
            onDragStart={() => handleDragStart(group.firstIndex)}
            onDragOver={() => setDragOverIndex(group.firstIndex)}
            onDrop={() => handleDrop(group.firstIndex)}
            onDragEnd={handleDragEnd}
            onSplit={() => handleSplit(group.gid)}
            onMergeDown={gi < groups.length - 1 ? () => handleMergeAdjacent(gi, 'down') : undefined}
            {...(() => {
              const stage = group.stages[0];
              if (group.stages.length !== 1 || !stageDateEditable(stage.type)) return {};
              const overridden = !!stageDateOverrides[stage.id] || (stage.type === 'sp-bsd' && exerciseOverridden) || (stage.type === 'sp' && sp15Overridden);
              const previousDate = group.firstIndex > 0 ? computedStages[group.firstIndex - 1]?.estimatedDate : null;
              const nextDate = group.firstIndex < computedStages.length - 1 ? computedStages[group.firstIndex + 1]?.estimatedDate : null;
              return {
                dateValue: stage.estimatedDate ? toISODate(stage.estimatedDate) : spDateStr,
                // A later milestone may never precede the stage above it. Conversely,
                // cap editable non-OTP milestones at the following stage so an edit
                // cannot invert the schedule in the other direction either. OTP has no
                // upper bound because changing it re-derives all downstream dates.
                dateMin: previousDate ? toISODate(previousDate) : undefined,
                dateMax: stage.type !== 'otp' && nextDate ? toISODate(nextDate) : undefined,
                onDateCommit: (iso: string) => commitStageDate(stage, iso),
                onDateReset: overridden
                  ? () => { if (stageDateOverrides[stage.id]) onStageDateChange?.(stage.id, null); else if (stage.type === 'sp-bsd') onExerciseDateChange?.(null); else if (stage.type === 'sp') onSp15DateChange?.(null); }
                  : undefined,
              };
            })()}
            spDateStr={spDateStr}
          />
        ))}
      </Box>

      <Typography variant="caption" sx={{ color: 'text.disabled', display: 'block', mt: 1, fontStyle: 'italic' }}>
        Tip: Drag a stage card onto another — or tap the ▼ arrow beside the handle — to merge stages; tap the split icon to separate a merged milestone.
      </Typography>
    </Box>
  );
};

// ─── Resale Schedule Component ───────────────────────────────────────────────

interface ResaleScheduleProps {
  purchasePrice: number;
  derivedLoan: number;
  interestRate: number;
  tenure: number;
  hasBankLoan: boolean;
  absdAmount: number;
  absdRatePct: number;
  /** Effective milestone dates, owned by BuyingCalculator (the report reads the same). */
  dates: { otp: Date; exercise: Date; completion: Date };
  /** Commit an edited milestone date. Editing the OTP re-derives the later two. */
  onDateChange: (stage: 'otp' | 'exercise' | 'completion', iso: string) => void;
  fmt: (v: number) => string;
  cardBg: string;
  cardBorder: string;
  onExplain?: (info: CalcDialogInfo) => void;
  // Timeline date visibility, owned by BuyingCalculator so the report follows the screen.
  showDates: boolean;
  onShowDatesChange: (v: boolean) => void;
}

const ResaleSchedule: React.FC<ResaleScheduleProps> = ({
  purchasePrice, derivedLoan, interestRate, tenure, hasBankLoan,
  absdAmount, absdRatePct, dates, onDateChange,
  fmt, cardBg, cardBorder, onExplain, showDates, onShowDatesChange,
}) => {
  const optionFee    = Math.round(purchasePrice * 0.01);
  const exerciseFee  = Math.round(purchasePrice * 0.04);
  const balanceAmt   = purchasePrice - optionFee - exerciseFee;
  const bsd          = calculateBsd(purchasePrice);
  const cashAtBalance = Math.max(0, balanceAmt - (hasBankLoan ? derivedLoan : 0));
  const installment   = hasBankLoan ? calcMonthlyInstallment(derivedLoan, interestRate, tenure) : 0;

  const [editingStage, setEditingStage] = useState<number | null>(null);
  const [dateDraft, setDateDraft] = useState('');

  const STAGE_KEYS = ['otp', 'exercise', 'completion'] as const;

  const explain = (info: CalcDialogInfo) => onExplain && onExplain(info);

  const RRow = ({ label, value, color, info }: { label: string; value?: string; color?: string; info?: CalcDialogInfo }) => (
    <Box onClick={info ? () => explain(info) : undefined}
      sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.4, cursor: info ? 'pointer' : 'default' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4, minWidth: 0 }}>
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>{label}</Typography>
        {info && <InfoOutlinedIcon sx={{ fontSize: 13, color: 'primary.main', flexShrink: 0 }} />}
      </Box>
      {value != null && <Typography variant="caption" fontWeight={700} sx={{ color }}>{value}</Typography>}
    </Box>
  );

  const DateCell = ({ stageIdx, date, sub }: { stageIdx: number; date: Date; sub: string }) => {
    const commit = () => {
      const d = new Date(dateDraft + 'T00:00:00');
      if (!isNaN(d.getTime())) onDateChange(STAGE_KEYS[stageIdx], dateDraft);
      setEditingStage(null);
    };
    return (
      <Box>
        {/* Dates hidden → show only the relative timing; there is nothing to edit. */}
        {showDates && (editingStage === stageIdx ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <TextField
              size="small"
              type="date"
              value={dateDraft}
              autoFocus
              onChange={e => setDateDraft(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter') commit();
                if (e.key === 'Escape') setEditingStage(null);
              }}
              onBlur={commit}
              sx={{ width: 140 }}
              inputProps={{ style: { fontSize: '0.75rem', padding: '4px 6px' } }}
            />
          </Box>
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>{formatDate(date)}</Typography>
            <IconButton size="small" sx={{ p: 0.2 }} onClick={() => { setDateDraft(toISODate(date)); setEditingStage(stageIdx); }}>
              <EditIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Box>
        ))}
        <Typography variant="caption" sx={{ color: 'text.disabled', display: 'block' }}>{sub}</Typography>
      </Box>
    );
  };

  const stages = [
    {
      num: 1,
      label: 'OTP (Option Fee 1%)',
      amount: optionFee,
      date: dates.otp,
      sub: 'Today',
      rows: () => (
        <>
          <RRow label="Option Fee" value={fmt(optionFee)}
            info={{ title: 'Option Fee (1%)', rows: [
              { label: 'Purchase Price', value: fmt(purchasePrice) },
              { label: '× 1%', value: '' },
              { isSeparator: true, label: '', value: '' },
              { label: `${fmt(purchasePrice)} × 1%`, value: fmt(optionFee), isResult: true },
            ]}}
          />
        </>
      ),
    },
    {
      num: 2,
      label: 'Exercise OTP (Exercise Fee 4%)',
      amount: exerciseFee,
      date: dates.exercise,
      sub: 'OTP + 14 Days',
      rows: () => (
        <>
          <RRow label="Exercise Fee (4%)" value={fmt(exerciseFee)}
            info={{ title: 'Exercise OTP Fee (4%)', rows: [
              { label: 'Purchase Price', value: fmt(purchasePrice) },
              { label: '× 4%', value: '' },
              { isSeparator: true, label: '', value: '' },
              { label: `${fmt(purchasePrice)} × 4%`, value: fmt(exerciseFee), isResult: true },
            ]}}
          />
          <RRow label="BSD" value={fmt(bsd)}
            info={{ title: 'Buyer\'s Stamp Duty (BSD)', rows: [
              { label: 'Purchase Price', value: fmt(purchasePrice) },
              { label: 'Progressive IRAS BSD rates:', value: '' },
              { label: '  First $180k × 1%', value: fmt(Math.round(Math.min(purchasePrice, 180000) * 0.01)) },
              { label: '  Next $180k × 2%', value: fmt(Math.round(Math.min(Math.max(purchasePrice - 180000, 0), 180000) * 0.02)) },
              { label: '  Next $640k × 3%', value: fmt(Math.round(Math.min(Math.max(purchasePrice - 360000, 0), 640000) * 0.03)) },
              { label: '  Next $500k × 4%', value: fmt(Math.round(Math.min(Math.max(purchasePrice - 1000000, 0), 500000) * 0.04)) },
              { label: '  Next $1.5m × 5%', value: fmt(Math.round(Math.min(Math.max(purchasePrice - 1500000, 0), 1500000) * 0.05)) },
              { label: '  Remainder × 6%', value: fmt(Math.round(Math.max(purchasePrice - 3000000, 0) * 0.06)) },
              { isSeparator: true, label: '', value: '' },
              { label: 'Total BSD', value: fmt(bsd), isResult: true },
            ]}}
          />
          {absdAmount > 0 && (
            <RRow label={`ABSD (${absdRatePct}%)`} value={fmt(absdAmount)} color="error.main"
              info={{ title: 'Additional Buyer\'s Stamp Duty (ABSD)', rows: [
                { label: 'Purchase Price', value: fmt(purchasePrice) },
                { label: `ABSD Rate`, value: `${absdRatePct}%` },
                { isSeparator: true, label: '', value: '' },
                { label: `${fmt(purchasePrice)} × ${absdRatePct}%`, value: fmt(absdAmount), isResult: true },
              ]}}
            />
          )}
        </>
      ),
    },
    {
      num: 3,
      label: 'Completion',
      amount: balanceAmt,
      date: dates.completion,
      sub: 'Exercise OTP + ~12 Weeks',
      rows: () => (
        <>
          <RRow label="Cash / CPF Payable" value={fmt(cashAtBalance)}
            info={{ title: 'Cash / CPF Payable', rows: [
              { label: 'Purchase Price', value: fmt(purchasePrice) },
              { label: '− Option Fee (1%)', value: fmt(optionFee) },
              { label: '− Exercise Fee (4%)', value: fmt(exerciseFee) },
              { label: 'Balance', value: fmt(balanceAmt) },
              { label: '− Bank Loan', value: fmt(hasBankLoan ? derivedLoan : 0) },
              { isSeparator: true, label: '', value: '' },
              { label: 'Cash / CPF payable', value: fmt(cashAtBalance), isResult: true },
            ]}}
          />
          {hasBankLoan && derivedLoan > 0 && (
            <RRow label="Loan Amount" value={fmt(derivedLoan)}
              info={{ title: 'Bank Loan Amount', rows: [
                { label: 'Purchase Price', value: fmt(purchasePrice) },
                { label: '× LTV', value: `${((derivedLoan / purchasePrice) * 100).toFixed(1)}%` },
                { isSeparator: true, label: '', value: '' },
                { label: 'Bank Loan', value: fmt(derivedLoan), isResult: true },
              ]}}
            />
          )}
          {installment > 0 && (
            <RRow label="Monthly Installment" value={fmtInstallment(installment)} color="#D97706"
              info={{ title: 'Monthly Installment', rows: [
                { label: 'Loan Amount', value: fmt(derivedLoan) },
                { label: 'Bank Rate', value: `${interestRate.toFixed(2)}%` },
                { label: 'Tenure', value: `${tenure} yrs = ${tenure * 12} mths` },
                { label: 'Formula: L × r(1+r)ⁿ / ((1+r)ⁿ−1)', value: '' },
                { isSeparator: true, label: '', value: '' },
                { label: 'Monthly Installment', value: fmtInstallment(installment), isResult: true },
              ]}}
            />
          )}
        </>
      ),
    },
  ];

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2, gap: 2 }}>
        <Box>
          <Typography variant="h6" fontWeight={800} sx={{ mb: 0.5, letterSpacing: '0.06em' }}>RESALE PURCHASE SCHEDULE</Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
            Tap any value to see the working.{showDates ? ' Edit dates with the pencil icon.' : ''}
          </Typography>
        </Box>
        <TimelineDatesToggle value={showDates} onChange={onShowDatesChange} />
      </Box>

      {stages.map((stage) => (
        <Paper key={stage.num} variant="outlined" sx={{ mb: 2, p: 2, borderRadius: 2, bgcolor: 'background.paper', borderColor: cardBorder }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
            <Box sx={{
              width: 28, height: 28, borderRadius: '50%', bgcolor: '#D97706', color: '#fff',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: '0.75rem', fontWeight: 700, flexShrink: 0, mt: 0.25,
            }}>{stage.num}</Box>
            <Box sx={{ flex: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                <Box>
                  <Typography variant="body2" fontWeight={700}>{stage.label}</Typography>
                  <DateCell stageIdx={stage.num - 1} date={stage.date} sub={stage.sub} />
                </Box>
                <Typography variant="body2" fontWeight={700} sx={{ flexShrink: 0 }}>
                  {fmt(stage.amount)}
                </Typography>
              </Box>
              <Box sx={{ mt: 1 }}>
                {stage.rows()}
              </Box>
            </Box>
          </Box>
        </Paper>
      ))}
    </Box>
  );
};

// ─── Calc Explanation Dialog types ──────────────────────────────────────────
interface CalcDialogRow { label: string; value: string; isResult?: boolean; isSeparator?: boolean; note?: boolean; }
interface CalcDialogInfo { title: string; rows: CalcDialogRow[]; }

// ─── HDB Schedule Component ───────────────────────────────────────────────────

interface HdbScheduleProps {
  h: HdbInputs;
  fmt: (v: number) => string;
  cardBg: string;
  cardBorder: string;
  onExplain?: (info: CalcDialogInfo) => void;
  absdAmount?: number;
  absdRatePct?: number;
  derivedLoanAmt?: number;  // loan derived from payment structure allocations
  cashAlloc?: number;       // cash down payment from payment structure
  cpfAlloc?: number;        // CPF+grants down payment from payment structure
  onOtpDateChange?: (iso: string) => void; // commit an edited OTP date (the whole schedule follows)
  /** Dates pinned on later milestones; each carries the ones after it only. */
  dateOverrides?: HdbDateOverrides;
  onMilestoneDateChange?: (key: HdbMilestoneKey, iso: string) => void;
  // Timeline date visibility, owned by BuyingCalculator so the report follows the screen.
  showDates: boolean;
  onShowDatesChange: (v: boolean) => void;
}

const HdbSchedule: React.FC<HdbScheduleProps> = ({ h, fmt, cardBg, cardBorder, onExplain, absdAmount = 0, absdRatePct = 0, derivedLoanAmt, cashAlloc, cpfAlloc, onOtpDateChange, dateOverrides, onMilestoneDateChange, showDates, onShowDatesChange }) => {
  // IRAS rule: BSD and ABSD are computed on the higher of purchase price or market value (valuation).
  const sdBase = Math.max(h.purchasePrice, h.valuationPrice);
  const bsd = calculateBsd(sdBase);
  const cov = Math.max(0, h.purchasePrice - h.valuationPrice);
  // OTP deposit (cash): Option Fee + Exercise Fee (editable in Other Expenses).
  const otpDeposit = h.optionFee + h.exerciseFee;
  // Use derived loan from payment structure if available, otherwise fall back to manually entered HFE amount
  const loanAmt = h.loanSource === 'none' ? 0 : (derivedLoanAmt ?? h.hdbLoanAmount);
  const effectiveRate = h.loanSource === 'hdb' ? h.hdbInterestRate : h.bankInterestRate;
  const installment = calcMonthlyInstallment(loanAmt, effectiveRate, h.tenure);

  const totalGrants = h.housingGrant + h.cpfResaleGrant + h.phgGrant;
  const cpfTotal = h.cpfOa + totalGrants;
  const cpfCosts = bsd + absdAmount + h.legalFee + h.hpsAmount;
  const cpfForDown = Math.max(0, cpfTotal - cpfCosts);
  const cashAtCompletion = Math.max(0, h.purchasePrice - otpDeposit - cpfForDown - loanAmt);

  const otpDateObj = new Date(h.otpDate + 'T00:00:00');
  const {
    valuation: valuationDate,
    exercise: exerciseDate,
    resaleApp: resaleAppDate,
    acceptance: acceptanceDate,
    completion: completionDate,
  } = hdbResaleSchedule(otpDateObj, dateOverrides);

  const explain = (info: CalcDialogInfo) => onExplain && onExplain(info);

  const TRow = ({ label, value, color, info }: { label: string; value?: string; color?: string; info?: CalcDialogInfo }) => (
    <Box onClick={info ? () => explain(info) : undefined}
      sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5, cursor: info ? 'pointer' : 'default' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4, minWidth: 0 }}>
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>{label}</Typography>
        {info && <InfoOutlinedIcon sx={{ fontSize: 13, color: 'primary.main', flexShrink: 0 }} />}
      </Box>
      {value != null && <Typography variant="caption" fontWeight={700} sx={{ color }}>{value}</Typography>}
    </Box>
  );

  // dateKey absent on the OTP row: editing it moves the whole schedule, while
  // editing any later row pins that row and carries only what follows it.
  const steps: Array<{
    num: number;
    label: string;
    date: Date | null;
    payment: number | null;
    dateKey?: HdbMilestoneKey;
    isCompletion?: boolean;
  }> = [
    { num: 1, label: 'HFE Letter', date: null, payment: null },
    { num: 2, label: 'Option to Purchase', date: otpDateObj, payment: h.optionFee },
    { num: 3, label: 'Request for Valuation', date: valuationDate, payment: null, dateKey: 'valuation' },
    { num: 4, label: 'Exercise OTP (Within 21 Days)', date: exerciseDate, payment: h.exerciseFee, dateKey: 'exercise' },
    { num: 5, label: 'Submit Resale Application', date: resaleAppDate, payment: null, dateKey: 'resaleApp' },
    { num: 6, label: 'HDB Acceptance', date: acceptanceDate, payment: null, dateKey: 'acceptance' },
    { num: 7, label: 'Completion', date: completionDate, payment: cashAtCompletion, dateKey: 'completion', isCompletion: true },
  ];

  const [editingStep, setEditingStep] = useState<number | null>(null);

  return (
    <Box>
      <Card variant="outlined" sx={{ p: 2, mb: 2, bgcolor: cardBg, borderColor: cardBorder }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2, gap: 2 }}>
          <Box>
            <Typography variant="h6" fontWeight={800} sx={{ mb: 0.5, letterSpacing: '0.06em' }}>HDB SCHEDULE</Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
              Tap any value to see the working
            </Typography>
          </Box>
          <TimelineDatesToggle value={showDates} onChange={onShowDatesChange} />
        </Box>

        {steps.map((step, si) => (
          <Paper key={step.num} variant="outlined" sx={{ mb: 1.5, p: 2, borderRadius: 2, bgcolor: 'background.paper', borderColor: cardBorder }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
              <Box sx={{
                width: 28, height: 28, borderRadius: '50%', bgcolor: '#D97706', color: '#fff',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '0.75rem', fontWeight: 700, flexShrink: 0, mt: 0.25,
              }}>{step.num}</Box>
              <Box sx={{ flex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                  <Box>
                    <Typography variant="body2" fontWeight={700}>{step.label}</Typography>
                    {showDates && step.date && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                          {editingStep === step.num
                            ? <TextField size="small" type="date" value={toISODate(step.date)}
                                autoFocus
                                onChange={e => {
                                  // The OTP row moves the whole schedule; every later row pins its
                                  // own date, which the milestones after it are then derived from.
                                  const picked = e.target.value;
                                  if (!picked) return;
                                  if (step.dateKey) onMilestoneDateChange?.(step.dateKey, picked);
                                  else onOtpDateChange?.(picked);
                                }}
                                onBlur={() => setEditingStep(null)}
                                sx={{ width: 140 }} inputProps={{ style: { fontSize: '0.75rem', padding: '2px 6px' } }} />
                            : formatDate(step.date)}
                        </Typography>
                        <IconButton size="small" onClick={() => setEditingStep(step.num)} sx={{ p: 0.25 }}>
                          <EditIcon sx={{ fontSize: 12, color: 'text.secondary' }} />
                        </IconButton>
                      </Box>
                    )}
                  </Box>
                  {step.payment != null && step.payment > 0 && (
                    <Typography variant="body2" fontWeight={700}
                      sx={{ cursor: onExplain ? 'pointer' : 'default', flexShrink: 0 }}
                      onClick={() => {
                        if (!onExplain) return;
                        if (step.num === 2) onExplain({ title: 'Option to Purchase Payment', rows: [
                          { label: 'Option Fee (paid on grant of OTP)', value: '' },
                          { isSeparator: true, label: '', value: '' },
                          { label: 'Option Fee', value: fmt(h.optionFee), isResult: true },
                        ]});
                        else if (step.num === 4) onExplain({ title: 'Exercise OTP Payment', rows: [
                          { label: 'Exercise Fee', value: fmt(h.exerciseFee) },
                          { label: `Note: Option fee (${fmt(h.optionFee)}) already paid`, value: '' },
                          { isSeparator: true, label: '', value: '' },
                          { label: 'Payment at exercise', value: fmt(h.exerciseFee), isResult: true },
                          { label: 'Total deposits so far', value: fmt(otpDeposit) },
                        ]});
                        else if (step.isCompletion) onExplain({ title: 'Completion — Cash Needed', rows: [
                          { label: 'Purchase Price', value: fmt(h.purchasePrice) },
                          { label: '− Option + Exercise Deposits', value: fmt(otpDeposit) },
                          { label: '− Loan Amount', value: fmt(loanAmt) },
                          { label: '− CPF for Down Payment', value: fmt(cpfForDown) },
                          { label: '  (CPF OA + Grant − BSD − Legal − HPS)', value: '' },
                          { isSeparator: true, label: '', value: '' },
                          { label: 'Cash at Completion', value: fmt(cashAtCompletion), isResult: true },
                        ]});
                      }}>
                      {fmt(step.payment)}
                    </Typography>
                  )}
                </Box>

                {/* HFE Letter details */}
                {step.num === 1 && (
                  <Box sx={{ mt: 1 }}>
                    {h.housingGrant > 0 && <TRow label="Enhanced CPF Housing Grant" value={fmt(h.housingGrant)} color="#10b981" />}
                    {h.cpfResaleGrant > 0 && <TRow label="CPF Housing Grant (Resale)" value={fmt(h.cpfResaleGrant)} color="#10b981" />}
                    {h.phgGrant > 0 && <TRow label="Proximity Housing Grant (PHG)" value={fmt(h.phgGrant)} color="#10b981" />}
                    {totalGrants === 0 && <TRow label="Estimated grants eligible" value="–" color="text.secondary" />}
                    <TRow label="Estimated HDB loan" value={h.loanSource === 'hdb' ? fmt(h.hdbLoanAmount) : h.loanSource === 'bank' ? 'Bank loan' : 'No loan'} color="#0ea5e9" />
                  </Box>
                )}

                {step.isCompletion && (() => {
                  // Use passed allocations if available, else fall back to legacy calc
                  const usedCash = cashAlloc ?? cashAtCompletion;
                  const usedCpf = cpfAlloc ?? cpfForDown;
                  const otpDeposits = otpDeposit;
                  const totalCosts = h.purchasePrice + bsd + absdAmount + h.legalFee + h.hpsAmount;

                  const WRow = ({ label, value, color, indent, bold }: { label: string; value: string; color?: string; indent?: boolean; bold?: boolean }) => (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5, pl: indent ? 1.5 : 0 }}>
                      <Typography variant="caption" sx={{ color: bold ? 'text.primary' : 'text.secondary', fontWeight: bold ? 700 : 400 }}>{label}</Typography>
                      <Typography variant="caption" fontWeight={bold ? 800 : 600} sx={{ color: color ?? 'text.primary', ml: 1, flexShrink: 0 }}>{value}</Typography>
                    </Box>
                  );

                  return (
                    <Box sx={{ mt: 1.5 }}>
                      {/* Total cost */}
                      <WRow label="Purchase Price" value={fmt(h.purchasePrice)} bold />
                      <WRow label="+ Buyer Stamp Duty (BSD)" value={fmt(bsd)} indent />
                      {absdAmount > 0 && <WRow label={`+ ABSD (${absdRatePct}%)`} value={fmt(absdAmount)} color="error.main" indent />}
                      <WRow label="+ Legal / conveyancing fees" value={fmt(h.legalFee)} indent />
                      {h.hpsAmount > 0 && <WRow label="+ HPS premium" value={fmt(h.hpsAmount)} indent />}

                      <Divider sx={{ my: 0.75 }} />

                      {/* Deductions */}
                      {h.cpfOa > 0 && <WRow label="− CPF OA" value={fmt(h.cpfOa)} color="#10b981" indent />}
                      {h.housingGrant > 0 && <WRow label="− Enhanced CPF Housing Grant" value={fmt(h.housingGrant)} color="#10b981" indent />}
                      {h.cpfResaleGrant > 0 && <WRow label="− CPF Housing Grant (Resale)" value={fmt(h.cpfResaleGrant)} color="#10b981" indent />}
                      {h.phgGrant > 0 && <WRow label="− Proximity Housing Grant (PHG)" value={fmt(h.phgGrant)} color="#10b981" indent />}

                      <Divider sx={{ my: 0.5, opacity: 0.4 }} />

                      {usedCash > 0 && <WRow label={`− Cash (down payment)`} value={fmt(usedCash)} indent />}
                      <WRow label="− OTP + Exercise deposits (paid)" value={fmt(otpDeposits)} indent />
                      {cov > 0 && <WRow label="− COV (cash only)" value={fmt(cov)} color="error.main" indent />}

                      <Divider sx={{ my: 0.75 }} />

                      {/* Loan at the bottom */}
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1.25, borderRadius: 1.5, bgcolor: 'rgba(14,165,233,0.08)', border: '1px solid rgba(14,165,233,0.3)', mb: 1 }}>
                        <Box>
                          <Typography variant="body2" fontWeight={800} sx={{ color: '#0ea5e9' }}>
                            = {h.loanSource === 'hdb' ? 'HDB Loan' : 'Bank Loan'} disbursed
                          </Typography>
                          <Typography variant="caption" sx={{ color: 'text.secondary' }}>Covers purchase price only</Typography>
                        </Box>
                        <Typography variant="body1" fontWeight={800} sx={{ color: '#0ea5e9' }}>{fmt(loanAmt)}</Typography>
                      </Box>

                      {installment > 0 && (
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                          <Typography variant="caption" sx={{ color: 'text.secondary' }}>Monthly installment after completion</Typography>
                          <Typography variant="caption" fontWeight={700} sx={{ color: '#D97706' }}>{fmtInstallment(installment)}</Typography>
                        </Box>
                      )}

                      {si < steps.length - 1 ? null : (
                        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.5 }}>Approx. 8 weeks after HDB acceptance</Typography>
                      )}
                    </Box>
                  );
                })()}
              </Box>
            </Box>
          </Paper>
        ))}
      </Card>

    </Box>
  );
};

// ─── Results Summary ──────────────────────────────────────────────────────────

// Strip a stage label down to its descriptive name, e.g.
// "Upon Foundation (10%)" → "Foundation", for compact breakdown rows.
function shortStageLabel(label: string): string {
  return label.replace(/^Upon\s+/i, '').replace(/\s*\(\d+%?\)\s*$/i, '').trim();
}

// months = months actually covered in this segment; stageMonths = the full length of
// the progressive stage interval (undefined for the open-ended steady-state segment).
interface RunwaySegment { installment: number; months: number; label: string; stageMonths?: number; }
interface RunwayResult { segments: RunwaySegment[]; totalMonths: number; graceMonths: number; sustainable: boolean; hasLoan: boolean; }

// Months covered are counted from the OTP stage. On a progressive purchase the bank
// only starts disbursing at the first construction milestone (~6 months after
// booking), so that window carries no installment at all — and CPF OA keeps
// accruing through it. Derived from the stage dates rather than hardcoded so an
// overridden OTP date or a different schedule stays in step.
function preLoanGraceMonths(stages: ComputedStage[], progressive: boolean): number {
  if (!progressive) return 0;
  const otpDate = stages.find((s) => s.type === 'otp')?.estimatedDate;
  const firstInstallmentDate = stages
    .filter((s) => s.estimatedDate && s.monthlyInstallment > 0)
    .sort((a, b) => a.estimatedDate!.getTime() - b.estimatedDate!.getTime())[0]?.estimatedDate;
  if (!otpDate || !firstInstallmentDate) return 0;
  return Math.max(
    0,
    (firstInstallmentDate.getFullYear() - otpDate.getFullYear()) * 12 +
      (firstInstallmentDate.getMonth() - otpDate.getMonth()),
  );
}

// Simulate how long the leftover funds can service the loan installment, returning
// the per-stage breakdown used both for the headline number and the tap-to-explain.
//  - For progressive (new-launch / EC) purchases the installment ramps up as the
//    loan is drawn down stage by stage, so the funds last longer than a flat
//    full-installment assumption (resale). We walk the stage dates to build that ramp.
//  - monthlyOaInflow > 0 models "continuous employment": CPF OA earned each month
//    is added back to the serviceable pool.
function computeRunway(
  startFunds: number,
  fullInstallment: number,
  stages: ComputedStage[],
  monthlyOaInflow: number,
  progressive: boolean,
): RunwayResult {
  if (fullInstallment <= 0) return { segments: [], totalMonths: 0, graceMonths: 0, sustainable: false, hasLoan: false };

  // Nothing to service the loan with (no leftover funds, no income): the pre-loan
  // window isn't coverage, so report zero rather than crediting the grace period.
  if (startFunds <= 0 && monthlyOaInflow <= 0) {
    return { segments: [], totalMonths: 0, graceMonths: 0, sustainable: false, hasLoan: true };
  }
  const graceMonths = preLoanGraceMonths(stages, progressive);

  // Build the progressive ramp intervals: the installment that applies during each
  // interval between consecutive dated stages, before the full installment.
  const intervals: { installment: number; months: number; label: string }[] = [];
  if (progressive && stages.length > 0) {
    const dated = stages
      .filter((s) => s.estimatedDate && s.monthlyInstallment > 0)
      .sort((a, b) => a.estimatedDate!.getTime() - b.estimatedDate!.getTime());
    for (let i = 0; i < dated.length - 1; i++) {
      const start = dated[i].estimatedDate!;
      const end = dated[i + 1].estimatedDate!;
      const months = Math.max(0, (end.getFullYear() - start.getFullYear()) * 12 + (end.getMonth() - start.getMonth()));
      if (months > 0) intervals.push({ installment: dated[i].monthlyInstallment, months, label: shortStageLabel(dated[i].label) });
    }
  }

  // CPF OA earned between OTP and the first disbursement joins the serviceable pool.
  let funds = startFunds + monthlyOaInflow * graceMonths;
  let totalMonths = graceMonths;
  const segments: RunwaySegment[] = [];
  for (const iv of intervals) {
    let covered = 0;
    for (let m = 0; m < iv.months; m++) {
      funds += monthlyOaInflow;
      if (funds < iv.installment) break;
      funds -= iv.installment;
      covered++;
    }
    if (covered > 0) segments.push({ installment: iv.installment, months: covered, label: iv.label, stageMonths: iv.months });
    totalMonths += covered;
    if (covered < iv.months) return { segments, totalMonths, graceMonths, sustainable: false, hasLoan: true }; // ran out mid-ramp
  }

  // Steady state: full installment applies indefinitely after the last stage.
  const net = fullInstallment - monthlyOaInflow;
  if (net <= 0) return { segments, totalMonths: Infinity, graceMonths, sustainable: true, hasLoan: true };
  const steady = Math.floor(funds / net);
  if (steady > 0) segments.push({ installment: fullInstallment, months: steady, label: 'Full installment' });
  totalMonths += steady;
  return { segments, totalMonths, graceMonths, sustainable: false, hasLoan: true };
}

interface ResultsSummaryProps {
  inputs: BuyingInputs;
  ltvDecimal: number;
  fmt: (v: number) => string;
  cardBg: string;
  cardBorder: string;
  onExplain?: (info: CalcDialogInfo) => void;
  bsdByCpf?: boolean;
  absdByCpf?: boolean;
  legalFeeByCpf?: boolean;
  effectivePledged?: number;
  effectiveUnpledged?: number;
  computedStages?: ComputedStage[];
  stagedDownPayment?: boolean;
  cashAvailable?: number;
  cpfAvailable?: number;
  hdbCpfUsed?: number;
  // Non-staged (resale) down payment split — cash & CPF the buyer allocates upfront.
  downCashAlloc?: number;
  downCpfAlloc?: number;
  // Mandatory min-cash % of price (5 normally, 10 under the stretched-tenure 55% LTV regime).
  minCashPct?: number;
  monthlyCpfOa?: number;
  bridgingAmount?: number;
  hdbOutlay?: {
    downCash: number; downCpf: number;
    bsdCash: number; bsdCpf: number;
    absdCash: number; absdCpf: number;
    legalCash: number; legalCpf: number;
    hpsCash: number; hpsCpf: number;
    valFee: number; adminFee: number; caveatFee: number; agencyFee: number;
  } | null;
  hdbLoanSource?: HdbInputs['loanSource'];
  hdbMandatoryCash?: number;
  ltvPass?: boolean;
  loanTypeLabel?: string;
  // Base the LTV is expressed against. HDB loans are capped against the lower of
  // purchase price and valuation (COV is cash-only), so HDB callers pass that
  // base; defaults to the purchase price.
  ltvBase?: number;
  ltvBaseLabel?: string;
  hideFundBalance?: boolean;
  // Simple mode: the buyer's cash/CPF split is unknown, so the single money column is
  // labelled "Amount" and only the mandatory min-cash is tagged Cash (the rest CPF/Cash).
  simpleMode?: boolean;
  // Simple private resale: BSD is paid in cash first, with CPF reimbursement afterward.
  simpleResale?: boolean;
  // Detailed-mode private resale: same cash-first / CPF-reimbursable stamp duty treatment,
  // but shown in the CPF/Cash column layout.
  detailedResale?: boolean;
  // Interest rate driving the monthly installment. HDB concessionary loans use the
  // HDB rate (2.6%), not the bank rate; falls back to inputs.interestRate when unset.
  installmentRate?: number;
  // Buyer's income-based loan eligibility — mirrors the left panel's Loan Info block.
  // Detailed mode only; simple mode has no borrower inputs to derive it from.
  loanEligibility?: {
    iwaa: number;
    maxLoan: number;
    maxPriceLoan: number;
    totalRecognisedIncome: number;
    rateNote: string;
  };
  // Joint-purchase ABSD rate (IRAS joint tables incl. married-couple remission),
  // computed by the parent. Falls back to the primary borrower's individual rate.
  absdRateOverride?: number;
}

const ResultsSummary: React.FC<ResultsSummaryProps> = ({ inputs, ltvDecimal, fmt, cardBg, cardBorder, onExplain, bsdByCpf = false, absdByCpf = false, legalFeeByCpf = false, effectivePledged = 0, effectiveUnpledged = 0, computedStages = [], stagedDownPayment = false, cashAvailable = 0, cpfAvailable = 0, hdbCpfUsed = 0, downCashAlloc, downCpfAlloc, minCashPct = 5, monthlyCpfOa = 0, hdbOutlay = null, hdbLoanSource, hdbMandatoryCash = 0, ltvPass = true, loanTypeLabel = 'Bank Loan', ltvBase, ltvBaseLabel, hideFundBalance = false, installmentRate, absdRateOverride, loanEligibility, simpleMode = false, simpleResale = false, detailedResale = false }) => {
  const price = inputs.purchasePrice;
  const totalLoan = Math.round(price * (inputs.hasBankLoan ? ltvDecimal : 0));
  const bsd = calculateBsd(price);
  const effectiveCitizenship = getEffectiveCitizenshipForAbsd(inputs.citizenship, inputs.ftaEligible);
  const absdRatePct = absdRateOverride ?? getAbsdRate(effectiveCitizenship, inputs.propertyCount);
  const absdAmount = Math.round(price * absdRatePct / 100);

  // CPF left after the down payment — the stamp-duty / legal CPF waterfall (BSD → ABSD →
  // Legal) draws from this, so paying any of them by CPF correctly reduces the leftover
  // balance. Base it on the CPF the schedule ACTUALLY consumes for the down payment
  // (every stage except the BSD stage itself) for progressive purchases, or the buyer's
  // CPF down-payment allocation for resale. Using the real allocation — instead of a
  // notional "20% of price comes from CPF" assumption — keeps this consistent with how
  // the schedule funds the down payment; otherwise a cash-funded down payment would
  // wrongly zero out the CPF available for BSD and understate what's spent.
  const downCpfConsumed = stagedDownPayment
    ? computedStages.reduce((a, s) => a + (s.type === 'sp-bsd' ? 0 : s.cpfUsed), 0)
    : (downCpfAlloc !== undefined ? downCpfAlloc : hdbCpfUsed);
  const cpfAfterDown = Math.max(0, cpfAvailable - downCpfConsumed);
  // BSD: CPF covers up to available balance, rest in cash
  const bsdCpf = bsdByCpf ? Math.min(bsd, cpfAfterDown) : 0;
  const bsdCash = bsd - bsdCpf;
  const cpfAfterBsd = Math.max(0, cpfAfterDown - bsdCpf);
  // ABSD: CPF covers up to remaining balance after BSD, rest in cash
  const absdCpf = absdByCpf ? Math.min(absdAmount, cpfAfterBsd) : 0;
  const absdCash = absdAmount - absdCpf;
  const totalStampDutyCpf = bsdCpf + absdCpf;
  const totalStampDutyCash = bsdCash + absdCash;
  // Legal Fee: last in the waterfall — CPF covers up to what's left after BSD/ABSD, the
  // rest falls to cash (so a buyer with no CPF pays the whole conveyancing fee in cash).
  const cpfAfterAbsd = Math.max(0, cpfAfterBsd - absdCpf);
  // A cash-funded purchase follows the same Cash -> CPF waterfall as its stages.
  // Stamp duty remains the exception above; legal fees only touch CPF after the
  // available cash left after stamp duty has been exhausted.
  const legalFeeCpf = !inputs.hasBankLoan
    ? 0
    : legalFeeByCpf ? Math.min(inputs.legalFee, cpfAfterAbsd) : 0;
  const legalFeeCash = inputs.legalFee - legalFeeCpf;
  // Effective loan rate: HDB concessionary loans pass their own rate (2.6%); everything
  // else falls back to the bank rate. Drives the installment, the runway and the dialog.
  const effRate = installmentRate ?? inputs.interestRate;
  const rateLabel = loanTypeLabel === 'HDB Loan' ? 'HDB Rate' : 'Bank Rate';
  // Progressive purchases: once the loan is fully drawn the buyer pays the last
  // drawdown's installment — the outstanding balance over the tenure left by then —
  // not the full loan over the full tenure.
  const completionStage = stagedDownPayment ? finalLoanStage(computedStages) : undefined;
  const monthlyInstallment = completionStage
    ? completionStage.monthlyInstallment
    : calcMonthlyInstallment(totalLoan, effRate, inputs.tenure);
  // LTV shown against its regulatory base — HDB passes the valuation base
  // (lower of price/valuation) via ltvBase; everything else uses the price.
  const ltvBaseAmt = ltvBase && ltvBase > 0 ? ltvBase : price;
  const ltvBaseName = ltvBaseLabel ?? 'Purchase Price';
  const ltvPct = (ltvBaseAmt > 0 ? (totalLoan / ltvBaseAmt) * 100 : ltvDecimal * 100).toFixed(1);
  const downPct = (100 - Number(ltvPct)).toFixed(1);
  const n = inputs.tenure * 12;
  const rBank = effRate / 100 / 12;
  const nLeft = completionStage?.remainingTenureMonths || n;
  const loanBase = completionStage ? completionStage.outstandingLoan : totalLoan;
  const pmtFactor = rBank > 0 ? rBank * Math.pow(1 + rBank, nLeft) / (Math.pow(1 + rBank, nLeft) - 1) : 1 / nLeft;
  // Shared explain rows for the installment (full-tenure, or at completion for progressive).
  const installmentRows: CalcDialogRow[] = completionStage
    ? [
      { label: 'Loan Amount (fully drawn)', value: fmt(completionStage.cumulativeLoan) },
      { label: 'Outstanding at last drawdown', value: fmt(loanBase) },
      { label: rateLabel, value: `${effRate.toFixed(2)}%` },
      { label: 'Remaining tenure', value: `${nLeft} of ${n} mths` },
      { label: 'Monthly rate r = rate ÷ 12', value: `${(effRate / 12).toFixed(5)}%` },
      { isSeparator: true, label: '', value: '' },
      { label: 'Formula: B × r(1+r)ⁿ / ((1+r)ⁿ−1), n = months left', value: '' },
      { label: `${fmt(loanBase)} × ${pmtFactor.toFixed(6)}`, value: fmtInstallment(monthlyInstallment), isResult: true },
      { note: true, label: 'Progressive purchase — the tenure starts at the first disbursement, and the bank resets the installment at each drawdown to repay the outstanding balance over the months left. This is the installment once the loan is fully drawn.', value: '' },
    ]
    : [
      { label: 'Loan Amount', value: fmt(totalLoan) },
      { label: rateLabel, value: `${effRate.toFixed(2)}%` },
      { label: 'Tenure', value: `${inputs.tenure} yrs = ${n} mths` },
      { label: 'Monthly rate r = rate ÷ 12', value: `${(effRate / 12).toFixed(5)}%` },
      { isSeparator: true, label: '', value: '' },
      { label: 'Formula: L × r(1+r)ⁿ / ((1+r)ⁿ−1)', value: '' },
      { label: `${fmt(totalLoan)} × ${pmtFactor.toFixed(6)}`, value: fmtInstallment(monthlyInstallment), isResult: true },
    ];

  const explain = (info: CalcDialogInfo) => onExplain && onExplain(info);

  const SummaryRow = ({ label, value, sub, indent, info, pass }: { label: string; value: string; sub?: string; indent?: boolean; info?: CalcDialogInfo; pass?: boolean }) => (
    <Box onClick={info ? () => explain(info) : undefined}
      sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.75, pl: indent ? 2 : 0, cursor: info ? 'pointer' : 'default' }}>
      <Typography variant={indent ? 'caption' : 'body2'} sx={{ color: indent ? 'text.secondary' : 'text.primary', flex: 1 }}>
        {label}
        {info && <InfoOutlinedIcon sx={{ fontSize: 14, color: 'primary.main', ml: 0.4, verticalAlign: 'text-bottom' }} />}
        {sub && <Typography component="span" variant="caption" sx={{ display: 'block', color: 'text.disabled' }}>{sub}</Typography>}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        {pass !== undefined && (pass
          ? <CheckCircleIcon sx={{ fontSize: 17, color: 'success.main' }} />
          : <CancelIcon sx={{ fontSize: 17, color: 'error.main' }} />)}
        <Typography variant={indent ? 'caption' : 'body2'} fontWeight={indent ? 500 : 700}
          sx={{ color: pass === false ? 'error.main' : undefined }}>
          {value}
        </Typography>
      </Box>
    </Box>
  );

  return (
    <Box>
      {/* Loan Summary — with eligibility (detailed mode) the card leads with the buyer's
          income-based figures; the LTV loan row is redundant there (the Payment Structure
          already shows it) and is kept only for simple mode. */}
      {inputs.hasBankLoan && (
        <Card data-tutorial="loan-summary" variant="outlined" sx={{ p: 2, mb: 2, bgcolor: cardBg, borderColor: cardBorder }}>
          {loanEligibility ? (() => {
            const maxLoanQ = loanEligibility.maxLoan;
            const fits = totalLoan <= maxLoanQ;
            const usedPct = maxLoanQ > 0 ? Math.min(100, (totalLoan / maxLoanQ) * 100) : 100;
            const diff = Math.abs(maxLoanQ - totalLoan);
            return (
              <>
                <Typography variant="caption" fontWeight={700} sx={{ color: 'text.secondary', letterSpacing: '0.06em', textTransform: 'uppercase', display: 'block', mb: 1 }}>
                  Loan Eligibility
                </Typography>

                {/* The two figures buyers actually compare: borrowing power vs this purchase */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: 2, mb: 1 }}>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', lineHeight: 1.3 }}>
                      Max you can borrow
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.disabled', display: 'block', lineHeight: 1.3 }}>
                      based on income
                    </Typography>
                    <Typography fontWeight={800} sx={{ fontSize: '1.1rem', mt: 0.25 }}>{fmt(maxLoanQ)}</Typography>
                  </Box>
                  <Box sx={{ minWidth: 0, textAlign: 'right' }}>
                    <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', lineHeight: 1.3 }}>
                      Loan needed
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.disabled', display: 'block', lineHeight: 1.3 }}>
                      after your cash + CPF · LTV {ltvPct}%
                    </Typography>
                    <Typography fontWeight={800} sx={{ fontSize: '1.1rem', mt: 0.25, color: fits ? undefined : 'error.main' }}>
                      {fmt(totalLoan)}
                    </Typography>
                  </Box>
                </Box>

                {/* Usage bar — share of borrowing power this purchase consumes */}
                <Box sx={{ position: 'relative', height: 8, borderRadius: 4, bgcolor: 'action.hover', overflow: 'hidden', mb: 0.5 }}>
                  <Box sx={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: `${usedPct}%`, bgcolor: fits ? 'success.main' : 'error.main', borderRadius: 4 }} />
                </Box>
                <Typography variant="caption" sx={{ color: fits ? 'success.main' : 'error.main', fontWeight: 700, display: 'block', mb: 1 }}>
                  {fits
                    ? `✓ Eligible — uses ${maxLoanQ > 0 ? Math.round((totalLoan / maxLoanQ) * 100) : 0}% of your limit, ${fmt(diff)} to spare`
                    : `✗ Shortfall ${fmt(diff)} — add cash/CPF to the down payment or lower the price`}
                </Typography>
              </>
            );
          })() : (
            <SummaryRow label={`${loanTypeLabel} (LTV ${ltvPct}%)`} value={fmt(totalLoan)} pass={ltvPass} info={{
              title: `${loanTypeLabel} (LTV)`,
              rows: [
                { label: ltvBaseName, value: fmt(ltvBaseAmt) },
                { label: `× LTV Ratio`, value: `${ltvPct}%` },
                { isSeparator: true, label: '', value: '' },
                { label: `= ${fmt(ltvBaseAmt)} × ${ltvPct}%`, value: fmt(totalLoan), isResult: true },
                ...(ltvBaseAmt < price ? [{ note: true, label: 'The loan is based on the valuation price (lower than the purchase price) — the difference (COV) is payable in cash.', value: '' }] : []),
              ],
            }} />
          )}
          <SummaryRow label="Estimated Monthly Installment" value={fmtInstallment(monthlyInstallment)} info={{
            title: 'Estimated Monthly Installment',
            rows: installmentRows,
          }} />
          {loanEligibility && (
            <>
              <Divider sx={{ my: 1 }} />
              <SummaryRow label="Income Weighted Avg Age (IWAA)" value={`${loanEligibility.iwaa} yrs`} />
              <SummaryRow label="Max Purchase Price" value={loanEligibility.maxPriceLoan > 0 ? fmt(loanEligibility.maxPriceLoan) : '–'} />
              <SummaryRow label="Total Recognised Income" value={fmt(loanEligibility.totalRecognisedIncome)} />
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25, lineHeight: 1.4 }}>
                {loanEligibility.rateNote}
              </Typography>
            </>
          )}
        </Card>
      )}

      {/* Initial Outlay */}
      {(() => {
        // Down payment: for new-launch/EC, mirror the staged schedule (OTP 5% + Pay 15%);
        // otherwise fall back to the simple minimum 5% cash row.
        const dpStages = stagedDownPayment
          ? computedStages.filter((s) => s.type === 'otp' || s.type === 'sp')
          : [];
        const useStagedDp = dpStages.length > 0;
        // Non-staged (resale): use the buyer's actual cash/CPF down-payment allocation
        // when provided (private resale), else fall back to the 5%-cash / HDB-CPF defaults.
        const dpCpf = useStagedDp
          ? dpStages.reduce((a, s) => a + s.cpfUsed, 0)
          : (downCpfAlloc !== undefined ? downCpfAlloc : hdbCpfUsed);
        const dpCash = useStagedDp
          ? dpStages.reduce((a, s) => a + s.cashUsed, 0)
          : (downCashAlloc !== undefined ? downCashAlloc : Math.round(price * 0.05));
        // Bridging loan (EC DPS) disburses at TOP, not the upfront down-payment
        // stages, so it never contributes to the Initial Outlay (this stays 0).
        const dpBridging = useStagedDp ? dpStages.reduce((a, s) => a + s.bridgingPayout, 0) : 0;

        // HDB: the full timeline outlay (all stages) is provided pre-split into CPF/Cash.
        const hdbTotalCpf = hdbOutlay ? hdbOutlay.downCpf + hdbOutlay.bsdCpf + hdbOutlay.absdCpf + hdbOutlay.legalCpf + hdbOutlay.hpsCpf : 0;
        const hdbTotalCash = hdbOutlay
          ? hdbOutlay.downCash + hdbOutlay.bsdCash + hdbOutlay.absdCash + hdbOutlay.legalCash + hdbOutlay.hpsCash + hdbOutlay.valFee + hdbOutlay.adminFee + hdbOutlay.caveatFee + hdbOutlay.agencyFee
          : 0;

        // Bridging column only when a bridging loan funds part of the initial outlay.
        const hasBridgingCol = dpBridging > 0;
        const hasCpfCol = hdbOutlay
          ? (!simpleMode && hdbTotalCpf > 0)
          : (bsdByCpf || absdByCpf || legalFeeByCpf || dpCpf > 0);
        // Total column only adds value when there's more than one funding column to sum.
        const hasTotalCol = hasCpfCol || hasBridgingCol;
        const colSpan = 1 + (hasCpfCol ? 1 : 0) + 1 + (hasBridgingCol ? 1 : 0) + (hasTotalCol ? 1 : 0);
        const hdrSx = { fontSize: '0.72rem', fontWeight: 700, color: 'text.secondary', textAlign: 'right' as const, pb: 0.75 };
        const cellSx = { fontSize: '0.8rem', textAlign: 'right' as const, whiteSpace: 'nowrap' as const };
        const labelSx = { fontSize: '0.8rem', color: 'text.primary' };
        const subLabelSx = { fontSize: '0.71rem', color: 'text.secondary' };

        const TRow = ({ label, sub, cpf, cash, bridging, bold, highlight, indent, color, info }: {
          label: string; sub?: string; cpf?: number; cash?: number; bridging?: number;
          bold?: boolean; highlight?: boolean; indent?: boolean; color?: string; info?: CalcDialogInfo;
        }) => (
          <Box component="tr" onClick={info ? () => explain(info) : undefined}
            sx={{ verticalAlign: 'middle', cursor: info ? 'pointer' : 'default', '&:hover': info ? { bgcolor: 'rgba(217,119,6,0.06)' } : undefined }}>
            <Box component="td" sx={{ ...labelSx, fontWeight: bold ? 700 : 400, color: highlight ? '#D97706' : color, pl: indent ? 2 : 0, py: 0.5 }}>
              {label}
              {info && <InfoOutlinedIcon sx={{ fontSize: 13, color: 'primary.main', ml: 0.4, verticalAlign: 'text-bottom' }} />}
              {sub && <Box component="span" sx={{ ...subLabelSx, display: 'block' }}>{sub}</Box>}
            </Box>
            {hasCpfCol && (
              <Box component="td" sx={{ ...cellSx, fontWeight: bold ? 700 : 400, color: highlight ? '#D97706' : 'text.secondary', pr: 1.5, py: 0.5 }}>
                {cpf !== undefined ? fmt(cpf) : ''}
              </Box>
            )}
            <Box component="td" sx={{ ...cellSx, fontWeight: bold ? 700 : 400, color: highlight ? '#D97706' : color, pr: (hasBridgingCol || hasTotalCol) ? 1.5 : 0, py: 0.5 }}>
              {cash !== undefined ? fmt(cash) : ''}
            </Box>
            {hasBridgingCol && (
              <Box component="td" sx={{ ...cellSx, fontWeight: bold ? 700 : 400, color: highlight ? '#D97706' : '#7c3aed', pr: hasTotalCol ? 1.5 : 0, py: 0.5 }}>
                {bridging !== undefined && bridging > 0 ? fmt(bridging) : ''}
              </Box>
            )}
            {hasTotalCol && (
              <Box component="td" sx={{ ...cellSx, fontWeight: bold ? 700 : 600, color: highlight ? '#D97706' : color, py: 0.5 }}>
                {(cpf !== undefined || cash !== undefined || bridging !== undefined) ? fmt((cpf ?? 0) + (cash ?? 0) + (bridging ?? 0)) : ''}
              </Box>
            )}
          </Box>
        );

        const Separator = ({ light }: { light?: boolean }) => (
          <Box component="tr">
            <Box component="td" colSpan={colSpan} sx={{ py: 0.15 }}>
              <Divider sx={{ opacity: light ? 0.4 : 1 }} />
            </Box>
          </Box>
        );

        const SectionLabel = ({ label }: { label: string }) => (
          <Box component="tr">
            <Box component="td" colSpan={colSpan} sx={{ fontSize: '0.75rem', fontWeight: 700, color: 'text.secondary', pt: 1.25, pb: 0.25, letterSpacing: '0.04em', textTransform: 'uppercase' as const }}>
              {label}
            </Box>
          </Box>
        );

        const totalCash = hdbOutlay ? hdbTotalCash : dpCash + totalStampDutyCash + legalFeeCash;
        const totalCpfUsed = hdbOutlay ? hdbTotalCpf : dpCpf + totalStampDutyCpf + legalFeeCpf;
        const totalCpf = hasCpfCol ? totalCpfUsed : undefined;
        // Bridging (EC DPS) disburses at TOP, so it never appears in the Initial Outlay.
        const totalBridging = hasBridgingCol ? dpBridging : undefined;

        // ── Fund Balance: available funds minus everything spent in the Initial Outlay ──
        // For progressive (new-launch / EC) purchases, the buyer-funded portion of the
        // construction-stage payments (the remaining down payment that spills past S&P,
        // e.g. the CPF paid at Foundation) is a committed outflow too — subtract it so the
        // leftover balance / reserves don't overstate what's actually free.
        const progStages = stagedDownPayment
          ? computedStages.filter((s) => s.type === 'construction' || s.type === 'top' || s.type === 'csc')
          : [];
        const progDpCash = progStages.reduce((a, s) => a + s.cashUsed, 0);
        const progDpCpf = progStages.reduce((a, s) => a + s.cpfUsed, 0);
        // Pledged/unpledged assets are committed to bridge the loan (pledged funds are
        // locked, unpledged are shown to the bank), so they're deducted from the balance —
        // they aren't free leftover reserves.
        const pledgeUnpledgeTotal = effectivePledged + effectiveUnpledged;
        // Label follows the actual mix — fully pledged / fully unpledged / both.
        const pledgeRowName = effectiveUnpledged <= 0
          ? 'Pledged Funds'
          : effectivePledged <= 0 ? 'Unpledged Funds' : 'Pledged & Unpledged Funds';
        const cashBalance = cashAvailable - totalCash - progDpCash - pledgeUnpledgeTotal;
        const cpfBalance = cpfAvailable - totalCpfUsed - progDpCpf;
        const fbHasCpf = cpfAvailable > 0 || totalCpfUsed > 0 || progDpCpf > 0;
        // Reserves: leftover balance (cash + CPF) available to service the installment.
        const reserveFunds = Math.max(0, cashBalance) + Math.max(0, cpfBalance);
        const signedFmt = (v: number) => (v < 0 ? `-${fmt(Math.abs(v))}` : fmt(v));

        // Detailed working for each HDB Initial Outlay row (tap the row to view).
        const hdbDownTotal = hdbOutlay ? hdbOutlay.downCpf + hdbOutlay.downCash : 0;
        const hdbLoanApprox = hdbOutlay ? Math.max(0, price - hdbDownTotal) : 0;
        const hdbAbsd = hdbOutlay ? hdbOutlay.absdCpf + hdbOutlay.absdCash : 0;
        const hdbLegal = hdbOutlay ? hdbOutlay.legalCpf + hdbOutlay.legalCash : 0;
        const hdbHps = hdbOutlay ? hdbOutlay.hpsCpf + hdbOutlay.hpsCash : 0;
        const agencyBase = hdbOutlay ? Math.round(hdbOutlay.agencyFee / AGENCY_FEE_GST) : 0;
        const simpleHdbCashDown = Math.min(hdbDownTotal, Math.max(0, hdbMandatoryCash));
        const simpleHdbFlexibleDown = Math.max(0, hdbDownTotal - simpleHdbCashDown);
        const bsdRows: CalcDialogRow[] = hdbOutlay
          ? getBsdBreakdown(price).filter((b) => b.taxable > 0).map((b) => ({ label: b.bracket, value: fmt(b.duty) }))
          : [];

        // ── Tap-to-explain breakdowns for each Fund Balance row ──
        const availableFundsDialog: CalcDialogInfo = {
          title: 'Available Funds',
          rows: [
            ...(fbHasCpf ? [{ label: 'CPF (OA + grants)', value: fmt(cpfAvailable) } as CalcDialogRow] : []),
            { label: 'Cash (on hand + net sale proceeds)', value: fmt(cashAvailable) },
            { isSeparator: true, label: '', value: '' },
            { label: 'Total Available', value: fmt(cpfAvailable + cashAvailable), isResult: true },
            { note: true, label: 'From your Available Funds entries — additional cash, sale proceeds (net of levies / set-asides), CPF OA and grants.', value: '' },
          ],
        };
        const outlayDialog: CalcDialogInfo = {
          title: 'Less: Initial Outlay',
          rows: hdbOutlay
            ? [
                ...(fbHasCpf ? [
                  { label: 'CPF — Down Payment', value: fmt(hdbOutlay.downCpf) } as CalcDialogRow,
                  { label: 'CPF — Stamp Duty (BSD + ABSD)', value: fmt(hdbOutlay.bsdCpf + hdbOutlay.absdCpf) },
                  { label: 'CPF — Legal + HPS', value: fmt(hdbOutlay.legalCpf + hdbOutlay.hpsCpf) },
                  { label: '= Total CPF', value: fmt(totalCpfUsed) },
                  { isSeparator: true, label: '', value: '' },
                ] : []),
                { label: 'Cash — Down Payment', value: fmt(hdbOutlay.downCash) },
                { label: 'Cash — Stamp Duty (BSD + ABSD)', value: fmt(hdbOutlay.bsdCash + hdbOutlay.absdCash) },
                { label: 'Cash — Legal + HPS', value: fmt(hdbOutlay.legalCash + hdbOutlay.hpsCash) },
                { label: 'Cash — Fees (valuation / admin / caveat / agency)', value: fmt(hdbOutlay.valFee + hdbOutlay.adminFee + hdbOutlay.caveatFee + hdbOutlay.agencyFee) },
                { isSeparator: true, label: '', value: '' },
                { label: '= Total Cash', value: fmt(totalCash), isResult: true },
              ]
            : [
                ...(fbHasCpf ? [
                  { label: 'CPF — Down Payment', value: fmt(dpCpf) } as CalcDialogRow,
                  { label: 'CPF — Stamp Duty (BSD + ABSD)', value: fmt(totalStampDutyCpf) },
                  { label: 'CPF — Legal / Conveyancing', value: fmt(legalFeeCpf) },
                  { label: '= Total CPF', value: fmt(totalCpfUsed) },
                  { isSeparator: true, label: '', value: '' },
                ] : []),
                { label: 'Cash — Down Payment', value: fmt(dpCash) },
                { label: 'Cash — Stamp Duty (BSD + ABSD)', value: fmt(totalStampDutyCash) },
                { label: 'Cash — Legal / Conveyancing', value: fmt(legalFeeCash) },
                { isSeparator: true, label: '', value: '' },
                { label: '= Total Cash', value: fmt(totalCash), isResult: true },
              ],
        };
        const progDpDialog: CalcDialogInfo = {
          title: 'Less: Progressive Down Payment',
          rows: [
            ...progStages
              .filter((s) => s.cashUsed + s.cpfUsed > 0)
              .map((s) => ({
                label: s.label + (s.cpfUsed > 0 ? ` (cash ${fmt(s.cashUsed)} + CPF ${fmt(s.cpfUsed)})` : ''),
                value: fmt(s.cashUsed + s.cpfUsed),
              })),
            { isSeparator: true, label: '', value: '' },
            { label: '= Total Buyer-Funded at Stages', value: fmt(progDpCash + progDpCpf), isResult: true },
            { note: true, label: 'Cash / CPF you pay at construction milestones before the bank starts disbursing the loan.', value: '' },
          ],
        };
        const pledgeDialog: CalcDialogInfo = {
          title: `Less: ${pledgeRowName}`,
          rows: [
            ...(effectivePledged > 0 ? [{ label: 'Pledged (locked with the bank, 48 months)', value: fmt(effectivePledged) } as CalcDialogRow] : []),
            ...(effectiveUnpledged > 0 ? [{ label: 'Unpledged (shown to the bank)', value: fmt(effectiveUnpledged) } as CalcDialogRow] : []),
            { isSeparator: true, label: '', value: '' },
            { label: '= Total Committed', value: fmt(pledgeUnpledgeTotal), isResult: true },
            { note: true, label: 'Assets committed to bridge the loan shortfall — locked or evidenced to the bank, so not free leftover reserves.', value: '' },
          ],
        };
        const balanceDialog: CalcDialogInfo = {
          title: 'Fund Balance',
          rows: [
            ...(fbHasCpf ? [
              { label: 'Available CPF', value: fmt(cpfAvailable) } as CalcDialogRow,
              { label: '− Initial Outlay (CPF)', value: fmt(totalCpfUsed) },
              ...(progDpCpf > 0 ? [{ label: '− Progressive Down Payment (CPF)', value: fmt(progDpCpf) }] : []),
              { label: '= CPF Balance', value: signedFmt(cpfBalance), isResult: true },
              { isSeparator: true, label: '', value: '' },
            ] : []),
            { label: 'Available Cash', value: fmt(cashAvailable) },
            { label: '− Initial Outlay (Cash)', value: fmt(totalCash) },
            ...(progDpCash > 0 ? [{ label: '− Progressive Down Payment (Cash)', value: fmt(progDpCash) }] : []),
            ...(pledgeUnpledgeTotal > 0 ? [{ label: `− ${pledgeRowName}`, value: fmt(pledgeUnpledgeTotal) }] : []),
            { isSeparator: true, label: '', value: '' },
            { label: '= Cash Balance', value: signedFmt(cashBalance), isResult: true },
          ],
        };
        // Clickable Fund Balance row affordance (icon + hover), mirroring SummaryRow.
        const fbRowSx = { verticalAlign: 'middle', cursor: 'pointer', transition: 'background-color 120ms', '&:hover': { bgcolor: 'action.hover' } };
        const fbInfoIcon = <InfoOutlinedIcon sx={{ fontSize: 14, color: 'primary.main', ml: 0.4, verticalAlign: 'text-bottom' }} />;

        return (
          <>
          <Card data-tutorial="initial-outlay" variant="outlined" sx={{ p: 2, mb: 2, bgcolor: cardBg, borderColor: cardBorder }}>
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5, pb: 1, borderBottom: '1px solid', borderColor: 'divider', textAlign: 'center' }}>
              Initial Outlay
            </Typography>
            <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse' }}>
              <Box component="thead">
                <Box component="tr">
                  <Box component="th" sx={{ textAlign: 'left', pb: 0.75 }} />
                  {hasCpfCol && <Box component="th" sx={{ ...hdrSx, pr: 1.5, width: 90 }}>CPF</Box>}
                  <Box component="th" sx={{ ...hdrSx, pr: (hasBridgingCol || hasTotalCol) ? 1.5 : 0, width: 90 }}>
                    {simpleMode ? 'Amount' : 'Cash'}
                  </Box>
                  {hasBridgingCol && <Box component="th" sx={{ ...hdrSx, color: '#7c3aed', pr: hasTotalCol ? 1.5 : 0, width: 90 }}>Bridging</Box>}
                  {hasTotalCol && <Box component="th" sx={{ ...hdrSx, width: 100 }}>Total</Box>}
                </Box>
              </Box>
              <Box component="tbody">

                {hdbOutlay ? (
                  <>
                    {/* HDB — full timeline outlay (stages 1–7), split CPF / Cash */}
                    <SectionLabel label="Down Payment" />
                    {simpleMode && hdbLoanSource === 'bank' ? (
                      <>
                        <TRow
                          label="Minimum cash down payment (5%)"
                          sub="incl. OTP deposits; COV is additional cash"
                          indent
                          cash={simpleHdbCashDown}
                        />
                        {simpleHdbFlexibleDown > 0 && (
                          <TRow
                            label="Remaining down payment (20%) - CPF/Cash"
                            indent
                            cash={simpleHdbFlexibleDown}
                          />
                        )}
                      </>
                    ) : (
                      <TRow
                        label={simpleMode
                          ? hdbLoanSource === 'none'
                            ? 'Purchase price (100%) - CPF/Cash'
                            : 'Down payment (25% of valuation) - CPF/Cash'
                          : 'Down payment'}
                        sub={simpleMode ? 'OTP deposits and any COV are cash-only' : 'incl. cash-only deposits & COV'}
                        indent
                        cpf={!simpleMode && hdbOutlay.downCpf > 0 ? hdbOutlay.downCpf : undefined}
                        cash={simpleMode ? hdbOutlay.downCpf + hdbOutlay.downCash : hdbOutlay.downCash}
                        info={{ title: 'Down Payment', rows: [
                        { label: 'Purchase Price', value: fmt(price) },
                        ...(inputs.hasBankLoan ? [{ label: `− ${loanTypeLabel}`, value: fmt(hdbLoanApprox) }] : []),
                        { isSeparator: true, label: '', value: '' },
                        { label: '= Down Payment (incl. deposits & COV)', value: fmt(hdbDownTotal), isResult: true },
                        ...(simpleMode
                          ? [
                              { label: 'Payment method', value: 'CPF/Cash' },
                              { note: true, label: 'Option and exercise deposits, and any Cash Over Valuation (COV), must be paid in cash.', value: '' },
                            ]
                          : [
                              ...(hdbOutlay.downCpf > 0 ? [{ label: 'Paid by CPF', value: fmt(hdbOutlay.downCpf) }] : []),
                              { label: 'Paid by Cash', value: fmt(hdbOutlay.downCash) },
                            ]),
                        ] }}
                      />
                    )}
                    <Separator light />

                    <SectionLabel label="Stamp Duty" />
                    <TRow
                      label={simpleMode ? 'BSD - CPF/Cash' : 'BSD'}
                      indent
                      cpf={!simpleMode && hdbOutlay.bsdCpf > 0 ? hdbOutlay.bsdCpf : undefined}
                      cash={simpleMode ? hdbOutlay.bsdCpf + hdbOutlay.bsdCash : hdbOutlay.bsdCash}
                      info={{ title: "Buyer's Stamp Duty (BSD)", rows: [
                        { label: `On purchase price ${fmt(price)}`, value: '' },
                        ...bsdRows,
                        { isSeparator: true, label: '', value: '' },
                        { label: 'Total BSD', value: fmt(bsd), isResult: true },
                      ] }} />
                    {absdAmount > 0 && (
                      <TRow label={`ABSD (${absdRatePct}%)`} indent cpf={hdbOutlay.absdCpf > 0 ? hdbOutlay.absdCpf : undefined} cash={hdbOutlay.absdCash}
                        info={{ title: "Additional Buyer's Stamp Duty (ABSD)", rows: [
                          { label: 'Purchase Price', value: fmt(price) },
                          { label: '× ABSD Rate', value: `${absdRatePct}%` },
                          { isSeparator: true, label: '', value: '' },
                          { label: '= ABSD', value: fmt(hdbAbsd), isResult: true },
                        ] }} />
                    )}
                    <Separator light />

                    <SectionLabel label="Legal Fee" />
                    <TRow
                      label={simpleMode ? 'Conveyancing - CPF/Cash' : 'Conveyancing'}
                      indent
                      cpf={!simpleMode && hdbOutlay.legalCpf > 0 ? hdbOutlay.legalCpf : undefined}
                      cash={simpleMode ? hdbOutlay.legalCpf + hdbOutlay.legalCash : hdbOutlay.legalCash}
                      info={{ title: 'Legal / Conveyancing Fee', rows: [{ label: 'Conveyancing fee (as entered)', value: fmt(hdbLegal), isResult: true }] }} />
                    {hdbOutlay.caveatFee > 0 && <TRow label="Caveat Fee" indent cash={hdbOutlay.caveatFee}
                      info={{ title: 'Caveat Fee', rows: [{ label: 'Caveat lodgement fee (as entered)', value: fmt(hdbOutlay.caveatFee), isResult: true }] }} />}

                    {(hdbOutlay.hpsCash + hdbOutlay.hpsCpf) > 0 && (
                      <>
                        <Separator light />
                        <SectionLabel label="Home Protection Scheme" />
                        <TRow
                          label={simpleMode ? 'HPS premium - CPF/Cash' : 'HPS premium'}
                          indent
                          cpf={!simpleMode && hdbOutlay.hpsCpf > 0 ? hdbOutlay.hpsCpf : undefined}
                          cash={simpleMode ? hdbOutlay.hpsCpf + hdbOutlay.hpsCash : hdbOutlay.hpsCash}
                          info={{ title: 'Home Protection Scheme (HPS)', rows: [{ label: 'HPS premium (as entered)', value: fmt(hdbHps), isResult: true }] }} />
                      </>
                    )}

                    {(hdbOutlay.valFee + hdbOutlay.adminFee + hdbOutlay.agencyFee) > 0 && (
                      <>
                        <Separator light />
                        <SectionLabel label="Other Fees" />
                        {hdbOutlay.valFee > 0 && <TRow label={simpleMode ? 'Valuation Fee - Cash' : 'Valuation Fee'} indent cash={hdbOutlay.valFee}
                          info={{ title: 'Valuation Fee', rows: [{ label: 'HDB valuation fee (as entered)', value: fmt(hdbOutlay.valFee), isResult: true }] }} />}
                        {hdbOutlay.adminFee > 0 && <TRow label={simpleMode ? 'HDB Admin Fee - Cash' : 'HDB Admin Fee'} indent cash={hdbOutlay.adminFee}
                          info={{ title: 'HDB Admin Fee', rows: [{ label: 'HDB administrative fee (as entered)', value: fmt(hdbOutlay.adminFee), isResult: true }] }} />}
                        {hdbOutlay.agencyFee > 0 && <TRow label={simpleMode ? 'Agency Service Fee - Cash' : 'Agency Service Fee'} sub="incl. 9% GST" indent cash={hdbOutlay.agencyFee}
                          info={{ title: 'Agency Service Fee', rows: [
                            { label: 'Fee (excl. GST)', value: fmt(agencyBase) },
                            { label: '+ 9% GST', value: fmt(hdbOutlay.agencyFee - agencyBase) },
                            { isSeparator: true, label: '', value: '' },
                            { label: '= Agency Service Fee', value: fmt(hdbOutlay.agencyFee), isResult: true },
                          ] }} />}
                      </>
                    )}
                    <Separator />

                    <TRow
                      label="Total Initial Outlay"
                      bold
                      highlight
                      cpf={!simpleMode ? totalCpf : undefined}
                      cash={simpleMode ? totalCpfUsed + totalCash : totalCash}
                      info={{ title: 'Total Initial Outlay', rows: [
                        { label: 'Down payment', value: fmt(hdbDownTotal) },
                        { label: 'BSD', value: fmt(bsd) },
                        ...(hdbAbsd > 0 ? [{ label: 'ABSD', value: fmt(hdbAbsd) }] : []),
                        { label: 'Conveyancing', value: fmt(hdbLegal) },
                        ...(hdbOutlay.caveatFee > 0 ? [{ label: 'Caveat Fee', value: fmt(hdbOutlay.caveatFee) }] : []),
                        ...(hdbHps > 0 ? [{ label: 'HPS premium', value: fmt(hdbHps) }] : []),
                        ...(hdbOutlay.valFee > 0 ? [{ label: 'Valuation Fee', value: fmt(hdbOutlay.valFee) }] : []),
                        ...(hdbOutlay.adminFee > 0 ? [{ label: 'HDB Admin Fee', value: fmt(hdbOutlay.adminFee) }] : []),
                        ...(hdbOutlay.agencyFee > 0 ? [{ label: 'Agency Service Fee', value: fmt(hdbOutlay.agencyFee) }] : []),
                        { isSeparator: true, label: '', value: '' },
                        { label: 'Total (CPF + Cash)', value: fmt((totalCpf ?? 0) + totalCash), isResult: true },
                      ] }} />
                    </>
                ) : (
                <>
                {/* Down Payment */}
                <SectionLabel label={useStagedDp || downCashAlloc !== undefined ? 'Down Payment' : '5% Down Payment'} />
                {useStagedDp && simpleMode
                  ? dpStages.flatMap((s) => {
                      // Booking fee (5%) is always cash; the 15% payment is CPF/Cash — but when the
                      // 55% LTV / 10% regime demands more cash, split off the mandatory 5% first.
                      if (s.type === 'otp') {
                        return [<TRow key={s.id} label="Booking Fee (5%) - Cash" indent cash={s.cashUsed} />];
                      }
                      const mand = s.simpleMandatoryCash ?? 0;
                      const flex = s.simpleFlex ?? s.cashUsed;
                      return mand > 0
                        ? [
                            <TRow key={`${s.id}-cash`} label="Pay 5% - Cash" indent cash={mand} />,
                            <TRow key={`${s.id}-flex`} label="Pay 10% - CPF/Cash" indent cash={flex} />,
                          ]
                        : [<TRow key={s.id} label="Pay 15% - CPF/Cash" indent cash={flex} />];
                    })
                  : useStagedDp
                  ? dpStages.map((s) => (
                      <TRow
                        key={s.id}
                        label={s.label}
                        indent
                        cpf={s.cpfUsed > 0 ? s.cpfUsed : undefined}
                        cash={s.cashUsed}
                        bridging={s.bridgingPayout > 0 ? s.bridgingPayout : undefined}
                      />
                    ))
                  : (downCashAlloc !== undefined || downCpfAlloc !== undefined) ? (
                    <>
                      <TRow label="Cash" sub={dpCash > Math.round(price * (minCashPct / 100)) ? `incl. min ${minCashPct}% (${fmt(Math.round(price * (minCashPct / 100)))})` : `min ${minCashPct}% required`} indent cash={dpCash} />
                      {dpCpf > 0 && <TRow label="CPF" indent cpf={dpCpf} />}
                    </>
                  ) : (
                    <>
                      <TRow label="Min Cash (5%)" indent cash={dpCash} />
                      {hdbCpfUsed > 0 && <TRow label="CPF + Grants" indent cpf={hdbCpfUsed} />}
                    </>
                  )}
                <Separator light />

                {/* Stamp Duty */}
                <SectionLabel label="Stamp Duty" />
                <TRow
                  label={
                    simpleResale ? 'BSD - Cash (Reimbursable from CPF)'
                    : detailedResale ? 'BSD (Reimbursable from CPF)'
                    : simpleMode ? 'BSD - CPF/Cash'
                    : 'BSD'
                  }
                  indent
                  cpf={bsdByCpf ? bsdCpf : undefined}
                  cash={bsdByCpf ? bsdCash : bsd}
                />
                {absdAmount > 0 && (
                  <TRow
                    label={
                      simpleResale ? `ABSD (${absdRatePct}%) - Cash (Reimbursable from CPF)`
                      : detailedResale ? `ABSD (${absdRatePct}%) (Reimbursable from CPF)`
                      : simpleMode ? `ABSD (${absdRatePct}%) - CPF/Cash`
                      : `ABSD (${absdRatePct}%)`
                    }
                    indent
                    cpf={absdByCpf ? absdCpf : undefined}
                    cash={absdByCpf ? absdCash : absdAmount}
                  />
                )}
                <Separator light />

                {/* Legal Fee */}
                <SectionLabel label="Legal Fee" />
                <TRow
                  label={simpleMode ? 'Conveyancing - CPF/Cash' : 'Conveyancing'}
                  indent
                  cpf={legalFeeByCpf ? legalFeeCpf : undefined}
                  cash={legalFeeByCpf ? legalFeeCash : inputs.legalFee}
                />

                <Separator />

                {/* Total */}
                <TRow
                  label="Total Initial Outlay"
                  bold highlight
                  cpf={totalCpf}
                  cash={totalCash}
                  bridging={totalBridging}
                />
                </>
                )}

                {/* Unpledged / Pledged — only show if non-zero */}
                {effectiveUnpledged > 0 && (
                  <>
                    <Separator light />
                    <SectionLabel label="Additional Funds Required" />
                    <TRow label="Unpledged Amount" indent cash={effectiveUnpledged} color="text.secondary" sub="(liquid assets, not locked)" />
                  </>
                )}
                {effectivePledged > 0 && (
                  <>
                    {effectiveUnpledged === 0 && <Separator light />}
                    {effectiveUnpledged === 0 && <SectionLabel label="Additional Funds Required" />}
                    <TRow label="Pledged Amount" indent cash={effectivePledged} color="text.secondary" sub="(CPF/fixed deposits locked in)" />
                  </>
                )}

                {/* Grand Total = Initial Outlay + fund pledging (unpledged + pledged) */}
                {(effectivePledged > 0 || effectiveUnpledged > 0) && (
                  <>
                    <Separator />
                    <TRow
                      label="Grand Total Outlay"
                      sub="incl. fund pledging"
                      bold highlight
                      cpf={totalCpf}
                      cash={totalCash + effectiveUnpledged + effectivePledged}
                      bridging={totalBridging}
                    />
                  </>
                )}

              </Box>
            </Box>
          </Card>

          {/* Fund Balance */}
          {!hideFundBalance && (
          <Card data-tutorial="fund-balance" variant="outlined" sx={{ p: 2, mb: 2, bgcolor: cardBg, borderColor: cardBorder }}>
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5, pb: 1, borderBottom: '1px solid', borderColor: 'divider', textAlign: 'center' }}>
              Fund Balance
            </Typography>
            <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse' }}>
              <Box component="thead">
                <Box component="tr">
                  <Box component="th" sx={{ textAlign: 'left', pb: 0.75 }} />
                  {fbHasCpf && <Box component="th" sx={{ ...hdrSx, pr: 1.5, width: 90 }}>CPF</Box>}
                  <Box component="th" sx={{ ...hdrSx, width: 100 }}>Cash</Box>
                </Box>
              </Box>
              <Box component="tbody">
                <Box component="tr" onClick={() => explain(availableFundsDialog)} sx={fbRowSx}>
                  <Box component="td" sx={{ ...labelSx, py: 0.5 }}>Available Funds{fbInfoIcon}</Box>
                  {fbHasCpf && <Box component="td" sx={{ ...cellSx, color: 'text.secondary', pr: 1.5, py: 0.5 }}>{fmt(cpfAvailable)}</Box>}
                  <Box component="td" sx={{ ...cellSx, color: 'text.secondary', py: 0.5 }}>{fmt(cashAvailable)}</Box>
                </Box>
                <Box component="tr" onClick={() => explain(outlayDialog)} sx={fbRowSx}>
                  <Box component="td" sx={{ ...labelSx, py: 0.5 }}>Less: Initial Outlay{fbInfoIcon}</Box>
                  {fbHasCpf && <Box component="td" sx={{ ...cellSx, color: 'text.secondary', pr: 1.5, py: 0.5 }}>{signedFmt(-totalCpfUsed)}</Box>}
                  <Box component="td" sx={{ ...cellSx, color: 'text.secondary', py: 0.5 }}>{signedFmt(-totalCash)}</Box>
                </Box>
                {(progDpCash > 0 || progDpCpf > 0) && (
                  <Box component="tr" onClick={() => explain(progDpDialog)} sx={fbRowSx}>
                    <Box component="td" sx={{ ...labelSx, py: 0.5 }}>
                      Less: Progressive Down Payment{fbInfoIcon}
                      <Box component="span" sx={{ ...subLabelSx, display: 'block' }}>Buyer-funded portion at construction stages</Box>
                    </Box>
                    {fbHasCpf && <Box component="td" sx={{ ...cellSx, color: 'text.secondary', pr: 1.5, py: 0.5 }}>{signedFmt(-progDpCpf)}</Box>}
                    <Box component="td" sx={{ ...cellSx, color: 'text.secondary', py: 0.5 }}>{signedFmt(-progDpCash)}</Box>
                  </Box>
                )}
                {pledgeUnpledgeTotal > 0 && (
                  <Box component="tr" onClick={() => explain(pledgeDialog)} sx={fbRowSx}>
                    <Box component="td" sx={{ ...labelSx, py: 0.5 }}>
                      Less: {pledgeRowName}{fbInfoIcon}
                      <Box component="span" sx={{ ...subLabelSx, display: 'block' }}>Assets committed / locked to bridge the loan</Box>
                    </Box>
                    {fbHasCpf && <Box component="td" sx={{ ...cellSx, color: 'text.secondary', pr: 1.5, py: 0.5 }} />}
                    <Box component="td" sx={{ ...cellSx, color: 'text.secondary', py: 0.5 }}>{signedFmt(-pledgeUnpledgeTotal)}</Box>
                  </Box>
                )}
                <Box component="tr"><Box component="td" colSpan={fbHasCpf ? 3 : 2} sx={{ py: 0.15 }}><Divider /></Box></Box>
                <Box component="tr" onClick={() => explain(balanceDialog)} sx={fbRowSx}>
                  <Box component="td" sx={{ ...labelSx, fontWeight: 700, py: 0.5 }}>Balance{fbInfoIcon}</Box>
                  {fbHasCpf && (
                    <Box component="td" sx={{ ...cellSx, fontWeight: 700, pr: 1.5, py: 0.5, color: cpfBalance < 0 ? 'error.main' : 'text.primary' }}>
                      {signedFmt(cpfBalance)}
                    </Box>
                  )}
                  <Box component="td" sx={{ ...cellSx, fontWeight: 700, py: 0.5, color: cashBalance < 0 ? 'error.main' : 'text.primary' }}>
                    {signedFmt(cashBalance)}
                  </Box>
                </Box>
              </Box>
            </Box>

            {/* Reserves */}
            {(() => {
              const runwayCurrent = computeRunway(reserveFunds, monthlyInstallment, computedStages, 0, stagedDownPayment);
              const runwayEmployed = computeRunway(reserveFunds, monthlyInstallment, computedStages, monthlyCpfOa, stagedDownPayment);
              const monthsCurrent = runwayCurrent.hasLoan ? runwayCurrent.totalMonths : null;
              const monthsEmployed = runwayEmployed.hasLoan ? runwayEmployed.totalMonths : null;
              const fmtMonths = (m: number | null) =>
                m === null ? '–' : m === Infinity ? 'Sustainable' : `${m} mth${m === 1 ? '' : 's'}`;
              const fmtMonthsSize = (m: number | null) => (m === Infinity ? '0.78rem' : '1.05rem');

              // Tap-to-explain breakdowns for each Months-Covered box.
              const reserveRows: CalcDialogRow[] = [
                { label: 'Leftover Cash', value: fmt(Math.max(0, cashBalance)) },
                { label: 'Leftover CPF', value: fmt(Math.max(0, cpfBalance)) },
                { isSeparator: true, label: '', value: '' },
                { label: '= Reserve Funds', value: fmt(reserveFunds) },
                { isSeparator: true, label: '', value: '' },
              ];
              // The pre-loan window (OTP → first disbursement) carries no installment, so
              // it is listed on its own rather than as a consumption segment.
              const graceRows = (r: RunwayResult): CalcDialogRow[] =>
                r.graceMonths > 0
                  ? [
                      { label: `OTP → first disbursement (no installment yet)`, value: fmtMonths(r.graceMonths) },
                      { isSeparator: true, label: '', value: '' },
                    ]
                  : [];
              // Per-stage consumption rows: installment × covered months = subtotal.
              // Progressive stages show "covered / stage-length"; the open-ended
              // steady-state segment (no stageMonths) shows just the covered count.
              const segmentRows = (r: RunwayResult): CalcDialogRow[] =>
                r.segments.map((s) => {
                  const monthsLabel = s.stageMonths != null ? `${s.months} / ${s.stageMonths}` : `${s.months}`;
                  return {
                    label: `${fmtInstallment(s.installment)} × ${monthsLabel} mth${s.months === 1 ? '' : 's'} (${s.label})`,
                    value: fmt(Math.round(s.installment * s.months)),
                  };
                });
              const currentFundDialog: CalcDialogInfo = {
                title: 'Months Covered — Current Fund',
                rows: [
                  ...reserveRows,
                  ...graceRows(runwayCurrent),
                  ...(stagedDownPayment
                    ? [{ label: 'Progressive — installment grows each stage:', value: '' } as CalcDialogRow]
                    : []),
                  ...segmentRows(runwayCurrent),
                  { isSeparator: true, label: '', value: '' },
                  { label: 'Months Covered (no further income)', value: fmtMonths(monthsCurrent), isResult: true },
                  ...(reserveFunds <= 0
                    ? [{ note: true, label: 'No leftover Cash or CPF, so nothing is available to service the loan.', value: '' } as CalcDialogRow]
                    : runwayCurrent.graceMonths > 0
                      ? [{ note: true, label: 'Counted from the OTP stage — the bank only starts disbursing at the first construction milestone, so no installment is due before then.', value: '' } as CalcDialogRow]
                      : []),
                ],
              };
              const netDrain = Math.max(0, monthlyInstallment - monthlyCpfOa);
              const employedMonthsNum = typeof monthsEmployed === 'number' && monthsEmployed !== Infinity ? monthsEmployed : 0;
              const oaContribution = Math.round(monthlyCpfOa * employedMonthsNum);
              const employmentDialog: CalcDialogInfo = {
                title: 'Months Covered — Continuous Employment',
                rows: [
                  ...(runwayEmployed.sustainable
                    ? [
                        ...reserveRows,
                        { label: stagedDownPayment ? 'Monthly Installment (at completion)' : 'Monthly Installment', value: fmtInstallment(monthlyInstallment) } as CalcDialogRow,
                        { label: '− Monthly CPF OA earned', value: fmtInstallment(monthlyCpfOa) },
                        { label: '= Net monthly drain', value: fmtInstallment(netDrain) },
                        { isSeparator: true, label: '', value: '' },
                        { label: 'CPF OA earned each month covers the installment — sustainable indefinitely.', value: '✓', isResult: true },
                      ]
                    : [
                        { label: 'Leftover Cash', value: fmt(Math.max(0, cashBalance)) } as CalcDialogRow,
                        { label: 'Leftover CPF', value: fmt(Math.max(0, cpfBalance)) },
                        { label: '= Reserve Funds', value: fmt(reserveFunds) },
                        { label: `+ CPF OA earned (${fmt(monthlyCpfOa)} × ${fmtMonths(monthsEmployed)})`, value: fmt(oaContribution) },
                        { isSeparator: true, label: '', value: '' },
                        { label: '= Total serviceable', value: fmt(reserveFunds + oaContribution) },
                        { isSeparator: true, label: '', value: '' },
                        ...graceRows(runwayEmployed),
                        ...(stagedDownPayment
                          ? [{ label: 'Progressive — installment grows each stage:', value: '' } as CalcDialogRow]
                          : []),
                        ...segmentRows(runwayEmployed),
                        { isSeparator: true, label: '', value: '' },
                        { label: 'Months Covered (with monthly CPF OA)', value: fmtMonths(monthsEmployed), isResult: true },
                        ...(runwayEmployed.graceMonths > 0
                          ? [{ note: true, label: `Counted from the OTP stage — no installment is due until the bank first disburses, and the ${fmtMonths(runwayEmployed.graceMonths)} of CPF OA earned over that window is included above.`, value: '' } as CalcDialogRow]
                          : []),
                      ]),
                ],
              };
              return (
                <Box data-tutorial="reserves" sx={{ mt: 1.5, pt: 1.25, borderTop: '1px solid', borderColor: 'divider' }}>
                  <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: 'text.secondary', letterSpacing: '0.04em', textTransform: 'uppercase', mb: 0.75 }}>
                    Reserves
                  </Typography>
                  <Box
                    onClick={monthlyInstallment > 0 ? () => explain({
                      title: `Monthly Installment${stagedDownPayment ? ' (at completion)' : ''}`,
                      rows: installmentRows,
                    }) : undefined}
                    sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5, cursor: monthlyInstallment > 0 ? 'pointer' : 'default', borderRadius: 1, transition: 'background-color 120ms', '&:hover': monthlyInstallment > 0 ? { bgcolor: 'action.hover' } : undefined }}
                  >
                    <Box>
                      <Typography sx={{ fontSize: '0.8rem', color: 'text.primary' }}>
                        Monthly Installment{stagedDownPayment ? ' (at completion)' : ''}
                        {monthlyInstallment > 0 && <InfoOutlinedIcon sx={{ fontSize: 13, color: 'primary.main', ml: 0.4, verticalAlign: 'text-bottom' }} />}
                      </Typography>
                      <Typography sx={{ fontSize: '0.71rem', color: 'text.secondary' }}>
                        {stagedDownPayment
                          ? 'Progressive — starts lower & grows as the loan draws down'
                          : 'Serviceable from leftover Cash + CPF balance'}
                      </Typography>
                    </Box>
                    <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, whiteSpace: 'nowrap' }}>{fmtInstallment(monthlyInstallment)}</Typography>
                  </Box>

                  <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: 'text.secondary', mt: 1, mb: 0.5 }}>
                    Months Covered{preLoanGraceMonths(computedStages, stagedDownPayment) > 0 ? ' (from OTP)' : ''}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1.5 }}>
                    {/* Based on current fund */}
                    <Box
                      onClick={monthlyInstallment > 0 ? () => onExplain?.(currentFundDialog) : undefined}
                      sx={{ flex: 1, p: 1.25, borderRadius: 1.5, border: '1px solid', borderColor: cardBorder, textAlign: 'center', cursor: monthlyInstallment > 0 ? 'pointer' : 'default', transition: 'background-color 120ms', '&:hover': monthlyInstallment > 0 ? { bgcolor: 'action.hover' } : undefined }}
                    >
                      <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: 'text.secondary', mb: 0.25 }}>
                        Based on current fund
                        {monthlyInstallment > 0 && <InfoOutlinedIcon sx={{ fontSize: 12, color: 'primary.main', ml: 0.4, verticalAlign: 'text-bottom' }} />}
                      </Typography>
                      <Typography sx={{ fontSize: fmtMonthsSize(monthsCurrent), fontWeight: 800, color: '#D97706', lineHeight: 1.3 }}>
                        {monthlyInstallment > 0 ? fmtMonths(monthsCurrent) : '–'}
                      </Typography>
                      <Typography sx={{ fontSize: '0.68rem', color: 'text.secondary' }}>Leftover funds only</Typography>
                    </Box>
                    {/* Based on continuous employment */}
                    <Box
                      onClick={monthlyInstallment > 0 ? () => onExplain?.(employmentDialog) : undefined}
                      sx={{ flex: 1, p: 1.25, borderRadius: 1.5, border: '1px solid', borderColor: cardBorder, textAlign: 'center', cursor: monthlyInstallment > 0 ? 'pointer' : 'default', transition: 'background-color 120ms', '&:hover': monthlyInstallment > 0 ? { bgcolor: 'action.hover' } : undefined }}
                    >
                      <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: 'text.secondary', mb: 0.25 }}>
                        Based on continuous employment
                        {monthlyInstallment > 0 && <InfoOutlinedIcon sx={{ fontSize: 12, color: 'primary.main', ml: 0.4, verticalAlign: 'text-bottom' }} />}
                      </Typography>
                      <Typography sx={{ fontSize: fmtMonthsSize(monthsEmployed), fontWeight: 800, color: '#D97706', lineHeight: 1.3 }}>
                        {monthlyInstallment > 0 ? fmtMonths(monthsEmployed) : '–'}
                      </Typography>
                      <Typography sx={{ fontSize: '0.68rem', color: 'text.secondary' }}>+ {fmt(monthlyCpfOa)}/mo CPF (OA)</Typography>
                    </Box>
                  </Box>
                </Box>
              );
            })()}
          </Card>
          )}
          </>
        );
      })()}
    </Box>
  );
};

// ─── Main BuyingCalculator Component ─────────────────────────────────────────

// ─── Tutorial ────────────────────────────────────────────────────────────────

/** Step-by-step guide shown by the cap button beside the page title. Each
 *  targetElement matches a `data-tutorial` attribute below, so "Show on Page"
 *  can scroll to and highlight the real control. */
const BUYING_TUTORIAL_STEPS: TutorialStepDef[] = [
  {
    title: 'Welcome to the Buying Calculator',
    content:
      'This calculator turns a purchase into a payment plan: what the buyer pays, from which pocket, and on which date.\n\n' +
      'Inputs run down the left in numbered sections; the right column holds the results and the payment timeline.\n\n' +
      'On any step below, tap "Show on Page" and the calculator will scroll to the control being described and pulse a highlight around it for a few seconds. Some steps only apply to a particular tab or mode — switch to it first, and the highlight will find the control.',
    targetElement: null,
  },
  {
    title: 'Load Example / History',
    content:
      'Start from something rather than a blank form:\n' +
      '• Load Example / History: preset buyer profiles, plus every snapshot you have saved before\n' +
      '• Save to History / Profile: with nothing loaded, choose history only, a new client profile, or both\n' +
      '• With a profile loaded, the same button saves to history and writes your edits back to that profile\n' +
      '• Clear: resets the calculator to its defaults\n' +
      '• Export PDF: a client-ready payment breakdown and schedule\n\n' +
      'History is shared across the Super Calculators, so a case sized up in the Max Affordability Calculator can be opened straight into this calculator.',
    targetElement: '[data-tutorial="load-example"]',
  },
  {
    title: 'Property Type Tabs',
    content:
      'Pick the tab that matches the purchase — each one has its own rules, fees and timeline:\n' +
      '• Private: New Launch (progressive payment) or Resale (OTP → exercise → completion)\n' +
      '• EC: Executive Condominium, with the NPS or DPS payment scheme and the CPF Housing Grant\n' +
      '• HDB: resale flats, with valuation and COV, HDB or bank loan, grants and the HDB resale timeline\n\n' +
      'Switching tabs keeps your inputs where they still apply.',
    targetElement: '[data-tutorial="property-tabs"]',
  },
  {
    title: 'Display Controls',
    content:
      'Two viewing controls, both remembered across calculators:\n' +
      '• A- / A+: shrink or enlarge the text — useful when presenting figures to a client\n' +
      '• 1 Scroll / 2 Scroll (desktop): 1 Scroll moves the page as one column; 2 Scroll lets the inputs and the results scroll independently, so the payment schedule stays in view while you adjust an input',
    targetElement: '[data-tutorial="display-controls"]',
  },
  {
    title: 'Calculator Mode: Simple vs Detailed',
    content:
      'Simple is the quick quote: purchase price, tenure and rate, standard 75% LTV, and the payment schedule. Good for a first conversation.\n\n' +
      'Detailed opens the full model — borrower profiles, ABSD from citizenship and property count, the cash/CPF split of every fee, available funds and fund balance, reserves, and any income-support pledging.\n\n' +
      'Everything you enter is kept when you switch, so start Simple and go Detailed once the client is serious.',
    targetElement: '[data-tutorial="calculator-mode"]',
  },
  {
    title: '1. Property',
    content:
      'The purchase itself:\n' +
      '• Purchase Price: what the buyer is paying\n' +
      '• Private only — New Launch or Resale: New Launch uses the progressive payment schedule; Resale uses the OTP 1% → exercise 4% → completion 95% timeline\n' +
      '• EC only — Payment Scheme: NPS pays progressively through construction; DPS pays 5% + 15% then defers 65% to TOP and 15% to CSC\n' +
      '• HDB only — Valuation Price: the loan is based on the lower of price and valuation, and any Cash Over Valuation (COV) is cash-only\n' +
      '• HDB only — Loan Type: Bank, HDB Loan or No Loan',
    targetElement: '[data-tutorial="property-section"]',
  },
  {
    title: '2. Loan',
    content:
      'What the bank will lend and what it costs:\n' +
      '• Age / IWAA: sets the tenure cap — with two borrowers this becomes the income-weighted average age\n' +
      '• Loan Tenure: capped at 30 years (25 for HDB) and by age; stretching past the cap drops the LTV\n' +
      '• LTV: 75% for a first housing loan, 55% when the tenure or age limits are exceeded\n' +
      '• Bank Rate: drives the monthly instalment shown in the results\n\n' +
      'Eligibility is assessed separately at the MAS stress-test rate (left menu → Settings), not at the bank rate.',
    targetElement: '[data-tutorial="loan-section"]',
  },
  {
    title: '2. Borrower (Detailed mode)',
    content:
      'One card per borrower, plus a toggle to add a second:\n' +
      '• Name: shown in the card header, and the name used if you save these inputs as a profile\n' +
      '• Age, Monthly Income, Other Annual Income (recognised at 70%) and Existing Monthly Debt — these drive TDSR and, for HDB/EC, MSR\n' +
      '• Cash / Saving and CPF OA Balance: each borrower declares their own — Available Funds adds them up, and each saves back to that person\u2019s profile\n' +
      '• Est. monthly CPF (OA): auto-estimated from age and income, and editable — it feeds the reserves runway\n' +
      '• Citizenship and Which Property: these set the ABSD rate. FTA nationals can be marked FTA-eligible and are charged the Singapore Citizen rate\n' +
      '• Relationship: a married couple with at least one SC spouse may qualify for ABSD remission',
    targetElement: '[data-tutorial="borrower-section"]',
  },
  {
    title: '3. Available Funds',
    content:
      'What the buyer can actually pay with:\n' +
      '• Cash and CPF: the sum of what each borrower entered on their own card — edit them there\n' +
      '• Additional Cash — including net proceeds carried over from the Selling Calculator\n' +
      '• Additional CPF (CPF refunds, planned withdrawals)\n' +
      '• EC: the CPF Housing Grant is added here as CPF\n\n' +
      'These totals are what the Fund Balance card on the right draws down as the payment schedule runs.',
    targetElement: '[data-tutorial="available-funds"]',
  },
  {
    title: '4. Other Expenses',
    content:
      'Everything beyond the price, each with a CPF / Cash switch so you control which pocket it comes from:\n' +
      '• Buyer’s Stamp Duty (BSD) and Additional Buyer’s Stamp Duty (ABSD), both derived from the borrower profiles\n' +
      '• Legal fee, option fee, exercise fee, caveat, valuation, HDB admin fee and agency service fee\n' +
      '• Home Protection Scheme (HPS) for HDB\n' +
      '• Resale levy, where a previous subsidised flat was sold\n\n' +
      'Note that OTP fees and COV must be paid in cash — the calculator enforces that regardless of the switches.',
    targetElement: '[data-tutorial="other-expenses"]',
  },
  {
    title: 'HDB Grants (HDB tab)',
    content:
      'On the HDB tab, three grants can be added, all credited to CPF:\n' +
      '• CPF Housing Grant\n' +
      '• Proximity Housing Grant (PHG)\n' +
      '• Enhanced CPF Housing Grant (EHG)\n\n' +
      'Tap the info icon on each for the eligibility rules. Grants increase the CPF available for the purchase but are paid after completion, so they do not cover the cash the buyer needs on the day.',
    targetElement: '[data-tutorial="hdb-grants"]',
  },
  {
    title: 'HDB Loan (HDB tab)',
    content:
      'The HDB-specific loan block:\n' +
      '• Maximum Loan Tenure for the flat and the buyers’ ages\n' +
      '• The HDB concessionary rate, pegged 0.10% above the CPF OA rate\n' +
      '• Second HDB loan: the cash set-aside from the previous sale proceeds\n\n' +
      'On an HDB loan the full 25% downpayment may come from CPF; on a bank loan at least 5% of the valuation base must be cash.',
    targetElement: '[data-tutorial="hdb-loan"]',
  },
  {
    title: 'Schedule — OTP / S&P Date',
    content:
      'The one date everything hangs off. Every milestone in the payment timeline is counted forward from it — the exercise deadline, the progressive stages, TOP and CSC, or the HDB resale milestones.\n\n' +
      'Individual milestone dates can still be pinned by hand in the schedule itself if the developer or HDB has given firm dates.',
    targetElement: '[data-tutorial="schedule-date"]',
  },
  {
    title: 'Loan Summary',
    content:
      'The first results card compares two figures:\n' +
      '• The loan this purchase needs, at the LTV applied\n' +
      '• The loan the buyer’s income supports, at the stress-test rate under TDSR (and MSR for HDB/EC)\n\n' +
      'The usage bar shows how much of the borrowing power this purchase consumes. A shortfall here is the number to solve before anything else — by price, by tenure, or by pledging.',
    targetElement: '[data-tutorial="loan-summary"]',
  },
  {
    title: 'Initial Outlay',
    content:
      'Everything payable up to completion, line by line, split into CPF and Cash columns:\n' +
      '• Down payment — for new launch and EC this mirrors the staged schedule (5% OTP + 15% at S&P)\n' +
      '• Stamp duty, legal fee and the other expenses, each in the pocket you assigned\n' +
      '• Total Initial Outlay, and the Grand Total once any pledged or unpledged funds are added\n\n' +
      'Tap any row to see how that figure was built.',
    targetElement: '[data-tutorial="initial-outlay"]',
  },
  {
    title: 'Fund Balance',
    content:
      'Available funds less everything spent in the Initial Outlay, kept separately for CPF and cash.\n\n' +
      'A negative cash line is the real problem to solve — CPF cannot be substituted for the cash-only portions. Tap a row for the working behind it.',
    targetElement: '[data-tutorial="fund-balance"]',
  },
  {
    title: 'Reserves',
    content:
      'How long the leftover funds would carry the monthly instalment:\n' +
      '• Based on current fund: the runway if income stopped today\n' +
      '• Based on continuous employment: the runway with monthly CPF OA contributions still coming in\n\n' +
      '"Sustainable" means contributions cover the instalment indefinitely. For a new launch the count starts at the OTP stage, since no instalment is due until the bank first disburses.',
    targetElement: '[data-tutorial="reserves"]',
  },
  {
    title: 'Payment Schedule',
    content:
      'The timeline the client actually cares about — every stage, its date, and what is paid from cash, CPF and loan:\n' +
      '• Drag a stage by its handle onto the one below to merge them into a single payment; split them again from the same row\n' +
      '• Tap a date to pin it when the developer or HDB has confirmed a firm date\n' +
      '• The dates toggle hides dates and leaves only the relative timing, for cases where no date is fixed yet\n' +
      '• Each stage expands to show the payment, the pocket it comes from, the bank payout and the monthly instalment from that point on',
    targetElement: '[data-tutorial="payment-schedule"]',
  },
  {
    title: 'Saving, Exporting & Resetting',
    content:
      'Wrapping up a case:\n' +
      '• Save to History: keeps a snapshot of the whole case, reopenable from any Super Calculator\n' +
      '• With a profile loaded, the same button writes the borrower details back to the profile\n' +
      '• Export PDF: the payment breakdown and schedule, ready to send\n' +
      '• Clear: resets the calculator\n\n' +
      'Your place in this tutorial is remembered — the cap button beside the title is red before you start, amber while in progress and green once finished. Use Restart to run through it again, or the download icon in the header for the full text manual.',
    targetElement: '[data-tutorial="load-example"]',
  },
];

/** Reference material appended to the downloaded manual, after the steps. */
const BUYING_MANUAL_SECTIONS = [
  {
    heading: 'Payment Timelines',
    body:
      'Private new launch / EC NPS (progressive payment):\n'
      + '  5% booking fee (OTP, cash) -> 15% on S&P signing -> stage payments as\n'
      + '  construction milestones complete -> 25% at TOP -> 15% at CSC.\n\n'
      + 'EC DPS (deferred payment):\n'
      + '  5% on OTP -> 15% on S&P within 9 weeks -> nothing during construction ->\n'
      + '  65% at TOP -> 15% at CSC.\n\n'
      + 'Private resale:\n'
      + '  1% option fee -> 4% on exercise -> 95% balance at completion\n'
      + '  (typically 8-12 weeks).\n\n'
      + 'HDB resale:\n'
      + '  Option fee (cash, up to $1,000) -> exercise fee (cash, option + exercise\n'
      + '  capped at $5,000) -> HDB submission and approval -> completion, with COV\n'
      + '  payable in cash.',
  },
  {
    heading: 'Key Rules Applied',
    body:
      'LTV: 75% first housing loan; 55% where tenure or age limits are exceeded.\n'
      + 'Minimum cash: 5% of the price at 75% LTV; 10% at 55% LTV.\n'
      + 'Tenure: capped at 30 years for private/EC and 25 years for HDB, and by the\n'
      + '  borrower age limit; the tighter cap applies.\n'
      + 'TDSR: 55% of recognised income less existing debt. MSR (30%) applies on top\n'
      + '  for HDB and EC.\n'
      + 'Stress test: eligibility is assessed at the MAS stress-test rate configured\n'
      + '  under the left menu > Settings, not at the bank rate entered here.\n'
      + 'HDB loan: up to 75% of the lower of resale price or valuation, at the\n'
      + '  concessionary rate pegged 0.10% above the CPF OA rate.\n'
      + 'Cash-only items: OTP and exercise fees, and any Cash Over Valuation (COV).',
  },
  {
    heading: 'Sources',
    body:
      'MAS - LTV limits, TDSR and MSR for residential property loans.\n'
      + 'IRAS - Buyer\u2019s Stamp Duty and Additional Buyer\u2019s Stamp Duty rates\n'
      + '  (residential rates on or after 15 Feb 2023).\n'
      + 'HDB - resale procedure, HFE eligibility, housing grants and resale levy.\n'
      + 'CPF Board - Ordinary Account usage and the Home Protection Scheme.',
  },
];

export default function BuyingCalculator({ isAdmin: _isAdmin = false }: { isAdmin?: boolean } = {}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isDark = theme.palette.mode === 'dark';

  const { selectedProfile, getSelectedProfiles, getSelectedName, updateProfile, clearSelection } = useProfile();
  const { settings: globalSettings } = useGlobalCalculatorSettings();
  const currentUser = useCurrentUser();
  const linkedCalc = useLinkedCalculator();

  // Tab state
  const [propertyTab, setPropertyTab] = useState<PropertyTab>('private');
  const [calculatorMode, setCalculatorMode] = useState<BuyingCalculatorMode>('simple');
  // Scroll mode: false = page scrolls as one (default); true = left/right columns scroll independently.
  const [splitScroll, setSplitScroll] = useState(false);
  // Step-by-step tutorial (cap button beside the page title).
  const tutorial = useCalculatorTutorial('buyingCalculatorTutorialProgress', BUYING_TUTORIAL_STEPS.length);
  // Announces the newest release once per agent (acknowledgement stored server-side),
  // then sits behind the megaphone button.
  // Release notes pop up on every visit until the agent ticks "Don't show this again".
  const whatsNew = useWhatsNew('buying', BUYING_WHATS_NEW, { untilSuppressed: true });
  // Text size is a shared preference (see TextSizeControl + GlobalCalculatorSettings).
  const [privateSubTab, setPrivateSubTab] = useState<PrivateSubTab>('new-launch');
  const [ecPaymentScheme, setEcPaymentScheme] = useState<'NPS' | 'DPS'>('NPS');
  const [hdbInputs, setHdbInputs] = useState<HdbInputs>(DEFAULT_HDB_INPUTS);
  const updateHdb = useCallback(<K extends keyof HdbInputs>(key: K, val: HdbInputs[K]) =>
    setHdbInputs(prev => ({ ...prev, [key]: val })), []);
  // OTP deposit (HDB resale, cash): Option Fee + Exercise Fee — editable in Other Expenses.
  const otpDeposit = hdbInputs.optionFee + hdbInputs.exerciseFee;
  // HDB valuation defaults to the purchase price (COV = 0) and tracks it on every
  // purchase-price change, until the user edits the valuation themselves.
  const [hdbValuationUserSet, setHdbValuationUserSet] = useState(false);
  const [hdbAgencyFeeUserSet, setHdbAgencyFeeUserSet] = useState(false);

  // Additional borrower stamp duty state (separate from main inputs, mirroring Maximizer)
  const [additionalCitizenship, setAdditionalCitizenship] = useState<CitizenshipType>('SC');
  const [additionalFtaEligible, setAdditionalFtaEligible] = useState(false);
  const [additionalPropertyCount, setAdditionalPropertyCount] = useState<PropertyCount>('1st');
  // Relationship between joint borrowers (mirrors the Profile Group "Relationship"
  // field). Drives the joint ABSD assessment (IRAS married-couple tables / remission)
  // and the EC & HDB citizenship/age eligibility checks.
  const [borrowerRelationship, setBorrowerRelationship] = useState<BorrowerRelationship>('SPOUSE');
  const [bsdByCpf, setBsdByCpf] = useState(true);
  const [absdByCpf, setAbsdByCpf] = useState(true);
  const [legalFeeByCpf, setLegalFeeByCpf] = useState(true);
  const [hpsByCpf, setHpsByCpf] = useState(true);
  // Payment Structure allocation — separate from Available Funds totals
  const [cashDownAlloc, setCashDownAlloc] = useState(0);
  const [cpfDownAlloc, setCpfDownAlloc] = useState(0);
  // Special Bridging Loan (EC DPS only) — short-term bank financing against the buyer's
  // existing HDB flat, disbursed at TOP to fund the deferred tranche and reduce the bank
  // loan. Max = 90% × HDB valuation − outstanding HDB loan − resale levy.
  const [sblHasHdb, setSblHasHdb] = useState(false);
  const [sblValuation, setSblValuation] = useState(0);
  const [sblOutstandingLoan, setSblOutstandingLoan] = useState(0);
  const [sblLevy, setSblLevy] = useState(0);
  const [hdbCashAlloc, setHdbCashAlloc] = useState(0);
  const [hdbCpfAlloc, setHdbCpfAlloc] = useState(0);

  // Loan tenure stretch (MAS rule): extending tenure beyond age 65 / 30 yrs
  // caps LTV at 55% and requires 10% min cash (instead of 75% / 5%).
  const [tenureStretched, setTenureStretched] = useState(false);
  const [stretchDialogOpen, setStretchDialogOpen] = useState(false);

  // Confirmation dialogs for removing borrowers.
  const [removeBorrowerDialogOpen, setRemoveBorrowerDialogOpen] = useState(false);
  const [removePrimaryDialogOpen, setRemovePrimaryDialogOpen] = useState(false);
  const [clearDialogOpen, setClearDialogOpen] = useState(false);

  // Editing the Bank Loan directly (private / EC): holds the pending loan value awaiting
  // confirmation. Applying it re-derives the down payment using CPF first, then Cash.
  const [pendingLoanChange, setPendingLoanChange] = useState<number | null>(null);
  // Same, for the HDB payment structure: edit the HDB loan and re-derive the cash / CPF
  // down-payment allocation (CPF used up first, remainder in cash).
  const [pendingHdbLoanChange, setPendingHdbLoanChange] = useState<number | null>(null);

  // Inputs
  const todayStr = useMemo(() => new Date().toISOString().split('T')[0], []);

  const [purchasePriceUserAdjusted, setPurchasePriceUserAdjusted] = useState(false);
  // Cash proceeds brought over from the Selling calculator. Kept separate from
  // the buyer's cash savings so it appears only in the Sales Proceed row.
  const [cashProceedFromSelling, setCashProceedFromSelling] = useState(0);
  // Net sales proceeds exactly as the Selling calculator reported them. The Additional
  // Cash field above is editable (the buyer may choose to deploy only part of it here),
  // so HDB's 50% rule must be measured against this untouched figure instead.
  const [sellingNetProceeds, setSellingNetProceeds] = useState(0);
  // The Selling calculator's per-seller split, kept so the sale-proceeds rows can be
  // re-derived whenever the borrower line-up changes (see the matching effect below).
  const [sellerProceeds, setSellerProceeds] = useState<SharedProceedsSeller[]>([]);
  // Set once the buyer types over a proceeds row — their figure then stands until the
  // next handoff from the Selling calculator.
  const [proceedsRowsUserSet, setProceedsRowsUserSet] = useState(false);
  // Optional explicit dates (YYYY-MM-DD) for the Pay Stamp Duty and Pay (15%) milestones
  // in the progressive schedule. null → auto-derive from the OTP/S&P date.
  // (exerciseDateOverride holds the stamp-duty date — the on-screen stage & report agree.)
  const [exerciseDateOverride, setExerciseDateOverride] = useState<string | null>(null);
  const [sp15DateOverride, setSp15DateOverride] = useState<string | null>(null);
  const [stageDateOverrides, setStageDateOverrides] = useState<Record<string, string>>({});
  const commitProgressiveDate = (id: string, iso: string | null) => {
    setStageDateOverrides((prev) => {
      const next = { ...prev };
      if (iso) next[id] = iso;
      else delete next[id];
      return next;
    });
  };
  // Optional explicit dates (YYYY-MM-DD) for the private-resale Exercise OTP and
  // Completion milestones. null → auto-derive from the OTP date. Lifted out of
  // ResaleSchedule so the exported report reads the same dates as the screen.
  const [resaleExerciseOverride, setResaleExerciseOverride] = useState<string | null>(null);
  const [resaleCompletionOverride, setResaleCompletionOverride] = useState<string | null>(null);
  // Explicit dates pinned on the HDB resale schedule. A pinned milestone anchors
  // everything downstream of it, so editing a late stage leaves earlier ones put.
  const [hdbDateOverrides, setHdbDateOverrides] = useState<HdbDateOverrides>({});
  // "Show dates in timeline" — shared by every on-screen schedule and the exported
  // report, so hiding dates on screen hides them in the report too.
  const [showTimelineDates, setShowTimelineDates] = useState(true);
  // Merged milestones on the Progressive Schedule, as contiguous groups of stage
  // indices reported by the mounted schedule. Null until it reports. The export
  // collapses the same groups so the report matches the screen.
  const [scheduleGroups, setScheduleGroups] = useState<number[][] | null>(null);
  const [ecHousingGrant, setEcHousingGrant] = useState(0); // EC CPF Housing Grant (counts as CPF)
  // EC Resale Levy — payable in cash when buying a 2nd subsidised flat (EC) after
  // having sold a subsidised HDB flat. Defaults to $55,000 when the buyer arrives
  // from the Selling calculator's HDB tab (see proceeds sync + effect below).
  const [resaleLevy, setResaleLevy] = useState(0);
  const [resaleLevyUserSet, setResaleLevyUserSet] = useState(false);
  const [sellerSoldHdb, setSellerSoldHdb] = useState(false);
  // Tracks whether the user has manually toggled the "2nd HDB Loan" checkbox, so the
  // auto-check (buyer arrived from selling an HDB flat) doesn't fight their choice.
  const [isSecondHdbLoanUserSet, setIsSecondHdbLoanUserSet] = useState(false);
  // Same for the "Cash proceeds to use" amount: it tracks the 50%-rule minimum until
  // the user types over it (e.g. they choose to put in more than HDB requires).
  const [secondHdbLoanSetAsideUserSet, setSecondHdbLoanSetAsideUserSet] = useState(false);
  // Fund pledging (declared here so the history snapshot below can read them; the
  // derivation/effects live in the "Pledge / Unpledge" section further down).
  // unpledgeInputValue is the committed unpledged amount (source of truth); pledgeMode
  // is the ADJUST BY tab (Total / Unpledge / Pledge).
  const [unpledgeInputValue, setUnpledgeInputValue] = useState('');
  const [pledgeMode, setPledgeMode] = useState<'total' | 'unpledge' | 'pledge'>('total');
  const [inputs, setInputs] = useState<BuyingInputs>({
    purchasePrice: 0,
    hasBankLoan: true,
    additionalCash: 0,
    cpf: 0,
    additionalCpf: 0,
    interestRate: 2.0,
    tenure: 30,
    citizenship: 'SC',
    propertyCount: '1st',
    ftaEligible: false,
    legalFee: 3000,
    spDate: todayStr,
    primary: DEFAULT_BORROWER,
    includeAdditionalBorrower: false,
    additional: DEFAULT_BORROWER,
  });

  // Which profile/group the Available Funds rows were last seeded from — see the
  // switch handling inside the profile-loading effect below.
  const loadedFundsProfileKeyRef = useRef<string | null>(null);

  // Profile loading — populates borrower inputs
  useEffect(() => {
    if (!selectedProfile) {
      // Nothing selected (including after Clear): the next selection — even of the
      // same profile — counts as a switch and re-seeds Available Funds.
      loadedFundsProfileKeyRef.current = null;
      return;
    }

    const selectedProfiles =
      selectedProfile.type === 'profile'
        ? [selectedProfile.profile]
        : (selectedProfile.group.profiles || []);

    if (selectedProfiles.length === 0) return;

    // Switching profile re-seeds Available Funds: all four rows are cleared, then Cash
    // and CPF reload from this profile's saved funds — so no amount from the previous
    // client, or from a Selling-calculator handoff, lingers. A plain refresh of the same
    // selection (profile-list refetch, an edit saved in Profile Manager) is not a switch
    // and leaves the entered amounts alone. Claimed before the guards below so the funds
    // in a restored snapshot survive the profile refresh that follows it.
    const fundsProfileKey = `${selectedProfile.type}-${selectedProfile.id}:${selectedProfiles.map((p) => p.Id).join(',')}`;
    const profileSwitched = loadedFundsProfileKeyRef.current !== fundsProfileKey;
    loadedFundsProfileKeyRef.current = fundsProfileKey;

    // A queued Profile Manager load is about to restore a full saved snapshot
    // (borrowers, funds, everything) — don't overwrite it with the profile's
    // default form population.
    if (hasPendingHistoryLoad()) return;

    // A save-back just wrote these same values to the profile — skip the
    // repopulation triggered by the refreshed selectedProfile (it would release
    // the purchase-price lock and re-add a removed group borrower).
    if (isProfileSaveBackInProgress()) return;

    // Loading a profile refreshes the form: release the manual purchase-price lock so
    // the price re-derives from this profile's affordability (Max Purchase Price)
    // instead of keeping a previously typed amount.
    setPurchasePriceUserAdjusted(false);

    const currentYear = new Date().getFullYear();
    const primary = selectedProfiles[0];
    const primaryAge = primary.BirthYear ? currentYear - primary.BirthYear : 30;
    const primaryIncome = primary.MonthlyIncome || 0;
    const primaryOtherAnnual = primary.OtherAnnualIncome || 0;
    const primaryDebt = (primary.CarLoan ?? 0) + (primary.HousingLoan ?? 0) + (primary.CreditCard ?? 0) + (primary.OtherLoan ?? 0);
    const primarySelfEmployed = Boolean(primary.SelfEmployedInd);
    const citizenship = (primary.Citizenship as CitizenshipType) || 'SC';
    const propertyCount: PropertyCount =
      (primary.PropertiesOwned ?? 0) >= 3 ? '3rd+' :
      (primary.PropertiesOwned ?? 0) === 2 ? '2nd' : '1st';

    const newPrimary: BorrowerInputs = {
      name: primary.Name?.trim() || undefined,
      age: primaryAge,
      monthlyIncome: Math.round(primaryIncome),
      otherAnnualIncome: Math.round(primaryOtherAnnual),
      isSelfEmployed: primarySelfEmployed,
      existingMonthlyDebt: Math.round(primaryDebt),
      cash: Math.round(primary.Saving ?? 0),
      cpf: Math.round(primary.Cpf ?? 0),
    };

    // Fund Availability from the profile(s) → each borrower's own Cash / CPF rows,
    // which Available Funds then sums. Falls back to the max-LTV CPF heuristic on
    // the primary borrower when no member of the profile has any fund data.
    const hasFundData = selectedProfiles.some((p) => p.Saving != null || p.Cpf != null);
    const totalCpf = selectedProfiles.reduce((sum, p) => sum + (p.Cpf ?? 0), 0);

    setInputs((prev) => {
      const hasSecond = selectedProfiles.length > 1;
      let newAdditional = prev.additional;
      if (hasSecond) {
        const add = selectedProfiles[1];
        const addAge = add.BirthYear ? currentYear - add.BirthYear : 30;
        newAdditional = {
          name: add.Name?.trim() || undefined,
          age: addAge,
          monthlyIncome: Math.round(add.MonthlyIncome || 0),
          otherAnnualIncome: Math.round(add.OtherAnnualIncome || 0),
          isSelfEmployed: Boolean(add.SelfEmployedInd),
          existingMonthlyDebt: Math.round((add.CarLoan ?? 0) + (add.HousingLoan ?? 0) + (add.CreditCard ?? 0) + (add.OtherLoan ?? 0)),
          cash: Math.round(add.Saving ?? 0),
          cpf: Math.round(add.Cpf ?? 0),
        };
      }
      const defaultDown = Math.round(prev.purchasePrice * 0.25);
      const defaultMinCash = Math.round(prev.purchasePrice * 0.05);
      // No fund data anywhere on the profile: keep the max-LTV CPF heuristic on the
      // primary borrower (a switch re-seeds from the profile alone, so it lands on 0).
      const fallbackPrimaryCpf = profileSwitched ? 0 : Math.max(0, defaultDown - defaultMinCash);
      return {
        ...prev,
        citizenship,
        propertyCount,
        ftaEligible: Boolean(primary.FtaNationalityInd),
        additionalCpf: profileSwitched ? 0 : prev.additionalCpf,
        primary: hasFundData ? newPrimary : { ...newPrimary, cpf: fallbackPrimaryCpf },
        includeAdditionalBorrower: hasSecond,
        additional: newAdditional,
      };
    });

    // Keep the HDB tab's OA balance in step (mirrors the Available Funds CPF field).
    if (hasFundData || profileSwitched) {
      setHdbInputs((prev) => ({ ...prev, cpfOa: hasFundData ? Math.round(totalCpf) : 0 }));
    }

    // Additional Cash (sale proceeds) and Additional CPF have no profile counterpart —
    // they only arrive from a Selling handoff or manual entry, so a switch clears them,
    // along with the "sold an HDB flat" context that would otherwise keep charging an
    // EC resale levy against proceeds that are now zero.
    if (profileSwitched) {
      setCashProceedFromSelling(0);
      setSellingNetProceeds(0);
      setSellerSoldHdb(false);
      setSellerProceeds([]);
      setProceedsRowsUserSet(false);
    }

    if (selectedProfiles.length > 1) {
      const add = selectedProfiles[1];
      setAdditionalCitizenship((add.Citizenship as CitizenshipType) || 'SC');
      setAdditionalFtaEligible(Boolean(add.FtaNationalityInd));
      const addCount: PropertyCount =
        (add.PropertiesOwned ?? 0) >= 3 ? '3rd+' :
        (add.PropertiesOwned ?? 0) === 2 ? '2nd' : '1st';
      setAdditionalPropertyCount(addCount);
    }

    // Mirror the loaded group's relationship for EC eligibility checks.
    if (selectedProfile.type === 'group') {
      const rel = (selectedProfile.group.Relationship || '').toUpperCase();
      if (rel === 'SPOUSE' || rel === 'INDIVIDUAL') setBorrowerRelationship(rel);
    }
  }, [selectedProfile]);

  // Backfill borrower names from the loaded profile whenever a card has none. The
  // profile-load effect seeds them, but a history snapshot restored afterwards (or
  // one saved before the Name field existed) carries no name and would blank the
  // header — this puts the loaded profile's names back without touching a typed one.
  const selectedMemberNames = useMemo<string[]>(() => {
    if (!selectedProfile) return [];
    const members =
      selectedProfile.type === 'profile'
        ? [selectedProfile.profile]
        : (selectedProfile.group.profiles || []);
    return members.map((m) => m.Name?.trim() || '');
  }, [selectedProfile]);

  useEffect(() => {
    if (selectedMemberNames.length === 0) return;
    setInputs((prev) => {
      const primary = !prev.primary.name?.trim() && selectedMemberNames[0]
        ? { ...prev.primary, name: selectedMemberNames[0] }
        : prev.primary;
      const additional = !prev.additional.name?.trim() && selectedMemberNames[1]
        ? { ...prev.additional, name: selectedMemberNames[1] }
        : prev.additional;
      return primary === prev.primary && additional === prev.additional
        ? prev
        : { ...prev, primary, additional };
    });
  }, [selectedMemberNames, inputs.primary.name, inputs.additional.name]);

  // Writes the current borrower/fund inputs back to the loaded profile — the full
  // input snapshot goes to history; the profile keeps the summary fields it owns.
  // Toast-free: the HistoryToolbar folds the outcome into its combined save feedback.
  const saveInputsToProfile = async (): Promise<boolean> => {
    if (!selectedProfile) return false;
    const members =
      selectedProfile.type === 'profile'
        ? [selectedProfile.profile]
        : (selectedProfile.group.profiles || []);
    if (members.length === 0) return false;

    const currentYear = new Date().getFullYear();
    const toPropertiesOwned = (count: PropertyCount): number =>
      count === '3rd+' ? 3 : count === '2nd' ? 2 : 1;
    // Keep the itemised debt breakdown when the aggregate is unchanged; the
    // calculator only knows the total, so a changed total is parked under OtherLoan.
    const borrowerDebtFields = (member: CalculatorProfile, totalDebt: number) => {
      const existing = Math.round(
        (member.CarLoan ?? 0) + (member.HousingLoan ?? 0) + (member.CreditCard ?? 0) + (member.OtherLoan ?? 0)
      );
      return existing === Math.round(totalDebt)
        ? {}
        : { CarLoan: 0, HousingLoan: 0, CreditCard: 0, OtherLoan: Math.round(totalDebt) };
    };

    beginProfileSaveBack();
    try {
      const primaryMember = members[0];
      await updateProfile(primaryMember.Id, {
        Name: borrowerName(inputs.primary) || primaryMember.Name,
        BirthYear: currentYear - inputs.primary.age,
        MonthlyIncome: Math.round(inputs.primary.monthlyIncome),
        OtherAnnualIncome: Math.round(inputs.primary.otherAnnualIncome),
        SelfEmployedInd: inputs.primary.isSelfEmployed ? 1 : 0,
        ...borrowerDebtFields(primaryMember, inputs.primary.existingMonthlyDebt),
        Citizenship: inputs.citizenship,
        FtaNationalityInd: inputs.ftaEligible ? 1 : 0,
        PropertiesOwned: toPropertiesOwned(inputs.propertyCount),
        // Each borrower declares their own funds on their card, so a group member
        // gets exactly what was entered against them — no total to split.
        Saving: Math.round(inputs.primary.cash),
        Cpf: Math.round(inputs.primary.cpf),
      });

      if (inputs.includeAdditionalBorrower && members.length > 1) {
        const additionalMember = members[1];
        await updateProfile(additionalMember.Id, {
          Name: borrowerName(inputs.additional) || additionalMember.Name,
          BirthYear: currentYear - inputs.additional.age,
          MonthlyIncome: Math.round(inputs.additional.monthlyIncome),
          OtherAnnualIncome: Math.round(inputs.additional.otherAnnualIncome),
          SelfEmployedInd: inputs.additional.isSelfEmployed ? 1 : 0,
          ...borrowerDebtFields(additionalMember, inputs.additional.existingMonthlyDebt),
          Citizenship: additionalCitizenship,
          FtaNationalityInd: additionalFtaEligible ? 1 : 0,
          PropertiesOwned: toPropertiesOwned(additionalPropertyCount),
          Saving: Math.round(inputs.additional.cash),
          Cpf: Math.round(inputs.additional.cpf),
        });
      }
      return true;
    } catch (err) {
      console.error('Failed to save inputs to profile:', err);
      return false;
    } finally {
      endProfileSaveBack();
    }
  };

  // Describes the profile(s) "Save to Profile" should create when nothing is loaded:
  // one member per borrower card, named from the card's Name field.
  const buildNewProfilesFromInputs = useCallback((): NewProfileDraft | null => {
    const currentYear = new Date().getFullYear();
    const toPropertiesOwned = (count: PropertyCount): number =>
      count === '3rd+' ? 3 : count === '2nd' ? 2 : 1;
    const member = (
      b: BorrowerInputs,
      citizenship: CitizenshipType,
      fta: boolean,
      count: PropertyCount,
    ) => ({
      Name: b.name?.trim() || '',
      BirthYear: currentYear - b.age,
      MonthlyIncome: Math.round(b.monthlyIncome),
      OtherAnnualIncome: Math.round(b.otherAnnualIncome),
      Saving: Math.round(b.cash),
      Cpf: Math.round(b.cpf),
      // The calculator only knows the debt total — park it under OtherLoan.
      CarLoan: 0,
      HousingLoan: 0,
      CreditCard: 0,
      OtherLoan: Math.round(b.existingMonthlyDebt),
      SelfEmployedInd: b.isSelfEmployed ? 1 : 0,
      Citizenship: citizenship,
      FtaNationalityInd: fta ? 1 : 0,
      PropertiesOwned: toPropertiesOwned(count),
    });

    const members = [member(inputs.primary, inputs.citizenship, inputs.ftaEligible, inputs.propertyCount)];
    if (inputs.includeAdditionalBorrower) {
      members.push(member(inputs.additional, additionalCitizenship, additionalFtaEligible, additionalPropertyCount));
    }
    return {
      members,
      groupName: members.map((m) => m.Name).filter(Boolean).join(' & '),
      relationship: borrowerRelationship,
    };
  }, [inputs, additionalCitizenship, additionalFtaEligible, additionalPropertyCount, borrowerRelationship]);

  // ── Linked Calculator: register state getter/setter/profile builder ─────
  const inputsRef = useRef(inputs);
  inputsRef.current = inputs;
  const selectedProfileRef = useRef(selectedProfile);
  selectedProfileRef.current = selectedProfile;
  // True while a history/example snapshot is being applied. The auto-default effects
  // (cash allocation, HDB allocation, tenure reset) check this and skip their one-time
  // reset so restored values survive, while still advancing their change-tracking refs
  // so later genuine edits behave normally. Cleared at the end of the same commit.
  const restoringRef = useRef(false);
  // Per-tab purchase price: each property tab (Private / EC / HDB) keeps its own
  // entered price, so a price typed on one tab never carries into another. Cleared
  // on Clear and on history restore (those set price + tab atomically).
  const tabPriceStashRef = useRef<Partial<Record<PropertyTab, { price: number; userAdjusted: boolean }>>>({});
  const buyingSnapshotRef = useRef<Record<string, unknown>>({});
  // Full input snapshot persisted to history — every user-editable field must be listed
  // here (and restored in the state setter below) so saving captures the exact state.
  buyingSnapshotRef.current = {
    calculatorMode, propertyTab, privateSubTab, ecPaymentScheme, hdbInputs, inputs,
    additionalCitizenship, additionalFtaEligible, additionalPropertyCount,
    borrowerRelationship, purchasePriceUserAdjusted, resaleLevy,
    cashProceedFromSelling, sellingNetProceeds, sellerSoldHdb, sellerProceeds, proceedsRowsUserSet,
    // Payment-by-CPF toggles
    bsdByCpf, absdByCpf, legalFeeByCpf, hpsByCpf,
    // Payment Structure allocations (private/EC + HDB) and bridging loan
    cashDownAlloc, cpfDownAlloc, hdbCashAlloc, hdbCpfAlloc,
    sblHasHdb, sblValuation, sblOutstandingLoan, sblLevy,
    // EC grant + progressive-schedule date overrides
    ecHousingGrant, exerciseDateOverride, sp15DateOverride, stageDateOverrides,
    // Resale-schedule date overrides + timeline date visibility
    resaleExerciseOverride, resaleCompletionOverride, hdbDateOverrides, showTimelineDates,
    // Tenure stretch + HDB manual-override flags (needed for faithful restore)
    tenureStretched, hdbValuationUserSet, hdbAgencyFeeUserSet,
    // Fund pledging: the committed unpledged amount is the source of truth; the
    // pledged/unpledged/total split is derived from it. pledgeMode is the ADJUST BY tab.
    unpledgeInputValue, pledgeMode,
  };

  useEffect(() => {
    linkedCalc.registerStateGetter('buying', () => buyingSnapshotRef.current);
  }, [linkedCalc]);

  useEffect(() => {
    linkedCalc.registerStateSetter('buying', (state: unknown) => {
      const s = state as Record<string, unknown> | null;
      if (!s) return;
      // Suppress the auto-default effects for this restore commit (see restoringRef).
      restoringRef.current = true;
      // The snapshot carries its own tab + price; stale per-tab stashes must not leak.
      tabPriceStashRef.current = {};
      if (s.calculatorMode) setCalculatorMode(s.calculatorMode as BuyingCalculatorMode);
      if (s.propertyTab) setPropertyTab(s.propertyTab as PropertyTab);
      if (s.privateSubTab) setPrivateSubTab(s.privateSubTab as PrivateSubTab);
      if (s.ecPaymentScheme) setEcPaymentScheme(s.ecPaymentScheme as 'NPS' | 'DPS');
      // Merge onto defaults so entries saved before newer fields (e.g. optionFee/exerciseFee)
      // existed don't restore as undefined → NaN.
      if (s.hdbInputs) {
        const savedHdb = s.hdbInputs as Partial<HdbInputs>;
        const restoredHdb = { ...DEFAULT_HDB_INPUTS, ...savedHdb };
        // Entries saved before the % field existed only stored the dollar amount — back-derive
        // the rate it represents so the % field doesn't restore as a misleading 1%.
        if (savedHdb.agencyServiceFeePct === undefined) {
          const savedPrice = (s.inputs as BuyingInputs | undefined)?.purchasePrice ?? 0;
          if (savedPrice > 0 && restoredHdb.agencyServiceFee > 0) {
            restoredHdb.agencyServiceFeePct = (restoredHdb.agencyServiceFee / AGENCY_FEE_GST) / savedPrice * 100;
          }
        }
        setHdbInputs(restoredHdb);
        setIsSecondHdbLoanUserSet(true);
        setSecondHdbLoanSetAsideUserSet(true);
      }
      if (s.inputs) {
        const savedInputs = s.inputs as BuyingInputs;
        // Entries saved before per-borrower funds existed only carry the totals —
        // park them on the primary borrower so the derived totals match the snapshot.
        const legacyFunds =
          (savedInputs.primary as Partial<BorrowerInputs> | undefined)?.cash === undefined &&
          (savedInputs.primary as Partial<BorrowerInputs> | undefined)?.cpf === undefined;
        const primary = normalizeBorrower(savedInputs.primary);
        const additional = normalizeBorrower(savedInputs.additional);
        setInputs({
          ...savedInputs,
          primary: legacyFunds
            ? { ...primary, cash: Math.round(savedInputs.additionalCash ?? 0), cpf: Math.round(savedInputs.cpf ?? 0) }
            : primary,
          additional,
        });
        setPurchasePriceUserAdjusted(true);
      }
      if (s.additionalCitizenship) setAdditionalCitizenship(s.additionalCitizenship as CitizenshipType);
      if (typeof s.additionalFtaEligible === 'boolean') setAdditionalFtaEligible(s.additionalFtaEligible);
      if (s.additionalPropertyCount) setAdditionalPropertyCount(s.additionalPropertyCount as PropertyCount);
      if (s.borrowerRelationship === 'SPOUSE' || s.borrowerRelationship === 'INDIVIDUAL') setBorrowerRelationship(s.borrowerRelationship);
      if (typeof s.resaleLevy === 'number') { setResaleLevy(s.resaleLevy); setResaleLevyUserSet(true); }
      if (typeof s.cashProceedFromSelling === 'number') setCashProceedFromSelling(s.cashProceedFromSelling);
      // Entries saved before sellingNetProceeds existed fall back to the allocated row.
      if (typeof s.sellingNetProceeds === 'number') setSellingNetProceeds(s.sellingNetProceeds);
      else if (typeof s.cashProceedFromSelling === 'number') setSellingNetProceeds(s.cashProceedFromSelling);
      if (typeof s.sellerSoldHdb === 'boolean') setSellerSoldHdb(s.sellerSoldHdb);
      // Restored figures are the ones saved — the split only re-derives them once the
      // borrower line-up changes, so a snapshot never overwrites what it stored.
      setSellerProceeds(Array.isArray(s.sellerProceeds) ? (s.sellerProceeds as SharedProceedsSeller[]) : []);
      setProceedsRowsUserSet(typeof s.proceedsRowsUserSet === 'boolean' ? s.proceedsRowsUserSet : true);
      if (typeof s.bsdByCpf === 'boolean') setBsdByCpf(s.bsdByCpf);
      if (typeof s.absdByCpf === 'boolean') setAbsdByCpf(s.absdByCpf);
      if (typeof s.legalFeeByCpf === 'boolean') setLegalFeeByCpf(s.legalFeeByCpf);
      if (typeof s.hpsByCpf === 'boolean') setHpsByCpf(s.hpsByCpf);
      if (typeof s.cashDownAlloc === 'number') setCashDownAlloc(s.cashDownAlloc);
      if (typeof s.cpfDownAlloc === 'number') setCpfDownAlloc(s.cpfDownAlloc);
      // Special Bridging Loan inputs. Older snapshots stored a free-form `bridgingAmount`;
      // it has no valuation behind it, so it is dropped rather than migrated.
      setSblHasHdb(typeof s.sblHasHdb === 'boolean' ? s.sblHasHdb : false);
      setSblValuation(typeof s.sblValuation === 'number' ? s.sblValuation : 0);
      setSblOutstandingLoan(typeof s.sblOutstandingLoan === 'number' ? s.sblOutstandingLoan : 0);
      setSblLevy(typeof s.sblLevy === 'number' ? s.sblLevy : 0);
      if (typeof s.hdbCashAlloc === 'number') setHdbCashAlloc(s.hdbCashAlloc);
      if (typeof s.hdbCpfAlloc === 'number') setHdbCpfAlloc(s.hdbCpfAlloc);
      if (typeof s.ecHousingGrant === 'number') setEcHousingGrant(s.ecHousingGrant);
      if (typeof s.exerciseDateOverride === 'string' || s.exerciseDateOverride === null) setExerciseDateOverride(s.exerciseDateOverride as string | null);
      if (typeof s.sp15DateOverride === 'string' || s.sp15DateOverride === null) setSp15DateOverride(s.sp15DateOverride as string | null);
      setStageDateOverrides((s.stageDateOverrides && typeof s.stageDateOverrides === 'object') ? s.stageDateOverrides as Record<string, string> : {});
      if (typeof s.resaleExerciseOverride === 'string' || s.resaleExerciseOverride === null) setResaleExerciseOverride(s.resaleExerciseOverride as string | null);
      if (typeof s.resaleCompletionOverride === 'string' || s.resaleCompletionOverride === null) setResaleCompletionOverride(s.resaleCompletionOverride as string | null);
      if (s.hdbDateOverrides && typeof s.hdbDateOverrides === 'object') setHdbDateOverrides(s.hdbDateOverrides as HdbDateOverrides);
      if (typeof s.showTimelineDates === 'boolean') setShowTimelineDates(s.showTimelineDates);
      if (typeof s.tenureStretched === 'boolean') setTenureStretched(s.tenureStretched);
      if (typeof s.hdbValuationUserSet === 'boolean') setHdbValuationUserSet(s.hdbValuationUserSet);
      if (typeof s.hdbAgencyFeeUserSet === 'boolean') setHdbAgencyFeeUserSet(s.hdbAgencyFeeUserSet);
      if (typeof s.unpledgeInputValue === 'string') setUnpledgeInputValue(s.unpledgeInputValue);
      if (s.pledgeMode === 'total' || s.pledgeMode === 'unpledge' || s.pledgeMode === 'pledge') setPledgeMode(s.pledgeMode);

      // ABSD status (citizenship, FTA remission, which property) belongs to the loaded
      // profile, not the snapshot — otherwise an older snapshot auto-restored on load
      // would undo an edit saved in Profile Manager (or another calculator) and the
      // next save would write the stale value back. Mirrors the Affordability
      // calculator, which never restores these from history.
      const sel = selectedProfileRef.current;
      const members = sel ? (sel.type === 'profile' ? [sel.profile] : (sel.group.profiles || [])) : [];
      const toPropertyCount = (owned?: number | null): PropertyCount =>
        (owned ?? 0) >= 3 ? '3rd+' : (owned ?? 0) === 2 ? '2nd' : '1st';
      if (members.length > 0) {
        const [primary, add] = members;
        setInputs((prev) => ({
          ...prev,
          citizenship: (primary.Citizenship as CitizenshipType) || 'SC',
          ftaEligible: Boolean(primary.FtaNationalityInd),
          propertyCount: toPropertyCount(primary.PropertiesOwned),
        }));
        if (add) {
          setAdditionalCitizenship((add.Citizenship as CitizenshipType) || 'SC');
          setAdditionalFtaEligible(Boolean(add.FtaNationalityInd));
          setAdditionalPropertyCount(toPropertyCount(add.PropertiesOwned));
        }
      }
    });
  }, [linkedCalc]);

  useEffect(() => {
    linkedCalc.registerProfileBuilder('buying', () => {
      const curr = inputsRef.current;
      const profiles: SharedCalculatorProfile[] = [{
        id: '1',
        age: curr.primary.age,
        monthlyIncome: Math.round(calculateAssessableIncome(
          String(curr.primary.monthlyIncome),
          String(curr.primary.otherAnnualIncome),
          curr.primary.isSelfEmployed ? 1 : 0,
        )),
        monthlyDebts: curr.primary.existingMonthlyDebt,
        selfEmployedInd: curr.primary.isSelfEmployed ? 1 : 0,
        incomeBasis: 'assessable',
        currentCashSavings: curr.primary.cash,
        currentCPFOA: curr.primary.cpf,
      }];
      if (curr.includeAdditionalBorrower) {
        profiles.push({
          id: '2',
          age: curr.additional.age,
          monthlyIncome: Math.round(calculateAssessableIncome(
            String(curr.additional.monthlyIncome),
            String(curr.additional.otherAnnualIncome),
            curr.additional.isSelfEmployed ? 1 : 0,
          )),
          monthlyDebts: curr.additional.existingMonthlyDebt,
          selfEmployedInd: curr.additional.isSelfEmployed ? 1 : 0,
          incomeBasis: 'assessable',
          currentCashSavings: curr.additional.cash,
          currentCPFOA: curr.additional.cpf,
        });
      }
      return profiles;
    });
  }, [linkedCalc]);

  // ── Borrower sync on navigation ─────────────────────────────────────────
  useEffect(() => {
    linkedCalc.registerBorrowerExtractor('buying', () => ({
      primary: inputsRef.current.primary,
      additional: inputsRef.current.additional,
      includeAdditional: inputsRef.current.includeAdditionalBorrower,
    }));
  }, [linkedCalc]);

  useEffect(() => {
    linkedCalc.registerBorrowerApplier('buying', (data) => {
      setInputs(prev => ({
        ...prev,
        // Spread onto the current borrowers: fields the sender doesn't carry
        // (per-borrower cash/CPF from an older calculator) keep their value.
        primary: { ...prev.primary, ...data.primary },
        additional: { ...prev.additional, ...data.additional },
        includeAdditionalBorrower: data.includeAdditional,
      }));
    });
  }, [linkedCalc]);

  // ── Proceeds sync (Selling → Buying on navigation) ──────────────────────
  // Net sales proceeds → Sales Proceed row; CPF withdrawal → Additional CPF.
  useEffect(() => {
    linkedCalc.registerProceedsApplier('buying', (data) => {
      setSellerSoldHdb(!!data.soldHdb);
      // A fresh handoff owns the proceeds rows again, whatever was typed before.
      setProceedsRowsUserSet(false);
      // Older senders carry only the totals — treat that as a single unnamed seller,
      // which then matches whoever is buying.
      setSellerProceeds(
        data.sellers && data.sellers.length > 0
          ? data.sellers.map((seller) => ({
              name: seller.name,
              cashProceeds: Math.round(seller.cashProceeds),
              cpfRefund: Math.max(0, Math.round(seller.cpfRefund)),
            }))
          : [{
              cashProceeds: Math.max(0, Math.round(data.netSalesProceeds)),
              cpfRefund: Math.max(0, Math.round(data.cpfWithdrawal)),
            }]
      );

      // Carry the sellers onto the borrower cards, so the people who sold are the
      // people buying — and so the proceeds keep matching them by name from here on.
      // A joint sale opens a second borrower card; removing it here is what trims that
      // seller's share out of Available Funds. Names already typed (or loaded from a
      // profile) are left alone.
      const sellerNames = (data.sellers ?? []).map((seller) => seller.name?.trim() || '');
      if (sellerNames.some(Boolean)) {
        setInputs((prev) => {
          const primary = !prev.primary.name?.trim() && sellerNames[0]
            ? { ...prev.primary, name: sellerNames[0] }
            : prev.primary;

          let additional = prev.additional;
          let includeAdditionalBorrower = prev.includeAdditionalBorrower;
          if (sellerNames[1]) {
            if (!includeAdditionalBorrower) {
              // Same starting point as the "Add additional borrower" button, named.
              includeAdditionalBorrower = true;
              additional = { ...DEFAULT_BORROWER, name: sellerNames[1] };
            } else if (!prev.additional.name?.trim()) {
              additional = { ...prev.additional, name: sellerNames[1] };
            }
          }

          return primary === prev.primary
            && additional === prev.additional
            && includeAdditionalBorrower === prev.includeAdditionalBorrower
            ? prev
            : { ...prev, primary, additional, includeAdditionalBorrower };
        });
      }
    });
  }, [linkedCalc]);

  // ── Sale proceeds: only the sellers who are also buying here ──────────────
  // A joint sale into a solo purchase carries one share, not the whole sale. Named
  // sellers match the borrower cards by name; an unnamed seller (an older handoff)
  // falls back to position, so a single-seller sale still lands on the primary buyer.
  const sellerProceedsMatch = useMemo(() => {
    const buyerNames = [
      inputs.primary.name?.trim().toLowerCase() ?? '',
      ...(inputs.includeAdditionalBorrower ? [inputs.additional.name?.trim().toLowerCase() ?? ''] : []),
    ];
    // Names only decide the match when this side has names to match against —
    // otherwise (nothing typed, no profile loaded) fall back to position, so the
    // proceeds still land rather than being excluded wholesale.
    const anyBuyerNamed = buyerNames.some((name) => name.length > 0);
    const matched: SharedProceedsSeller[] = [];
    const excluded: SharedProceedsSeller[] = [];
    sellerProceeds.forEach((seller, idx) => {
      const name = seller.name?.trim().toLowerCase() ?? '';
      const isBuyer = name && anyBuyerNamed ? buyerNames.includes(name) : idx < buyerNames.length;
      (isBuyer ? matched : excluded).push(seller);
    });
    return {
      matched,
      excluded,
      cash: matched.reduce((sum, seller) => sum + seller.cashProceeds, 0),
      cpf: matched.reduce((sum, seller) => sum + seller.cpfRefund, 0),
    };
  }, [sellerProceeds, inputs.primary.name, inputs.additional.name, inputs.includeAdditionalBorrower]);

  // Re-derive the two proceeds rows whenever the match changes — removing a borrower
  // drops their share straight out of Available Funds. A typed figure wins until the
  // next handoff.
  const matchedProceedsCash = Math.max(0, Math.round(sellerProceedsMatch.cash));
  const matchedProceedsCpf = Math.max(0, Math.round(sellerProceedsMatch.cpf));
  useEffect(() => {
    if (sellerProceeds.length === 0 || proceedsRowsUserSet) return;
    setCashProceedFromSelling(matchedProceedsCash);
    setSellingNetProceeds(matchedProceedsCash);
    setInputs((prev) => (prev.additionalCpf === matchedProceedsCpf ? prev : { ...prev, additionalCpf: matchedProceedsCpf }));
  }, [matchedProceedsCash, matchedProceedsCpf, proceedsRowsUserSet, sellerProceeds.length]);

  /** Explains a proceeds row: who it came from, and who was left out. */
  const proceedsDialog = (kind: 'cash' | 'cpf'): CalcDialogInfo => {
    const amountOf = (seller: SharedProceedsSeller) => (kind === 'cash' ? seller.cashProceeds : seller.cpfRefund);
    const rows: CalcDialogRow[] = sellerProceedsMatch.matched.map((seller, i) => ({
      label: `${i === 0 ? '' : '+ '}${seller.name?.trim() || 'Seller'}`,
      value: fmt(amountOf(seller)),
    }));
    rows.push({ isSeparator: true, label: '', value: '' });
    rows.push({
      label: kind === 'cash' ? '= Additional Cash' : '= Additional CPF',
      value: fmt(kind === 'cash' ? matchedProceedsCash : matchedProceedsCpf),
      isResult: true,
    });
    if (sellerProceedsMatch.excluded.length > 0) {
      rows.push({ isSeparator: true, label: '', value: '' });
      rows.push({ note: true, label: 'Not carried over — not a buyer on this purchase:', value: '' });
      sellerProceedsMatch.excluded.forEach((seller) => {
        rows.push({ label: seller.name?.trim() || 'Seller', value: fmt(amountOf(seller)) });
      });
    }
    rows.push({
      note: true,
      label: kind === 'cash'
        ? 'From the Selling Calculator: each seller\u2019s share of the sale after their own CPF refund. Type over the amount to use a different figure.'
        : 'From the Selling Calculator: each seller\u2019s CPF refund, back in their own OA. Type over the amount to use a different figure.',
      value: '',
    });
    return { title: kind === 'cash' ? 'Sale Proceeds Carried Over' : 'CPF Refund Carried Over', rows };
  };

  // When the buyer arrives from selling an HDB flat and is looking at an EC purchase,
  // default the Resale Levy to $55,000 (payable on a 2nd subsidised flat). Tracks the
  // condition automatically until the user edits the field themselves (which freezes it).
  useEffect(() => {
    if (resaleLevyUserSet) return;
    setResaleLevy(propertyTab === 'ec' && sellerSoldHdb ? 55000 : 0);
  }, [propertyTab, sellerSoldHdb, resaleLevyUserSet]);

  // Auto-check "2nd HDB Loan" when the buyer arrives from selling an HDB flat and is
  // buying another HDB flat with an HDB concessionary loan. Tracks the condition until
  // the user toggles the checkbox themselves (which freezes their choice).
  useEffect(() => {
    if (isSecondHdbLoanUserSet) return;
    const shouldCheck = sellerSoldHdb && propertyTab === 'hdb' && hdbInputs.loanSource === 'hdb';
    if (hdbInputs.isSecondHdbLoan !== shouldCheck) updateHdb('isSecondHdbLoan', shouldCheck);
  }, [isSecondHdbLoanUserSet, sellerSoldHdb, propertyTab, hdbInputs.loanSource, hdbInputs.isSecondHdbLoan, updateHdb]);

  // ── 2nd HDB loan: 50% cash proceeds rule ──────────────────────────────────
  // Live once the buyer is putting sale proceeds into another HDB flat on an HDB
  // concessionary loan. Gated on the "Second HDB loan" checkbox (which auto-follows
  // sellerSoldHdb) so a buyer who has never taken an HDB loan can switch it off.
  // The resale levy comes off the proceeds before the split, matching HDB's order.
  //
  // Measured against sellingNetProceeds — the figure the Selling calculator produced —
  // not the editable Additional Cash row. HDB sizes the rule on what the sale actually
  // yielded; trimming how much of it you park here does not shrink the obligation.
  // Falls back to the typed row when the buyer never came from the Selling calculator.
  const secondHdbLoanRule = useMemo(() => {
    const base = sellingNetProceeds > 0 ? sellingNetProceeds : cashProceedFromSelling;
    const split = getSecondHdbLoanProceedsSplit(base - resaleLevy);
    const active = propertyTab === 'hdb'
      && hdbInputs.loanSource === 'hdb'
      && hdbInputs.isSecondHdbLoan
      && split.proceeds > 0;
    return { active, ...split };
  }, [propertyTab, hdbInputs.loanSource, hdbInputs.isSecondHdbLoan, sellingNetProceeds, cashProceedFromSelling, resaleLevy]);

  // Seed "Cash proceeds to use" with the rule's minimum, tracking the proceeds until
  // the user types their own figure (which freezes it, as with the resale levy).
  useEffect(() => {
    if (secondHdbLoanSetAsideUserSet) return;
    const next = secondHdbLoanRule.active ? secondHdbLoanRule.mustUse : 0;
    if (hdbInputs.secondHdbLoanCashSetAside !== next) updateHdb('secondHdbLoanCashSetAside', next);
  }, [
    secondHdbLoanSetAsideUserSet, secondHdbLoanRule.active, secondHdbLoanRule.mustUse,
    hdbInputs.secondHdbLoanCashSetAside, updateHdb,
  ]);

  // True when the buyer has overridden the set-aside below what HDB would require.
  const secondHdbLoanShortfall = secondHdbLoanRule.active
    ? Math.max(0, secondHdbLoanRule.mustUse - hdbInputs.secondHdbLoanCashSetAside)
    : 0;
  // The buyer has parked less of the proceeds in Additional Cash than HDB compels into
  // the flat — the plan cannot fund the minimum from the cash declared here.
  const secondHdbLoanUnallocated = secondHdbLoanRule.active
    ? Math.max(0, secondHdbLoanRule.mustUse - cashProceedFromSelling)
    : 0;

  const applySharedProfilesToBuying = useCallback(
    (profiles: readonly SharedCalculatorProfile[]) => {
      const primary = profiles[0];
      if (!primary) return;
      setInputs(prev => ({
        ...prev,
        primary: {
          age: primary.age,
          monthlyIncome: primary.monthlyIncome,
          otherAnnualIncome: 0,
          isSelfEmployed: !!primary.selfEmployedInd,
          existingMonthlyDebt: primary.monthlyDebts,
          cash: Math.round(primary.currentCashSavings ?? 0),
          cpf: Math.round(primary.currentCPFOA ?? 0),
        },
        includeAdditionalBorrower: profiles.length > 1,
        additional: profiles.length > 1 ? {
          age: profiles[1].age,
          monthlyIncome: profiles[1].monthlyIncome,
          otherAnnualIncome: 0,
          isSelfEmployed: !!profiles[1].selfEmployedInd,
          existingMonthlyDebt: profiles[1].monthlyDebts,
          cash: Math.round(profiles[1].currentCashSavings ?? 0),
          cpf: Math.round(profiles[1].currentCPFOA ?? 0),
        } : prev.additional,
      }));
    },
    [],
  );

  // ── Payment Structure Derived Values ──────────────────────────────────────
  const minCashPct = tenureStretched ? 10 : 5;
  const maxLtvPct = tenureStretched ? 55 : 75;
  const minCash = useMemo(
    () => Math.round(inputs.purchasePrice * (minCashPct / 100)),
    [inputs.purchasePrice, minCashPct],
  );
  const totalDown = useMemo(
    () => minCash + cashDownAlloc + cpfDownAlloc,
    [minCash, cashDownAlloc, cpfDownAlloc],
  );
  // Special Bridging Loan applies only to EC purchases under the DPS payment scheme by
  // a buyer who still owns an HDB flat. It is disbursed at TOP towards the deferred
  // tranche, so it is capped at that tranche and at the non-down-payment balance —
  // it never funds the 20% down payment and never drives the bank loan below zero.
  const bridgingApplies = propertyTab === 'ec' && ecPaymentScheme === 'DPS';
  const sblMax = useMemo(
    () => Math.max(0, Math.round(sblValuation * SBL_VALUATION_PCT) - sblOutstandingLoan - sblLevy),
    [sblValuation, sblOutstandingLoan, sblLevy],
  );
  const sblTrancheCap = useMemo(
    () => Math.min(
      Math.round(inputs.purchasePrice * (EC_DPS_STAGES.find((s) => s.type === 'top')?.percentage ?? 0) / 100),
      Math.max(0, inputs.purchasePrice - totalDown),
    ),
    [inputs.purchasePrice, totalDown],
  );
  const bridgingForLoan = useMemo(
    () => (bridgingApplies && sblHasHdb ? Math.min(sblMax, sblTrancheCap) : 0),
    [bridgingApplies, sblHasHdb, sblMax, sblTrancheCap],
  );
  const derivedLoan = useMemo(() => {
    if (calculatorMode === 'simple') {
      if (propertyTab === 'hdb') {
        return getHdbResaleFinancing(
          inputs.purchasePrice,
          hdbInputs.valuationPrice,
          hdbInputs.loanSource,
          otpDeposit,
          tenureStretched,
        ).maxLoan;
      }
      return Math.round(inputs.purchasePrice * (maxLtvPct / 100));
    }
    // HDB: loan is driven by the HDB payment-structure allocations (cash + CPF), not the
    // private cashDownAlloc/cpfDownAlloc. Loan = price − deposits − COV − cash − CPF.
    if (propertyTab === 'hdb') {
      if (hdbInputs.loanSource === 'none') return 0;
      const financing = getHdbResaleFinancing(
        inputs.purchasePrice,
        hdbInputs.valuationPrice,
        hdbInputs.loanSource,
        otpDeposit,
        tenureStretched,
      );
      return Math.max(0, inputs.purchasePrice - financing.mandatoryCash - hdbCashAlloc - hdbCpfAlloc);
    }
    return inputs.hasBankLoan ? Math.max(0, inputs.purchasePrice - totalDown - bridgingForLoan) : 0;
  }, [tenureStretched, calculatorMode, propertyTab, hdbInputs.loanSource, hdbInputs.valuationPrice, otpDeposit, hdbCashAlloc, hdbCpfAlloc, inputs.hasBankLoan, inputs.purchasePrice, totalDown, bridgingForLoan, maxLtvPct]);

  const ltvDecimal = useMemo(
    () => (inputs.purchasePrice > 0 ? derivedLoan / inputs.purchasePrice : 0),
    [derivedLoan, inputs.purchasePrice],
  );
  const hdbResaleFinancing = useMemo(
    () => getHdbResaleFinancing(
      inputs.purchasePrice,
      hdbInputs.valuationPrice,
      hdbInputs.loanSource,
      otpDeposit,
      tenureStretched,
    ),
    [tenureStretched, inputs.purchasePrice, hdbInputs.valuationPrice, hdbInputs.loanSource, otpDeposit],
  );
  // LTV is expressed against the regulatory base: HDB uses the lower of purchase
  // price and valuation (COV is cash-only, outside the loan base), others the price.
  const ltvBaseAmount = propertyTab === 'hdb' ? hdbResaleFinancing.valuationBase : inputs.purchasePrice;
  const derivedLtvPct = useMemo(
    () => (ltvBaseAmount > 0 ? (derivedLoan / ltvBaseAmount) * 100 : 0),
    [derivedLoan, ltvBaseAmount],
  );
  const totalPct = useMemo(
    () => (inputs.purchasePrice > 0 ? ((minCash + cashDownAlloc + cpfDownAlloc) / inputs.purchasePrice) * 100 : 0),
    [minCash, cashDownAlloc, cpfDownAlloc, inputs.purchasePrice],
  );

  // Available Funds (additionalCash, cpf, etc.) are user-entered and not auto-computed.
  // Payment Structure allocations (cashDownAlloc, cpfDownAlloc) are separate state.

  // The private / EC CPF-first default is applied further below, once stamp duty,
  // legal-fee deductions and the EC CPF reserve have all been calculated.
  const privateAllocDefaultKeyRef = useRef('');

  // The HDB CPF-first allocation default lives below the hdbOutlay memo, so it can net
  // CPF-paid expenses (BSD/ABSD/legal/HPS) off the available CPF before allocating.
  const prevHdbAllocKeyRef = useRef('');

  // ── Compute IWAA from borrower inputs
  // Weight each borrower's age by their *recognised* (assessable) income — base salary
  // plus haircut variable/other income — so the blend matches the income the bank actually
  // assesses (and the "Total Recognised Income" shown below). Mirrors the Maximizer.
  const computedIwaa = useMemo(() => {
    const p = inputs.primary;
    if (!inputs.includeAdditionalBorrower) return p.age;
    const a = inputs.additional;
    const pRec = calculateAssessableIncome(String(p.monthlyIncome), String(p.otherAnnualIncome), p.isSelfEmployed ? 1 : 0);
    const aRec = calculateAssessableIncome(String(a.monthlyIncome), String(a.otherAnnualIncome), a.isSelfEmployed ? 1 : 0);
    const totalRec = pRec + aRec;
    if (totalRec === 0) return p.age;
    return Math.ceil((p.age * pRec + a.age * aRec) / totalRec);
  }, [inputs.primary, inputs.additional, inputs.includeAdditionalBorrower]);

  // Simple mode shows a single "Age" field. When a group profile with two borrowers is
  // loaded, that field represents the income-weighted average age (IWAA). It's seeded from
  // both members but stays adjustable — the user can nudge it and that value feeds the
  // tenure-cap math. The override clears itself whenever the computed IWAA changes (a
  // borrower edited in detailed mode, or a different profile loaded), so it never goes stale.
  const groupIwaaLoaded = selectedProfile?.type === 'group' && inputs.includeAdditionalBorrower;
  const [iwaaOverride, setIwaaOverride] = useState<number | null>(null);
  useEffect(() => { setIwaaOverride(null); }, [computedIwaa]);
  // Effective IWAA — the simple-mode override when set, else the computed value. The
  // override only applies to the loaded-group simple view; detailed mode stays driven by
  // the per-borrower inputs.
  const iwaa = groupIwaaLoaded && calculatorMode === 'simple' && iwaaOverride != null
    ? iwaaOverride
    : computedIwaa;

  const updateInput = useCallback(<K extends keyof BuyingInputs>(key: K, value: BuyingInputs[K]) => {
    setInputs((prev) => ({ ...prev, [key]: value }));
  }, []);

  // HDB: keep valuation in sync with the purchase price (COV = 0 by default) until
  // the user overrides it. Re-applies on every purchase-price change.
  useEffect(() => {
    if (propertyTab !== 'hdb' || hdbValuationUserSet) return;
    setHdbInputs((prev) => (prev.valuationPrice === inputs.purchasePrice ? prev : { ...prev, valuationPrice: inputs.purchasePrice }));
  }, [propertyTab, inputs.purchasePrice, hdbValuationUserSet]);

  // HDB: while the fee is driven by the percentage (the default 1%, or whatever the
  // user typed into the % field), keep it at pct of purchase price + 9% GST and
  // re-derive on every purchase-price change. Entering a dollar amount pins it.
  useEffect(() => {
    if (propertyTab !== 'hdb' || hdbAgencyFeeUserSet) return;
    const def = agencyFeeFromPct(inputs.purchasePrice, hdbInputs.agencyServiceFeePct);
    setHdbInputs((prev) => (prev.agencyServiceFee === def ? prev : { ...prev, agencyServiceFee: def }));
  }, [propertyTab, inputs.purchasePrice, hdbAgencyFeeUserSet, hdbInputs.agencyServiceFeePct]);

  // Agency fee entered as a percentage: re-derive the amount and let it track the price.
  const handleAgencyFeePctChange = useCallback((pct: number) => {
    setHdbAgencyFeeUserSet(false);
    setHdbInputs((prev) => ({
      ...prev,
      agencyServiceFeePct: pct,
      agencyServiceFee: agencyFeeFromPct(inputs.purchasePrice, pct),
    }));
  }, [inputs.purchasePrice]);

  // Agency fee entered as a dollar amount: pin it and back-derive the % it represents.
  const handleAgencyFeeAmountChange = useCallback((amount: number) => {
    setHdbAgencyFeeUserSet(true);
    setHdbInputs((prev) => ({
      ...prev,
      agencyServiceFee: amount,
      agencyServiceFeePct: inputs.purchasePrice > 0
        ? (amount / AGENCY_FEE_GST) / inputs.purchasePrice * 100
        : prev.agencyServiceFeePct,
    }));
  }, [inputs.purchasePrice]);

  const updatePrimary = useCallback((field: keyof BorrowerInputs, value: number | boolean | string | undefined) => {
    setInputs((prev) => ({ ...prev, primary: { ...prev.primary, [field]: value } }));
  }, []);

  const updateAdditional = useCallback((field: keyof BorrowerInputs, value: number | boolean | string | undefined) => {
    setInputs((prev) => ({ ...prev, additional: { ...prev.additional, [field]: value } }));
  }, []);

  // Available Funds → Cash and CPF are the sum of what each borrower declared on
  // their own card, so a group's funds stay attributable to the person who holds
  // them (and save back to that person's profile). Everything downstream keeps
  // reading the two totals.
  const borrowerCashTotal = inputs.primary.cash + (inputs.includeAdditionalBorrower ? inputs.additional.cash : 0);
  const borrowerCpfTotal = inputs.primary.cpf + (inputs.includeAdditionalBorrower ? inputs.additional.cpf : 0);
  useEffect(() => {
    setInputs((prev) =>
      prev.additionalCash === borrowerCashTotal && prev.cpf === borrowerCpfTotal
        ? prev
        : { ...prev, additionalCash: borrowerCashTotal, cpf: borrowerCpfTotal }
    );
    // The HDB tab's OA balance mirrors the CPF total (as the funds row always has).
    if (propertyTab === 'hdb') {
      setHdbInputs((prev) => (prev.cpfOa === borrowerCpfTotal ? prev : { ...prev, cpfOa: borrowerCpfTotal }));
    }
  }, [borrowerCashTotal, borrowerCpfTotal, propertyTab]);

  // Promote the additional borrower to primary (called when user removes the primary).
  const promoteAdditionalToPrimary = useCallback(() => {
    setInputs((prev) => ({
      ...prev,
      primary: prev.additional,
      additional: DEFAULT_BORROWER,
      includeAdditionalBorrower: false,
    }));
  }, []);

  // Computed values
  const spDate = useMemo(() => new Date(inputs.spDate + 'T00:00:00'), [inputs.spDate]);
  const stages = propertyTab === 'ec'
    ? (ecPaymentScheme === 'DPS' ? EC_DPS_STAGES : EC_NPS_STAGES)
    : privateSubTab === 'new-launch' ? NEW_LAUNCH_STAGES
    : RESALE_STAGES;

  const fmt = useCallback((v: number) =>
    new Intl.NumberFormat('en-SG', { style: 'currency', currency: 'SGD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(v),
    []);

  const cardBg = isDark ? 'rgba(255,255,255,0.04)' : theme.palette.grey[50];
  const cardBorder = isDark ? 'rgba(255,255,255,0.08)' : theme.palette.divider;
  const highlightColor = isDark ? '#60a5fa' : theme.palette.primary.main;

  const renderSectionHeader = (
    title: string,
    opts?: { step?: number; trailing?: React.ReactNode }
  ) => (
    <Box sx={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      px: 2.5, py: 1.5,
      bgcolor: isDark ? 'rgba(255,255,255,0.04)' : theme.palette.grey[100],
      borderBottom: `1px solid ${cardBorder}`,
    }}>
      <Typography variant="subtitle1" fontWeight={800} sx={{
        color: highlightColor, letterSpacing: '0.08em',
        fontSize: '0.95rem', textTransform: 'uppercase',
      }}>
        {title}
      </Typography>
      {opts?.trailing ?? (opts?.step !== undefined && (
        <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500, fontSize: '0.85rem' }}>
          Step {opts.step}
        </Typography>
      ))}
    </Box>
  );

  // Available Funds → Cash / CPF. Both are owned by the borrower cards now, so the
  // row shows the total with an info icon that explains where it came from.
  const renderBorrowerFundsRow = (kind: 'cash' | 'cpf') => {
    const label = kind === 'cash' ? 'Cash' : 'CPF';
    const total = kind === 'cash' ? borrowerCashTotal : borrowerCpfTotal;
    const people: { name: string; amount: number }[] = [
      { name: inputs.primary.name?.trim() || 'Primary borrower', amount: inputs.primary[kind] },
      ...(inputs.includeAdditionalBorrower
        ? [{ name: inputs.additional.name?.trim() || 'Additional borrower', amount: inputs.additional[kind] }]
        : []),
    ];
    const dialog: CalcDialogInfo = {
      title: kind === 'cash' ? 'Cash Available' : 'CPF Available',
      rows: [
        ...people.map((p, i) => ({ label: i === 0 ? p.name : `+ ${p.name}`, value: fmt(p.amount) })),
        { isSeparator: true, label: '', value: '' },
        { label: `= Total ${label}`, value: fmt(total), isResult: true },
        {
          note: true,
          label: `Entered on each borrower's card under ${kind === 'cash' ? 'Cash / Saving' : 'CPF OA Balance'} — edit it there.`,
          value: '',
        },
      ],
    };

    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.82rem' }}>{label}</Typography>
          <InfoOutlinedIcon
            onClick={() => setCalcDialog(dialog)}
            sx={{ fontSize: 15, color: 'primary.main', cursor: 'pointer', ml: 0.5 }}
          />
        </Box>
        <Typography variant="body2" fontWeight={700} sx={{ width: 180, flexShrink: 0, textAlign: 'right', fontSize: '0.9rem' }}>
          {fmt(total)}
        </Typography>
      </Box>
    );
  };

  const sectionCardSx = {
    mb: 2,
    overflow: 'hidden' as const,
    borderRadius: 2,
    borderColor: cardBorder,
  };
  const sectionBodySx = { p: 2 };
  // Open list layout — group labels give the structure, so rows carry no borders.
  const expenseGroupSx = { px: 0.25 };
  const expenseRowSx = {
    py: 0.625,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 2,
  };
  const expenseInputSx = { width: { xs: 116, sm: 132 }, flexShrink: 0 };
  const renderExpenseGroupLabel = (title: string, detail?: string) => (
    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5, mb: 0.5, mt: 2, '&:first-of-type': { mt: 0.5 } }}>
      <Typography variant="caption" fontWeight={800} sx={{ color: highlightColor, letterSpacing: '0.08em', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>
        {title}
      </Typography>
      <Box sx={{ flex: 1, borderBottom: `1px solid ${cardBorder}`, alignSelf: 'center' }} />
      {detail && (
        <Typography variant="caption" sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}>
          {detail}
        </Typography>
      )}
    </Box>
  );
  // Compact checkbox line for pay-with-CPF (and similar) options — sits directly
  // under the fee it modifies instead of occupying a full row of its own.
  const renderExpenseCheckbox = (
    label: string,
    checked: boolean,
    onChange: (checked: boolean) => void,
    opts?: { disabled?: boolean; hint?: string; infoIcon?: React.ReactNode },
  ) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, pb: 0.75, mt: -0.25, pl: 0.25 }}>
      <Checkbox
        size="small"
        checked={checked}
        disabled={opts?.disabled}
        onChange={(e) => onChange(e.target.checked)}
        sx={{ p: 0, '& .MuiSvgIcon-root': { fontSize: 18 }, '&.Mui-checked': { color: highlightColor } }}
        inputProps={{ 'aria-label': label }}
      />
      <Typography
        variant="caption"
        onClick={opts?.disabled ? undefined : () => onChange(!checked)}
        sx={{
          fontWeight: 600,
          color: opts?.disabled ? 'text.disabled' : checked ? 'text.primary' : 'text.secondary',
          cursor: opts?.disabled ? 'default' : 'pointer',
          userSelect: 'none',
        }}
      >
        {label}
      </Typography>
      {opts?.hint && (
        <Typography variant="caption" sx={{ color: 'text.disabled' }}>· {opts.hint}</Typography>
      )}
      {opts?.infoIcon}
    </Box>
  );

  // When split-scroll is on (desktop only), each column gets its own scroll region so
  // the results on the right stay visible while filling in inputs on the left.
  // Column cap: 64px app bar + 24px container bottom padding + 24px breathing room at the
  // top — so at max page scroll the two panels fill nearly the whole viewport.
  const columnScrollSx = (splitScroll && !isMobile)
    ? { maxHeight: 'calc(100vh - 112px)', overflowY: 'auto' as const, pr: 1 }
    : undefined;

  // HDB concessionary loans accrue at the HDB rate (2.6%), not the bank rate; every
  // other loan (private/EC, or an HDB bank loan) uses the bank rate.
  const isHdbConcessionaryLoan = propertyTab === 'hdb' && hdbInputs.loanSource === 'hdb';
  const effectiveInterestRate = isHdbConcessionaryLoan
    ? HDB_CONCESSIONARY_INTEREST_RATE
    : inputs.interestRate;
  const monthlyInstallment = calcMonthlyInstallment(derivedLoan, effectiveInterestRate, inputs.tenure);

  // Borrower names — typed on the card, seeded from the loaded profile. Shown in the
  // section headers ("Primary Borrower - Adam") and used when saving a new profile.
  const borrowerName = (b: BorrowerInputs): string | undefined => b.name?.trim() || undefined;

  // Effective ABSD — joint purchases follow the IRAS joint-purchase rules (mirrors
  // AffordabilityCalculator): base rate is the higher of the two borrowers' individual
  // rates; married couples instead use the IRAS married-couple tables, which can grant
  // spousal remission (e.g. SC + SC/SPR/FR first-home cases).
  const effectiveCitizenship = getEffectiveCitizenshipForAbsd(inputs.citizenship, inputs.ftaEligible);
  const effectiveAdditionalCitizenship = getEffectiveCitizenshipForAbsd(additionalCitizenship, additionalFtaEligible);
  const primaryAbsdRate = getAbsdRate(effectiveCitizenship, inputs.propertyCount);
  const jointAbsd = inputs.includeAdditionalBorrower
    ? getJointAbsdRate(
        effectiveCitizenship, inputs.propertyCount,
        effectiveAdditionalCitizenship, additionalPropertyCount,
        borrowerRelationship === 'SPOUSE',
      )
    : { rate: primaryAbsdRate, remissionApplied: false, rateBeforeRemission: primaryAbsdRate };
  // No ABSD on an EC purchase — zeroing the rate drops every ABSD row (they render
  // only when absdAmount > 0) and keeps it out of the cash/CPF waterfall.
  const absdApplies = propertyTab !== 'ec';
  const absdRatePct = absdApplies ? jointAbsd.rate : 0;
  const absdRemissionApplied = absdApplies && jointAbsd.remissionApplied;
  const absdRateBeforeRemission = absdApplies ? jointAbsd.rateBeforeRemission : 0;
  // IRAS rule: ABSD computed on the higher of purchase price or market value.
  // For HDB, valuation price represents market value; for private/EC we use purchase price.
  const sdBase = propertyTab === 'hdb'
    ? Math.max(inputs.purchasePrice, hdbInputs.valuationPrice)
    : inputs.purchasePrice;
  // ABSD applies on the HDB tab too — an SPR-only household pays 5% on a resale flat,
  // and an SC/SPR buyer who already owns a property pays the joint-purchase rate. It
  // flows through hdbOutlay, the HDB schedule and the PDF like BSD does; citizen-only
  // first-time buyers simply get a 0% rate.
  const absdAmount = Math.round(sdBase * absdRatePct / 100);
  const absdRemissionAmount = absdRemissionApplied
    ? Math.round(sdBase * absdRateBeforeRemission / 100) - absdAmount
    : 0;
  const bsd = calculateBsd(sdBase);
  // Simple mode has no borrower inputs of its own, so it only charges ABSD when a
  // loaded profile supplies the buyers' ABSD status (citizenship, FTA, property count,
  // joint/married-couple rules) — the same rate Detailed mode uses.
  const simpleAbsdRatePct = selectedProfile ? absdRatePct : 0;
  const simpleAbsdAmount = selectedProfile ? absdAmount : 0;
  // HDB agency service fee — editable dollar amount (auto-defaults to 1% + 9% GST).
  const hdbAgencyFee = propertyTab === 'hdb' ? hdbInputs.agencyServiceFee : 0;
  // Agency fee is stored all-in; split it back out so the row can show the GST portion.
  const agencyFeeBase = Math.round(hdbInputs.agencyServiceFee / AGENCY_FEE_GST);
  const agencyFeeGst = hdbInputs.agencyServiceFee - agencyFeeBase;
  // CPF available after down payment (rough — used for BSD/ABSD CPF split in UI)
  const cpfTotal = inputs.cpf + inputs.additionalCpf;
  // CPF available toward fees (BSD/ABSD/Legal/HPS). HDB additionally counts CPF housing
  // grants; used to enable/disable the "pay by CPF" toggles.
  const feesCpfAvailable = propertyTab === 'hdb'
    ? cpfTotal + hdbInputs.housingGrant + hdbInputs.cpfResaleGrant + hdbInputs.phgGrant
    : cpfTotal;
  const cashTotal = Math.round(inputs.purchasePrice * 0.05) + inputs.additionalCash;
  const bsdCpf = bsdByCpf ? Math.min(bsd, cpfTotal) : 0;
  const bsdCash = bsd - bsdCpf;
  const absdCpf = absdByCpf ? Math.min(absdAmount, Math.max(0, cpfTotal - bsdCpf)) : 0;
  const absdCash = absdAmount - absdCpf;

  // computedStages needs bsdCpf/bsdCash so it must live after those declarations
  const computedStages = useMemo(() => {
    // With no bank loan, assign the full non-CPF balance to cash even when declared
    // Available Cash is insufficient (the Fund Balance will surface that shortfall).
    // This keeps the timeline strictly Cash -> CPF: CPF is reserved for the final
    // stages instead of appearing at OTP. Stamp duty remains a separate exception.
    const availableCpfForStages = Math.max(
      0,
      inputs.cpf + inputs.additionalCpf + (propertyTab === 'ec' ? ecHousingGrant : 0),
    );
    const useAllAvailableFunds = !inputs.hasBankLoan && propertyTab !== 'hdb';
    const stampCpfForStages = bsdCpf + absdCpf;
    const cpfAfterStamp = Math.max(0, availableCpfForStages - stampCpfForStages);
    const legalCpfForStages = useAllAvailableFunds
      ? 0
      : legalFeeByCpf ? Math.min(inputs.legalFee, cpfAfterStamp) : 0;
    const noLoanCpfPool = Math.min(
      cpfAfterStamp,
      Math.max(0, inputs.purchasePrice - minCash - bridgingForLoan),
    );
    const stageOpts = {
      cashDownAlloc,
      cpfDownAlloc,
      cashPoolAmt: useAllAvailableFunds
        ? Math.max(0, inputs.purchasePrice - bridgingForLoan - noLoanCpfPool)
        : undefined,
      cpfPoolAmt: useAllAvailableFunds
        ? noLoanCpfPool
        : undefined,
      minCashAmt: minCash,
      bsdByCpf,
      bsdCpfAmt: bsdCpf,
      bsdCashAmt: bsdCash,
      bridgingAmount: bridgingForLoan,
      exerciseDateOverride,
      sp15DateOverride,
      stageDateOverrides,
    };
    if (propertyTab === 'ec' || privateSubTab === 'new-launch') {
      return computeNewLaunchStages(inputs, stages, spDate, ltvDecimal, stageOpts);
    }
    return computeResaleStages(inputs, stages, spDate, ltvDecimal, stageOpts);
  }, [inputs, stages, spDate, privateSubTab, propertyTab, ltvDecimal, cashDownAlloc, cpfDownAlloc, minCash, bsdByCpf, bsdCpf, bsdCash, absdCpf, legalFeeByCpf, ecHousingGrant, bridgingForLoan, exerciseDateOverride, sp15DateOverride, stageDateOverrides]);

  const simpleOutputInputs = useMemo<BuyingInputs>(() => ({
    ...inputs,
    hasBankLoan: true,
    additionalCash: 0,
    cpf: 0,
    additionalCpf: 0,
    citizenship: 'SC',
    propertyCount: '1st',
    ftaEligible: false,
    includeAdditionalBorrower: false,
  }), [inputs]);

  // Private-resale milestone dates — the single source of truth for the on-screen
  // ResaleSchedule and the exported report. Exercise falls 14 days after OTP and
  // completion ~12 weeks after exercise unless the agent overrode either; stamp duty
  // is due within 14 days of exercising.
  const resaleDates = useMemo(() => {
    const otp = new Date(inputs.spDate + 'T00:00:00');
    const exercise = resaleExerciseOverride ? new Date(resaleExerciseOverride + 'T00:00:00') : addDays(otp, 14);
    const completion = resaleCompletionOverride ? new Date(resaleCompletionOverride + 'T00:00:00') : addDays(exercise, 84);
    return { otp, exercise, stampDuty: addDays(exercise, 14), completion };
  }, [inputs.spDate, resaleExerciseOverride, resaleCompletionOverride]);

  // Commit an edited private-resale milestone date. Editing the OTP moves the whole
  // schedule (the later two revert to their default offsets); editing a later milestone
  // pins just that one.
  const commitResaleDate = useCallback((stage: 'otp' | 'exercise' | 'completion', iso: string) => {
    if (stage === 'otp') {
      updateInput('spDate', iso);
      setResaleExerciseOverride(null);
      setResaleCompletionOverride(null);
    } else if (stage === 'exercise') {
      setResaleExerciseOverride(iso);
    } else {
      setResaleCompletionOverride(iso);
    }
  }, [updateInput]);

  // Pin one HDB resale milestone. Everything downstream is re-derived from it and
  // everything upstream stays where it is.
  const pinHdbMilestone = useCallback((key: HdbMilestoneKey, iso: string) => {
    setHdbDateOverrides((prev) => ({ ...prev, [key]: iso }));
  }, []);

  // Editing the OTP moves the whole HDB schedule, so the pins go with it —
  // same rule as commitResaleDate above.
  const clearHdbMilestonePins = useCallback(() => setHdbDateOverrides({}), []);

  const simpleComputedStages = useMemo(() => {
    const simpleBsd = calculateBsd(inputs.purchasePrice);
    const simpleStages = propertyTab === 'ec'
      ? (ecPaymentScheme === 'DPS' ? EC_DPS_STAGES : EC_NPS_STAGES)
      : NEW_LAUNCH_STAGES;
    const raw = computeNewLaunchStages(
      simpleOutputInputs,
      simpleStages,
      spDate,
      maxLtvPct / 100,
      {
        cashDownAlloc: Math.round(inputs.purchasePrice * ((100 - maxLtvPct - minCashPct) / 100)),
        cpfDownAlloc: 0,
        minCashAmt: minCash,
        bsdByCpf: false,
        bsdCpfAmt: 0,
        bsdCashAmt: simpleBsd,
        bridgingAmount: 0,
        stageDateOverrides,
      },
    );
    // Split each stage into its mandatory-cash / flexible (CPF/Cash) portions so the
    // simple-mode Initial Outlay and Progressive Schedule can label them accordingly.
    return augmentSimpleFunding(raw, inputs.purchasePrice, minCashPct);
  }, [ecPaymentScheme, inputs.purchasePrice, propertyTab, simpleOutputInputs, spDate, maxLtvPct, minCashPct, minCash, stageDateOverrides]);

  // Full HDB initial outlay — sums the entire timeline (down payment incl. deposits & COV,
  // stamp duty, legal, HPS, fees) and splits it into CPF + Cash, mirroring the HDB schedule's
  // waterfall (CPF pays BSD/ABSD/Legal/HPS first, then the down payment beyond the $5k deposits).
  const hdbOutlay = useMemo(() => {
    if (propertyTab !== 'hdb' || inputs.purchasePrice <= 0) return null;
    const cov = getHdbResaleFinancing(
      inputs.purchasePrice,
      hdbInputs.valuationPrice,
      hdbInputs.loanSource,
      otpDeposit,
      tenureStretched,
    ).cov;
    const loan = calculatorMode === 'simple'
      ? derivedLoan
      : hdbInputs.loanSource === 'hdb'
        ? Math.max(0, inputs.purchasePrice - otpDeposit - cov - hdbCashAlloc - hdbCpfAlloc)
        : hdbInputs.loanSource === 'bank' ? derivedLoan : 0;
    const legal = inputs.legalFee;
    const hps = hdbInputs.hpsAmount;
    const valFee = hdbInputs.valuationFee;
    const adminFee = hdbInputs.hdbAdminFee;
    // Caveat lodgement fee applies to the HDB concessionary loan path only.
    const caveatFee = hdbInputs.loanSource === 'hdb' ? hdbInputs.caveatFee : 0;
    const agencyFee = hdbAgencyFee;
    const cpfAvail = inputs.cpf + inputs.additionalCpf + hdbInputs.housingGrant + hdbInputs.cpfResaleGrant + hdbInputs.phgGrant;
    // CPF waterfall: the opted-in fees (default on) draw CPF FIRST — BSD, ABSD, legal fee,
    // then HPS — so they show under CPF. In detailed mode, preserve the CPF amount the
    // buyer explicitly entered in Payment Structure; the remainder of the down payment
    // is cash (including the mandatory deposits and any COV). Simple mode has no editable
    // split, so it continues to use the remaining CPF first. Valuation / admin / caveat /
    // agency fees are always cash.
    const downTotal = Math.max(0, inputs.purchasePrice - loan); // incl. OTP deposits + COV
    const depositsCash = Math.min(otpDeposit, downTotal);       // deposits are always cash
    const downFlexible = Math.max(0, downTotal - depositsCash); // CPF-eligible down portion
    let cpfLeft = cpfAvail;
    const bsdCpf = bsdByCpf ? Math.min(bsd, cpfLeft) : 0; cpfLeft -= bsdCpf;
    const absdCpf = absdByCpf ? Math.min(absdAmount, cpfLeft) : 0; cpfLeft -= absdCpf;
    const legalCpf = legalFeeByCpf ? Math.min(legal, cpfLeft) : 0; cpfLeft -= legalCpf;
    const hpsCpf = hpsByCpf ? Math.min(hps, cpfLeft) : 0; cpfLeft -= hpsCpf;
    const requestedDownCpf = calculatorMode === 'detailed' ? hdbCpfAlloc : cpfLeft;
    const downCpfA = Math.min(downFlexible, cpfLeft, requestedDownCpf); cpfLeft -= downCpfA;
    return {
      downCash: downTotal - downCpfA, downCpf: downCpfA,
      bsdCash: bsd - bsdCpf, bsdCpf,
      absdCash: absdAmount - absdCpf, absdCpf,
      legalCash: legal - legalCpf, legalCpf,
      hpsCash: hps - hpsCpf, hpsCpf,
      valFee, adminFee, caveatFee, agencyFee,
    };
  }, [tenureStretched, propertyTab, calculatorMode, inputs.purchasePrice, inputs.legalFee, inputs.cpf, inputs.additionalCpf, hdbInputs, otpDeposit, hdbCashAlloc, hdbCpfAlloc, derivedLoan, bsd, absdAmount, hdbAgencyFee, bsdByCpf, absdByCpf, legalFeeByCpf, hpsByCpf]);

  // CPF consumed by the opted-in expenses (BSD/ABSD/legal/HPS, per the hdbOutlay
  // waterfall). The down payment can only use what's left, so the Payment Structure,
  // allocation default and loan-edit re-derivation all net this off "Available" CPF.
  const hdbCpfExpenses = hdbOutlay
    ? hdbOutlay.bsdCpf + hdbOutlay.absdCpf + hdbOutlay.legalCpf + hdbOutlay.hpsCpf
    : 0;

  // HDB: CPF must go toward the home — default the CPF allocation to the FULL available
  // CPF + grants net of CPF-paid expenses (wiped out; buyers may retain up to $20k OA,
  // see info note), then fill cash so the loan reaches the 75% LTV max. Re-defaults when
  // price / valuation / available CPF / grants / loan type change; manual edits persist
  // until then.
  useEffect(() => {
    if (propertyTab !== 'hdb' || inputs.purchasePrice <= 0) return;
    const availCpfGrants = Math.max(0, inputs.cpf + inputs.additionalCpf + hdbInputs.housingGrant + hdbInputs.cpfResaleGrant + hdbInputs.phgGrant - hdbCpfExpenses);
    const key = `${inputs.purchasePrice}|${hdbInputs.valuationPrice}|${hdbInputs.loanSource}|${availCpfGrants}|${otpDeposit}`;
    if (key === prevHdbAllocKeyRef.current) return;
    prevHdbAllocKeyRef.current = key;
    if (restoringRef.current) return; // keep the restored HDB allocation on history load
    const financing = getHdbResaleFinancing(
      inputs.purchasePrice,
      hdbInputs.valuationPrice,
      hdbInputs.loanSource,
      otpDeposit,
      tenureStretched,
    );
    const targetDown = financing.totalDownPayment;
    setHdbCpfAlloc(availCpfGrants); // wipe out CPF toward the purchase
    setHdbCashAlloc(Math.max(0, targetDown - financing.mandatoryCash - availCpfGrants));
  }, [tenureStretched, propertyTab, inputs.purchasePrice, inputs.cpf, inputs.additionalCpf, hdbInputs.valuationPrice, hdbInputs.loanSource, hdbInputs.housingGrant, hdbInputs.cpfResaleGrant, hdbInputs.phgGrant, otpDeposit, hdbCpfExpenses]);

  // Tenure caps (MAS), by income-weighted average age:
  //   Standard: max age 65 / 30 yrs — keeps the full 75% LTV.
  //   Extended: max age 75 / 35 yrs — allowed, but running past the standard cap drops
  //     the max LTV to 55% and the minimum cash to 10% (buyer confirms via a dialog).
  //   HDB flat: standard 25 yrs / age 65; a bank loan may extend to 30 yrs / age 75 at
  //     55% LTV, while an HDB concessionary loan can't be extended.
  const isHdbTab = propertyTab === 'hdb';
  const hdbBankLoan = isHdbTab && hdbInputs.loanSource === 'bank';
  const standardTenureCap = useMemo(
    () => Math.min(isHdbTab ? 25 : 30, Math.max(0, 65 - iwaa)),
    [iwaa, isHdbTab],
  );
  const extendedTenureCap = useMemo(
    () => (isHdbTab && !hdbBankLoan
      ? Math.min(25, Math.max(0, 65 - iwaa))
      : Math.min(isHdbTab ? 30 : 35, Math.max(0, 75 - iwaa))),
    [iwaa, isHdbTab, hdbBankLoan],
  );
  // Tenure may run up to the extended cap; going past the standard cap drops the max LTV
  // to 55% (for an HDB concessionary loan the two caps are equal, so it never stretches).
  const maxTenureCap = extendedTenureCap;

  // Pending tenure value awaiting user confirmation (when dragging past standard cap).
  const [pendingTenure, setPendingTenure] = useState<number | null>(null);

  // Keep the stretched flag in sync with the tenure: a bank loan running past the standard
  // cap falls under the MAS 55% LTV / 10% cash regime.
  useEffect(() => {
    const shouldStretch = (propertyTab !== 'hdb' || hdbBankLoan) && inputs.tenure > standardTenureCap;
    if (shouldStretch !== tenureStretched) setTenureStretched(shouldStretch);
  }, [propertyTab, hdbBankLoan, inputs.tenure, standardTenureCap, tenureStretched]);

  // Clamp the tenure down whenever the cap tightens (age change, tab switch, etc.).
  useEffect(() => {
    if (maxTenureCap > 0 && inputs.tenure > maxTenureCap) updateInput('tenure', maxTenureCap);
  }, [maxTenureCap, inputs.tenure, updateInput]);

  // The tenure cap is driven by the IWAA, which is INCOME-weighted — so editing either
  // borrower's income (or the simple-mode IWAA override) moves the cap even when both ages
  // stay put. Key the reset on the effective IWAA, not the raw ages, so the tenure always
  // re-defaults to the new maximum: e.g. an income edit that pulls the IWAA 40 → 39 lifts
  // the standard cap 25 → 26 yrs, and the tenure follows instead of sticking at 25.
  // Switching tab or HDB loan source changes the caps too, so it also re-defaults the
  // tenure to the maximum the buyer is eligible for.
  const tenureResetKey = `${iwaa}|${propertyTab}|${hdbInputs.loanSource}`;
  const prevTenureResetKey = useRef(tenureResetKey);
  useEffect(() => {
    if (prevTenureResetKey.current === tenureResetKey) return;
    prevTenureResetKey.current = tenureResetKey;
    if (restoringRef.current) return; // keep the restored tenure on history load

    // Default to the longest tenure that still keeps the best LTV (the standard cap for
    // private/EC; the 25-yr HDB cap otherwise). The stretch-sync effect keeps the flag right.
    const defaultTenure = Math.max(1, standardTenureCap);
    if (inputs.tenure !== defaultTenure) updateInput('tenure', defaultTenure);
  }, [tenureResetKey, inputs.tenure, standardTenureCap, updateInput]);

  // Detailed mode: tenure defaults to the maximum the buyer is eligible for. The "Extend"
  // checkbox jumps to the extended cap; the pencil lets the buyer key in any whole-year
  // tenure up to the cap in force (standard, or extended once ticked).
  // Detailed-mode Loan section (Private, EC, and HDB with a bank loan).
  const ecFixedTenure = calculatorMode === 'detailed' && (propertyTab !== 'hdb' || hdbBankLoan);
  const canExtendTenure = extendedTenureCap > standardTenureCap;

  // Intercept tenure changes: trigger confirm dialog if user pushes past standard cap.
  const requestTenureChange = (newTenure: number) => {
    const clamped = Math.min(Math.max(1, newTenure), Math.max(1, maxTenureCap));
    // First time crossing the standard cap: confirm the 55% LTV / 10% cash consequence.
    // (The stretch-sync effect sets/clears the flag once the tenure actually updates.)
    if (clamped > standardTenureCap && !tenureStretched) {
      setPendingTenure(clamped);
      setStretchDialogOpen(true);
      return;
    }
    updateInput('tenure', clamped);
  };

  // TDSR from borrower inputs
  const totalRecognisedIncome = useMemo(() => {
    const pIncome = calculateAssessableIncome(String(inputs.primary.monthlyIncome), String(inputs.primary.otherAnnualIncome), inputs.primary.isSelfEmployed ? 1 : 0);
    const aIncome = inputs.includeAdditionalBorrower
      ? calculateAssessableIncome(String(inputs.additional.monthlyIncome), String(inputs.additional.otherAnnualIncome), inputs.additional.isSelfEmployed ? 1 : 0)
      : 0;
    return Math.round(pIncome + aIncome);
  }, [inputs.primary, inputs.additional, inputs.includeAdditionalBorrower]);

  // ── EC / HDB eligibility ────────────────────────────────────────────────────
  // Rules live in utils/housingEligibility (shared with the Affordability Calculator).
  // Here they surface as review warnings on the matching tab. Income ceilings are
  // assessed on gross monthly household income, not the haircut assessable income.
  const eligibilityInput = useMemo((): EligibilityInput => {
    const grossMonthly = (b: BorrowerInputs) => b.monthlyIncome + b.otherAnnualIncome / 12;
    return {
      applicants: [
        { label: 'Primary applicant', age: inputs.primary.age, citizenship: inputs.citizenship, propertyCount: inputs.propertyCount },
        ...(inputs.includeAdditionalBorrower
          ? [{ label: 'Additional applicant', age: inputs.additional.age, citizenship: additionalCitizenship, propertyCount: additionalPropertyCount }]
          : []),
      ],
      relationship: borrowerRelationship,
      grossMonthlyIncome: Math.round(
        grossMonthly(inputs.primary) + (inputs.includeAdditionalBorrower ? grossMonthly(inputs.additional) : 0),
      ),
    };
  }, [inputs.primary, inputs.citizenship, inputs.propertyCount, inputs.includeAdditionalBorrower, inputs.additional, additionalCitizenship, additionalPropertyCount, borrowerRelationship]);

  const ecEligibility = useMemo(() => {
    if (propertyTab !== 'ec') {
      return { active: false, warnings: [] as string[] };
    }
    const r = assessEcEligibility(eligibilityInput);
    return { active: true, warnings: [...r.blockers, ...r.notes] };
  }, [propertyTab, eligibilityInput]);

  // HDB concessionary loan — only relevant when the buyer picks the HDB (2.6%) loan.
  const hdbLoanEligibility = useMemo(() => {
    if (propertyTab !== 'hdb' || hdbInputs.loanSource !== 'hdb') {
      return { active: false, warnings: [] as string[] };
    }
    return { active: true, warnings: assessHdbLoanEligibility(eligibilityInput).blockers };
  }, [propertyTab, hdbInputs.loanSource, eligibilityInput]);

  // HDB flat purchase eligibility (buyer profile) plus the ABSD note.
  const hdbEligibility = useMemo(() => {
    if (propertyTab !== 'hdb') {
      return { active: false, scheme: '', warnings: [] as string[], absd: null as { rate: number; amount: number; reason: string } | null };
    }
    const flat = assessHdbFlatEligibility(eligibilityInput);
    const scheme = flat.scheme;
    const warnings = [...flat.blockers, ...flat.notes];
    const applicants = eligibilityInput.applicants;
    const allSpr = applicants.every((a) => a.citizenship === 'SPR');

    // ABSD on an HDB resale purchase. A citizen-only household pays nothing, so the
    // note only surfaces when a rate actually bites — most commonly an all-SPR
    // married couple, where 5% applies and no married-couple remission is available
    // (the IRAS tables need at least one Singapore Citizen spouse).
    let absd: { rate: number; amount: number; reason: string } | null = null;
    if (absdRatePct > 0) {
      const reason = allSpr
        ? applicants.length > 1
          ? 'SPR rate on a first residential property — no married-couple remission for an SPR–SPR couple (the IRAS tables need at least one Singapore Citizen spouse).'
          : 'SPR rate on a first residential property.'
        : applicants.length > 1
          ? borrowerRelationship === 'SPOUSE'
            ? 'per the IRAS married-couple joint-purchase tables.'
            : 'for a joint purchase, the profile with the higher rate applies.'
          : 'based on the buyer profile and properties already owned.';
      absd = { rate: absdRatePct, amount: Math.round(sdBase * absdRatePct / 100), reason };
    }

    return { active: true, scheme, warnings, absd };
  }, [propertyTab, eligibilityInput, borrowerRelationship, absdRatePct, sdBase]);

  // ── Loan Affordability ────────────────────────────────────────────────────
  // A concessionary loan is granted by HDB, not a financial institution, so the MAS
  // medium-term interest rate floor (4%) does not apply to it — HDB assesses the
  // eligible loan amount at its own floor (3%). Both are configurable in Settings.
  const stressTestRate = isHdbConcessionaryLoan
    ? globalSettings.hdbStressTestRate
    : globalSettings.stressTestRate;
  const totalExistingDebt = useMemo(
    () => inputs.primary.existingMonthlyDebt + (inputs.includeAdditionalBorrower ? inputs.additional.existingMonthlyDebt : 0),
    [inputs.primary.existingMonthlyDebt, inputs.additional.existingMonthlyDebt, inputs.includeAdditionalBorrower],
  );
  const availableTDSR = useMemo(
    () => Math.max(0, totalRecognisedIncome * TDSR_RATIO - totalExistingDebt),
    [totalRecognisedIncome, totalExistingDebt],
  );
  // MSR applies only to HDB and EC loans. The repayment ceiling is 30% of gross
  // monthly income (MSR considers only the property loan, so existing non-property
  // debt is not deducted here — TDSR handles that separately).
  const msrApplies = propertyTab === 'ec' || propertyTab === 'hdb';
  const availableMSR = useMemo(
    () => Math.max(0, totalRecognisedIncome * MSR_RATIO),
    [totalRecognisedIncome],
  );
  // Binding monthly repayment capacity: for HDB/EC take the lower of TDSR and MSR.
  const bindingMonthly = useMemo(
    () => (msrApplies ? Math.min(availableTDSR, availableMSR) : availableTDSR),
    [msrApplies, availableTDSR, availableMSR],
  );
  // The ratio governing the binding constraint (used for income-shortfall maths).
  const msrBinds = msrApplies && availableMSR < availableTDSR;
  const bindingRatio = msrBinds ? MSR_RATIO : TDSR_RATIO;
  // Extra recognised monthly income needed to service `requiredMonthly`. Every applicable
  // ratio must pass after the income is added, so take the larger requirement — TDSR
  // (55%, net of existing debt) and, for HDB/EC, MSR (30%, property loan only). Using only
  // the ratio that binds at current income under-counts when existing debts make TDSR
  // bind today but MSR still needs more income.
  const extraIncomeNeeded = useCallback((requiredMonthly: number) => {
    const tdsrNeed = Math.max(0, requiredMonthly - availableTDSR) / TDSR_RATIO;
    const msrNeed = msrApplies ? Math.max(0, requiredMonthly - availableMSR) / MSR_RATIO : 0;
    return msrNeed > tdsrNeed
      ? { income: msrNeed, ratio: 'MSR' as const, available: availableMSR, extraMonthly: Math.max(0, requiredMonthly - availableMSR) }
      : { income: tdsrNeed, ratio: 'TDSR' as const, available: availableTDSR, extraMonthly: Math.max(0, requiredMonthly - availableTDSR) };
  }, [availableTDSR, availableMSR, msrApplies]);
  const maxLoan = useMemo(
    () => calculateMaxLoan(bindingMonthly, stressTestRate, inputs.tenure),
    [bindingMonthly, stressTestRate, inputs.tenure],
  );
  // Loan quantum at the standard (non-extended) tenure — compared against the extended
  // tenure in Loan Info once the buyer extends.
  const standardTenureMaxLoan = useMemo(
    () => calculateMaxLoan(bindingMonthly, stressTestRate, Math.max(1, standardTenureCap)),
    [bindingMonthly, stressTestRate, standardTenureCap],
  );
  // Loan the "Max Loan" action pins to. Private: the MAS LTV cap. EC: banks lend the LOWER
  // of the LTV cap and the loan quantum that MSR (30%) / TDSR (55%) allow. The default
  // payment structure still uses the LTV down payment, so for EC any excess over the
  // quantum surfaces as a loan shortfall (pay by cash / fund pledging).
  const ltvCapLoan = Math.round(inputs.purchasePrice * (maxLtvPct / 100));
  const ecQuantumBinds = propertyTab === 'ec' && maxLoan < ltvCapLoan;
  const targetBankLoan = ecQuantumBinds ? maxLoan : ltvCapLoan;

  // Private / EC default payment waterfall:
  //   1. Keep the bank loan at the applicable maximum LTV (75%, or 55% when stretched).
  //   2. Keep the mandatory 5% / 10% cash down payment.
  //   3. Fund the remaining down payment with usable CPF first.
  //   4. Put only the CPF shortfall into Additional Cash.
  // CPF already earmarked for stamp duty or legal fees is excluded.
  // The key deliberately excludes cashDownAlloc / cpfDownAlloc so manual allocation edits
  // remain untouched until an underlying input (price, CPF, LTV, fees, etc.) changes.
  const privateTotalCpf = inputs.cpf + inputs.additionalCpf + (propertyTab === 'ec' ? ecHousingGrant : 0);
  const privateStampCpf = (bsdByCpf ? bsdCpf : 0) + (absdByCpf ? absdCpf : 0);
  const privateLegalCpf = legalFeeByCpf ? Math.min(inputs.legalFee, Math.max(0, privateTotalCpf - privateStampCpf)) : 0;
  const privateCpfForDown = Math.max(0, privateTotalCpf - privateStampCpf - privateLegalCpf);

  // A manually edited loan changes the required down payment. Preserve the mandatory
  // cash component, then use the CPF genuinely available for the down payment before
  // assigning any remainder to Additional Cash.
  const getLoanChangeAllocation = useCallback((loan: number) => {
    const price = inputs.purchasePrice;
    const maxLoanAfterMandatoryCash = Math.max(0, price - minCash - bridgingForLoan);
    const clampedLoan = Math.max(0, Math.min(loan, maxLoanAfterMandatoryCash));
    const flexibleDownNeeded = Math.max(0, price - clampedLoan - bridgingForLoan - minCash);
    const cpfAllocation = Math.min(flexibleDownNeeded, privateCpfForDown);
    return {
      clampedLoan,
      cpfAllocation,
      cashAllocation: flexibleDownNeeded - cpfAllocation,
    };
  }, [inputs.purchasePrice, minCash, bridgingForLoan, privateCpfForDown]);

  const applyLoanChange = useCallback((loan: number) => {
    const allocation = getLoanChangeAllocation(loan);
    setCpfDownAlloc(allocation.cpfAllocation);
    setCashDownAlloc(allocation.cashAllocation);
  }, [getLoanChangeAllocation]);

  // Set the structural loan to the applicable MAS LTV cap. Increasing the loan reduces
  // the buyer-funded portion of the payment structure: Additional Cash is released first,
  // followed by CPF. Affordability remains separate, so any amount above Max Loan Quantum
  // is surfaced by the Loan Result card as a loan shortfall.
  const applyMaxLtv = useCallback(() => {
    const price = inputs.purchasePrice;
    if (!inputs.hasBankLoan || price <= 0) return;

    const targetLoan = Math.min(
      targetBankLoan,
      Math.max(0, price - minCash - bridgingForLoan),
    );
    // The bridging loan never funds the 20% DPS down payment.
    const minFlexibleDown = bridgingForLoan > 0 ? Math.max(0, Math.round(price * EC_DPS_DOWN_PCT) - minCash) : 0;
    const targetFlexibleDown = Math.max(
      minFlexibleDown,
      price - targetLoan - minCash - bridgingForLoan,
    );
    let nextCash = Math.max(0, cashDownAlloc);
    let nextCpf = Math.max(0, cpfDownAlloc);
    const currentFlexibleDown = nextCash + nextCpf;

    if (currentFlexibleDown > targetFlexibleDown) {
      let reduction = currentFlexibleDown - targetFlexibleDown;
      const cashReduction = Math.min(nextCash, reduction);
      nextCash -= cashReduction;
      reduction -= cashReduction;
      nextCpf = Math.max(0, nextCpf - reduction);
    } else if (currentFlexibleDown < targetFlexibleDown) {
      // This path caps a manually entered loan that exceeds the permitted LTV.
      nextCash += targetFlexibleDown - currentFlexibleDown;
    }

    setCashDownAlloc(nextCash);
    setCpfDownAlloc(nextCpf);
  }, [
    inputs.hasBankLoan,
    inputs.purchasePrice,
    targetBankLoan,
    minCash,
    bridgingForLoan,
    cashDownAlloc,
    cpfDownAlloc,
  ]);

  // HDB equivalent: editing the HDB loan re-derives the down-payment split. OTP deposit and
  // COV stay payable in cash (mandatory); the flexible down payment is filled with CPF +
  // grants first (used up), then the remainder in cash.
  const getHdbLoanChangeAllocation = useCallback((loan: number) => {
    const price = inputs.purchasePrice;
    const availCpf = Math.max(0, inputs.cpf + inputs.additionalCpf + hdbInputs.housingGrant + hdbInputs.cpfResaleGrant + hdbInputs.phgGrant - hdbCpfExpenses);
    const financing = getHdbResaleFinancing(
      price,
      hdbInputs.valuationPrice,
      hdbInputs.loanSource,
      otpDeposit,
      tenureStretched,
    );
    const clampedLoan = Math.max(0, Math.min(Math.round(loan), financing.maxLoan));
    const flexibleDown = Math.max(0, price - financing.mandatoryCash - clampedLoan);
    const cpfAllocation = Math.min(flexibleDown, availCpf); // CPF used up first
    const cashAllocation = flexibleDown - cpfAllocation;    // remainder in cash
    const newLtv = price > 0 ? (clampedLoan / price) * 100 : 0;
    return { clampedLoan, cpfAllocation, cashAllocation, newLtv, availCpf };
  }, [tenureStretched, inputs.purchasePrice, inputs.cpf, inputs.additionalCpf, hdbInputs.valuationPrice, hdbInputs.loanSource, hdbInputs.housingGrant, hdbInputs.cpfResaleGrant, hdbInputs.phgGrant, otpDeposit, hdbCpfExpenses]);

  const applyHdbLoanChange = useCallback((loan: number) => {
    const { cpfAllocation, cashAllocation } = getHdbLoanChangeAllocation(loan);
    setHdbCpfAlloc(cpfAllocation);
    setHdbCashAlloc(cashAllocation);
  }, [getHdbLoanChangeAllocation]);

  const privateAllocDefaultKey = [
    calculatorMode,
    propertyTab,
    inputs.purchasePrice,
    maxLtvPct,
    minCash,
    privateCpfForDown,
    propertyTab === 'ec' ? maxLoan : 0,
  ].join('|');

  useEffect(() => {
    if (privateAllocDefaultKey === privateAllocDefaultKeyRef.current) return;
    privateAllocDefaultKeyRef.current = privateAllocDefaultKey;
    if (restoringRef.current) return; // keep the restored allocation on history load
    if (calculatorMode !== 'detailed' || propertyTab === 'hdb') return;
    if (inputs.purchasePrice <= 0) {
      setCpfDownAlloc(0);
      setCashDownAlloc(0);
      return;
    }

    // Default down payment = mandatory cash + the rest of the LTV down payment (e.g. 5% + 20%),
    // CPF first. The Special Bridging Loan never funds it — it reduces the bank loan instead.
    const targetDown = inputs.purchasePrice - ltvCapLoan;
    const flexibleDownNeeded = Math.max(0, targetDown - minCash);
    const cpfFirst = Math.min(flexibleDownNeeded, privateCpfForDown);
    setCpfDownAlloc(cpfFirst);
    setCashDownAlloc(flexibleDownNeeded - cpfFirst);
  }, [
    privateAllocDefaultKey,
    calculatorMode,
    propertyTab,
    inputs.purchasePrice,
    ltvCapLoan,
    minCash,
    privateCpfForDown,
  ]);

  // For Private and EC, Max Purchase Price is computed against the maximum allowable LTV (75%),
  // not the user's current derived LTV — this represents the true ceiling assuming the buyer
  // borrows up to the cap. For HDB tab we keep the existing derived-LTV behaviour.
  // Use the max allowable LTV (75%) — NOT the live derived LTV — so Max Purchase Price stays
  // stable. Tying it to the derived LTV for HDB caused an auto-price feedback loop on load.
  const maxPriceLtv = 0.75;
  const maxPriceLoan = maxPriceLtv > 0 ? Math.round(maxLoan / maxPriceLtv) : 0;
  const maxAffordablePrice = useMemo(
    () => maxLoan > 0 ? Math.round((maxLoan + inputs.cpf + inputs.additionalCash + inputs.additionalCpf) / 0.95) : 0,
    [maxLoan, inputs.cpf, inputs.additionalCash, inputs.additionalCpf],
  );
  // Auto-derive the purchase price from loan eligibility (Max Purchase Price):
  // always in detailed mode; in simple mode only when a profile is loaded (its
  // income powers the eligibility figures — without one, simple mode starts at $0).
  useEffect(() => {
    const autoDerive = calculatorMode === 'detailed' || (calculatorMode === 'simple' && !!selectedProfile);
    if (autoDerive && !purchasePriceUserAdjusted && maxPriceLoan > 0) {
      updateInput('purchasePrice', maxPriceLoan);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calculatorMode, maxPriceLoan, purchasePriceUserAdjusted, selectedProfile]);
  const loanShortfall = useMemo(() => Math.max(0, derivedLoan - maxLoan), [derivedLoan, maxLoan]);

  const ltvPass = derivedLtvPct <= maxLtvPct + 0.05;

  // ── Pledge / Unpledge ─────────────────────────────────────────────────────
  // (unpledgeInputValue / pledgeMode state declared near the top so the history snapshot
  // can read them.)
  const fullPledgedNeed = useMemo(() => {
    if (loanShortfall <= 0) return 0;
    const requiredMonthly = calcMonthlyInstallment(derivedLoan, stressTestRate, inputs.tenure);
    return Math.round(extraIncomeNeeded(requiredMonthly).income * RECOGNITION_MONTHS / PLEDGED_RECOGNITION);
  }, [loanShortfall, derivedLoan, stressTestRate, inputs.tenure, extraIncomeNeeded]);
  const fullUnpledgedNeed = useMemo(
    () => (UNPLEDGED_RECOGNITION > 0 ? Math.round(fullPledgedNeed / UNPLEDGED_RECOGNITION) : 0),
    [fullPledgedNeed],
  );
  // Reset the committed unpledged amount to 0 whenever the shortfall need changes — except
  // during a history restore, where the saved amount must survive (see restoringRef).
  useEffect(() => {
    if (restoringRef.current) return;
    setUnpledgeInputValue(formatCurrencyDraftForInput(0));
  }, [fullPledgedNeed]);

  // Clear the restore guard at the end of the commit. This effect MUST stay declared after
  // EVERY restoringRef-guarded auto-default effect above (currently the private/EC CPF-first,
  // HDB allocation, IWAA-tenure and pledge-reset effects): effects run in declaration order, so
  // those run (and skip their reset) first, then this flips the flag back off so later
  // genuine edits re-default normally. No dependency array → runs after each commit.
  useEffect(() => {
    if (restoringRef.current) restoringRef.current = false;
  });
  const unpledgeParsed = useMemo(() => parseFlexibleNumber(unpledgeInputValue), [unpledgeInputValue]);
  const effectiveUnpledged = useMemo(() => {
    if (unpledgeParsed === null) return 0;
    return Math.max(0, Math.min(Math.round(unpledgeParsed), fullUnpledgedNeed));
  }, [unpledgeParsed, fullUnpledgedNeed]);
  const effectivePledged = useMemo(() => {
    if (fullPledgedNeed <= 0) return 0;
    return Math.max(0, Math.min(Math.round(fullPledgedNeed - effectiveUnpledged * UNPLEDGED_RECOGNITION), fullPledgedNeed));
  }, [fullPledgedNeed, effectiveUnpledged]);
  const pledgeStepFor = (maxVal: number) => (maxVal > 6_666_667 ? 50_000 : maxVal > 1_666_667 ? 10_000 : 5_000);
  const applyUnpledged = useCallback((n: number) => {
    setUnpledgeInputValue(formatCurrencyDraftForInput(Math.max(0, Math.min(Math.round(n), fullUnpledgedNeed))));
  }, [fullUnpledgedNeed]);
  // Fund-pledging adjustment mode (pledgeMode) — the buyer drives one of Total / Unpledge /
  // Pledge and the other two are derived. `effectiveUnpledged` stays the single source of
  // truth; the pledged/unpledged split always satisfies  P·100% + U·30% = fullPledgedNeed.
  const effectiveTotal = effectivePledged + effectiveUnpledged;
  // Pledge mode: buyer picks the pledged amount P; unpledged fills the rest so the
  // recognised-income requirement still holds → U = (fullPledgedNeed − P) ÷ 30%.
  const applyPledged = useCallback((p: number) => {
    const clampedP = Math.max(0, Math.min(Math.round(p), fullPledgedNeed));
    applyUnpledged(UNPLEDGED_RECOGNITION > 0 ? (fullPledgedNeed - clampedP) / UNPLEDGED_RECOGNITION : 0);
  }, [fullPledgedNeed, applyUnpledged]);
  // Total mode: buyer picks the combined commitment T = P + U. Since P = fullPledgedNeed − 30%·U,
  // T = fullPledgedNeed + (100% − 30%)·U  ⟹  U = (T − fullPledgedNeed) ÷ 70%.
  const applyTotal = useCallback((tVal: number) => {
    const clampedT = Math.max(fullPledgedNeed, Math.min(Math.round(tVal), fullUnpledgedNeed));
    const denom = PLEDGED_RECOGNITION - UNPLEDGED_RECOGNITION;
    applyUnpledged(denom > 0 ? (clampedT - fullPledgedNeed) / denom : 0);
  }, [fullPledgedNeed, fullUnpledgedNeed, applyUnpledged]);

  // ── Calc Explanation Dialog ───────────────────────────────────────────────
  const [calcDialog, setCalcDialog] = useState<CalcDialogInfo | null>(null);
  // Field explainer dialog (HPS / grants): free-text description + a reference link
  // that opens in a new browser tab. `embed` just selects a friendly link label.
  const [fieldInfo, setFieldInfo] = useState<FieldInfo | null>(null);
  const renderFieldInfoIcon = (info: FieldInfo) => (
    <IconButton size="small" onClick={(e) => { e.stopPropagation(); setFieldInfo(info); }} sx={{ p: 0.25, ml: 0.25 }}>
      <InfoOutlinedIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
    </IconButton>
  );
  // CPF OA retention info dialog (tap-friendly for iPad)
  const [cpfInfoOpen, setCpfInfoOpen] = useState(false);

  const clearCalculator = useCallback(() => {
    // Restore every Buying Calculator value to the same defaults used on first load.
    setPropertyTab('private');
    setCalculatorMode('simple');
    setSplitScroll(false);
    setPrivateSubTab('new-launch');
    setEcPaymentScheme('NPS');
    setHdbInputs({ ...DEFAULT_HDB_INPUTS, otpDate: todayStr });
    setHdbValuationUserSet(false);
    setHdbAgencyFeeUserSet(false);

    setAdditionalCitizenship('SC');
    setAdditionalFtaEligible(false);
    setAdditionalPropertyCount('1st');
    setBorrowerRelationship('SPOUSE');
    setBsdByCpf(true);
    setAbsdByCpf(true);
    setLegalFeeByCpf(true);
    setHpsByCpf(true);

    setCashDownAlloc(0);
    setCpfDownAlloc(0);
    setSblHasHdb(false);
    setSblValuation(0);
    setSblOutstandingLoan(0);
    setSblLevy(0);
    setHdbCashAlloc(0);
    setHdbCpfAlloc(0);
    setTenureStretched(false);
    setPurchasePriceUserAdjusted(false);
    setCashProceedFromSelling(0);
    setSellerProceeds([]);
    setProceedsRowsUserSet(false);
    setExerciseDateOverride(null);
    setSp15DateOverride(null);
    setResaleExerciseOverride(null);
    setResaleCompletionOverride(null);
    setHdbDateOverrides({});
    setShowTimelineDates(true);
    setEcHousingGrant(0);
    setResaleLevy(0);
    setResaleLevyUserSet(false);
    setSellerSoldHdb(false);
    setIsSecondHdbLoanUserSet(false);
    setSecondHdbLoanSetAsideUserSet(false);
    setSellingNetProceeds(0);
    setUnpledgeInputValue('');
    setPledgeMode('total');

    setInputs({
      purchasePrice: 0,
      hasBankLoan: true,
      additionalCash: 0,
      cpf: 0,
      additionalCpf: 0,
      interestRate: 2.0,
      tenure: 30,
      citizenship: 'SC',
      propertyCount: '1st',
      ftaEligible: false,
      legalFee: 3000,
      spDate: todayStr,
      primary: { ...DEFAULT_BORROWER },
      includeAdditionalBorrower: false,
      additional: { ...DEFAULT_BORROWER },
    });

    // Close any in-progress editors/dialogs and reset auto-default tracking.
    setStretchDialogOpen(false);
    setRemoveBorrowerDialogOpen(false);
    setRemovePrimaryDialogOpen(false);
    setPendingLoanChange(null);
    setPendingHdbLoanChange(null);
    setPendingTenure(null);
    setCalcDialog(null);
    setFieldInfo(null);
    setCpfInfoOpen(false);
    restoringRef.current = false;
    tabPriceStashRef.current = {};
    privateAllocDefaultKeyRef.current = '';
    prevHdbAllocKeyRef.current = '';

    // Clear the active example/history selection and detach the loaded profile.
    // Saved history entries and the profile itself remain intact.
    linkedCalc.setSelectedExample('');
    linkedCalc.setActiveSelection(null);
    clearSelection();
    linkedCalc.showToast('Buying Calculator reset');
    setClearDialogOpen(false);
  }, [linkedCalc, todayStr, clearSelection]);

  // Tab switch keeps each property tab's purchase price separate: stash the outgoing
  // tab's price (and its manual-edit lock), restore the incoming tab's last price.
  // Never-visited tabs start fresh at $0 with the price lock released.
  const handlePropertyTabChange = useCallback((next: PropertyTab) => {
    if (next === propertyTab) return;
    tabPriceStashRef.current[propertyTab] = {
      price: inputs.purchasePrice,
      userAdjusted: purchasePriceUserAdjusted,
    };
    const saved = tabPriceStashRef.current[next];
    setInputs((prev) => ({ ...prev, purchasePrice: saved?.price ?? 0 }));
    setPurchasePriceUserAdjusted(saved?.userAdjusted ?? false);
    setPropertyTab(next);
  }, [propertyTab, inputs.purchasePrice, purchasePriceUserAdjusted]);

  const openExplain = useCallback((key: string) => {
    const n = inputs.tenure * 12;
    const rStress = stressTestRate / 100 / 12;
    const rBank = effectiveInterestRate / 100 / 12;
    const rateLabel = isHdbConcessionaryLoan ? 'HDB Rate' : 'Bank Rate';
    const pvStress = rStress > 0 ? (1 - Math.pow(1 + rStress, -n)) / rStress : n;
    const pmtBank = rBank > 0
      ? rBank * Math.pow(1 + rBank, n) / (Math.pow(1 + rBank, n) - 1)
      : 1 / n;
    const minCashAmt = Math.round(inputs.purchasePrice * (minCashPct / 100));
    const reqMonthly = calcMonthlyInstallment(derivedLoan, stressTestRate, inputs.tenure);
    const incomeNeed = extraIncomeNeeded(reqMonthly);
    const extraMonthly = incomeNeed.extraMonthly;
    const extraIncome = incomeNeed.income;
    const requiredLoanLtvBase = propertyTab === 'hdb'
      ? hdbResaleFinancing.valuationBase
      : inputs.purchasePrice;

    // Per-borrower recognised (assessable) income — powers the IWAA weighting and the
    // Total Recognised Income breakdown. Matches calculateAssessableIncome() used for TDSR.
    const primaryRec = calculateAssessableIncome(String(inputs.primary.monthlyIncome), String(inputs.primary.otherAnnualIncome), inputs.primary.isSelfEmployed ? 1 : 0);
    const additionalRec = inputs.includeAdditionalBorrower
      ? calculateAssessableIncome(String(inputs.additional.monthlyIncome), String(inputs.additional.otherAnnualIncome), inputs.additional.isSelfEmployed ? 1 : 0)
      : 0;
    const totalRec = primaryRec + additionalRec;
    const recBreakdown = (b: BorrowerInputs) => {
      const base = b.isSelfEmployed ? `${fmt(b.monthlyIncome)} × 70%` : fmt(b.monthlyIncome);
      const other = b.otherAnnualIncome > 0 ? ` + ${fmt(Math.round(b.otherAnnualIncome / 12))}/mo × 70%` : '';
      return `${base}${other}`;
    };

    const dialogs: Record<string, CalcDialogInfo> = {
      iwaa: inputs.includeAdditionalBorrower ? {
        title: 'Income Weighted Avg Age (IWAA)',
        rows: [
          { label: 'IWAA weights each borrower’s age by their recognised income, so the higher earner has more pull on the effective age used for the loan-tenure cap.', value: '', note: true },
          { label: 'Formula: ceil( Σ(Age × Recognised Income) ÷ Σ Recognised Income )', value: '' },
          { isSeparator: true, label: '', value: '' },
          { label: `Primary · Age ${inputs.primary.age}`, value: `${fmt(Math.round(primaryRec))}/mo` },
          { label: `Additional · Age ${inputs.additional.age}`, value: `${fmt(Math.round(additionalRec))}/mo` },
          { isSeparator: true, label: '', value: '' },
          { label: `= ceil( (${inputs.primary.age}×${fmt(Math.round(primaryRec))} + ${inputs.additional.age}×${fmt(Math.round(additionalRec))}) ÷ ${fmt(Math.round(totalRec))} )`, value: '' },
          { label: `= ceil( ${fmt(Math.round(inputs.primary.age * primaryRec + inputs.additional.age * additionalRec))} ÷ ${fmt(Math.round(totalRec))} )`, value: `${iwaa} yrs`, isResult: true },
          { isSeparator: true, label: '', value: '' },
          { label: `Max age 65 − IWAA ${iwaa}`, value: `${Math.max(0, 65 - iwaa)} yrs` },
          { label: 'Loan-tenure cap = min(30, above)', value: `${standardTenureCap} yrs`, isResult: true },
        ],
      } : {
        title: 'Income Weighted Avg Age (IWAA)',
        rows: [
          { label: 'With a single borrower, IWAA is simply that borrower’s age — no income weighting applies.', value: '', note: true },
          { label: `Primary · Age`, value: `${iwaa} yrs`, isResult: true },
        ],
      },
      recognisedIncome: {
        title: 'Total Recognised Income',
        rows: [
          { label: 'Income the bank recognises for loan assessment: base salary at 100% (self-employed 70%) plus variable / other income at 70%.', value: '', note: true },
          { label: `Primary — ${recBreakdown(inputs.primary)}`, value: `${fmt(Math.round(primaryRec))}/mo` },
          ...(inputs.includeAdditionalBorrower
            ? [{ label: `Additional — ${recBreakdown(inputs.additional)}`, value: `${fmt(Math.round(additionalRec))}/mo` }]
            : []),
          { isSeparator: true, label: '', value: '' },
          { label: 'Total Recognised Income', value: fmt(totalRecognisedIncome), isResult: true },
        ],
      },
      maxLoan: {
        title: 'Max Loan Quantum',
        rows: [
          { label: 'Total Recognised Income', value: fmt(totalRecognisedIncome) },
          { label: '× TDSR Ratio (55%)', value: fmt(Math.round(totalRecognisedIncome * TDSR_RATIO)) },
          { label: '− Existing Monthly Obligations', value: fmt(totalExistingDebt) },
          { label: '= Available TDSR / month', value: fmt(availableTDSR) },
          ...(msrApplies ? [
            { isSeparator: true, label: '', value: '' },
            { label: `MSR applies (${propertyTab.toUpperCase()})`, value: '' },
            { label: 'Total Recognised Income', value: fmt(totalRecognisedIncome) },
            { label: '× MSR Ratio (30%)', value: fmt(Math.round(totalRecognisedIncome * MSR_RATIO)) },
            { label: '= Available MSR / month', value: fmt(Math.round(availableMSR)) },
            { isSeparator: true, label: '', value: '' },
            { label: 'Binding cap = min(TDSR, MSR)', value: fmt(Math.round(bindingMonthly)) },
          ] : []),
          { isSeparator: true, label: '', value: '' },
          { label: isHdbConcessionaryLoan ? 'HDB Interest Rate Floor (Settings)' : 'Stress Test Rate (Settings)', value: `${stressTestRate}%` },
          { label: 'Tenure', value: `${inputs.tenure} yrs = ${n} mths` },
          { label: 'Monthly rate r = rate ÷ 12', value: `${(stressTestRate / 12).toFixed(5)}%` },
          { label: 'PV annuity factor', value: pvStress.toFixed(4) },
          { isSeparator: true, label: '', value: '' },
          { label: `Max Loan = ${msrApplies ? 'Binding cap' : 'Available TDSR'} × PV factor`, value: '' },
          { label: `= ${fmt(Math.round(bindingMonthly))} × ${pvStress.toFixed(2)}`, value: fmt(maxLoan), isResult: true },
        ],
      },
      maxPriceLoan: {
        title: 'Max Purchase Price (Loan)',
        rows: [
          { label: 'Max Loan Quantum', value: fmt(maxLoan) },
          { label: '÷ Max LTV Ratio', value: `${(maxPriceLtv * 100).toFixed(1)}%` },
          { isSeparator: true, label: '', value: '' },
          { label: `${fmt(maxLoan)} ÷ ${(maxPriceLtv * 100).toFixed(1)}%`, value: maxPriceLoan > 0 ? fmt(maxPriceLoan) : '–', isResult: true },
        ],
      },
      requiredLoan: {
        title: 'Required Loan',
        rows: [
          { label: propertyTab === 'hdb' ? 'Valuation Base' : 'Purchase Price', value: fmt(requiredLoanLtvBase) },
          { label: '× LTV (derived from payment structure)', value: `${derivedLtvPct.toFixed(1)}%` },
          { isSeparator: true, label: '', value: '' },
          { label: `${fmt(requiredLoanLtvBase)} × ${derivedLtvPct.toFixed(1)}%`, value: fmt(derivedLoan), isResult: true },
        ],
      },
      ecLoanAmount: {
        title: 'Loan Amount (MSR / TDSR)',
        rows: [
          { label: 'Required loan (Payment Structure)', value: fmt(derivedLoan) },
          { label: `Max Loan Quantum (${msrBinds ? 'MSR 30%' : 'TDSR 55%'})`, value: fmt(maxLoan) },
          { isSeparator: true, label: '', value: '' },
          { label: '= Lower of the two', value: fmt(Math.min(derivedLoan, maxLoan)), isResult: true },
          { note: true, label: 'EC loans are capped by MSR (30% of income) and TDSR (55%), so the bank lends at most the loan quantum. Any required loan above it is the Loan Shortfall.', value: '' },
        ],
      },
      loanShortfall: {
        title: 'Loan Shortfall',
        rows: [
          { label: 'Required Loan', value: fmt(derivedLoan) },
          { label: '− Max Loan Quantum', value: fmt(maxLoan) },
          { isSeparator: true, label: '', value: '' },
          { label: 'Shortfall = max(0, Required − Max)', value: fmt(loanShortfall), isResult: true },
          ...(loanShortfall === 0 ? [{ label: 'Income is sufficient — no shortfall.', value: '✓' }] : []),
        ],
      },
      pledgedFull: {
        title: 'Pledged Funds Needed (Full)',
        rows: [
          { label: `Pledging = locking SGD cash / fixed deposits with the bank for at least 48 months. MAS allows a 0% haircut on pledged SGD deposits, so they count in full (÷ 48 months) toward the income used for your ${msrApplies ? 'MSR and TDSR' : 'TDSR'}. Other pledged assets (shares, unit trusts, foreign currency) take at least a 30% haircut.`, value: '', note: true },
          { label: `Required Monthly (@ ${stressTestRate}% stress rate)`, value: fmt(Math.round(reqMonthly)) },
          { label: `− Available ${incomeNeed.ratio} (${incomeNeed.ratio === 'MSR' ? '30% of income' : '55% of income − debts'})`, value: fmt(Math.round(incomeNeed.available)) },
          { label: '= Extra Monthly Payment', value: fmt(Math.round(extraMonthly)) },
          { isSeparator: true, label: '', value: '' },
          { label: `÷ ${incomeNeed.ratio === 'MSR' ? 'MSR Ratio (30%)' : 'TDSR Ratio (55%)'} = Extra Income Needed`, value: fmt(Math.round(extraIncome)) },
          { label: '× Recognition Period (48 mths)', value: '' },
          { label: '÷ Pledged Recognition (100%)', value: '' },
          { isSeparator: true, label: '', value: '' },
          { label: `${fmt(Math.round(extraIncome))} × 48 ÷ 1.0`, value: fmt(fullPledgedNeed), isResult: true },
        ],
      },
      unpledgedFull: {
        title: 'Unpledged Equivalent (Full)',
        rows: [
          { label: `Unpledging = showing liquid assets you keep (not locked with the bank). MAS applies a 70% haircut, so they are recognised at only 30% (÷ 48 months) — you need a larger amount than pledging to lift your ${msrApplies ? 'MSR / TDSR' : 'TDSR'} income by the same figure. The bank checks the funds are still in your account before disbursing.`, value: '', note: true },
          { label: 'Pledged Funds Needed', value: fmt(fullPledgedNeed) },
          { label: '÷ Unpledged Recognition Rate (30%)', value: '' },
          { label: 'Unpledged assets count at only 30%', value: '' },
          { isSeparator: true, label: '', value: '' },
          { label: `${fmt(fullPledgedNeed)} ÷ 30%`, value: fmt(fullUnpledgedNeed), isResult: true },
        ],
      },
      pledgedAdjusted: {
        title: 'Pledged Needed (Adjusted)',
        rows: [
          { label: 'Pledged assets are locked with the bank for 48 months and recognised at 100%. This is how much you still need to pledge after your unpledged assets are counted.', value: '', note: true },
          { label: 'Full Pledged Needed', value: fmt(fullPledgedNeed) },
          { label: '− Unpledged Planned × 30%', value: fmt(Math.round(effectiveUnpledged * UNPLEDGED_RECOGNITION)) },
          { isSeparator: true, label: '', value: '' },
          { label: `Pledged needed = ${fmt(fullPledgedNeed)} − ${fmt(Math.round(effectiveUnpledged * UNPLEDGED_RECOGNITION))}`, value: fmt(effectivePledged), isResult: true },
          { isSeparator: true, label: '', value: '' },
          { label: 'Recognised monthly income = pledged amount ÷ 48', value: '' },
          { label: `= ${fmt(effectivePledged)} ÷ 48`, value: fmt(Math.round(effectivePledged * PLEDGED_RECOGNITION / RECOGNITION_MONTHS)), isResult: true },
        ],
      },
      unpledgedPlanned: {
        title: 'Unpledged Planned',
        rows: [
          { label: 'Unpledged assets stay liquid (not locked with the bank) but are recognised at only 30%, spread over 48 months, toward the income used for your TDSR.', value: '', note: true },
          { label: 'Unpledged asset value', value: fmt(effectiveUnpledged) },
          { label: 'Recognition Rate', value: '30%' },
          { label: 'Recognition Period', value: '48 months' },
          { isSeparator: true, label: '', value: '' },
          { label: 'Recognised monthly income = unpledged value ÷ 48 × 30%', value: '' },
          { label: `= ${fmt(effectiveUnpledged)} ÷ 48 × 30%`, value: fmt(Math.round(effectiveUnpledged * UNPLEDGED_RECOGNITION / RECOGNITION_MONTHS)), isResult: true },
        ],
      },
      pledgeTotal: {
        title: 'Total Pledged + Unpledged',
        rows: [
          { label: 'The combined assets you commit to bridge the loan shortfall — pledged (locked, 100% recognised) plus unpledged (liquid, 30% recognised).', value: '', note: true },
          { label: 'Pledged needed (adjusted)', value: fmt(effectivePledged) },
          { label: '+ Unpledged planned', value: fmt(effectiveUnpledged) },
          { isSeparator: true, label: '', value: '' },
          { label: 'Total committed', value: fmt(effectivePledged + effectiveUnpledged), isResult: true },
        ],
      },
      bankLoan: {
        title: 'Bank Loan (LTV)',
        rows: [
          { label: 'Purchase Price', value: fmt(inputs.purchasePrice) },
          { label: `− Cash (min ${minCashPct}%)`, value: fmt(minCashAmt) },
          { label: '− Additional Cash', value: fmt(inputs.additionalCash) },
          { label: '− CPF', value: fmt(inputs.cpf) },
          { label: '− Additional CPF', value: fmt(inputs.additionalCpf) },
          { isSeparator: true, label: '', value: '' },
          { label: '= Bank Loan', value: fmt(derivedLoan), isResult: true },
          { label: 'LTV = Loan ÷ Price', value: `${derivedLtvPct.toFixed(1)}%` },
        ],
      },
      monthlyInstallment: {
        title: 'Estimated Monthly Installment',
        rows: [
          { label: 'Loan Amount', value: fmt(derivedLoan) },
          { label: rateLabel, value: `${effectiveInterestRate.toFixed(2)}%` },
          { label: 'Tenure', value: `${inputs.tenure} yrs = ${n} mths` },
          { label: 'Monthly rate r = rate ÷ 12', value: `${(effectiveInterestRate / 12).toFixed(5)}%` },
          { isSeparator: true, label: '', value: '' },
          { label: 'Formula: L × r(1+r)ⁿ / ((1+r)ⁿ−1)', value: '' },
          { label: `${fmt(derivedLoan)} × ${pmtBank.toFixed(6)}`, value: fmtInstallment(monthlyInstallment), isResult: true },
        ],
      },
    };
    setCalcDialog(dialogs[key] ?? null);
  }, [inputs, totalRecognisedIncome, totalExistingDebt, availableTDSR, availableMSR, msrApplies, msrBinds,
      bindingMonthly, bindingRatio, extraIncomeNeeded, propertyTab, maxLoan, maxPriceLoan, maxPriceLtv, derivedLoan,
      derivedLtvPct, ltvDecimal, stressTestRate, loanShortfall, fullPledgedNeed, fullUnpledgedNeed,
      effectivePledged, effectiveUnpledged, monthlyInstallment, effectiveInterestRate, isHdbConcessionaryLoan, fmt,
      iwaa, standardTenureCap, hdbResaleFinancing.valuationBase]);

  // ── Loan-card sub-section helpers ─────────────────────────────────────────
  // Affordability + pledging info lives inside the left Loan section (not a
  // separate right-hand card) so everything about the loan is in one place.
  const renderLoanSubLabel = (text: string) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5, mb: 1.5 }}>
      <Typography variant="caption" sx={{ fontWeight: 800, color: highlightColor, letterSpacing: '0.07em', textTransform: 'uppercase', fontSize: '0.72rem', whiteSpace: 'nowrap' }}>{text}</Typography>
      <Box sx={{ flex: 1, height: '1px', bgcolor: cardBorder }} />
    </Box>
  );

  const renderLoanInfoRow = (
    label: string,
    value: React.ReactNode,
    opts?: { explainKey?: string; bold?: boolean; color?: string; labelAction?: React.ReactNode },
  ) => (
    <Box
      onClick={opts?.explainKey ? () => openExplain(opts.explainKey!) : undefined}
      sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.75, px: 0.5, mx: -0.5, borderRadius: 1, cursor: opts?.explainKey ? 'pointer' : 'default' }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
        <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: opts?.bold ? 700 : 400, fontSize: '0.82rem' }}>{label}</Typography>
        {opts?.explainKey && <InfoOutlinedIcon sx={{ fontSize: 15, color: highlightColor, flexShrink: 0 }} />}
        {opts?.labelAction}
      </Box>
      <Typography variant="body2" fontWeight={700} sx={{ color: opts?.color, fontSize: '0.85rem' }}>{value}</Typography>
    </Box>
  );

  // Loan Info — IWAA (label only, no input) + income-based affordability.
  // Normal vs extended tenure — compact two-column card; the column in use is tinted and
  // tagged so the buyer sees at a glance which loan applies.
  const renderTenureCompare = () => {
    const cols = [
      { key: 'normal', title: 'Normal', tenure: standardTenureCap, loan: standardTenureMaxLoan, active: !tenureStretched },
      // The extended column reflects the tenure actually in use (the buyer may key in less
      // than the extended cap).
      { key: 'extended', title: 'Stretched', tenure: inputs.tenure, loan: maxLoan, active: tenureStretched },
    ];
    const activeBg = isDark ? 'rgba(255,255,255,0.06)' : `${highlightColor}12`;
    return (
      <Box sx={{ display: 'grid', gridTemplateColumns: 'auto 1fr 1fr', border: `1px solid ${cardBorder}`, borderRadius: 1.5, overflow: 'hidden', my: 1, fontSize: '0.82rem' }}>
        {/* header */}
        <Box sx={{ px: 1.25, py: 0.75 }} />
        {cols.map((c) => (
          <Box key={c.key} sx={{ px: 1.25, py: 0.75, textAlign: 'right', bgcolor: c.active ? activeBg : undefined }}>
            <Typography sx={{ fontSize: '0.72rem', fontWeight: 800, letterSpacing: '0.05em', textTransform: 'uppercase', color: c.active ? highlightColor : 'text.secondary' }}>
              {c.title}{c.active ? ' ✓' : ''}
            </Typography>
          </Box>
        ))}
        {[
          { label: 'Tenure', value: (c: typeof cols[number]) => `${c.tenure} yrs` },
          { label: 'Max Loan', value: (c: typeof cols[number]) => fmt(c.loan) },
          // Same basis as the Max Purchase Price row (loan ÷ 75%).
          { label: 'Max Purchase Price', value: (c: typeof cols[number]) => (c.loan > 0 ? fmt(Math.round(c.loan / maxPriceLtv)) : '–') },
        ].map((r) => (
          <React.Fragment key={r.label}>
            <Box sx={{ px: 1.25, py: 0.75, borderTop: `1px solid ${cardBorder}`, color: 'text.secondary' }}>{r.label}</Box>
            {cols.map((c) => (
              <Box key={c.key} sx={{ px: 1.25, py: 0.75, borderTop: `1px solid ${cardBorder}`, textAlign: 'right', bgcolor: c.active ? activeBg : undefined, fontWeight: c.active ? 800 : 500, color: c.active ? 'text.primary' : 'text.secondary' }}>
                {r.value(c)}
              </Box>
            ))}
          </React.Fragment>
        ))}
      </Box>
    );
  };

  const renderLoanInfo = () => (
    <>
      {renderLoanSubLabel('Loan Info')}
      {renderLoanInfoRow('Income Weighted Avg Age (IWAA)', `${iwaa} yrs`, { explainKey: 'iwaa' })}
      {ecFixedTenure && tenureStretched && propertyTab !== 'ec' ? (
        // Extended tenure: price + loan by tenure in one comparison card.
        renderTenureCompare()
      ) : propertyTab === 'ec' && ecFixedTenure ? (
        // EC: IWAA → Max Purchase Price → loan by tenure (normal vs extended once extended).
        <>
          {tenureStretched ? renderTenureCompare() : (
            <>
              {renderLoanInfoRow('Max Purchase Price', maxPriceLoan > 0 ? fmt(maxPriceLoan) : '–', { explainKey: 'maxPriceLoan' })}
              {renderLoanInfoRow('Max Loan Quantum', fmt(maxLoan), { explainKey: 'maxLoan' })}
            </>
          )}
        </>
      ) : (
        <>
          {renderLoanInfoRow('Max Loan Quantum', fmt(maxLoan), { explainKey: 'maxLoan' })}
          {renderLoanInfoRow('Max Purchase Price', maxPriceLoan > 0 ? fmt(maxPriceLoan) : '–', { explainKey: 'maxPriceLoan' })}
        </>
      )}
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25, mb: 1, lineHeight: 1.4 }}>
        {isHdbConcessionaryLoan
          ? `HDB interest rate floor: ${stressTestRate}% · HDB Concessionary Housing Loan interest rate: ${HDB_CONCESSIONARY_INTEREST_RATE.toFixed(1)}%`
          : `Stress test rate: ${stressTestRate}% · Bank rate: ${inputs.interestRate.toFixed(2)}%`}
        {msrApplies && <> · MSR 30% ({propertyTab.toUpperCase()}){msrBinds ? ' — capping loan' : ''}</>}
      </Typography>
    </>
  );

  // Emphasised sub-section card — makes the loan outcome and the pledging controls easy to
  // spot inside the long Loan section (they used to be thin inline labels buyers missed).
  const renderLoanCard = (cfg: {
    title: string;
    icon: React.ReactNode;
    accent: string;
    headerBg: string;
    trailing?: React.ReactNode;
    children: React.ReactNode;
  }) => (
    <Box sx={{ mt: 2, border: '1.5px solid', borderColor: cfg.accent, borderRadius: 2, overflow: 'hidden' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1.25, bgcolor: cfg.headerBg, borderBottom: '1px solid', borderColor: cfg.accent }}>
        {cfg.icon}
        <Typography variant="subtitle2" fontWeight={800} sx={{ color: cfg.accent, letterSpacing: '0.06em', textTransform: 'uppercase', fontSize: '0.85rem', flex: 1 }}>
          {cfg.title}
        </Typography>
        {cfg.trailing}
      </Box>
      <Box sx={{ p: 2, pt: 1.75 }}>
        {cfg.children}
      </Box>
    </Box>
  );

  // Loan Result — the required loan vs affordability + monthly instalment.
  const renderLoanResult = (opts?: { loanAmount?: number; ltvPctLabel?: number; monthly?: number; showLoanAmount?: boolean; showShortfall?: boolean }) => {
    // EC: the bank lends the lower of the required loan and the MSR/TDSR loan quantum, so
    // Loan Result shows that amount (and its instalment); any excess is the Loan Shortfall.
    const ecMsrLoan = propertyTab === 'ec' && opts?.loanAmount === undefined && inputs.hasBankLoan;
    const loanAmt = opts?.loanAmount ?? (ecMsrLoan ? Math.min(derivedLoan, maxLoan) : derivedLoan);
    const ltvLabel = opts?.ltvPctLabel ?? derivedLtvPct;
    const monthly = opts?.monthly
      ?? (ecMsrLoan ? calcMonthlyInstallment(loanAmt, effectiveInterestRate, inputs.tenure) : monthlyInstallment);
    const showLoanAmount = opts?.showLoanAmount ?? true;
    const showShortfall = opts?.showShortfall ?? true;
    const hasShortfall = loanShortfall > 0;
    const accent = hasShortfall ? theme.palette.error.main : theme.palette.success.main;
    const headerBg = hasShortfall
      ? (isDark ? 'rgba(244,67,54,0.14)' : 'rgba(211,47,47,0.06)')
      : (isDark ? 'rgba(102,187,106,0.14)' : 'rgba(46,125,50,0.06)');
    return renderLoanCard({
      title: 'Loan Result',
      accent,
      headerBg,
      icon: hasShortfall
        ? <WarningAmberIcon sx={{ fontSize: 20, color: accent }} />
        : <CheckCircleIcon sx={{ fontSize: 20, color: accent }} />,
      children: (
        <>
          {showLoanAmount && renderLoanInfoRow(
            // EC loans are sized by MSR/TDSR (the loan quantum), so the LTV % is not shown.
            propertyTab === 'ec' ? 'Loan Amount' : `Loan Amount (LTV ${ltvLabel.toFixed(1)}%)`,
            fmt(loanAmt),
            {
              explainKey: ecMsrLoan ? 'ecLoanAmount' : 'requiredLoan',
              bold: true,
              labelAction: (propertyTab === 'hdb' ? hdbInputs.loanSource !== 'none' : inputs.hasBankLoan)
                && (ecQuantumBinds ? Math.abs(derivedLoan - targetBankLoan) > 1 : Math.abs(ltvLabel - maxLtvPct) > 0.05) ? (
                <Link
                  component="button"
                  type="button"
                  onClick={(event) => {
                    event.stopPropagation();
                    if (propertyTab === 'hdb') applyHdbLoanChange(hdbResaleFinancing.maxLoan);
                    else applyMaxLtv();
                  }}
                  title={ecQuantumBinds ? `Set loan to the MSR/TDSR loan quantum (${fmt(targetBankLoan)})` : `Set loan to the ${maxLtvPct}% LTV cap`}
                  sx={{
                    p: 0,
                    border: 0,
                    bgcolor: 'transparent',
                    color: highlightColor,
                    fontSize: '0.72rem',
                    fontWeight: 700,
                    lineHeight: 1.2,
                    textUnderlineOffset: '2px',
                    whiteSpace: 'nowrap',
                    cursor: 'pointer',
                  }}
                >
                  {ecQuantumBinds ? 'Max Loan' : 'Max LTV'}
                </Link>
              ) : undefined,
            },
          )}
          {renderLoanInfoRow('Est. Monthly Installment', fmtInstallment(monthly), { explainKey: 'monthlyInstallment' })}
          {showShortfall && (
            <>
              <Box
                onClick={() => openExplain('loanShortfall')}
                sx={{ mt: 1.25, px: 1.5, py: 1.25, borderRadius: 1.5, bgcolor: headerBg, border: '1px solid', borderColor: accent, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, cursor: 'pointer' }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
                  <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 700, fontSize: '0.82rem' }}>Loan Shortfall</Typography>
                  <InfoOutlinedIcon sx={{ fontSize: 15, color: accent, flexShrink: 0 }} />
                </Box>
                <Typography variant="body2" fontWeight={700} sx={{ color: accent, fontSize: '0.85rem', whiteSpace: 'nowrap' }}>
                  {hasShortfall ? fmt(loanShortfall) : 'None ✓'}
                </Typography>
              </Box>
              {hasShortfall && (
                <Typography variant="caption" sx={{ display: 'block', mt: 0.75, color: 'text.secondary', lineHeight: 1.6 }}>
                  <Box
                    component="span"
                    onClick={() => propertyTab === 'hdb'
                      ? setHdbCashAlloc(hdbCashAlloc + loanShortfall)
                      : setCashDownAlloc(cashDownAlloc + loanShortfall)}
                    sx={{ cursor: 'pointer', color: highlightColor, fontWeight: 700, textDecoration: 'underline', textUnderlineOffset: '2px' }}
                  >
                    Pay by cash
                  </Box>
                  {' '}— adds {fmt(loanShortfall)} to {propertyTab === 'hdb' ? 'Cash (down payment)' : 'Additional Cash'}, or bridge it in the{' '}
                  <strong>Fund Pledging</strong> section below ↓
                </Typography>
              )}
            </>
          )}
        </>
      ),
    });
  };

  // Fund Pledging — only when there is an income shortfall to bridge.
  const renderLoanPledging = () => {
    if (loanShortfall <= 0) return null;
    // The buyer adjusts ONE of the three; the other two are derived. All share the same
    // slider (its label / range / value / commit switch with the mode).
    const modeCfg = pledgeMode === 'pledge'
      ? { label: 'Amount you will pledge', value: effectivePledged, min: 0, max: fullPledgedNeed,
          step: pledgeStepFor(fullPledgedNeed), commit: applyPledged,
          hint: 'You set the pledged amount — the unpledged amount fills the rest.' }
      : pledgeMode === 'total'
      ? { label: 'Total assets you will commit', value: effectiveTotal, min: fullPledgedNeed, max: fullUnpledgedNeed,
          step: pledgeStepFor(fullUnpledgedNeed), commit: applyTotal,
          hint: 'You set the total — it is split into pledged & unpledged automatically.' }
      : { label: 'Amount you will leave unpledged', value: effectiveUnpledged, min: 0, max: fullUnpledgedNeed,
          step: pledgeStepFor(fullUnpledgedNeed), commit: applyUnpledged,
          hint: 'You set the unpledged amount — the pledged amount fills the rest.' };
    const accent = highlightColor;
    const headerBg = isDark ? 'rgba(217,119,6,0.16)' : 'rgba(217,119,6,0.07)';
    return renderLoanCard({
      title: 'Fund Pledging',
      accent,
      headerBg,
      icon: <LockOutlinedIcon sx={{ fontSize: 20, color: accent }} />,
      children: (
      <>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5, lineHeight: 1.5 }}>
          Commit pledged (locked 48 months) / unpledged assets to bridge the shortfall.
        </Typography>
        {renderLoanInfoRow('Pledged funds needed (full)', fmt(fullPledgedNeed), { explainKey: 'pledgedFull' })}
        {renderLoanInfoRow('Unpledged equivalent (full)', fmt(fullUnpledgedNeed), { explainKey: 'unpledgedFull' })}
        <Divider sx={{ my: 1.5 }} />
        {/* Adjustment mode — pick which figure you drive */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1, flexWrap: 'wrap' }}>
          <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', letterSpacing: '0.05em' }}>ADJUST BY</Typography>
          <ToggleButtonGroup
            size="small" exclusive value={pledgeMode}
            onChange={(_, v) => { if (v !== null) setPledgeMode(v); }}
            sx={{
              '& .MuiToggleButton-root': { textTransform: 'none', fontWeight: 600, fontSize: '0.72rem', px: 1.1, py: 0.3 },
              '& .Mui-selected': { color: '#D97706 !important', bgcolor: 'rgba(217,119,6,0.08) !important' },
            }}
          >
            <ToggleButton value="total">Total</ToggleButton>
            <ToggleButton value="unpledge">Unpledge</ToggleButton>
            <ToggleButton value="pledge">Pledge</ToggleButton>
          </ToggleButtonGroup>
        </Box>
        <EditableSliderValueRow
          label={<Typography variant="body2">{modeCfg.label}</Typography>}
          value={modeCfg.value}
          min={modeCfg.min}
          max={modeCfg.max}
          step={modeCfg.step}
          onCommit={modeCfg.commit}
          formatDisplay={(v) => fmt(v)}
          valueColor={highlightColor}
          currencyInput
        />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
          <IconButton size="small" onClick={() => modeCfg.commit(modeCfg.value - modeCfg.step)} sx={{ border: 1, borderColor: cardBorder, borderRadius: 1 }} aria-label="Decrease">
            <RemoveIcon fontSize="small" />
          </IconButton>
          <IntentGuardedSlider
            isMobile={isMobile}
            value={modeCfg.value}
            min={modeCfg.min}
            max={modeCfg.max}
            step={modeCfg.step}
            onChange={(_, val) => modeCfg.commit(val as number)}
            sx={{ color: highlightColor, height: 6, flex: 1 }}
          />
          <IconButton size="small" onClick={() => modeCfg.commit(modeCfg.value + modeCfg.step)} sx={{ border: 1, borderColor: cardBorder, borderRadius: 1 }} aria-label="Increase">
            <AddIcon fontSize="small" />
          </IconButton>
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75, lineHeight: 1.4 }}>
          {modeCfg.hint}
        </Typography>
        <Divider sx={{ my: 1.5 }} />
        {renderLoanInfoRow('Pledged needed (adjusted)', fmt(effectivePledged), { explainKey: 'pledgedAdjusted' })}
        {renderLoanInfoRow('Unpledged planned', fmt(effectiveUnpledged), { explainKey: 'unpledgedPlanned' })}
        <Divider sx={{ my: 1 }} />
        {renderLoanInfoRow('Total (pledged + unpledged)', fmt(effectiveTotal), { explainKey: 'pledgeTotal', bold: true })}
      </>
      ),
    });
  };

  // ── PDF Export ──────────────────────────────────────────────────────────
  const handleExportPdf = useCallback(() => {
    // Dates are omitted from the report when the agent hid them on screen. The renderer
    // drops the matching column / line when it receives a blank string.
    const dateStr = (d: Date) => (showTimelineDates ? formatDate(d) : '');
    const isSimpleMode = calculatorMode === 'simple';
    const exportInputs = isSimpleMode ? simpleOutputInputs : inputs;
    const exportComputedStages = isSimpleMode ? simpleComputedStages : computedStages;
    const exportLtvDecimal = isSimpleMode && propertyTab !== 'hdb'
      ? maxLtvPct / 100
      : ltvDecimal;
    // Simple mode is progressive by default, except private resale — which follows the
    // resale (OTP 1% → exercise 4% → balance 95%) timeline, mirroring the on-screen view.
    const stagedDownPayment = isSimpleMode
      ? propertyTab !== 'hdb' && !(propertyTab === 'private' && privateSubTab === 'resale')
      : ((propertyTab === 'private' && privateSubTab === 'new-launch') || propertyTab === 'ec');

    // Down-payment stages drive the Initial Outlay (mirror ResultsSummary filter).
    const dpStagesPdf: BuyingScheduleRowPdf[] = stagedDownPayment
      ? exportComputedStages
          .filter((s) => s.type === 'otp' || s.type === 'sp')
          .map((s) => ({ label: s.label, cash: s.cashUsed, cpf: s.cpfUsed, bridging: s.bridgingPayout }))
      : [];

    // Milestones the agent merged on screen, collapsed the same way in the report.
    // Guarded on the group count matching the exported stages, so a stale report from a
    // schedule that has since changed shape is ignored rather than mis-merged.
    const exportScheduleGroups = stagedDownPayment
      && scheduleGroups
      && scheduleGroups.reduce((a, g) => a + g.length, 0) === exportComputedStages.length
      && scheduleGroups.some((g) => g.length > 1)
      ? scheduleGroups
      : undefined;

    // Full payment schedule for the Schedule section.
    let scheduleRows: BuyingScheduleRowPdf[] = [];
    if (stagedDownPayment) {
      scheduleRows = exportComputedStages.map((s) => ({
        label: s.label,
        date: s.estimatedDate ? dateStr(s.estimatedDate) : undefined,
        percentLabel: `${s.percentage}%`,
        payment: s.paymentAmount,
        loan: s.loanPayout > 0 ? s.loanPayout : undefined,
        cash: s.cashUsed,
        cpf: s.cpfUsed,
        bridging: s.bridgingPayout > 0 ? s.bridgingPayout : undefined,
      }));
    } else if (propertyTab === 'private' && privateSubTab === 'resale') {
      // Simple mode uses the active maximum LTV (75%, or 55% when tenure is stretched);
      // detailed mode uses the buyer's configured payment structure.
      const resaleHasLoan = isSimpleMode ? true : inputs.hasBankLoan;
      const resaleLoan = derivedLoan;
      const optionFee = Math.round(inputs.purchasePrice * 0.01);
      const exerciseFee = Math.round(inputs.purchasePrice * 0.04);
      const balanceAmt = inputs.purchasePrice - optionFee - exerciseFee;
      const cashAtBalance = Math.max(0, balanceAmt - (resaleHasLoan ? resaleLoan : 0));
      // resaleDates carries the agent's on-screen milestone edits, so the report follows them.
      scheduleRows = [
        { label: 'OTP (Option Fee 1%)', date: dateStr(resaleDates.otp), percentLabel: '1%', payment: optionFee, cash: optionFee },
        { label: 'Exercise OTP (4%)', date: dateStr(resaleDates.exercise), percentLabel: '4%', payment: exerciseFee, cash: exerciseFee },
        { label: 'Balance Payment (95%)', date: dateStr(resaleDates.completion), percentLabel: '95%', payment: balanceAmt, loan: resaleHasLoan ? resaleLoan : undefined, cash: cashAtBalance },
      ];
    } else if (!isSimpleMode && propertyTab === 'hdb') {
      const hdbFinancing = getHdbResaleFinancing(
        inputs.purchasePrice,
        hdbInputs.valuationPrice,
        hdbInputs.loanSource,
        otpDeposit,
        tenureStretched,
      );
      const derivedHdbLoan = hdbInputs.loanSource === 'hdb'
        ? Math.max(0, inputs.purchasePrice - hdbFinancing.mandatoryCash - hdbCashAlloc - hdbCpfAlloc)
        : hdbInputs.loanSource === 'bank' ? derivedLoan : 0;
      const sdBaseHdb = Math.max(inputs.purchasePrice, hdbInputs.valuationPrice);
      const bsdHdb = calculateBsd(sdBaseHdb);
      const totalGrants = hdbInputs.housingGrant + hdbInputs.cpfResaleGrant + hdbInputs.phgGrant;
      const cpfTotalHdb = inputs.cpf + inputs.additionalCpf + totalGrants;
      const cpfCosts = bsdHdb + absdAmount + hdbInputs.legalFee + hdbInputs.hpsAmount;
      const cpfForDown = Math.max(0, cpfTotalHdb - cpfCosts);
      const cashAtCompletion = Math.max(0, inputs.purchasePrice - otpDeposit - cpfForDown - derivedHdbLoan);
      const otpDateObj = new Date(inputs.spDate + 'T00:00:00');
      const {
        valuation: valuationDate,
        exercise: exerciseDate,
        resaleApp: resaleAppDate,
        acceptance: acceptanceDate,
        completion: completionDate,
      } = hdbResaleSchedule(otpDateObj, hdbDateOverrides);
      scheduleRows = [
        { label: 'HFE Letter' },
        { label: 'Option to Purchase', date: dateStr(otpDateObj), payment: hdbInputs.optionFee, cash: hdbInputs.optionFee },
        { label: 'Request for Valuation', date: dateStr(valuationDate) },
        { label: 'Exercise OTP (Within 21 Days)', date: dateStr(exerciseDate), payment: hdbInputs.exerciseFee, cash: hdbInputs.exerciseFee },
        { label: 'Submit Resale Application', date: dateStr(resaleAppDate) },
        { label: 'HDB Acceptance', date: dateStr(acceptanceDate) },
        { label: 'Completion', date: dateStr(completionDate), payment: cashAtCompletion, loan: derivedHdbLoan > 0 ? derivedHdbLoan : undefined, cash: cashAtCompletion },
      ];
    }

    const profiles = getSelectedProfiles();
    const profileName = selectedProfile ? getSelectedName() ?? undefined : undefined;

    // Per-borrower profiles for the timeline page's Borrower Profile table.
    // Simple mode zeroes the financials (mirroring how income is zeroed elsewhere).
    const timelineBorrowers: TimelineBorrower[] = (() => {
      const zero = isSimpleMode;
      const mk = (b: BorrowerInputs, name?: string): TimelineBorrower => {
        const recognised = zero ? 0 : calculateAssessableIncome(
          String(b.monthlyIncome), String(b.otherAnnualIncome), b.isSelfEmployed ? 1 : 0);
        // Standalone eligibility — same TDSR/MSR stress assessment as the joint
        // maxLoan, but on this borrower's recognised income and debts alone.
        const tdsrCap = Math.max(0, recognised * TDSR_RATIO - b.existingMonthlyDebt);
        const bindingCap = msrApplies ? Math.min(tdsrCap, recognised * MSR_RATIO) : tdsrCap;
        const soloMaxLoan = calculateMaxLoan(bindingCap, stressTestRate, inputs.tenure);
        return {
          name: name?.trim() || undefined,
          age: b.age,
          selfEmployed: !zero && b.isSelfEmployed,
          income: zero ? 0 : b.monthlyIncome,
          recognisedIncome: Math.round(recognised),
          otherAnnualIncome: zero ? 0 : b.otherAnnualIncome,
          existingMonthlyDebt: zero ? 0 : b.existingMonthlyDebt,
          monthlyCpfOa: zero ? 0 : getMonthlyCpfOa(b),
          loanEligibility: zero ? undefined : soloMaxLoan,
          maxPurchasePrice: zero ? undefined : Math.round(soloMaxLoan / maxPriceLtv),
        };
      };
      return [
        mk(inputs.primary, profiles[0]?.Name),
        ...(!zero && inputs.includeAdditionalBorrower ? [mk(inputs.additional, profiles[1]?.Name)] : []),
      ];
    })();

    // ── Project Investment Timeline (last page) — progressive purchases only ──
    let investmentTimeline: InvestmentTimelineData | undefined;
    let fundSummary: FundSummaryData | undefined;
    if (stagedDownPayment) {
      const price = exportInputs.purchasePrice;
      const timelineBsd = isSimpleMode ? calculateBsd(price) : bsd;
      const timelineAbsd = isSimpleMode ? simpleAbsdAmount : absdAmount;
      const timelineLoan = derivedLoan;
      // Installment once the progressive loan is fully drawn (bank rule: outstanding
      // balance over the remaining tenure) — matches the on-screen Reserves figure.
      const timelineMonthlyInstallment = finalLoanStage(exportComputedStages)?.monthlyInstallment
        ?? calcMonthlyInstallment(timelineLoan, exportInputs.interestRate, exportInputs.tenure);
      const booking = new Date(exportInputs.spDate + 'T00:00:00');
      const spReceive = addDays(booking, 3);
      // Use the same effective milestone dates as the on-screen schedule.
      const stampDuty = exportComputedStages.find((s) => s.type === 'sp-bsd')?.estimatedDate ?? addDays(booking, 38);  // ~14 days after exercise
      const exercise = addDays(stampDuty, -14);
      const pay15 = exportComputedStages.find((s) => s.type === 'sp')?.estimatedDate ?? addDays(booking, 56);         // ~8 weeks
      const launch = booking;
      // Tracks NEW_LAUNCH_STAGES' foundation timing (6 months from launch).
      const foundation = exportComputedStages.find((s) => s.type === 'construction' || s.type === 'top')?.estimatedDate ?? addMonths(launch, 6);

      const progStages = exportComputedStages.filter(
        (s) => s.type === 'construction' || s.type === 'top' || s.type === 'csc',
      );
      const foundationTopUp = progStages.reduce((a, s) => a + s.cashUsed + s.cpfUsed, 0);
      const sum20PlusSd = Math.round(price * 0.2) + timelineBsd + timelineAbsd;

      const stressInstallment = calcMonthlyInstallment(timelineLoan, stressTestRate, exportInputs.tenure);
      const minSalaryTdsr = TDSR_RATIO > 0 ? Math.round((stressInstallment + totalExistingDebt) / TDSR_RATIO) : 0;
      // MSR only applies to HDB/EC — not assessed for private purchases.
      const minSalaryMsr = (propertyTab === 'ec' && MSR_RATIO > 0) ? Math.round(stressInstallment / MSR_RATIO) : undefined;

      const ageIncomeRows = [
        { age: exportInputs.primary.age, salary: isSimpleMode ? 0 : exportInputs.primary.monthlyIncome },
        ...(!isSimpleMode && exportInputs.includeAdditionalBorrower ? [{ age: exportInputs.additional.age, salary: exportInputs.additional.monthlyIncome }] : []),
      ];

      const stages = progStages.map((s) => {
        const mthsFromLaunch = s.estimatedDate
          ? Math.max(0, Math.round((s.estimatedDate.getTime() - launch.getTime()) / (1000 * 60 * 60 * 24 * 30.44)))
          : 0;
        const shortDate = s.estimatedDate
          ? s.estimatedDate.toLocaleDateString('en-SG', { month: 'short', year: '2-digit' })
          : '';
        return {
          label: `${s.percentage}% ${shortStageLabel(s.label).toUpperCase()}`,
          periodLabel: showTimelineDates ? `${mthsFromLaunch} mths · ${shortDate}` : `${mthsFromLaunch} mths`,
          cashCpf: s.cashUsed + s.cpfUsed,
          cpf: s.cpfUsed,
          loanPct: price > 0 ? Math.round((s.loanPayout / price) * 100) : 0,
          loanAmount: s.loanPayout,
          bridging: s.bridgingPayout > 0 ? s.bridgingPayout : undefined,
          monthlyRepayment: Math.round(s.monthlyInstallment),
        };
      });

      // ── Fund Summary (Initial Outlay + Fund Balance with two months-covered) ──
      // Resale Levy — EC-only cash cost, deducted from the sale proceeds (not an outlay).
      const resaleLevyV = !isSimpleMode && propertyTab === 'ec' ? resaleLevy : 0;
      const availableCashV = isSimpleMode ? 0 : inputs.additionalCash + cashProceedFromSelling - resaleLevyV;
      const availableCpfV = isSimpleMode ? 0 : inputs.cpf + inputs.additionalCpf + (propertyTab === 'ec' ? ecHousingGrant : 0);
      // CPF left after the down payment — the stamp-duty / legal CPF waterfall draws from
      // this. Base it on the CPF the schedule ACTUALLY consumes (every stage except the BSD
      // stage), so a cash-funded down payment leaves the full CPF balance free for BSD/ABSD/
      // Legal instead of wrongly assuming 20% of the price came out of CPF (mirrors ResultsSummary).
      const downCpfConsumedV = isSimpleMode ? 0 : exportComputedStages.reduce((a, s) => a + (s.type === 'sp-bsd' ? 0 : s.cpfUsed), 0);
      const cpfAfterDown = Math.max(0, availableCpfV - downCpfConsumedV);
      const bsdCpfV = !isSimpleMode && bsdByCpf ? Math.min(timelineBsd, cpfAfterDown) : 0;
      const bsdCashV = timelineBsd - bsdCpfV;
      const cpfAfterBsd = Math.max(0, cpfAfterDown - bsdCpfV);
      const absdCpfV = !isSimpleMode && absdByCpf ? Math.min(timelineAbsd, cpfAfterBsd) : 0;
      const absdCashV = timelineAbsd - absdCpfV;
      // Legal fee follows Cash -> CPF for a no-loan purchase; stamp duty remains the
      // separately allocated exception. Loan-backed purchases retain the chosen CPF toggle.
      const cpfAfterAbsd = Math.max(0, cpfAfterBsd - absdCpfV);
      const legalCpfV = !isSimpleMode && !exportInputs.hasBankLoan
        ? 0
        : !isSimpleMode && legalFeeByCpf ? Math.min(exportInputs.legalFee, cpfAfterAbsd) : 0;
      const legalCashV = exportInputs.legalFee - legalCpfV;
      const dpCashV = dpStagesPdf.reduce((a, s) => a + (s.cash ?? 0), 0);
      const dpCpfV = dpStagesPdf.reduce((a, s) => a + (s.cpf ?? 0), 0);
      const dpBridgingV = dpStagesPdf.reduce((a, s) => a + (s.bridging ?? 0), 0);
      const totalOutlayCash = dpCashV + bsdCashV + absdCashV + legalCashV;
      const totalOutlayCpf = dpCpfV + bsdCpfV + absdCpfV + legalCpfV;
      const progDpCashV = progStages.reduce((a, s) => a + s.cashUsed, 0);
      const progDpCpfV = progStages.reduce((a, s) => a + s.cpfUsed, 0);
      // CPF portions of the buyer-funded milestones — drives the timeline cards'
      // CASH DUE / CPF DUE split (mirrors the schedule's cash→CPF waterfall).
      const otpCpfV = exportComputedStages.find((s) => s.type === 'otp')?.cpfUsed ?? 0;
      const pay15CpfV = exportComputedStages.find((s) => s.type === 'sp')?.cpfUsed ?? 0;
      // Simple mode: mandatory-cash portions (the rest of each milestone is flexible CPF/Cash).
      const otpMandV = exportComputedStages.find((s) => s.type === 'otp')?.simpleMandatoryCash ?? 0;
      const pay15MandV = exportComputedStages.find((s) => s.type === 'sp')?.simpleMandatoryCash ?? 0;
      // Pledged & unpledged funds are committed to bridge the loan shortfall, so they
      // are not spendable reserves — subtract them from the cash balance (mirrors the
      // on-screen Fund Balance).
      const pledgeUnpledgeV = isSimpleMode ? 0 : effectivePledged + effectiveUnpledged;
      const balanceCashV = availableCashV - totalOutlayCash - progDpCashV - pledgeUnpledgeV;
      const balanceCpfV = availableCpfV - totalOutlayCpf - progDpCpfV;
      const reserveFundsV = Math.max(0, balanceCashV) + Math.max(0, balanceCpfV);
      const mCpfOa = isSimpleMode ? 0 : getMonthlyCpfOa(inputs.primary) + (inputs.includeAdditionalBorrower ? getMonthlyCpfOa(inputs.additional) : 0);
      const rwCur = computeRunway(reserveFundsV, timelineMonthlyInstallment, exportComputedStages, 0, true);
      const rwEmp = computeRunway(reserveFundsV, timelineMonthlyInstallment, exportComputedStages, mCpfOa, true);
      fundSummary = {
        outlayRows: isSimpleMode
          ? [
              // Relabelled + split into mandatory Cash / flexible CPF/Cash (we don't know the buyer's mix).
              ...exportComputedStages
                .filter((s) => s.type === 'otp' || s.type === 'sp')
                .map((s) => ({ label: simpleStageLabel(s.label), cpf: 0, cash: s.cashUsed, bridging: 0, mandatoryCash: s.simpleMandatoryCash ?? 0 })),
              { label: 'BSD', cpf: 0, cash: bsdCashV, mandatoryCash: 0 },
              ...(timelineAbsd > 0 ? [{ label: 'ABSD', cpf: 0, cash: absdCashV, mandatoryCash: 0 }] : []),
              { label: 'Legal / Conveyancing', cpf: 0, cash: legalCashV, mandatoryCash: 0 },
            ]
          : [
              ...dpStagesPdf.map((s) => ({ label: s.label, cpf: s.cpf ?? 0, cash: s.cash ?? 0, bridging: s.bridging ?? 0 })),
              { label: 'BSD', cpf: bsdCpfV, cash: bsdCashV },
              ...(timelineAbsd > 0 ? [{ label: 'ABSD', cpf: absdCpfV, cash: absdCashV }] : []),
              { label: 'Legal / Conveyancing', cpf: legalCpfV, cash: legalCashV },
            ],
        totalMandatoryCash: isSimpleMode ? otpMandV + pay15MandV : undefined,
        hasCpfCol: totalOutlayCpf > 0,
        hasBridgingCol: dpBridgingV > 0,
        totalOutlayBridging: dpBridgingV,
        totalOutlayCpf,
        totalOutlayCash,
        pledged: effectivePledged,
        unpledged: effectiveUnpledged,
        grandTotalCash: totalOutlayCash + effectivePledged + effectiveUnpledged,
        availableCpf: availableCpfV,
        availableCash: availableCashV,
        progDpCpf: progDpCpfV,
        progDpCash: progDpCashV,
        balanceCpf: balanceCpfV,
        balanceCash: balanceCashV,
        monthlyInstallment: timelineMonthlyInstallment,
        monthlyCpfOa: mCpfOa,
        monthsCurrent: rwCur.hasLoan ? rwCur.totalMonths : null,
        monthsEmployed: rwEmp.hasLoan ? rwEmp.totalMonths : null,
        specialBridging: !isSimpleMode && bridgingApplies && sblHasHdb
          ? { valuation: sblValuation, outstandingLoan: sblOutstandingLoan, levy: sblLevy, maxLoan: sblMax, applied: bridgingForLoan }
          : undefined,
      };

      investmentTimeline = {
        bucPrice: price,
        bookingDate: dateStr(booking),
        spReceiveDate: dateStr(spReceive),
        exerciseDate: dateStr(exercise),
        stampDutyDate: dateStr(stampDuty),
        pay15Date: dateStr(pay15),
        launchDate: dateStr(launch),
        foundationDate: dateStr(foundation),
        otpAmount: Math.round(price * 0.05),
        cancellationPenalty: Math.round(price * 0.0125),
        refundable: Math.round(price * 0.0375),
        bsd: timelineBsd,
        absd: timelineAbsd,
        pay15Amount: Math.round(price * 0.15),
        sum20PlusSd,
        foundationTopUp,
        additionalTopUp: 0,
        totalCashCpf: sum20PlusSd + foundationTopUp,
        otpCpf: otpCpfV,
        pay15Cpf: pay15CpfV,
        stampDutyCpf: bsdCpfV + absdCpfV,
        foundationCpf: progDpCpfV,
        simpleMode: isSimpleMode,
        pay15MandatoryCash: pay15MandV,
        ageIncomeRows,
        borrowers: timelineBorrowers,
        iwaa,
        maxLoanTenure: exportInputs.tenure,
        interestRate: exportInputs.interestRate,
        monthlyCpfOa: mCpfOa,
        totalMonthlyDebt: isSimpleMode ? 0 : totalExistingDebt,
        totalIncome: isSimpleMode ? 0 : totalRecognisedIncome,
        ltvPct: isSimpleMode ? exportLtvDecimal * 100 : derivedLtvPct,
        ltvLoan: timelineLoan,
        estLtvPct: isSimpleMode ? exportLtvDecimal * 100 : price > 0 ? (maxLoan / price) * 100 : 0,
        estLtvLoan: isSimpleMode ? timelineLoan : maxLoan,
        maxPropertyPrice: isSimpleMode ? price : maxPriceLoan,
        minSalaryTdsr,
        minSalaryMsr,
        shortfallLtvPct: isSimpleMode ? 0 : price > 0 ? (loanShortfall / price) * 100 : 0,
        shortfallLtvAmt: isSimpleMode ? 0 : loanShortfall,
        unpledge: isSimpleMode ? 0 : effectiveUnpledged,
        pledge: isSimpleMode ? 0 : effectivePledged,
        stages,
      };
    } else if (propertyTab === 'private' && privateSubTab === 'resale') {
      // ── Private Resale — timeline + fund summary for the landscape results page ──
      // Field reuse mirrors the HDB mapping above: the renderer branches on resale and
      // reads otpAmount = 1% option fee, pay15Amount = 4% exercise fee,
      // foundationTopUp = cash/CPF at completion, foundationDate = completion date.
      const price = exportInputs.purchasePrice;
      const resaleBsd = isSimpleMode ? calculateBsd(price) : bsd;
      const resaleAbsd = isSimpleMode ? simpleAbsdAmount : absdAmount;
      const resaleLoan = derivedLoan;
      const resaleInstallment = calcMonthlyInstallment(resaleLoan, exportInputs.interestRate, exportInputs.tenure);
      const optionFee = Math.round(price * 0.01);
      const exerciseFee = Math.round(price * 0.04);
      // Resale timeline — taken straight from resaleDates so the report mirrors the
      // on-screen ResaleSchedule, including any milestone dates the agent edited there.
      const { otp: otpDateObj, exercise: exerciseDate, stampDuty: stampDutyDate, completion: completionDate } = resaleDates;

      // Down payment split — the buyer's Payment Structure allocation (detailed), or the
      // maximum-LTV cash-down assumption (simple: 25% normally, 45% when tenure is
      // stretched), mirroring the on-screen ResultsSummary.
      const downCash = isSimpleMode ? Math.round(price * (1 - exportLtvDecimal)) : minCash + cashDownAlloc;
      const downCpf = isSimpleMode ? 0 : cpfDownAlloc;
      // Cash/CPF the buyer tops up at completion: the 95% balance less the bank loan
      // (equals total down − the 5% already paid when the loan fills the rest).
      const completionCashCpf = Math.max(0, price - optionFee - exerciseFee - resaleLoan);

      // Stamp-duty / legal CPF waterfall — draws from the CPF left after the down payment
      // (mirrors ResultsSummary's non-staged path).
      const availableCashV = isSimpleMode ? 0 : inputs.additionalCash + cashProceedFromSelling;
      const availableCpfV = isSimpleMode ? 0 : inputs.cpf + inputs.additionalCpf;
      const cpfAfterDown = Math.max(0, availableCpfV - downCpf);
      const bsdCpfV = !isSimpleMode && bsdByCpf ? Math.min(resaleBsd, cpfAfterDown) : 0;
      const bsdCashV = resaleBsd - bsdCpfV;
      const cpfAfterBsd = Math.max(0, cpfAfterDown - bsdCpfV);
      const absdCpfV = !isSimpleMode && absdByCpf ? Math.min(resaleAbsd, cpfAfterBsd) : 0;
      const absdCashV = resaleAbsd - absdCpfV;
      // Legal fee follows Cash -> CPF for a no-loan purchase; stamp duty remains the
      // separately allocated exception. Loan-backed purchases retain the chosen CPF toggle.
      const cpfAfterAbsd = Math.max(0, cpfAfterBsd - absdCpfV);
      const legalCpfV = !isSimpleMode && !exportInputs.hasBankLoan
        ? 0
        : !isSimpleMode && legalFeeByCpf ? Math.min(exportInputs.legalFee, cpfAfterAbsd) : 0;
      const legalCashV = exportInputs.legalFee - legalCpfV;
      const totalOutlayCash = downCash + bsdCashV + absdCashV + legalCashV;
      const totalOutlayCpf = downCpf + bsdCpfV + absdCpfV + legalCpfV;
      // Pledged & unpledged funds are committed to bridge the loan shortfall, so they
      // are not spendable reserves (mirrors the on-screen Fund Balance).
      const pledgeUnpledgeV = isSimpleMode ? 0 : effectivePledged + effectiveUnpledged;
      const balanceCashV = availableCashV - totalOutlayCash - pledgeUnpledgeV;
      const balanceCpfV = availableCpfV - totalOutlayCpf;
      const reserveFundsV = Math.max(0, balanceCashV) + Math.max(0, balanceCpfV);
      const mCpfOa = isSimpleMode ? 0 : getMonthlyCpfOa(inputs.primary) + (inputs.includeAdditionalBorrower ? getMonthlyCpfOa(inputs.additional) : 0);
      const rwCur = computeRunway(reserveFundsV, resaleInstallment, [], 0, false);
      const rwEmp = computeRunway(reserveFundsV, resaleInstallment, [], mCpfOa, false);
      const simpleResaleMandatoryDown = isSimpleMode ? Math.round(price * (minCashPct / 100)) : 0;

      fundSummary = {
        outlayRows: [
          { label: 'Down Payment', cpf: downCpf, cash: downCash, mandatoryCash: simpleResaleMandatoryDown },
          { label: isSimpleMode ? 'BSD (Reimbursable from CPF)' : 'BSD', cpf: bsdCpfV, cash: bsdCashV, mandatoryCash: isSimpleMode ? bsdCashV : 0 },
          ...(resaleAbsd > 0 ? [{ label: 'ABSD', cpf: absdCpfV, cash: absdCashV }] : []),
          { label: 'Legal / Conveyancing', cpf: legalCpfV, cash: legalCashV },
        ],
        hasCpfCol: totalOutlayCpf > 0,
        hasBridgingCol: false,
        totalOutlayBridging: 0,
        totalOutlayCpf,
        totalOutlayCash,
        totalMandatoryCash: isSimpleMode ? simpleResaleMandatoryDown + bsdCashV : undefined,
        pledged: isSimpleMode ? 0 : effectivePledged,
        unpledged: isSimpleMode ? 0 : effectiveUnpledged,
        grandTotalCash: totalOutlayCash + (isSimpleMode ? 0 : effectivePledged + effectiveUnpledged),
        availableCpf: availableCpfV,
        availableCash: availableCashV,
        progDpCpf: 0,
        progDpCash: 0,
        balanceCpf: balanceCpfV,
        balanceCash: balanceCashV,
        monthlyInstallment: resaleInstallment,
        monthlyCpfOa: mCpfOa,
        monthsCurrent: rwCur.hasLoan ? rwCur.totalMonths : null,
        monthsEmployed: rwEmp.hasLoan ? rwEmp.totalMonths : null,
      };

      const resaleStressInstallment = calcMonthlyInstallment(resaleLoan, stressTestRate, exportInputs.tenure);
      const loanPctOfPrice = price > 0 ? Math.round((resaleLoan / price) * 100) : 0;
      investmentTimeline = {
        bucPrice: price,
        bookingDate: dateStr(otpDateObj),
        spReceiveDate: dateStr(exerciseDate),
        exerciseDate: dateStr(exerciseDate),
        stampDutyDate: dateStr(stampDutyDate),
        pay15Date: dateStr(completionDate),
        launchDate: dateStr(otpDateObj),
        foundationDate: dateStr(completionDate),
        otpAmount: optionFee,
        cancellationPenalty: 0,
        refundable: 0,
        bsd: resaleBsd,
        absd: resaleAbsd,
        pay15Amount: exerciseFee,
        sum20PlusSd: optionFee + exerciseFee + resaleBsd + resaleAbsd,
        foundationTopUp: completionCashCpf,
        additionalTopUp: 0,
        totalCashCpf: optionFee + exerciseFee + completionCashCpf + resaleBsd + resaleAbsd,
        // Option (1%) & exercise (4%) fees are cash; the down-payment CPF goes in at
        // completion; stamp duty splits per the CPF toggle.
        stampDutyCpf: bsdCpfV + absdCpfV,
        foundationCpf: Math.min(downCpf, completionCashCpf),
        ageIncomeRows: [
          { age: exportInputs.primary.age, salary: isSimpleMode ? 0 : exportInputs.primary.monthlyIncome },
          ...(!isSimpleMode && exportInputs.includeAdditionalBorrower ? [{ age: exportInputs.additional.age, salary: exportInputs.additional.monthlyIncome }] : []),
        ],
        borrowers: timelineBorrowers,
        iwaa,
        maxLoanTenure: exportInputs.tenure,
        interestRate: exportInputs.interestRate,
        monthlyCpfOa: mCpfOa,
        totalMonthlyDebt: isSimpleMode ? 0 : totalExistingDebt,
        totalIncome: isSimpleMode ? 0 : totalRecognisedIncome,
        ltvPct: isSimpleMode ? exportLtvDecimal * 100 : derivedLtvPct,
        ltvLoan: resaleLoan,
        estLtvPct: isSimpleMode ? exportLtvDecimal * 100 : price > 0 ? (maxLoan / price) * 100 : 0,
        estLtvLoan: isSimpleMode ? resaleLoan : maxLoan,
        maxPropertyPrice: isSimpleMode ? price : maxPriceLoan,
        minSalaryTdsr: TDSR_RATIO > 0 ? Math.round((resaleStressInstallment + (isSimpleMode ? 0 : totalExistingDebt)) / TDSR_RATIO) : 0,
        shortfallLtvPct: isSimpleMode ? 0 : price > 0 ? (loanShortfall / price) * 100 : 0,
        shortfallLtvAmt: isSimpleMode ? 0 : loanShortfall,
        unpledge: isSimpleMode ? 0 : effectiveUnpledged,
        pledge: isSimpleMode ? 0 : effectivePledged,
        simpleMode: isSimpleMode,
        stages: [
          { label: '1% OPTION FEE (OTP)', periodLabel: dateStr(otpDateObj), cashCpf: optionFee, loanPct: 0, loanAmount: 0, monthlyRepayment: 0 },
          { label: '4% EXERCISE OTP', periodLabel: dateStr(exerciseDate), cashCpf: exerciseFee, loanPct: 0, loanAmount: 0, monthlyRepayment: 0 },
          { label: resaleAbsd > 0 ? 'STAMP DUTY (BSD + ABSD)' : 'STAMP DUTY (BSD)', periodLabel: dateStr(stampDutyDate), cashCpf: resaleBsd + resaleAbsd, cpf: bsdCpfV + absdCpfV, loanPct: 0, loanAmount: 0, monthlyRepayment: 0 },
          {
            label: '95% COMPLETION BALANCE',
            periodLabel: dateStr(completionDate),
            cashCpf: completionCashCpf,
            cpf: Math.min(downCpf, completionCashCpf),
            loanPct: loanPctOfPrice,
            loanAmount: resaleLoan,
            monthlyRepayment: Math.round(resaleInstallment),
          },
        ],
      };
    }

    if (propertyTab === 'hdb') {
      const price = inputs.purchasePrice;
      const sdBaseHdb = Math.max(price, hdbInputs.valuationPrice);
      const hdbBsd = calculateBsd(sdBaseHdb);
      const hdbAbsd = absdAmount;
      const hdbFinancing = getHdbResaleFinancing(
        price,
        hdbInputs.valuationPrice,
        hdbInputs.loanSource,
        otpDeposit,
        tenureStretched,
      );
      const derivedHdbLoan = isSimpleMode
        ? derivedLoan
        : hdbInputs.loanSource === 'hdb'
          ? Math.max(0, price - hdbFinancing.mandatoryCash - hdbCashAlloc - hdbCpfAlloc)
          : hdbInputs.loanSource === 'bank' ? derivedLoan : 0;
      const hdbRate = hdbInputs.loanSource === 'hdb' ? HDB_CONCESSIONARY_INTEREST_RATE : inputs.interestRate;
      const hdbMonthlyInstallment = calcMonthlyInstallment(derivedHdbLoan, hdbRate, inputs.tenure);
      const totalGrants = hdbInputs.housingGrant + hdbInputs.cpfResaleGrant + hdbInputs.phgGrant;
      const cpfAvailableHdb = inputs.cpf + inputs.additionalCpf + totalGrants;
      const downCash = hdbOutlay?.downCash ?? otpDeposit;
      const downCpf = hdbOutlay?.downCpf ?? 0;
      const completionCash = Math.max(0, downCash - otpDeposit);
      const completionCpf = downCpf;
      const valuationFee = hdbInputs.valuationFee;
      const adminFee = hdbInputs.hdbAdminFee;
      const caveatFee = hdbInputs.loanSource === 'hdb' ? hdbInputs.caveatFee : 0;
      const agencyFee = hdbAgencyFee;
      // Fee CPF/cash split mirrors the on-screen outlay (hdbOutlay) so the PDF matches.
      const bsdCpfV = hdbOutlay?.bsdCpf ?? 0, bsdCashV = hdbOutlay?.bsdCash ?? hdbBsd;
      const absdCpfV = hdbOutlay?.absdCpf ?? 0, absdCashV = hdbOutlay?.absdCash ?? hdbAbsd;
      const legalHpsCpfV = (hdbOutlay?.legalCpf ?? 0) + (hdbOutlay?.hpsCpf ?? 0);
      const legalHpsCashV = (hdbOutlay?.legalCash ?? inputs.legalFee) + (hdbOutlay?.hpsCash ?? hdbInputs.hpsAmount);

      const otpDateObj = new Date(inputs.spDate + 'T00:00:00');
      const {
        valuation: valuationDate,
        exercise: exerciseDate,
        resaleApp: resaleAppDate,
        acceptance: acceptanceDate,
        completion: completionDate,
      } = hdbResaleSchedule(otpDateObj, hdbDateOverrides);

      scheduleRows = [
        { label: 'HFE Letter' },
        { label: 'Option to Purchase', date: dateStr(otpDateObj), payment: hdbInputs.optionFee, cash: hdbInputs.optionFee },
        { label: 'Request for Valuation', date: dateStr(valuationDate), payment: valuationFee, cash: valuationFee },
        { label: 'Exercise OTP (Within 21 Days)', date: dateStr(exerciseDate), payment: hdbInputs.exerciseFee, cash: hdbInputs.exerciseFee },
        { label: 'Submit Resale Application', date: dateStr(resaleAppDate), payment: adminFee + caveatFee, cash: adminFee + caveatFee },
        { label: 'HDB Acceptance', date: dateStr(acceptanceDate), payment: hdbBsd + hdbAbsd + inputs.legalFee + hdbInputs.hpsAmount, cash: bsdCashV + absdCashV + legalHpsCashV, cpf: bsdCpfV + absdCpfV + legalHpsCpfV },
        {
          label: 'Completion',
          date: dateStr(completionDate),
          payment: completionCash + completionCpf + derivedHdbLoan,
          loan: derivedHdbLoan > 0 ? derivedHdbLoan : undefined,
          cash: completionCash,
          cpf: completionCpf,
        },
      ];

      const hdbDownPaymentTotal = downCash + downCpf;
      const simpleHdbMandatoryDown = Math.min(
        hdbDownPaymentTotal,
        Math.max(0, hdbFinancing.mandatoryCash),
      );
      const simpleHdbFlexibleDown = Math.max(
        0,
        hdbDownPaymentTotal - simpleHdbMandatoryDown,
      );
      const hdbOutlayRows = [
        ...(isSimpleMode
          ? hdbInputs.loanSource === 'bank'
            ? [
                {
                  label: 'Minimum cash down payment (5%)',
                  cpf: 0,
                  cash: simpleHdbMandatoryDown,
                  mandatoryCash: simpleHdbMandatoryDown,
                },
                ...(simpleHdbFlexibleDown > 0 ? [{
                  label: 'Remaining down payment (20%) - CPF/Cash',
                  cpf: 0,
                  cash: simpleHdbFlexibleDown,
                  mandatoryCash: 0,
                }] : []),
              ]
            : [{
                label: hdbInputs.loanSource === 'none'
                  ? 'Purchase price (100%) - CPF/Cash'
                  : 'Down payment (25% of valuation) - CPF/Cash',
                cpf: 0,
                cash: hdbDownPaymentTotal,
                mandatoryCash: simpleHdbMandatoryDown,
              }]
          : [
              {
                label: 'Option to Purchase',
                cpf: 0,
                cash: hdbInputs.optionFee,
              },
              {
                label: 'Exercise OTP',
                cpf: 0,
                cash: hdbInputs.exerciseFee,
              },
              ...(completionCash > 0 || completionCpf > 0 ? [{
                label: 'Completion',
                cpf: completionCpf,
                cash: completionCash,
              }] : []),
            ]),
        {
          label: 'BSD',
          cpf: bsdCpfV,
          cash: bsdCashV,
          mandatoryCash: isSimpleMode ? 0 : undefined,
        },
        ...(hdbAbsd > 0 ? [{
          label: 'ABSD',
          cpf: absdCpfV,
          cash: absdCashV,
          mandatoryCash: isSimpleMode ? 0 : undefined,
        }] : []),
        {
          label: hdbInputs.hpsAmount > 0 ? 'Legal / Conveyancing + HPS' : 'Legal / Conveyancing',
          cpf: legalHpsCpfV,
          cash: legalHpsCashV,
          mandatoryCash: isSimpleMode ? 0 : undefined,
        },
        ...(isSimpleMode
          ? [
              ...(caveatFee > 0 ? [{
                label: 'Caveat Fee - Cash',
                cpf: 0,
                cash: caveatFee,
                mandatoryCash: caveatFee,
              }] : []),
              ...(valuationFee > 0 ? [{
                label: 'Valuation Fee - Cash',
                cpf: 0,
                cash: valuationFee,
                mandatoryCash: valuationFee,
              }] : []),
              ...(adminFee > 0 ? [{
                label: 'HDB Admin Fee - Cash',
                cpf: 0,
                cash: adminFee,
                mandatoryCash: adminFee,
              }] : []),
              ...(agencyFee > 0 ? [{
                label: 'Agency Service Fee - Cash',
                cpf: 0,
                cash: agencyFee,
                mandatoryCash: agencyFee,
              }] : []),
            ]
          : [
              ...((valuationFee + adminFee + caveatFee) > 0 ? [{
                label: 'HDB Fees',
                cpf: 0,
                cash: valuationFee + adminFee + caveatFee,
              }] : []),
              ...(agencyFee > 0 ? [{
                label: 'Agency Service Fee',
                cpf: 0,
                cash: agencyFee,
              }] : []),
            ]),
      ];
      const totalOutlayCpf = hdbOutlayRows.reduce((a, r) => a + r.cpf, 0);
      const totalOutlayCash = hdbOutlayRows.reduce((a, r) => a + r.cash, 0);
      const totalMandatoryCashHdb = hdbOutlayRows.reduce(
        (a, r) => a + (r.mandatoryCash ?? 0),
        0,
      );
      // The 2nd-HDB-loan cash set-aside applies to the HDB concessionary loan only
      // (mirrors the web's Available Funds gating).
      const availableCashHdb = inputs.additionalCash + cashProceedFromSelling
        - (hdbInputs.loanSource === 'hdb' && hdbInputs.isSecondHdbLoan ? hdbInputs.secondHdbLoanCashSetAside : 0);
      // Pledged & unpledged funds are committed to bridge the loan shortfall, so they
      // are not spendable reserves (mirrors the on-screen Fund Balance).
      const pledgeUnpledgeHdb = isSimpleMode ? 0 : effectivePledged + effectiveUnpledged;
      const balanceCashHdb = availableCashHdb - totalOutlayCash - pledgeUnpledgeHdb;
      const balanceCpfHdb = cpfAvailableHdb - totalOutlayCpf;
      const reserveFundsHdb = Math.max(0, balanceCashHdb) + Math.max(0, balanceCpfHdb);
      const monthlyCpfOaHdb = getMonthlyCpfOa(inputs.primary) + (inputs.includeAdditionalBorrower ? getMonthlyCpfOa(inputs.additional) : 0);
      const rwCurHdb = computeRunway(reserveFundsHdb, hdbMonthlyInstallment, [], 0, false);
      const rwEmpHdb = computeRunway(reserveFundsHdb, hdbMonthlyInstallment, [], monthlyCpfOaHdb, false);

      fundSummary = {
        outlayRows: hdbOutlayRows,
        hasCpfCol: totalOutlayCpf > 0,
        hasBridgingCol: false,
        totalOutlayBridging: 0,
        totalOutlayCpf,
        totalOutlayCash,
        totalMandatoryCash: isSimpleMode ? totalMandatoryCashHdb : undefined,
        pledged: effectivePledged,
        unpledged: effectiveUnpledged,
        grandTotalCash: totalOutlayCash + effectivePledged + effectiveUnpledged,
        availableCpf: cpfAvailableHdb,
        availableCash: availableCashHdb,
        progDpCpf: 0,
        progDpCash: 0,
        balanceCpf: balanceCpfHdb,
        balanceCash: balanceCashHdb,
        monthlyInstallment: hdbMonthlyInstallment,
        monthlyCpfOa: monthlyCpfOaHdb,
        monthsCurrent: rwCurHdb.hasLoan ? rwCurHdb.totalMonths : null,
        monthsEmployed: rwEmpHdb.hasLoan ? rwEmpHdb.totalMonths : null,
      };

      investmentTimeline = {
        bucPrice: price,
        bookingDate: 'Before OTP',
        spReceiveDate: dateStr(otpDateObj),
        exerciseDate: dateStr(valuationDate),
        stampDutyDate: dateStr(exerciseDate),
        hdbResaleAppDate: dateStr(resaleAppDate),
        hdbResaleAppAmount: adminFee + caveatFee,
        pay15Date: dateStr(acceptanceDate),
        launchDate: dateStr(otpDateObj),
        foundationDate: dateStr(completionDate),
        otpAmount: hdbInputs.optionFee,
        cancellationPenalty: valuationFee,
        refundable: totalGrants,
        bsd: hdbBsd,
        absd: hdbAbsd,
        pay15Amount: hdbInputs.exerciseFee,
        sum20PlusSd: hdbBsd + hdbAbsd + inputs.legalFee + hdbInputs.hpsAmount,
        foundationTopUp: completionCash + completionCpf,
        additionalTopUp: 0,
        totalCashCpf: totalOutlayCash,
        // Option ($1k) & exercise ($4k) fees are cash; stamp duty and the completion
        // top-up carry the CPF the HDB waterfall assigns.
        stampDutyCpf: (hdbOutlay?.bsdCpf ?? 0) + (hdbOutlay?.absdCpf ?? 0),
        foundationCpf: completionCpf,
        ageIncomeRows: [
          { age: inputs.primary.age, salary: inputs.primary.monthlyIncome },
          ...(inputs.includeAdditionalBorrower ? [{ age: inputs.additional.age, salary: inputs.additional.monthlyIncome }] : []),
        ],
        borrowers: timelineBorrowers,
        iwaa,
        maxLoanTenure: maxTenureCap,
        interestRate: hdbRate,
        monthlyCpfOa: monthlyCpfOaHdb,
        totalMonthlyDebt: totalExistingDebt,
        totalIncome: totalRecognisedIncome,
        ltvPct: price > 0 ? (derivedHdbLoan / price) * 100 : 0,
        ltvLoan: derivedHdbLoan,
        estLtvPct: price > 0 ? (maxLoan / price) * 100 : 0,
        estLtvLoan: maxLoan,
        maxPropertyPrice: maxPriceLoan,
        minSalaryTdsr: TDSR_RATIO > 0 ? Math.round((calcMonthlyInstallment(derivedHdbLoan, stressTestRate, inputs.tenure) + totalExistingDebt) / TDSR_RATIO) : 0,
        shortfallLtvPct: price > 0 ? (loanShortfall / price) * 100 : 0,
        shortfallLtvAmt: loanShortfall,
        unpledge: effectiveUnpledged,
        pledge: effectivePledged,
        stages: scheduleRows.map((s) => {
          const rowLoan = s.loan ?? 0;
          return {
            label: s.label.toUpperCase(),
            periodLabel: s.date ?? '',
            cashCpf: (s.cash ?? 0) + (s.cpf ?? 0),
            cpf: s.cpf ?? 0,
            loanPct: price > 0 ? Math.round((rowLoan / price) * 100) : 0,
            loanAmount: rowLoan,
            monthlyRepayment: rowLoan > 0 ? Math.round(hdbMonthlyInstallment) : 0,
          };
        }),
      };
    }

    logPdfExport('buying_calculator', currentUser);
    // Returned, not fire-and-forget: the Export PDF button awaits it to run its progress bar.
    return exportBuyingToPDF({
      inputs: {
        mode: calculatorMode,
        propertyTab,
        privateSubTab,
        ecPaymentScheme,
        purchasePrice: exportInputs.purchasePrice,
        hasBankLoan: isSimpleMode
          ? propertyTab !== 'hdb' || hdbInputs.loanSource !== 'none'
          : inputs.hasBankLoan,
        ltvDecimal: exportLtvDecimal,
        interestRate: propertyTab === 'hdb' && hdbInputs.loanSource === 'hdb'
          ? HDB_CONCESSIONARY_INTEREST_RATE
          : exportInputs.interestRate,
        tenure: exportInputs.tenure,
        legalFee: exportInputs.legalFee,
        spDate: dateStr(new Date(exportInputs.spDate + 'T00:00:00')),
        citizenship: exportInputs.citizenship,
        propertyCount: exportInputs.propertyCount,
        ftaEligible: exportInputs.ftaEligible,
        additionalCash: exportInputs.additionalCash,
        cashProceedFromSelling: isSimpleMode ? 0 : cashProceedFromSelling,
        cpf: exportInputs.cpf,
        additionalCpf: exportInputs.additionalCpf,
        bsdByCpf: isSimpleMode ? false : bsdByCpf,
        absdByCpf: isSimpleMode ? false : absdByCpf,
        legalFeeByCpf: isSimpleMode ? false : legalFeeByCpf,
        absdRatePctOverride: isSimpleMode ? simpleAbsdRatePct : absdRatePct,
        primary: exportInputs.primary,
        includeAdditionalBorrower: exportInputs.includeAdditionalBorrower,
        additional: exportInputs.additional,
        additionalCitizenship,
        additionalPropertyCount,
        additionalFtaEligible,
        hdbLoanSource: hdbInputs.loanSource,
        hdbValuationPrice: hdbInputs.valuationPrice,
        hdbInterestRate: HDB_CONCESSIONARY_INTEREST_RATE,
      },
      results: {
        maxLoan,
        maxPriceLoan,
        derivedLoan,
        loanShortfall,
        availableTDSR,
        totalRecognisedIncome,
        totalExistingDebt,
        stressTestRate,
        effectivePledged,
        effectiveUnpledged,
      },
      computedStages: dpStagesPdf,
      completionInstallment: stagedDownPayment ? finalLoanStage(exportComputedStages)?.monthlyInstallment : undefined,
      scheduleRows,
      scheduleGroups: exportScheduleGroups,
      investmentTimeline,
      fundSummary,
      profileName,
      primaryBorrowerName: profiles[0]?.Name ?? undefined,
      additionalBorrowerName: profiles.length > 1 ? profiles[1]?.Name ?? undefined : undefined,
      agentInfo: currentUser ?? undefined,
    }).catch((err) => {
      // eslint-disable-next-line no-console
      console.error('Failed to export buying calculator PDF', err);
    });
  }, [tenureStretched,
    calculatorMode, propertyTab, privateSubTab, ecPaymentScheme, computedStages, simpleComputedStages, inputs, simpleOutputInputs, ltvDecimal,
    bridgingApplies, bridgingForLoan, sblHasHdb, sblValuation, sblOutstandingLoan, sblLevy, sblMax,
    derivedLoan, maxLoan, maxPriceLoan, loanShortfall, availableTDSR, totalRecognisedIncome,
    totalExistingDebt, stressTestRate, msrApplies, maxPriceLtv, effectivePledged, effectiveUnpledged,
    bsdByCpf, absdByCpf, legalFeeByCpf, cashProceedFromSelling, hdbInputs, otpDeposit,
    hdbCashAlloc, hdbCpfAlloc, absdAmount, absdRatePct, simpleAbsdAmount, simpleAbsdRatePct, bsd, iwaa, maxTenureCap, derivedLtvPct,
    minCash, maxLtvPct, cashDownAlloc, cpfDownAlloc, resaleLevy,
    monthlyInstallment, ecHousingGrant, effectivePledged, effectiveUnpledged, currentUser, getSelectedProfiles,
    selectedProfile, getSelectedName,
    additionalCitizenship, additionalPropertyCount, additionalFtaEligible,
    exerciseDateOverride, sp15DateOverride, resaleDates, hdbDateOverrides, showTimelineDates,
    scheduleGroups,
  ]);

  const renderHdbSchedule = () => {
    const derivedHdbLoan = calculatorMode === 'simple'
      ? derivedLoan
      : hdbInputs.loanSource === 'hdb'
        ? Math.max(0, inputs.purchasePrice - hdbResaleFinancing.mandatoryCash - hdbCashAlloc - hdbCpfAlloc)
        : hdbInputs.loanSource === 'bank' ? derivedLoan : 0;

    return (
      <Box data-tutorial="payment-schedule">
      <HdbSchedule
        h={{
          ...hdbInputs,
          purchasePrice: inputs.purchasePrice,
          tenure: inputs.tenure,
          hdbInterestRate: HDB_CONCESSIONARY_INTEREST_RATE,
          bankInterestRate: inputs.interestRate,
          hdbLoanAmount: derivedHdbLoan,
          cpfOa: inputs.cpf + inputs.additionalCpf,
          legalFee: inputs.legalFee,
          otpDate: inputs.spDate,
        }}
        fmt={fmt}
        cardBg={cardBg}
        cardBorder={cardBorder}
        onExplain={setCalcDialog}
        absdAmount={absdAmount}
        absdRatePct={absdRatePct}
        derivedLoanAmt={derivedHdbLoan}
        cashAlloc={hdbCashAlloc}
        cpfAlloc={hdbCpfAlloc}
        onOtpDateChange={(iso) => { updateInput('spDate', iso); clearHdbMilestonePins(); }}
        dateOverrides={hdbDateOverrides}
        onMilestoneDateChange={pinHdbMilestone}
        showDates={showTimelineDates}
        onShowDatesChange={setShowTimelineDates}
      />
      </Box>
    );
  };

  return (
    <Box sx={{ p: { xs: 0.5, sm: 1, md: 1.5 }, width: '100%', maxWidth: 1400, mx: 'auto' }}>
      {/* Header */}
      <Box sx={{ mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Typography variant={isMobile ? 'h5' : 'h4'} fontWeight={700}>
            Super Buying Calculator
          </Typography>
          <TutorialLaunchButton controller={tutorial} label="the Buying Calculator" />
          <WhatsNewLaunchButton controller={whatsNew} label="the Buying Calculator" />
        </Box>
        <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
          Payment breakdown & progressive schedule for property purchase
        </Typography>
      </Box>

      {/* Load Example / History + Actions */}
      <Box data-tutorial="load-example">
      <HistoryToolbar
        analyticsCalculator="buying_calculator"
        onExampleLoad={(_key, profiles) => applySharedProfilesToBuying(profiles)}
        onHistoryLoad={(entry) => {
          if (!entry.purchasePlannerState?.buyingCalculatorState && entry.profiles?.length) {
            applySharedProfilesToBuying(entry.profiles);
          }
        }}
        onSaveToProfile={saveInputsToProfile}
        onBuildNewProfiles={buildNewProfilesFromInputs}
        onBeforeProfileCreate={beginProfileSaveBack}
        onAfterProfileCreate={endProfileSaveBack}
        extraButtons={
          <>
            <Button
              variant="outlined"
              color="error"
              size="small"
              startIcon={<RestartAltIcon />}
              onClick={() => setClearDialogOpen(true)}
            >
              Clear
            </Button>
            <ExportPdfButton onExport={handleExportPdf} />
          </>
        }
      />

      </Box>

      {/* Property Type Tabs + Scroll-mode control */}
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
        <Tabs
          data-tutorial="property-tabs"
          value={propertyTab}
          onChange={(_, v) => {
            handlePropertyTabChange(v);
            logCalculatorInteraction('buying_calculator', 'tab_switch', { tab: v });
          }}
          sx={{
            '& .MuiTab-root': { fontWeight: 700, fontSize: '0.9rem' },
            '& .Mui-selected': { color: '#D97706 !important' },
            '& .MuiTabs-indicator': { bgcolor: '#D97706' },
          }}
        >
          <Tab label="Private" value="private" />
          <Tab label="EC" value="ec" />
          <Tab label="HDB" value="hdb" />
        </Tabs>
        <Box data-tutorial="display-controls" sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          {/* Text size — smaller / larger (shared across calculators) */}
          <TextSizeControl />
          {!isMobile && (
            <ToggleButtonGroup
              size="small"
              exclusive
              value={splitScroll ? 'split' : 'single'}
              onChange={(_, v) => { if (v !== null) setSplitScroll(v === 'split'); }}
              sx={{
                '& .MuiToggleButton-root': { textTransform: 'none', fontWeight: 600, fontSize: '0.78rem', px: 1.25, py: 0.5 },
                '& .Mui-selected': { color: '#D97706 !important', bgcolor: 'rgba(217,119,6,0.08) !important' },
              }}
            >
              <Tooltip title="Page scrolls as one column">
                <ToggleButton value="single">1 Scroll</ToggleButton>
              </Tooltip>
              <Tooltip title="Inputs and results scroll independently">
                <ToggleButton value="split">2 Scroll</ToggleButton>
              </Tooltip>
            </ToggleButtonGroup>
          )}
        </Box>
      </Box>

      {(propertyTab === 'private' || propertyTab === 'ec' || propertyTab === 'hdb') && (
        <>
          {/* Main 2-Column Layout */}
          <Grid container spacing={3} sx={{ alignItems: 'flex-start' }}>
            {/* ── LEFT: Inputs ── */}
            <Grid item xs={12} md={5}>
              <Box sx={columnScrollSx}>
                {/* Profile Banner */}
                <ProfileBanner />

                {/* PAYMENT BREAKDOWN header — standalone band */}
                <Box
                  sx={{
                    mb: 2, px: 3, py: 1.75,
                    bgcolor: propertyTab === 'ec' ? '#0ea5e9' : '#D97706',
                    borderRadius: 2,
                  }}
                >
                  <Typography variant="subtitle1" fontWeight={800} sx={{ color: '#fff', textAlign: 'center', letterSpacing: '0.1em' }}>
                    {propertyTab === 'ec' ? 'EC PAYMENT BREAKDOWN' : propertyTab === 'hdb' ? 'HDB PAYMENT BREAKDOWN' : 'PAYMENT BREAKDOWN'}
                  </Typography>
                </Box>

                {/* ── Calculator Mode ── */}
                <Box data-tutorial="calculator-mode" sx={{ mb: 2, display: 'flex', alignItems: { xs: 'stretch', sm: 'center' }, justifyContent: 'space-between', gap: 1.5, flexDirection: { xs: 'column', sm: 'row' } }}>
                  <Box>
                    <Typography variant="body2" fontWeight={800} sx={{ fontSize: '0.85rem', color: 'text.primary' }}>
                      Calculator Mode
                    </Typography>
                  </Box>
                  <ToggleButtonGroup
                    value={calculatorMode}
                    exclusive
                    onChange={(_, v) => {
                      if (!v) return;
                      setCalculatorMode(v);
                      logCalculatorInteraction('buying_calculator', 'mode_switch', { mode: v });
                    }}
                    size="small"
                    sx={{
                      alignSelf: { xs: 'stretch', sm: 'center' },
                      '& .MuiToggleButton-root': { px: 2.5, py: 0.5, fontWeight: 700, fontSize: '0.78rem', flex: { xs: 1, sm: 'initial' } },
                      '& .Mui-selected': { bgcolor: `${highlightColor} !important`, color: '#fff !important', borderColor: `${highlightColor} !important` },
                    }}
                  >
                    <ToggleButton value="simple">Simple</ToggleButton>
                    <ToggleButton value="detailed">Detailed</ToggleButton>
                  </ToggleButtonGroup>
                </Box>

                {/* ── EC Scheme — only the current framework is modelled; the 8 May 2026
                    framework is shown disabled with an explainer. ── */}
                {propertyTab === 'ec' && (
                  <Box sx={{ mb: 2, display: 'flex', alignItems: { xs: 'stretch', sm: 'center' }, justifyContent: 'space-between', gap: 1.5, flexDirection: { xs: 'column', sm: 'row' } }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Typography variant="body2" fontWeight={800} sx={{ fontSize: '0.85rem', color: 'text.primary' }}>
                        Scheme
                      </Typography>
                      {renderFieldInfoIcon(EC_SCHEME_COMPARE_INFO)}
                    </Box>
                    <ToggleButtonGroup
                      value="current"
                      exclusive
                      // New Scheme isn't selectable yet — tapping it opens the explainer
                      // with the effective date and what changes.
                      onChange={(_, v) => { if (v === 'new') setFieldInfo(EC_NEW_SCHEME_INFO); }}
                      size="small"
                      sx={{
                        alignSelf: { xs: 'stretch', sm: 'center' },
                        '& .MuiToggleButton-root': { px: 2.5, py: 0.5, fontWeight: 700, fontSize: '0.78rem', flex: { xs: 1, sm: 'initial' } },
                        '& .Mui-selected': { bgcolor: `${highlightColor} !important`, color: '#fff !important', borderColor: `${highlightColor} !important` },
                      }}
                    >
                      <ToggleButton value="current">Current Scheme</ToggleButton>
                      <ToggleButton value="new" sx={{ color: 'text.disabled', borderStyle: 'dashed' }}>New Scheme</ToggleButton>
                    </ToggleButtonGroup>
                  </Box>
                )}

                {/* ── Property Section ── */}
                <Card data-tutorial="property-section" variant="outlined" sx={sectionCardSx}>
                  {renderSectionHeader('Property', { step: 1 })}
                  <Box sx={sectionBodySx}>

                  {/* Sub-tabs: New Launch / Resale (private only) */}
                  {propertyTab === 'private' && (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                      <Typography variant="body2" fontWeight={700} sx={{ fontSize: '0.82rem', color: 'text.primary' }}>
                        Property Type
                      </Typography>
                      <ToggleButtonGroup
                        value={privateSubTab}
                        exclusive
                        onChange={(_, v) => {
                          if (!v) return;
                          setPrivateSubTab(v);
                          logCalculatorInteraction('buying_calculator', 'tab_switch', { tab: `private_${v}` });
                        }}
                        size="small"
                        sx={{
                          '& .MuiToggleButton-root': { px: 2.5, py: 0.5, fontWeight: 600, fontSize: '0.75rem' },
                          '& .Mui-selected': { bgcolor: `${highlightColor} !important`, color: '#fff !important', borderColor: `${highlightColor} !important` },
                        }}
                      >
                        <ToggleButton value="new-launch">New Launch</ToggleButton>
                        <ToggleButton value="resale">Resale</ToggleButton>
                      </ToggleButtonGroup>
                    </Box>
                  )}

                  {/* Purchase Price */}
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" fontWeight={700} sx={{ fontSize: '0.82rem', color: 'text.primary', mb: 0.75 }}>Purchase Price</Typography>
                    <CurrencyTextField
                      value={inputs.purchasePrice}
                      onChange={(v) => { setPurchasePriceUserAdjusted(true); updateInput('purchasePrice', v); }}
                      highlightColor={highlightColor}
                    />
                  </Box>

                  {/* Valuation Price (HDB) — moved here from the Valuation section */}
                  {calculatorMode === 'detailed' && propertyTab === 'hdb' && (
                    <Box sx={{ mb: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.75 }}>
                        <Typography variant="body2" fontWeight={700} sx={{ fontSize: '0.82rem', color: 'text.primary' }}>Valuation Price</Typography>
                        {hdbValuationUserSet && (
                          <Typography variant="caption"
                            onClick={() => { setHdbValuationUserSet(false); updateHdb('valuationPrice', inputs.purchasePrice); }}
                            sx={{ cursor: 'pointer', color: highlightColor, textDecoration: 'underline' }}
                          >
                            Reset to purchase price
                          </Typography>
                        )}
                      </Box>
                      <CurrencyTextField value={hdbInputs.valuationPrice} onChange={(v) => { setHdbValuationUserSet(true); updateHdb('valuationPrice', v); }} highlightColor={highlightColor} />
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', px: 0.5, mt: 0.75 }}>
                        <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.82rem' }}>Cash Over Value (COV)</Typography>
                        <Typography variant="body2" fontWeight={700}>
                          {fmt(Math.max(0, inputs.purchasePrice - hdbInputs.valuationPrice))}
                        </Typography>
                      </Box>
                    </Box>
                  )}

                  {/* EC Payment Scheme (NPS / DPS) */}
                  {propertyTab === 'ec' && (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography variant="body2" fontWeight={500}>Payment Scheme</Typography>
                        <Tooltip
                          title={
                            <Typography variant="body2" component="span" sx={{ display: 'block', maxWidth: 360, whiteSpace: 'pre-line' }}>
                              {`Normal Progressive Scheme (NPS):\nStandard scheme where buyers pay progressively as construction milestones are completed (foundation, framework, brick walls, etc.). Suitable for buyers who want to spread payments over construction.\n\nDeferred Payment Scheme (DPS):\nShorter scheme — buyers pay 5% on OTP, 15% on S&P signing (within 9 weeks), then defer 65% until TOP and the final 15% at CSC. No payments during construction. Often offered as an incentive at later launch stages.`}
                            </Typography>
                          }
                          arrow placement="top" enterTouchDelay={0}
                        >
                          <IconButton size="small" sx={{ p: 0.25 }}>
                            <InfoOutlinedIcon fontSize="small" color="primary" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                      <ToggleButtonGroup
                        value={ecPaymentScheme}
                        exclusive
                        onChange={(_, v) => { if (v) setEcPaymentScheme(v); }}
                        size="small"
                        sx={{
                          '& .MuiToggleButton-root': { px: 2, py: 0.25, fontWeight: 600, fontSize: '0.75rem' },
                          '& .Mui-selected': { bgcolor: '#0ea5e9 !important', color: '#fff !important', borderColor: '#0ea5e9 !important' },
                        }}
                      >
                        <ToggleButton value="NPS">NPS</ToggleButton>
                        <ToggleButton value="DPS">DPS</ToggleButton>
                      </ToggleButtonGroup>
                    </Box>
                  )}

                  {/* HDB resale financing choice applies in both Simple and Detailed modes. */}
                  {propertyTab === 'hdb' && (
                    <Box sx={{ mb: 1.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <Typography variant="body2" fontWeight={500}>Loan Type</Typography>
                        {renderFieldInfoIcon({
                          title: 'HDB resale financing options',
                          body: `Bank loan\n• Up to 75% of the lower of purchase price or valuation\n• At least 5% of that valuation base must be paid in cash; the remaining down payment may use CPF/cash\n• Bank rates may change and packages may have lock-in or early-repayment conditions\n\nHDB loan\n• Subject to HFE eligibility; up to 75% of the lower of resale price or valuation\n• The 25% down payment may use CPF/cash\n• Current concessionary rate: 2.60% p.a. (pegged 0.10% above CPF OA)\n\nNo loan\n• Purchase price is funded with CPF/cash\n\nFor every option, resale OTP fees and any Cash Over Valuation (COV) are cash-only.`,
                          url: 'https://www.cpf.gov.sg/member/infohub/educational-resources/3-differences-between-hdb-loan-and-bank-loan',
                        })}
                      </Box>
                      <ToggleButtonGroup
                        value={hdbInputs.loanSource}
                        exclusive
                        onChange={(_, v) => { if (v) updateHdb('loanSource', v); }}
                        size="small"
                        fullWidth
                        sx={{
                          '& .MuiToggleButton-root': { fontWeight: 600, fontSize: '0.75rem', py: 0.5 },
                          '& .Mui-selected': { bgcolor: '#D97706 !important', color: '#fff !important', borderColor: '#D97706 !important' },
                        }}
                      >
                        <ToggleButton value="bank">Bank</ToggleButton>
                        <ToggleButton value="hdb">HDB Loan</ToggleButton>
                        <ToggleButton value="none">No Loan</ToggleButton>
                      </ToggleButtonGroup>
                    </Box>
                  )}

                  </Box>
                </Card>

                {calculatorMode === 'simple' ? (
                  propertyTab === 'hdb' && hdbInputs.loanSource === 'none' ? null : (
                  <Card data-tutorial="loan-section" variant="outlined" sx={sectionCardSx}>
                    {renderSectionHeader('Loan', { step: 2 })}
                    <Box sx={sectionBodySx}>
                      <Box sx={{ mb: 2 }}>
                        {groupIwaaLoaded ? (
                          // Group profile loaded → the single age input becomes the income-
                          // weighted average age (IWAA), seeded from both borrowers but still
                          // adjustable. Nudging it feeds the tenure-cap math; it re-derives when
                          // the borrowers change (detailed mode) or a different profile loads.
                          <>
                            <EditableSliderValueRow
                              label={<Typography variant="body2" fontWeight={700} sx={{ fontSize: '0.82rem', color: 'text.primary' }}>IWAA</Typography>}
                              value={iwaa}
                              min={18}
                              max={75}
                              step={1}
                              onCommit={(v) => setIwaaOverride(v)}
                              formatDisplay={(v) => `${v} yrs`}
                              valueColor={highlightColor}
                            />
                            <Box sx={{ pl: 1.5, pr: 3, overflow: 'hidden' }}>
                              <IntentGuardedSlider
                                isMobile={isMobile}
                                value={iwaa}
                                min={18}
                                max={75}
                                step={1}
                                onChange={(_, v) => setIwaaOverride(v as number)}
                                sx={{ color: highlightColor, height: 6 }}
                              />
                            </Box>
                            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                              {iwaaOverride != null && iwaaOverride !== computedIwaa ? (
                                <>
                                  Adjusted — income-weighted average is {computedIwaa} yrs (ages {inputs.primary.age} &amp; {inputs.additional.age}).{' '}
                                  <Box
                                    component="span"
                                    onClick={() => setIwaaOverride(null)}
                                    sx={{ cursor: 'pointer', color: highlightColor, textDecoration: 'underline' }}
                                  >
                                    Reset
                                  </Box>
                                </>
                              ) : (
                                <>Income-weighted average age of both borrowers (ages {inputs.primary.age} &amp; {inputs.additional.age})</>
                              )}
                            </Typography>
                          </>
                        ) : (
                          <>
                            <EditableSliderValueRow
                              label={<Typography variant="body2" fontWeight={700} sx={{ fontSize: '0.82rem', color: 'text.primary' }}>Age / IWAA</Typography>}
                              value={inputs.primary.age}
                              min={18}
                              max={75}
                              step={1}
                              onCommit={(v) => updatePrimary('age', v)}
                              formatDisplay={(v) => `${v} yrs`}
                              valueColor={highlightColor}
                            />
                            <Box sx={{ pl: 1.5, pr: 3, overflow: 'hidden' }}>
                              <IntentGuardedSlider
                                isMobile={isMobile}
                                value={inputs.primary.age}
                                min={18}
                                max={75}
                                step={1}
                                onChange={(_, v) => updatePrimary('age', v as number)}
                                sx={{ color: highlightColor, height: 6 }}
                              />
                            </Box>
                          </>
                        )}
                      </Box>

                      <Box sx={{ mb: 2 }}>
                        <EditableSliderValueRow
                          label={<Typography variant="body2" fontWeight={700} sx={{ fontSize: '0.82rem', color: 'text.primary' }}>Loan Tenure</Typography>}
                          value={inputs.tenure}
                          min={1}
                          max={Math.max(1, maxTenureCap)}
                          step={1}
                          onCommit={(v) => requestTenureChange(v)}
                          formatDisplay={(v) => `${v} yrs`}
                          valueColor={highlightColor}
                        />
                        <Box sx={{ pl: 1.5, pr: 3, overflow: 'hidden' }}>
                          <IntentGuardedSlider
                            isMobile={isMobile}
                            value={inputs.tenure}
                            min={1}
                            max={Math.max(1, maxTenureCap)}
                            step={1}
                            onChange={(_, v) => requestTenureChange(v as number)}
                            sx={{ color: highlightColor, height: 6 }}
                          />
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="caption" sx={{ color: 'text.disabled' }}>1 yr</Typography>
                          <Typography variant="caption" sx={{ color: 'text.disabled' }}>Cap: {maxTenureCap} yrs</Typography>
                        </Box>
                      </Box>

                      {propertyTab === 'hdb' && hdbInputs.loanSource === 'hdb' ? (
                        <Box sx={{ mb: 0.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <Typography variant="body2" fontWeight={700} sx={{ fontSize: '0.82rem', color: 'text.primary' }}>
                            HDB Loan Interest Rate
                          </Typography>
                          <Typography variant="body2" fontWeight={700} sx={{ color: highlightColor }}>
                            {HDB_CONCESSIONARY_INTEREST_RATE.toFixed(2)}%
                          </Typography>
                        </Box>
                      ) : (
                      <Box sx={{ mb: 0.5 }}>
                        <EditableSliderValueRow
                          label={<Typography variant="body2" fontWeight={700} sx={{ fontSize: '0.82rem', color: 'text.primary' }}>Bank Rate</Typography>}
                          value={inputs.interestRate}
                          min={0.5}
                          max={6.0}
                          step={0.01}
                          onCommit={(v) => updateInput('interestRate', v)}
                          formatDisplay={(v) => `${v.toFixed(2)}%`}
                          valueColor={highlightColor}
                          commitMax={15}
                        />
                        <Box sx={{ pl: 1.5, pr: 3, overflow: 'hidden' }}>
                          <IntentGuardedSlider
                            isMobile={isMobile}
                            value={inputs.interestRate}
                            min={0.5}
                            max={6.0}
                            step={0.05}
                            onChange={(_, v) => updateInput('interestRate', v as number)}
                            sx={{ color: highlightColor, height: 6 }}
                          />
                        </Box>
                      </Box>
                      )}
                    </Box>
                  </Card>
                  )
                ) : (
                  <>
                {/* ── Borrower Section ── */}
                <Card data-tutorial="borrower-section" variant="outlined" sx={sectionCardSx}>
                  {renderSectionHeader('Borrower', { step: 2 })}
                  <Box sx={sectionBodySx}>

                  <BuyerBorrowerSection
                    title={borrowerName(inputs.primary) ? `Primary Borrower - ${borrowerName(inputs.primary)}` : 'Primary Borrower'}
                    borrower={inputs.primary}
                    onUpdate={updatePrimary}
                    cardBg={cardBg}
                    cardBorder={cardBorder}
                    highlightColor={highlightColor}
                    isMobile={isMobile}
                    fmt={fmt}
                    onRemove={inputs.includeAdditionalBorrower ? () => setRemovePrimaryDialogOpen(true) : undefined}
                    citizenship={inputs.citizenship}
                    ftaEligible={inputs.ftaEligible}
                    propertyCount={inputs.propertyCount}
                    onCitizenshipChange={(v) => { updateInput('citizenship', v); if (v === 'SC') updateInput('ftaEligible', false); }}
                    onFtaEligibleChange={(v) => updateInput('ftaEligible', v)}
                    onPropertyCountChange={(v) => updateInput('propertyCount', v)}
                    hideThirdProperty={propertyTab === 'ec'}
                  />

                  {/* Add additional borrower (only when none yet) */}
                  {!inputs.includeAdditionalBorrower && (
                    <Box sx={{ mb: 2 }}>
                      <Button
                        fullWidth
                        variant="outlined"
                        startIcon={<AddIcon />}
                        onClick={() => updateInput('includeAdditionalBorrower', true)}
                        sx={{
                          py: 1.25,
                          borderStyle: 'dashed',
                          borderWidth: 2,
                          borderColor: highlightColor,
                          color: highlightColor,
                          fontWeight: 700,
                          textTransform: 'none',
                          fontSize: '0.9rem',
                          '&:hover': {
                            borderStyle: 'dashed',
                            borderWidth: 2,
                            borderColor: highlightColor,
                            bgcolor: isDark ? 'rgba(96,165,250,0.08)' : 'rgba(217,119,6,0.06)',
                          },
                        }}
                      >
                        Add additional borrower
                      </Button>
                    </Box>
                  )}

                  {inputs.includeAdditionalBorrower && (
                    <BuyerBorrowerSection
                      title={borrowerName(inputs.additional) ? `Additional Borrower - ${borrowerName(inputs.additional)}` : 'Additional Borrower'}
                      borrower={inputs.additional}
                      onUpdate={updateAdditional}
                      cardBg={cardBg}
                      cardBorder={cardBorder}
                      highlightColor={highlightColor}
                      isMobile={isMobile}
                      fmt={fmt}
                      onRemove={() => setRemoveBorrowerDialogOpen(true)}
                      citizenship={additionalCitizenship}
                      ftaEligible={additionalFtaEligible}
                      propertyCount={additionalPropertyCount}
                      onCitizenshipChange={(v) => { setAdditionalCitizenship(v); if (v === 'SC') setAdditionalFtaEligible(false); }}
                      onFtaEligibleChange={(v) => setAdditionalFtaEligible(v)}
                      onPropertyCountChange={(v) => setAdditionalPropertyCount(v)}
                      hideThirdProperty={propertyTab === 'ec'}
                    />
                  )}

                  {/* Relationship — shown whenever there is a joint borrower. Mirrors the
                      Profile Group "Relationship" field. Drives the joint ABSD assessment
                      (married-couple tables / remission) and, for EC & HDB, the
                      eligibility scheme. */}
                  {inputs.includeAdditionalBorrower && (
                    <Card variant="outlined" sx={{ p: 2, bgcolor: cardBg, borderColor: cardBorder, mb: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.75 }}>
                        <Typography variant="body2" fontWeight={700} sx={{ fontSize: '0.82rem', color: 'text.primary' }}>
                          Relationship
                        </Typography>
                        <Tooltip
                          title={
                            <Typography variant="body2" component="span" sx={{ display: 'block', maxWidth: 320, whiteSpace: 'pre-line' }}>
                              {(propertyTab === 'hdb'
                                ? 'Determines the HDB eligibility scheme:\n• Married Couple — at least 1 Singapore Citizen (spouse SC/PR → Public Scheme; non-citizen spouse → Non-Citizen Spouse Scheme); everyone ≥ 21.\n• Individual (Joint Singles) — everyone must be a Singapore Citizen and ≥ 35.\n\n'
                                : propertyTab === 'ec'
                                  ? 'Determines the EC eligibility scheme:\n• Married Couple — at least 1 Singapore Citizen, the other SC or PR; everyone ≥ 21.\n• Individual (Joint Singles) — everyone must be a Singapore Citizen and ≥ 35.\n\n'
                                  : '')
                                + (propertyTab === 'ec'
                                  ? 'No ABSD is payable on an EC purchase.'
                                  : 'ABSD for joint purchases:\n• Married Couple — follows the IRAS married-couple joint-purchase tables; spousal remission can apply (e.g. first home with at least one SC spouse).\n• Individual — ABSD is based on the borrower profile with the higher rate.')}
                            </Typography>
                          }
                          arrow placement="top" enterTouchDelay={0}
                        >
                          <IconButton size="small" sx={{ p: 0.25 }}><InfoOutlinedIcon fontSize="small" color="primary" /></IconButton>
                        </Tooltip>
                      </Box>
                      <RadioGroup
                        row
                        value={borrowerRelationship}
                        onChange={(e) => setBorrowerRelationship(e.target.value as BorrowerRelationship)}
                      >
                        {(Object.keys(RELATIONSHIP_LABELS) as BorrowerRelationship[]).map((key) => (
                          <FormControlLabel
                            key={key} value={key}
                            control={<Radio size="small" sx={{ color: highlightColor, '&.Mui-checked': { color: highlightColor } }} />}
                            label={<Typography variant="body2">{RELATIONSHIP_LABELS[key]}</Typography>}
                          />
                        ))}
                      </RadioGroup>
                      {/* Joint ABSD outcome (mirrors AffordabilityCalculator) */}
                      {propertyTab !== 'ec' && (
                      <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: absdRemissionApplied ? (isDark ? '#4ade80' : '#2e7d32') : 'text.secondary' }}>
                        {borrowerRelationship === 'SPOUSE'
                          ? absdRemissionApplied
                            ? `Married couple remission applied — ABSD reduced from ${absdRateBeforeRemission}% to ${absdRatePct}% (saving ${fmt(absdRemissionAmount)}).`
                            : `Married joint purchase — ABSD ${absdRatePct}% per the IRAS joint-purchase tables (based on both spouses' profiles).`
                          : `Joint purchase — ABSD ${absdRatePct}% (the borrower profile with the higher rate applies).`}
                      </Typography>
                      )}
                    </Card>
                  )}

                  {/* CPF retirement-sum requirement for a private second/subsequent
                      property. This is advisory because the calculator does not collect
                      each buyer's RA/SA balances or existing-property lease coverage. */}
                  {propertyTab === 'private'
                    && (inputs.propertyCount !== '1st'
                      || (inputs.includeAdditionalBorrower && additionalPropertyCount !== '1st')) && (
                    <Box sx={{ mb: 2, p: 1.25, borderRadius: 1.5, bgcolor: 'rgba(217,119,6,0.08)', border: '1px solid', borderColor: '#D97706', display: 'flex', alignItems: 'flex-start', gap: 0.75 }}>
                      <WarningAmberIcon sx={{ color: '#D97706', fontSize: 18, flexShrink: 0, mt: 0.15 }} />
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="caption" sx={{ color: '#B45309', fontWeight: 800, display: 'block', mb: 0.5 }}>
                          CPF usage for a second or subsequent property — please review
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#B45309', display: 'block', mb: 0.5 }}>
                          • You may use only the OA savings remaining after setting aside the applicable retirement sum, subject to the CPF housing limit for this property.
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#B45309', display: 'block', mb: 0.5 }}>
                          • Set aside the <Box component="span" sx={{ fontWeight: 800 }}>Basic Retirement Sum (BRS)</Box> if you have at least one property that can last you until age 95.
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#B45309', display: 'block' }}>
                          • Otherwise, you must set aside the <Box component="span" sx={{ fontWeight: 800 }}>Full Retirement Sum (FRS)</Box>. Savings from your Retirement Account or Special Account, and OA, can be used to meet the requirement.
                        </Typography>
                        <Link
                          href={CPF_SUBSEQUENT_PROPERTY_URL}
                          target="_blank"
                          rel="noopener noreferrer"
                          variant="caption"
                          sx={{ color: '#B45309', fontWeight: 700, display: 'block', mt: 0.75 }}
                        >
                          CPF usage for second or subsequent property ↗
                        </Link>
                      </Box>
                    </Box>
                  )}

                  {/* EC eligibility warnings — income ceiling, citizenship & age. */}
                  {ecEligibility.active && ecEligibility.warnings.length > 0 && (
                    <Box sx={{ mb: 2, p: 1.25, borderRadius: 1.5, bgcolor: 'rgba(217,119,6,0.08)', border: '1px solid', borderColor: '#D97706', display: 'flex', alignItems: 'flex-start', gap: 0.75 }}>
                      <WarningAmberIcon sx={{ color: '#D97706', fontSize: 18, flexShrink: 0, mt: 0.15 }} />
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="caption" sx={{ color: '#B45309', fontWeight: 800, display: 'block', mb: 0.5 }}>
                          EC eligibility — please review
                        </Typography>
                        {ecEligibility.warnings.map((w, i) => (
                          <Typography key={i} variant="caption" sx={{ color: '#B45309', display: 'block', mb: i < ecEligibility.warnings.length - 1 ? 0.5 : 0 }}>
                            • {w}
                          </Typography>
                        ))}
                        <Box sx={{ mt: 0.5 }}>
                          <Typography variant="caption" sx={{ color: '#B45309', display: 'block', fontWeight: 700 }}>
                            Applicable EC income ceiling based on when the site was awarded:
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#B45309', display: 'block' }}>
                            • Before 24 August 2026: {fmt(EC_INCOME_CEILING)}/month
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#B45309', display: 'block' }}>
                            • On or after 24 August 2026: {fmt(EC_INCOME_CEILING_NEW_SITES)}/month
                          </Typography>
                        </Box>
                        <Link
                          href={EC_ELIGIBILITY_URL}
                          target="_blank"
                          rel="noopener noreferrer"
                          variant="caption"
                          sx={{ color: '#B45309', fontWeight: 700, display: 'block', mt: 0.75 }}
                        >
                          HDB EC eligibility conditions ↗
                        </Link>
                      </Box>
                    </Box>
                  )}

                  {/* HDB flat eligibility — scheme, citizenship & age. */}
                  {hdbEligibility.active && (hdbEligibility.warnings.length > 0 || hdbEligibility.absd) && (
                    <Box sx={{ mb: 2, p: 1.25, borderRadius: 1.5, bgcolor: 'rgba(217,119,6,0.08)', border: '1px solid', borderColor: '#D97706', display: 'flex', alignItems: 'flex-start', gap: 0.75 }}>
                      <WarningAmberIcon sx={{ color: '#D97706', fontSize: 18, flexShrink: 0, mt: 0.15 }} />
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="caption" sx={{ color: '#B45309', fontWeight: 800, display: 'block', mb: 0.5 }}>
                          HDB eligibility — please review{hdbEligibility.scheme ? ` (${hdbEligibility.scheme})` : ''}
                        </Typography>
                        {hdbEligibility.warnings.map((w, i) => (
                          <Typography key={i} variant="caption" sx={{ color: '#B45309', display: 'block', mb: 0.5 }}>
                            • {w}
                          </Typography>
                        ))}
                        {hdbEligibility.absd && (
                          <Typography variant="caption" sx={{ color: '#B45309', display: 'block' }}>
                            • <Box component="span" sx={{ fontWeight: 800 }}>ABSD {hdbEligibility.absd.rate}% — {fmt(hdbEligibility.absd.amount)} payable</Box> on top of the Buyer&apos;s Stamp Duty: {hdbEligibility.absd.reason} Due within 14 days of exercising the Option — included in the outlay and schedule below.
                          </Typography>
                        )}
                        <Link
                          href={hdbEligibility.absd ? IRAS_ABSD_URL : HDB_RESALE_ELIGIBILITY_URL}
                          target="_blank"
                          rel="noopener noreferrer"
                          variant="caption"
                          sx={{ color: '#B45309', fontWeight: 700, display: 'block', mt: 0.75 }}
                        >
                          {hdbEligibility.absd ? 'IRAS ABSD rates ↗' : 'HDB flat eligibility conditions ↗'}
                        </Link>
                      </Box>
                    </Box>
                  )}

                  {/* HDB concessionary loan income ceiling — buyers above the ceiling must use a bank loan. */}
                  {hdbLoanEligibility.active && hdbLoanEligibility.warnings.length > 0 && (
                    <Box sx={{ mb: 2, p: 1.25, borderRadius: 1.5, bgcolor: 'rgba(217,119,6,0.08)', border: '1px solid', borderColor: '#D97706', display: 'flex', alignItems: 'flex-start', gap: 0.75 }}>
                      <WarningAmberIcon sx={{ color: '#D97706', fontSize: 18, flexShrink: 0, mt: 0.15 }} />
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="caption" sx={{ color: '#B45309', fontWeight: 800, display: 'block', mb: 0.5 }}>
                          HDB loan eligibility — please review
                        </Typography>
                        {hdbLoanEligibility.warnings.map((w, i) => (
                          <Typography key={i} variant="caption" sx={{ color: '#B45309', display: 'block', mb: 0.5 }}>
                            • {w}
                          </Typography>
                        ))}
                        <Link
                          href={HDB_LOAN_ELIGIBILITY_URL}
                          target="_blank"
                          rel="noopener noreferrer"
                          variant="caption"
                          sx={{ color: '#B45309', fontWeight: 700, display: 'block', mt: 0.75 }}
                        >
                          HDB housing loan eligibility conditions ↗
                        </Link>
                      </Box>
                    </Box>
                  )}
                  </Box>
                </Card>

                {/* ── Available Funds Section ── */}
                <Card data-tutorial="available-funds" variant="outlined" sx={sectionCardSx}>
                  {renderSectionHeader('Available Funds', { step: 3 })}
                  <Box sx={sectionBodySx}>

                  {/* Cash (savings) — summed from each borrower's own card */}
                  {renderBorrowerFundsRow('cash')}
                  <Divider sx={{ mb: 1, opacity: 0.5 }} />

                  {/* Additional Cash (Sales Proceed) */}
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, mb: 1 }}>
                    <Box sx={{ flex: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.82rem' }}>Additional Cash</Typography>
                        {/* Where the carried-over proceeds came from, and who was left out. */}
                        {sellerProceeds.length > 0 && (
                          <InfoOutlinedIcon
                            onClick={() => setCalcDialog(proceedsDialog('cash'))}
                            sx={{ fontSize: 15, color: 'primary.main', cursor: 'pointer', ml: 0.5 }}
                          />
                        )}
                        {/* HDB → HDB loan: explain the 50% cash proceeds rule that governs this figure. */}
                        {propertyTab === 'hdb' && hdbInputs.loanSource === 'hdb' && renderFieldInfoIcon(SECOND_HDB_LOAN_INFO)}
                      </Box>
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        Sales Proceed
                        {/* What the sale actually yielded, so a partial allocation above is obvious. */}
                        {sellingNetProceeds > 0 && (
                          <>
                            {' — '}
                            <Box component="span" sx={{ fontWeight: 700, color: sellingNetProceeds !== cashProceedFromSelling ? highlightColor : 'text.secondary' }}>
                              {fmt(sellingNetProceeds)}
                            </Box>
                            {' available'}
                          </>
                        )}
                      </Typography>
                    </Box>
                    <Box sx={{ width: 180, flexShrink: 0 }}>
                      <CurrencyTextField
                        value={cashProceedFromSelling}
                        onChange={(v) => { setCashProceedFromSelling(v); setProceedsRowsUserSet(true); }}
                        highlightColor={highlightColor}
                      />
                    </Box>
                  </Box>

                  {/* 2nd HDB loan — the minimum slice of these proceeds HDB requires in the purchase */}
                  {secondHdbLoanRule.active && (
                    <Box sx={{ mb: 1, p: 1.25, borderRadius: 1.5, bgcolor: 'rgba(217,119,6,0.08)', border: '1px solid', borderColor: '#D97706', display: 'flex', alignItems: 'flex-start', gap: 0.75 }}>
                      <WarningAmberIcon sx={{ color: '#D97706', fontSize: 18, flexShrink: 0, mt: 0.15 }} />
                      <Box sx={{ flex: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                          <Typography variant="caption" sx={{ color: '#B45309', fontWeight: 800 }}>
                            2nd HDB loan — 50% cash proceeds rule
                          </Typography>
                          {renderFieldInfoIcon(SECOND_HDB_LOAN_INFO)}
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                          <Typography variant="caption" sx={{ color: '#B45309' }}>Cash proceeds from sale</Typography>
                          <Typography variant="caption" sx={{ color: '#B45309' }}>{fmt(secondHdbLoanRule.proceeds)}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                          <Typography variant="caption" sx={{ color: '#B45309' }}>
                            You may keep {secondHdbLoanRule.mustUse > 0
                              ? `(greater of ${fmt(SECOND_HDB_LOAN_MIN_RETAINED)} or 50%)`
                              : `(within the ${fmt(SECOND_HDB_LOAN_MIN_RETAINED)} floor — keep it all)`}
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#B45309' }}>{fmt(secondHdbLoanRule.retained)}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, mt: 0.25, pt: 0.25, borderTop: '1px dashed', borderColor: 'rgba(180,83,9,0.4)' }}>
                          <Typography variant="caption" fontWeight={800} sx={{ color: '#B45309' }}>Minimum to use for this purchase</Typography>
                          <Typography variant="caption" fontWeight={800} sx={{ color: '#B45309' }}>{fmt(secondHdbLoanRule.mustUse)}</Typography>
                        </Box>
                        {secondHdbLoanShortfall > 0 ? (
                          <Box sx={{ mt: 0.5, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.75 }}>
                            <Typography variant="caption" sx={{ color: 'error.main', fontWeight: 700 }}>
                              &quot;Cash proceeds to use&quot; is set to {fmt(hdbInputs.secondHdbLoanCashSetAside)} — {fmt(secondHdbLoanShortfall)} short of the minimum.
                            </Typography>
                            <Button
                              size="small"
                              onClick={() => { setSecondHdbLoanSetAsideUserSet(false); updateHdb('secondHdbLoanCashSetAside', secondHdbLoanRule.mustUse); }}
                              sx={{ color: '#B45309', fontWeight: 700, minWidth: 0, px: 0.75, py: 0 }}
                            >
                              Use {fmt(secondHdbLoanRule.mustUse)}
                            </Button>
                          </Box>
                        ) : (
                          <Typography variant="caption" sx={{ color: '#B45309', display: 'block', mt: 0.5, fontStyle: 'italic' }}>
                            Carried into &quot;Cash proceeds to use&quot; under HDB loan details — it goes into the flat and reduces the loan, so it is not counted as spendable reserves.
                          </Typography>
                        )}
                        {/* Measured on the full proceeds, so a trimmed Additional Cash row can't cover it. */}
                        {secondHdbLoanUnallocated > 0 && (
                          <Typography variant="caption" sx={{ color: 'error.main', fontWeight: 700, display: 'block', mt: 0.5 }}>
                            Additional Cash is set to {fmt(cashProceedFromSelling)} — {fmt(secondHdbLoanUnallocated)} less than HDB requires from these proceeds. Raise it to at least {fmt(secondHdbLoanRule.mustUse)}.
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  )}
                  {/* Resale Levy — EC: paid out of the sale proceeds, so it reduces cash available */}
                  {propertyTab === 'ec' && resaleLevy > 0 && (
                    <Box sx={{ pl: 1.5, mb: 1 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.25 }}>
                        <Typography variant="caption" sx={{ color: 'error.main' }}>− Resale Levy (from proceeds)</Typography>
                        <Typography variant="caption" sx={{ color: 'error.main' }}>{fmt(resaleLevy)}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="caption" fontWeight={700} sx={{ color: 'text.secondary' }}>Net Sales Proceed</Typography>
                        <Typography variant="caption" fontWeight={700}>{fmt(Math.max(0, cashProceedFromSelling - resaleLevy))}</Typography>
                      </Box>
                    </Box>
                  )}
                  <Divider sx={{ mb: 1, opacity: 0.5 }} />

                  {/* CPF — summed from each borrower's own card */}
                  {renderBorrowerFundsRow('cpf')}
                  <Divider sx={{ mb: 1, opacity: 0.5 }} />

                  {/* Additional CPF */}
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, mb: 0.5 }}>
                    <Box sx={{ flex: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.82rem' }}>Additional CPF</Typography>
                        {sellerProceeds.length > 0 && (
                          <InfoOutlinedIcon
                            onClick={() => setCalcDialog(proceedsDialog('cpf'))}
                            sx={{ fontSize: 15, color: 'primary.main', cursor: 'pointer', ml: 0.5 }}
                          />
                        )}
                      </Box>
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>From CPF withdrawal / sales proceed</Typography>
                    </Box>
                    <Box sx={{ width: 180, flexShrink: 0 }}>
                      <CurrencyTextField
                        value={inputs.additionalCpf}
                        onChange={(v) => { updateInput('additionalCpf', v); setProceedsRowsUserSet(true); }}
                        highlightColor={highlightColor}
                      />
                    </Box>
                  </Box>

                  {propertyTab === 'ec' && (
                    <>
                      <Divider sx={{ mb: 1, mt: 1, opacity: 0.5 }} />
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, mb: 0.5 }}>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.82rem' }}>Housing Grant</Typography>
                          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                            CPF Housing Grant for ECs —{' '}
                            <Link href="https://www.hdb.gov.sg/buying-a-flat/executive-condominiums/cpf-housing-grant" target="_blank" rel="noopener noreferrer" sx={{ color: '#D97706' }}>
                              eligibility
                            </Link>
                          </Typography>
                        </Box>
                        <Box sx={{ width: 180, flexShrink: 0 }}>
                          <CurrencyTextField value={ecHousingGrant} onChange={(v) => setEcHousingGrant(v)} highlightColor={highlightColor} />
                        </Box>
                      </Box>
                    </>
                  )}

                  </Box>
                </Card>


                {/* ── Special Bridging Loan (EC DPS, detailed mode) ── */}
                {calculatorMode === 'detailed' && bridgingApplies && (
                  <Card data-tutorial="special-bridging-section" variant="outlined" sx={sectionCardSx}>
                    {renderSectionHeader('Special Bridging Loan', {
                      trailing: (
                        <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500, fontSize: '0.85rem' }}>
                          DPS only
                        </Typography>
                      ),
                    })}
                    <Box sx={sectionBodySx}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5, mb: sblHasHdb ? 2 : 0 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
                          <Typography variant="body2" fontWeight={500}>Do you own an existing HDB flat?</Typography>
                          {renderFieldInfoIcon({
                            title: 'Special Bridging Loan (EC DPS)',
                            body: `A short-term bank loan for HDB upgraders buying a new EC under the Deferred Payment Scheme, secured against the HDB flat they still own.\n\nHow it works\n• You pay the 20% down payment (5% cash + 15% CPF/cash) yourself — the bridging loan cannot fund it\n• At TOP, the bridging loan is disbursed together with the bank loan to fund the deferred tranche, so you need a smaller bank loan\n• Needs HDB's approval letter for the sale of your flat before the bank disburses\n• Repaid from the HDB sale proceeds / CPF refund, typically within 6 months\n• Interest is roughly 3M SORA + 1% p.a.\n\nMaximum loan\n90% × current HDB valuation − outstanding HDB loan − resale levy\n\nSome banks no longer deduct the resale levy — enter $0 if your bank doesn't.`,
                            url: SBL_REFERENCE_URL,
                          })}
                        </Box>
                        <ToggleButtonGroup
                          value={sblHasHdb ? 'yes' : 'no'}
                          exclusive
                          onChange={(_, v) => { if (v) setSblHasHdb(v === 'yes'); }}
                          size="small"
                          sx={{
                            flexShrink: 0,
                            '& .MuiToggleButton-root': { px: 2, py: 0.25, fontWeight: 600, fontSize: '0.75rem' },
                            '& .Mui-selected': { bgcolor: '#0ea5e9 !important', color: '#fff !important', borderColor: '#0ea5e9 !important' },
                          }}
                        >
                          <ToggleButton value="no">No</ToggleButton>
                          <ToggleButton value="yes">Yes</ToggleButton>
                        </ToggleButtonGroup>
                      </Box>

                      {sblHasHdb && (
                        <>
                          {([
                            { label: 'HDB Valuation', value: sblValuation, set: setSblValuation, hint: 'Current market value' },
                            { label: 'Outstanding Loan', value: sblOutstandingLoan, set: setSblOutstandingLoan, hint: 'HDB / bank loan still owed' },
                            // One-way sync: the levy keyed in here also defaults the Other Expenses
                            // resale levy (editing that one never writes back here).
                            { label: 'Resale Levy', value: sblLevy, set: (v: number) => { setSblLevy(v); setResaleLevy(v); setResaleLevyUserSet(true); }, hint: 'For a 2nd subsidised flat (EC)' },
                          ] as const).map((f, i) => (
                            // Label left / input right — same row layout as Available Funds.
                            <React.Fragment key={f.label}>
                              {i > 0 && <Divider sx={{ mb: 1, mt: 1, opacity: 0.5 }} />}
                              <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, mb: 0.5 }}>
                                <Box sx={{ flex: 1 }}>
                                  <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.82rem' }}>{f.label}</Typography>
                                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>{f.hint}</Typography>
                                </Box>
                                <Box sx={{ width: 180, flexShrink: 0 }}>
                                  <CurrencyTextField value={f.value} onChange={(v) => f.set(Math.max(0, v))} highlightColor={highlightColor} />
                                </Box>
                              </Box>
                            </React.Fragment>
                          ))}

                          <Box sx={{ mt: 2, p: 1.5, borderRadius: 1.5, bgcolor: isDark ? 'rgba(124,58,237,0.12)' : 'rgba(124,58,237,0.06)', border: '1px solid rgba(124,58,237,0.25)' }}>
                            {[
                              { label: '90% × Valuation', value: fmt(Math.round(sblValuation * SBL_VALUATION_PCT)) },
                              { label: '− Outstanding Loan', value: fmt(sblOutstandingLoan) },
                              { label: '− Resale Levy', value: fmt(sblLevy) },
                            ].map((r) => (
                              <Box key={r.label} sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.82rem' }}>{r.label}</Typography>
                                <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.82rem' }}>{r.value}</Typography>
                              </Box>
                            ))}
                            <Divider sx={{ my: 0.75 }} />
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                              <Typography variant="body2" fontWeight={700} sx={{ color: '#7c3aed' }}>Special Bridging Loan</Typography>
                              <Typography variant="body1" fontWeight={800} sx={{ color: '#7c3aed' }}>{fmt(sblMax)}</Typography>
                            </Box>
                            {bridgingForLoan < sblMax && (
                              <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.5 }}>
                                {`Only ${fmt(bridgingForLoan)} is applied — capped at the TOP tranche / loan balance (${fmt(sblTrancheCap)}).`}
                              </Typography>
                            )}
                            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.5 }}>
                              Applied in the Payment Structure at TOP, reducing the bank loan.
                            </Typography>
                          </Box>
                        </>
                      )}
                    </Box>
                  </Card>
                )}

                {/* ── Other Expenses Section ── */}
                <Card data-tutorial="other-expenses" variant="outlined" sx={sectionCardSx}>
                  {renderSectionHeader('Other Expenses', { step: 4 })}
                  <Box sx={sectionBodySx}>

                  {renderExpenseGroupLabel('Taxes & legal', 'Calculated fees')}
                  <Box sx={expenseGroupSx}>
                    <Box sx={expenseRowSx}>
                      <Typography variant="body2" fontWeight={700}>Buyer’s Stamp Duty (BSD)</Typography>
                      <Typography variant="body2" fontWeight={800} sx={{ color: highlightColor }}>
                        {fmt(bsd)}
                      </Typography>
                    </Box>

                    {propertyTab === 'private' && (
                      <Box sx={expenseRowSx}>
                        <Box>
                          <Typography variant="body2" fontWeight={700}>Additional Buyer’s Stamp Duty</Typography>
                          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                            ABSD · {absdRatePct}% from borrower profile
                          </Typography>
                        </Box>
                        <Typography variant="body2" fontWeight={800} sx={{ color: absdAmount > 0 ? 'error.main' : 'text.secondary' }}>
                          {absdAmount > 0 ? fmt(absdAmount) : 'Not applicable'}
                        </Typography>
                      </Box>
                    )}

                    {(propertyTab === 'ec' || propertyTab === 'hdb' || (propertyTab === 'private' && privateSubTab === 'new-launch')) &&
                      renderExpenseCheckbox(
                        'Use CPF for stamp duty',
                        bsdByCpf,
                        (c) => { setBsdByCpf(c); setAbsdByCpf(c); },
                        { disabled: feesCpfAvailable === 0 },
                      )}

                    <Box sx={expenseRowSx}>
                      <Typography variant="body2" fontWeight={700}>Legal fee</Typography>
                      <Box sx={expenseInputSx}>
                        <CurrencyTextField value={inputs.legalFee} onChange={(v) => updateInput('legalFee', v)} highlightColor={highlightColor} />
                      </Box>
                    </Box>

                    {(propertyTab === 'ec' || propertyTab === 'hdb' || (propertyTab === 'private' && privateSubTab === 'new-launch')) &&
                      renderExpenseCheckbox(
                        'Use CPF for legal fee',
                        legalFeeByCpf,
                        setLegalFeeByCpf,
                        { disabled: feesCpfAvailable === 0 },
                      )}
                  </Box>

                  {propertyTab === 'ec' && (
                    <>
                      {renderExpenseGroupLabel('EC adjustment')}
                      <Box sx={expenseGroupSx}>
                        <Box sx={expenseRowSx}>
                          <Box sx={{ minWidth: 0 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <Typography variant="body2" fontWeight={700}>Resale levy</Typography>
                              {renderFieldInfoIcon({
                                title: 'Resale Levy',
                                body: 'A resale levy is payable when you buy a second subsidised flat (e.g. a new Executive Condominium from a developer) after having previously enjoyed a housing subsidy — for example, from a flat bought directly from HDB or a resale flat bought with a CPF Housing Grant.\n\nIt is a fixed amount based on the type of the first subsidised flat sold (e.g. $55,000 for a household whose first subsidised flat was an Executive Condominium / Executive flat; less for smaller flat types). It is typically deducted from the proceeds of selling your existing flat, so it reduces the cash you have available for the purchase.\n\nWhen you arrive here from the Selling calculator after selling an HDB flat, this defaults to $55,000 — adjust it to match your first subsidised flat type.',
                                url: 'https://www.hdb.gov.sg/buying-a-flat/bto-sbf-and-open-booking-of-flats/conditions-after-buying-a-new-flat',
                              })}
                            </Box>
                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>Deducted from sale proceeds</Typography>
                          </Box>
                          <Box sx={expenseInputSx}>
                            <CurrencyTextField value={resaleLevy} onChange={(v) => { setResaleLevyUserSet(true); setResaleLevy(v); }} highlightColor={highlightColor} />
                          </Box>
                        </Box>
                      </Box>
                    </>
                  )}

                  {propertyTab === 'hdb' && (
                    <>
                      {renderExpenseGroupLabel('OTP deposit', `Cash · ${fmt(otpDeposit)}`)}
                      <Box sx={expenseGroupSx}>
                        <Box sx={expenseRowSx}>
                          <Typography variant="body2" fontWeight={700}>Option fee</Typography>
                          <Box sx={expenseInputSx}>
                            <CurrencyTextField value={hdbInputs.optionFee} onChange={(v) => updateHdb('optionFee', v)} highlightColor={highlightColor} minValue={1} />
                          </Box>
                        </Box>
                        <Box sx={expenseRowSx}>
                          <Typography variant="body2" fontWeight={700}>Exercise fee</Typography>
                          <Box sx={expenseInputSx}>
                            <CurrencyTextField value={hdbInputs.exerciseFee} onChange={(v) => updateHdb('exerciseFee', v)} highlightColor={highlightColor} minValue={1} />
                          </Box>
                        </Box>
                      </Box>

                      {renderExpenseGroupLabel('HDB & service fees')}
                      <Box sx={expenseGroupSx}>
                        {hdbInputs.loanSource === 'hdb' && (
                          <Box sx={expenseRowSx}>
                            <Typography variant="body2" fontWeight={700}>Caveat fee</Typography>
                            <Box sx={expenseInputSx}>
                              <CurrencyTextField value={hdbInputs.caveatFee} onChange={(v) => updateHdb('caveatFee', v)} highlightColor={highlightColor} />
                            </Box>
                          </Box>
                        )}
                        <Box sx={expenseRowSx}>
                          <Typography variant="body2" fontWeight={700}>Valuation fee</Typography>
                          <Box sx={expenseInputSx}>
                            <CurrencyTextField value={hdbInputs.valuationFee} onChange={(v) => updateHdb('valuationFee', v)} highlightColor={highlightColor} />
                          </Box>
                        </Box>
                        <Box sx={expenseRowSx}>
                          <Typography variant="body2" fontWeight={700}>HDB admin fee</Typography>
                          <Box sx={expenseInputSx}>
                            <CurrencyTextField value={hdbInputs.hdbAdminFee} onChange={(v) => updateHdb('hdbAdminFee', v)} highlightColor={highlightColor} />
                          </Box>
                        </Box>
                        <Box sx={expenseRowSx}>
                          <Box sx={{ minWidth: 0 }}>
                            <Typography variant="body2" fontWeight={700}>Agency service fee</Typography>
                            {/* The $ field is the all-in amount, so spell out how much of it is
                                GST — agents quote commission ex-GST but the buyer pays incl. */}
                            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                              {hdbInputs.agencyServiceFee > 0
                                ? `Incl. 9% GST — ${fmt(agencyFeeBase)} commission + ${fmt(agencyFeeGst)} GST`
                                : 'Enter % of price or the amount — 9% GST is added on top'}
                              {hdbAgencyFeeUserSet && (
                                <>
                                  {' · '}
                                  <Box
                                    component="span"
                                    onClick={() => handleAgencyFeePctChange(1)}
                                    sx={{ color: highlightColor, fontWeight: 700, cursor: 'pointer' }}
                                  >
                                    Reset to 1%
                                  </Box>
                                </>
                              )}
                            </Typography>
                          </Box>
                          <AgencyFeeField
                            pct={hdbInputs.agencyServiceFeePct}
                            amount={hdbInputs.agencyServiceFee}
                            highlightColor={highlightColor}
                            onPctChange={handleAgencyFeePctChange}
                            onAmountChange={handleAgencyFeeAmountChange}
                          />
                        </Box>
                      </Box>

                      {renderExpenseGroupLabel('Protection')}
                      <Box sx={expenseGroupSx}>
                        <Box sx={expenseRowSx}>
                          <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
                            <Box>
                              <Typography variant="body2" fontWeight={700}>Home Protection Scheme</Typography>
                              <Typography variant="caption" sx={{ color: 'text.secondary' }}>HPS premium</Typography>
                            </Box>
                            {renderFieldInfoIcon({
                              title: 'Home Protection Scheme (HPS)',
                              body: 'HPS is a mortgage-reducing insurance that protects HDB flat owners and their families from losing their home should an insured owner pass away, become terminally ill, or be totally and permanently disabled before the housing loan is paid up. It is compulsory if you use CPF savings to pay your monthly housing loan instalments.\n\nThe premium depends on your age, gender, loan amount and tenure. Use the official CPF calculator to estimate your premium, then enter the amount here.',
                              url: 'https://www.cpf.gov.sg/member/tools-and-services/calculators/home-protection-scheme-premium-calculator',
                              embed: true,
                            })}
                          </Box>
                          <Box sx={expenseInputSx}>
                            <CurrencyTextField value={hdbInputs.hpsAmount} onChange={(v) => updateHdb('hpsAmount', v)} highlightColor={highlightColor} />
                          </Box>
                        </Box>
                        {renderExpenseCheckbox(
                          'Use CPF for HPS',
                          hpsByCpf,
                          setHpsByCpf,
                          { disabled: feesCpfAvailable === 0, hint: 'always available for HDB' },
                        )}
                      </Box>

                      {hdbInputs.loanSource === 'hdb' && (
                        <>
                          {renderExpenseGroupLabel('HDB loan details')}
                          <Box sx={expenseGroupSx}>
                            {renderExpenseCheckbox(
                              'Second HDB loan',
                              hdbInputs.isSecondHdbLoan,
                              (c) => { setIsSecondHdbLoanUserSet(true); updateHdb('isSecondHdbLoan', c); },
                              { infoIcon: renderFieldInfoIcon(SECOND_HDB_LOAN_INFO) },
                            )}
                            {hdbInputs.isSecondHdbLoan && (
                              <>
                                <Box sx={expenseRowSx}>
                                  <Typography variant="body2" fontWeight={700}>Cash proceeds to use</Typography>
                                  <Box sx={expenseInputSx}>
                                    <CurrencyTextField
                                      value={hdbInputs.secondHdbLoanCashSetAside}
                                      onChange={(v) => { setSecondHdbLoanSetAsideUserSet(true); updateHdb('secondHdbLoanCashSetAside', v); }}
                                      highlightColor={highlightColor}
                                    />
                                  </Box>
                                </Box>
                                {secondHdbLoanRule.active && (
                                  <Typography variant="caption" sx={{ display: 'block', color: secondHdbLoanShortfall > 0 ? 'error.main' : 'text.secondary', mt: -0.5, mb: 0.5 }}>
                                    HDB minimum on {fmt(secondHdbLoanRule.proceeds)} of proceeds: <b>{fmt(secondHdbLoanRule.mustUse)}</b>
                                    {secondHdbLoanShortfall > 0 && ` — currently ${fmt(secondHdbLoanShortfall)} short`}
                                  </Typography>
                                )}
                              </>
                            )}
                          </Box>
                        </>
                      )}
                    </>
                  )}

                  </Box>
                </Card>

                {/* Loan section — always visible for private/EC with switch; for HDB only when loanSource='bank' */}
                {(propertyTab !== 'hdb' || hdbInputs.loanSource === 'bank') && (
                  <>
                    {/* ── Loan Section ── */}
                    <Card data-tutorial="loan-section" variant="outlined" sx={sectionCardSx}>
                      {renderSectionHeader('Loan', {
                        step: 5,
                        trailing: propertyTab !== 'hdb' ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Typography variant="caption" sx={{ color: inputs.hasBankLoan ? highlightColor : 'text.disabled', fontWeight: 600, fontSize: '0.72rem' }}>
                              {inputs.hasBankLoan ? 'Yes' : 'No'}
                            </Typography>
                            <Switch
                              size="small"
                              checked={inputs.hasBankLoan}
                              onChange={(e) => updateInput('hasBankLoan', e.target.checked)}
                              sx={{
                                '& .MuiSwitch-switchBase.Mui-checked': { color: highlightColor },
                                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: highlightColor },
                              }}
                            />
                          </Box>
                        ) : undefined,
                      })}

                      <Box sx={sectionBodySx}>

                      {(propertyTab === 'hdb' || inputs.hasBankLoan) && <>
                      {/* ── Loan Info (IWAA + affordability) ── */}
                      {renderLoanInfo()}

                      {/* ── Loan Parameter ── */}
                      {renderLoanSubLabel('Loan Parameter')}

                      {/* Loan Tenure — EC: defaults to the eligible maximum; Stretch checkbox beside
                          the label, pencil to key in a tenure (validated against the cap in force). */}
                      {ecFixedTenure ? (
                        <Box sx={{ mb: 2 }}>
                          <TenureInlineEditor
                            label={(
                              <>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flexWrap: 'wrap' }}>
                                  <Typography variant="body2" fontWeight={700} sx={{ fontSize: '0.82rem', color: 'text.primary' }}>Loan Tenure</Typography>
                                  {canExtendTenure && (
                                    <FormControlLabel
                                      label={<Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: 'text.secondary' }}>Stretch</Typography>}
                                      control={(
                                        <Checkbox
                                          size="small"
                                          checked={tenureStretched}
                                          onChange={(e) => (e.target.checked
                                            ? requestTenureChange(extendedTenureCap)
                                            : updateInput('tenure', Math.max(1, standardTenureCap)))}
                                          sx={{ p: 0.25, mr: 0.25, color: 'text.disabled', '&.Mui-checked': { color: highlightColor } }}
                                        />
                                      )}
                                      sx={{ m: 0 }}
                                    />
                                  )}
                                </Box>
                                <Typography variant="caption" sx={{ color: tenureStretched ? 'warning.main' : 'text.secondary', display: 'block', lineHeight: 1.35 }}>
                                  {tenureStretched
                                    ? `Stretched (max ${extendedTenureCap} yrs) · LTV 55%, min cash 10%`
                                    : inputs.tenure === standardTenureCap ? 'Max you are eligible for' : `Max eligible: ${standardTenureCap} yrs`}
                                </Typography>
                              </>
                            )}
                            value={inputs.tenure}
                            min={1}
                            // Up to the extended cap; keying in more than the standard cap
                            // prompts the extend confirmation (55% LTV / 10% cash).
                            max={Math.max(1, extendedTenureCap)}
                            color={highlightColor}
                            maxMessage={`Max ${Math.max(1, extendedTenureCap)} yrs${canExtendTenure ? ' (stretched)' : ''}`}
                            hint={canExtendTenure && !tenureStretched ? `Above ${standardTenureCap} yrs stretches the tenure` : undefined}
                            onCommit={(v) => requestTenureChange(v)}
                          />
                        </Box>
                      ) : (
                        <>
                        {/* Loan Tenure */}
                        <Box sx={{ mb: 2 }}>
                          <EditableSliderValueRow
                            label={<Typography variant="body2" fontWeight={700} sx={{ fontSize: '0.82rem', color: 'text.primary' }}>Loan Tenure</Typography>}
                            value={inputs.tenure}
                            min={1}
                            max={Math.max(1, maxTenureCap)}
                            step={1}
                            onCommit={(v) => requestTenureChange(v)}
                            formatDisplay={(v) => `${v} yrs`}
                            valueColor={highlightColor}
                          />
                          <Box sx={{ pl: 1.5, pr: 3, overflow: 'hidden' }}>
                            <IntentGuardedSlider
                              isMobile={isMobile}
                              value={inputs.tenure}
                              min={1}
                              max={Math.max(1, maxTenureCap)}
                              step={1}
                              onChange={(_, v) => requestTenureChange(v as number)}
                              sx={{
                                color: highlightColor, height: 6,
                                '& .MuiSlider-rail': {
                                  background: 'linear-gradient(90deg, #22c55e 0%, #eab308 50%, #ef4444 100%)',
                                  opacity: 1,
                                },
                              }}
                            />
                          </Box>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="caption" sx={{ color: 'text.disabled' }}>5 yrs</Typography>
                            <Typography variant="caption" sx={{ color: 'text.disabled' }}>20 yrs</Typography>
                            <Typography variant="caption" sx={{ color: 'text.disabled' }}>Cap: {maxTenureCap} yrs</Typography>
                          </Box>
                        </Box>
                        </>
                      )}

                      {/* Bank Rate */}
                      <Box sx={{ mb: 2 }}>
                        <EditableSliderValueRow
                          label={<Typography variant="body2" fontWeight={700} sx={{ fontSize: '0.82rem', color: 'text.primary' }}>Bank Rate</Typography>}
                          value={inputs.interestRate}
                          min={0.5}
                          max={6.0}
                          step={0.01}
                          onCommit={(v) => updateInput('interestRate', v)}
                          formatDisplay={(v) => `${v.toFixed(2)}%`}
                          valueColor={highlightColor}
                          commitMax={15}
                        />
                        <Box sx={{ pl: 1.5, pr: 3, overflow: 'hidden' }}>
                          <IntentGuardedSlider
                            isMobile={isMobile}
                            value={inputs.interestRate}
                            min={0.5}
                            max={6.0}
                            step={0.05}
                            onChange={(_, v) => updateInput('interestRate', v as number)}
                            sx={{ color: highlightColor, height: 6 }}
                          />
                        </Box>
                      </Box>
                      </>}

                      {/* Payment Structure — HDB Bank Loan */}
                      {propertyTab === 'hdb' && inputs.purchasePrice > 0 && (() => {
                        const {
                          valuationBase: valBase,
                          cov,
                          maxLoan: maxLoan75,
                          mandatoryCash,
                        } = hdbResaleFinancing;
                        // Cash bears only the cash portion of the expenses — the CPF-paid
                        // BSD/ABSD/legal/HPS come out of the CPF pool instead.
                        const hdbExpenses = bsd + absdAmount + inputs.legalFee + hdbInputs.valuationFee + hdbInputs.hdbAdminFee + hdbInputs.hpsAmount + hdbAgencyFee - hdbCpfExpenses;
                        const totalCashIn = inputs.additionalCash + cashProceedFromSelling;
                        // The OTP deposit is part of (not additional to) the bank's 5%
                        // minimum cash requirement. COV remains additional cash.
                        const maxCashForDown = Math.max(0, totalCashIn - hdbExpenses - mandatoryCash);
                        const availCpfGrants = Math.max(0, (inputs.cpf + inputs.additionalCpf) + hdbInputs.housingGrant + hdbInputs.cpfResaleGrant + hdbInputs.phgGrant - hdbCpfExpenses);
                        const hdbLoan = Math.max(0, inputs.purchasePrice - mandatoryCash - hdbCashAlloc - hdbCpfAlloc);
                        const hdbLtvPct = valBase > 0 ? (hdbLoan / valBase) * 100 : 0;
                        const overLtv = hdbLtvPct > 75.05;
                        // Any allocation beyond what's available lights up red — including the
                        // $0 Available Funds state, so a missing fund declaration is visible.
                        const cashExceeds = hdbCashAlloc > maxCashForDown;
                        const cpfExceeds = hdbCpfAlloc > availCpfGrants;
                        // Workings shown when the (i) beside each available figure is tapped
                        // (mirrors the Private payment structure's AvailableNote).
                        const hdbGrantsTotal = hdbInputs.housingGrant + hdbInputs.cpfResaleGrant + hdbInputs.phgGrant;
                        const hdbCashAvailDialog: CalcDialogInfo = {
                          title: 'Cash Available for Down Payment',
                          rows: [
                            { label: 'Additional Cash (savings)', value: fmt(inputs.additionalCash) },
                            ...(cashProceedFromSelling > 0 ? [{ label: '+ Sale Proceeds', value: fmt(cashProceedFromSelling) }] : []),
                            { isSeparator: true, label: '', value: '' },
                            { label: '= Total Cash', value: fmt(totalCashIn) },
                            { label: '− Expenses (cash portion)', value: fmt(hdbExpenses) },
                            { label: '− Min Cash (5% incl. OTP + COV)', value: fmt(mandatoryCash) },
                            { isSeparator: true, label: '', value: '' },
                            { label: '= Available for Down Payment', value: fmt(maxCashForDown), isResult: true },
                          ],
                        };
                        const hdbCpfAvailDialog: CalcDialogInfo = {
                          title: 'CPF Available for Down Payment',
                          rows: [
                            { label: 'CPF (OA)', value: fmt(inputs.cpf) },
                            ...(inputs.additionalCpf > 0 ? [{ label: '+ Additional CPF', value: fmt(inputs.additionalCpf) }] : []),
                            ...(hdbGrantsTotal > 0 ? [{ label: '+ Grants (Housing / Resale / PHG)', value: fmt(hdbGrantsTotal) }] : []),
                            { isSeparator: true, label: '', value: '' },
                            { label: '= Total CPF + Grants', value: fmt(inputs.cpf + inputs.additionalCpf + hdbGrantsTotal) },
                            ...(hdbCpfExpenses > 0 ? [{ label: '− CPF-paid expenses (BSD/ABSD/Legal/HPS)', value: fmt(hdbCpfExpenses) }] : []),
                            { isSeparator: true, label: '', value: '' },
                            { label: '= Available for Down Payment', value: fmt(availCpfGrants), isResult: true },
                          ],
                        };
                        // Editable bank loan — tap to type an amount; the down payment is re-derived
                        // (CPF used up first, then cash) via the confirmation dialog.
                        const BankLoanRow = ({ value, onCommit, error }: { value: number; onCommit: (v: number) => void; error?: boolean }) => {
                          const [editing, setEditing] = React.useState(false);
                          const [draft, setDraft] = React.useState('');
                          const commit = () => {
                            setEditing(false);
                            const n = parseInt(draft.replace(/[^0-9]/g, ''), 10);
                            if (!isNaN(n) && n !== value) onCommit(n);
                          };
                          const color = error ? 'error.main' : highlightColor;
                          if (editing) {
                            return (
                              <TextField
                                size="small" autoFocus
                                value={draft === '' ? '' : Number(draft).toLocaleString('en-SG')}
                                placeholder="0"
                                inputProps={{ inputMode: 'numeric' }}
                                onChange={(e) => setDraft(e.target.value.replace(/[^0-9]/g, ''))}
                                onBlur={commit}
                                onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); if (e.key === 'Escape') setEditing(false); }}
                                InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                                sx={{ width: 150, '& input': { textAlign: 'right', py: '4px', fontSize: '0.85rem', fontWeight: 800 } }}
                              />
                            );
                          }
                          return (
                            <Box onClick={() => { setDraft(value === 0 ? '' : String(value)); setEditing(true); }}
                              sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, cursor: 'pointer', flexShrink: 0, '&:hover .hdb-loan-edit-icon': { opacity: 1 } }}>
                              <Typography variant="body2" fontWeight={700} sx={{ fontSize: '0.8rem', color }}>{fmt(value)}</Typography>
                              <EditIcon className="hdb-loan-edit-icon" sx={{ fontSize: 14, color, opacity: 0.6, transition: 'opacity 120ms' }} />
                            </Box>
                          );
                        };
                        return (
                          <Box sx={{ mb: 2 }}>
                            <Typography variant="body2" fontWeight={700} sx={{ mb: 1, fontSize: '0.82rem' }}>Payment Structure</Typography>
                            <Box sx={{ p: 1.5, borderRadius: 1, bgcolor: cardBg, border: '1px solid', borderColor: cardBorder, mb: 1 }}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                <Typography variant="caption" sx={{ color: 'text.secondary' }}>Valuation Base</Typography>
                                <Typography variant="caption" fontWeight={600}>{fmt(valBase)}</Typography>
                              </Box>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                <Typography variant="caption" sx={{ color: 'text.secondary' }}>Max Bank Loan (75%)</Typography>
                                <Typography variant="caption" fontWeight={600} sx={{ color: '#0ea5e9' }}>{fmt(maxLoan75)}</Typography>
                              </Box>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                <Typography variant="caption" sx={{ color: 'text.secondary' }}>Min Cash (5% incl. OTP + COV)</Typography>
                                <Typography variant="caption" fontWeight={600}>{fmt(mandatoryCash)}</Typography>
                              </Box>
                              {cov > 0 && <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                <Typography variant="caption" sx={{ color: 'text.secondary' }}>of which COV</Typography>
                                <Typography variant="caption" fontWeight={600} sx={{ color: 'error.main' }}>{fmt(cov)}</Typography>
                              </Box>}
                            </Box>

                            {/* Cash input */}
                            <Box sx={{ mb: 0.5 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                                <Box sx={{ flex: 1 }}>
                                  <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.8rem' }}>Cash (down payment)</Typography>
                                  <Typography variant="caption" sx={{ color: cashExceeds ? 'error.main' : 'text.secondary' }}>
                                    After expenses, max:{' '}
                                    <Box component="span" onClick={() => setHdbCashAlloc(maxCashForDown)}
                                      sx={{ color: 'primary.main', fontWeight: 700, cursor: 'pointer', textDecoration: 'underline', textUnderlineOffset: '2px' }}>
                                      {fmt(maxCashForDown)}
                                    </Box>
                                    <InfoOutlinedIcon onClick={() => setCalcDialog(hdbCashAvailDialog)}
                                      sx={{ fontSize: 15, color: 'primary.main', cursor: 'pointer', verticalAlign: 'text-bottom', ml: 0.4 }} />
                                  </Typography>
                                </Box>
                                <HdbAllocField value={hdbCashAlloc} cap={maxCashForDown} base={inputs.purchasePrice} onChange={setHdbCashAlloc} highlightColor={highlightColor} exceeds={cashExceeds} />
                              </Box>
                            </Box>

                            {/* CPF + Grants input */}
                            <Box sx={{ mb: 1 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                                <Box sx={{ flex: 1 }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.8rem' }}>CPF + Grants</Typography>
                                  <IconButton size="small" sx={{ p: 0.25 }} onClick={() => setCpfInfoOpen(true)} aria-label="CPF usage info">
                                    <InfoOutlinedIcon fontSize="small" color="primary" />
                                  </IconButton>
                                </Box>
                                  <Typography variant="caption" sx={{ color: cpfExceeds ? 'error.main' : 'text.secondary' }}>
                                    Available:{' '}
                                    <Box component="span" onClick={() => setHdbCpfAlloc(availCpfGrants)}
                                      sx={{ color: 'primary.main', fontWeight: 700, cursor: 'pointer', textDecoration: 'underline', textUnderlineOffset: '2px' }}>
                                      {fmt(availCpfGrants)}
                                    </Box>
                                    <InfoOutlinedIcon onClick={() => setCalcDialog(hdbCpfAvailDialog)}
                                      sx={{ fontSize: 15, color: 'primary.main', cursor: 'pointer', verticalAlign: 'text-bottom', ml: 0.4 }} />
                                  </Typography>
                                </Box>
                                <HdbAllocField value={hdbCpfAlloc} cap={availCpfGrants} base={inputs.purchasePrice} onChange={setHdbCpfAlloc} highlightColor={highlightColor} exceeds={cpfExceeds} />
                              </Box>
                              {(() => {
                                const retained = availCpfGrants - hdbCpfAlloc;
                                const maxRetain = inputs.includeAdditionalBorrower ? 40000 : 20000;
                                if (retained <= maxRetain) return null;
                                return (
                                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, mt: 0.5 }}>
                                    <WarningAmberIcon sx={{ color: 'error.main', fontSize: 16, mt: '1px', flexShrink: 0 }} />
                                    <Typography variant="caption" sx={{ color: 'error.main' }}>
                                      You can keep aside at most {fmt(maxRetain)} in your CPF OA{inputs.includeAdditionalBorrower ? ' (2 borrowers)' : ''} — currently keeping {fmt(retained)}.
                                    </Typography>
                                  </Box>
                                );
                              })()}
                            </Box>

                            <Divider sx={{ my: 0.75 }} />
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, mb: 0.25 }}>
                              <Box sx={{ minWidth: 0 }}>
                                <Typography variant="body2" fontWeight={700} sx={{ fontSize: '0.8rem', color: overLtv ? 'error.main' : highlightColor }}>
                                  Bank Loan (LTV {hdbLtvPct.toFixed(1)}%)
                                </Typography>
                                <Typography variant="caption" sx={{ color: 'text.secondary' }}>Tap to edit — CPF first, then Cash</Typography>
                              </Box>
                              <BankLoanRow value={hdbLoan} error={overLtv} onCommit={(v) => setPendingHdbLoanChange(v)} />
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                              <Typography variant="body2" fontWeight={700} sx={{ fontSize: '0.8rem' }}>Total</Typography>
                              <Typography variant="body2" fontWeight={700} sx={{ fontSize: '0.8rem' }}>{fmt(inputs.purchasePrice)}</Typography>
                            </Box>
                            {overLtv && (
                              <Box sx={{ mt: 1, p: 1, borderRadius: 1, bgcolor: 'rgba(211,47,47,0.08)', border: '1px solid', borderColor: 'error.main', display: 'flex', alignItems: 'flex-start', gap: 0.75 }}>
                                <WarningAmberIcon sx={{ color: 'error.main', fontSize: 18, flexShrink: 0 }} />
                                <Box>
                                  <Typography variant="caption" fontWeight={800} sx={{ color: 'error.main', display: 'block' }}>
                                    Short {fmt(Math.round(hdbLoan - maxLoan75))} — exceeds 75% LTV
                                  </Typography>
                                  <Typography variant="caption" sx={{ color: 'error.main' }}>Add more cash/CPF or negotiate a lower price.</Typography>
                                </Box>
                              </Box>
                            )}
                          </Box>
                        );
                      })()}

                      {/* Payment Structure — Private / EC */}
                      {propertyTab !== 'hdb' && (() => {
                        // Total funds from Available Funds section
                        // Resale levy (EC) is paid out of the sale proceeds, so it reduces the cash available.
                        const totalCashIn = inputs.additionalCash + cashProceedFromSelling - (propertyTab === 'ec' ? resaleLevy : 0); // buyer's total cash available
                        const totalCpfIn = inputs.cpf + inputs.additionalCpf + (propertyTab === 'ec' ? ecHousingGrant : 0);
                        // Stamp duty costs (cash portion)
                        const stampCashCost = (bsdByCpf ? bsdCash : bsd) + (absdByCpf ? absdCash : absdAmount);
                        const stampCpfCost = (bsdByCpf ? bsdCpf : 0) + (absdByCpf ? absdCpf : 0);
                        // Legal fee draws whatever CPF is left after stamp duty (the rest is
                        // paid in cash); that CPF is no longer available for the down payment.
                        const legalCpfCost = legalFeeByCpf ? Math.min(inputs.legalFee, Math.max(0, totalCpfIn - stampCpfCost)) : 0;
                        // Net available for ADDITIONAL down payment:
                        // total cash − stamp duty (cash) − mandatory 5% min cash
                        const netCashForDown = Math.max(0, totalCashIn - stampCashCost - minCash);
                        const netCpfForDown = Math.max(0, totalCpfIn - stampCpfCost - legalCpfCost);
                        // CPF usable for the down payment. Any gap between the bank loan and the
                        // price is surfaced as a Loan Shortfall, so no CPF is held in reserve.
                        const cpfForDown = netCpfForDown;
                        // Allocation state — independent from Available Funds totals
                        const cashAllocated = cashDownAlloc;
                        const cpfAllocated = cpfDownAlloc;
                        // Flag any allocation the declared funds can't cover — including when no
                        // cash / CPF has been entered at all, so an unfunded allocation is obvious.
                        const cashExceeds = cashAllocated > netCashForDown;
                        const cpfExceeds = cpfAllocated > cpfForDown;
                        // Cash on hand for the mandatory 5% minimum (before additional allocations).
                        const cashOnHand = Math.max(0, totalCashIn - stampCashCost);
                        const minCashShort = minCash > 0 && cashOnHand < minCash;
                        // Down-payment shortfall against the applicable max-LTV cap (75%
                        // normally, 55% when the tenure is stretched). Tolerance of 2 absorbs
                        // per-component rounding (each % rounds independently).
                        const targetDownForCap = Math.round(inputs.purchasePrice * ((100 - maxLtvPct) / 100));
                        // Bridging loan also covers the non-loan portion of the price, so it
                        // counts toward reaching the down payment needed for the LTV cap.
                        const totalAllocated = minCash + cashAllocated + cpfAllocated + bridgingForLoan;
                        const shortfallToCap = Math.max(0, targetDownForCap - totalAllocated - 2);

                        const inputSx = { flex: 1.5, '& input': { textAlign: 'right', py: '4px', fontSize: '0.8rem', fontWeight: 600 } };

                        const price = inputs.purchasePrice;

                        // Snap a down-payment field up to close a tiny rounding gap to the
                        // LTV-cap target. Entering e.g. 11.1% rounds ~$200 short of the exact
                        // 11.111% ($200,000) needed to reach 25% down / 75% LTV — snapping
                        // reaches the target so the buyer isn't left keying in the last $198.
                        // Only fires when the gap is small (clearly %-rounding) and the field
                        // can fully absorb it within what's available for the down payment.
                        const roundSnap = Math.max(1000, Math.round(price * 0.001));
                        const snapDownAlloc = (which: 'cash' | 'cpf', value: number, cap: number): number => {
                          if (value <= 0 || targetDownForCap <= 0) return value;
                          const other = which === 'cash' ? cpfAllocated : cashAllocated;
                          const gap = targetDownForCap - (minCash + other + value + bridgingForLoan);
                          return (gap > 0 && gap <= roundSnap && value + gap <= cap) ? value + gap : value;
                        };

                        // ── Standardized typography for the Payment Structure section ──
                        // One place for every label / hint / value / icon size so the whole
                        // block reads as one system (previously each row set its own sizes).
                        const PS_INFO_ICON = 15;
                        const psSubLabelSx = { fontWeight: 700, color: 'text.secondary', letterSpacing: '0.07em', textTransform: 'uppercase' as const, fontSize: '0.68rem', display: 'block' };
                        const psRowLabelSx = { fontSize: '0.85rem', fontWeight: 600, color: 'text.primary' };
                        const psHintSx = { fontSize: '0.72rem', color: 'text.secondary' };
                        const psValueSx = { fontSize: '0.95rem', fontWeight: 700 };

                        const FRow = ({ cap, allocated, onCommit, disabled, exceeds: exceedsProp }: {
                          cap: number; allocated: number;
                          onCommit: (v: number) => void; disabled?: boolean;
                          label?: string; hint?: string; exceeds?: boolean;
                        }) => {
                          const [amtDraft, setAmtDraft] = React.useState('');
                          const [pctDraft, setPctDraft] = React.useState('');
                          const [editingAmt, setEditingAmt] = React.useState(false);
                          const [editingPct, setEditingPct] = React.useState(false);
                          const exceeds = exceedsProp ?? (allocated > cap && cap > 0);
                          const pctVal = price > 0 ? (allocated / price * 100) : 0;

                          const commitAmt = () => {
                            const n = parseInt(amtDraft.replace(/[^0-9]/g, ''), 10);
                            onCommit(isNaN(n) ? 0 : n);
                            setEditingAmt(false);
                          };
                          const commitPct = () => {
                            setEditingPct(false);
                            const p = parseFloat(pctDraft);
                            if (isNaN(p) || price <= 0) return;
                            // Untouched draft (tap in / tap out) — the 1-dp % display is lossy,
                            // so re-deriving the amount from it would silently shift the value.
                            if (pctDraft === (pctVal === 0 ? '' : pctVal.toFixed(1))) return;
                            let n = Math.round(price * p / 100);
                            // Nearby amounts can read as the same 1-dp %. If the typed % matches
                            // the cap's displayed %, commit the cap.
                            if (cap > 0 && n > cap && p.toFixed(1) === (cap / price * 100).toFixed(1)) n = cap;
                            onCommit(n);
                          };

                          const fieldSx = {
                            '& .MuiOutlinedInput-root': {
                              bgcolor: disabled ? 'action.disabledBackground' : undefined,
                              '& fieldset': { borderColor: exceeds ? '#ef4444' : undefined },
                            },
                          };

                          return (
                            <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center' }}>
                              {/* % input */}
                              <TextField
                                size="small"
                                disabled={disabled}
                                value={editingPct ? pctDraft : (pctVal === 0 ? '' : pctVal.toFixed(1))}
                                placeholder="0"
                                inputProps={{ inputMode: 'decimal' }}
                                onFocus={() => { setPctDraft(pctVal === 0 ? '' : pctVal.toFixed(1)); setEditingPct(true); }}
                                onChange={(e) => setPctDraft(e.target.value.replace(/[^0-9.]/g, ''))}
                                onBlur={commitPct}
                                onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
                                InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                                sx={{ width: 80, '& input': { textAlign: 'right', py: '6px', fontSize: '0.82rem', fontWeight: 600 }, ...fieldSx }}
                              />
                              {/* $ input — formats with thousands separators while typing */}
                              <TextField
                                size="small"
                                disabled={disabled}
                                value={editingAmt ? (amtDraft === '' ? '' : Number(amtDraft).toLocaleString('en-SG')) : (allocated === 0 ? '' : allocated.toLocaleString('en-SG'))}
                                placeholder="0"
                                inputProps={{ inputMode: 'numeric' }}
                                onFocus={() => { setAmtDraft(allocated === 0 ? '' : String(allocated)); setEditingAmt(true); }}
                                onChange={(e) => setAmtDraft(e.target.value.replace(/[^0-9]/g, ''))}
                                onBlur={commitAmt}
                                onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
                                InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                                sx={{ width: 140, '& input': { textAlign: 'right', py: '6px', fontSize: '0.85rem', fontWeight: 700 }, ...fieldSx }}
                              />
                            </Box>
                          );
                        };

                        // "Available $X (i)" note — tap the amount to fill the field with the
                        // full available figure, tap the (i) to see how that figure is derived.
                        const AvailableNote = ({ cap, onFill, info, exceeds: ex, suffix }: {
                          cap: number; onFill: () => void; info: CalcDialogInfo; exceeds?: boolean; suffix?: string;
                        }) => (
                          <Typography component="span"
                            sx={{ ...psHintSx, display: 'inline-flex', alignItems: 'center', gap: 0.4, color: ex ? 'error.main' : 'text.secondary' }}>
                            Available
                            <Box component="span" onClick={onFill}
                              sx={{ color: 'primary.main', fontWeight: 700, cursor: 'pointer', textDecoration: 'underline', textUnderlineOffset: '2px' }}>
                              {fmt(cap)}
                            </Box>
                            {suffix && <Box component="span">{suffix}</Box>}
                            <InfoOutlinedIcon onClick={() => setCalcDialog(info)}
                              sx={{ fontSize: PS_INFO_ICON, color: 'primary.main', cursor: 'pointer' }} />
                          </Typography>
                        );

                        // Editable Bank Loan value — click to edit; on commit the parent
                        // prompts for confirmation, then re-derives the down payment.
                        const LoanRow = ({ value, onCommit, error }: { value: number; onCommit: (v: number) => void; error?: boolean }) => {
                          const [editing, setEditing] = React.useState(false);
                          const [draft, setDraft] = React.useState('');
                          const commit = () => {
                            setEditing(false);
                            const n = parseInt(draft.replace(/[^0-9]/g, ''), 10);
                            if (!isNaN(n) && n !== value) onCommit(n);
                          };
                          const color = error ? 'error.main' : highlightColor;
                          if (editing) {
                            return (
                              <TextField
                                size="small" autoFocus
                                value={draft === '' ? '' : Number(draft).toLocaleString('en-SG')}
                                placeholder="0"
                                inputProps={{ inputMode: 'numeric' }}
                                onChange={(e) => setDraft(e.target.value.replace(/[^0-9]/g, ''))}
                                onBlur={commit}
                                onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); if (e.key === 'Escape') setEditing(false); }}
                                InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                                sx={{ width: 160, '& input': { textAlign: 'right', py: '6px', fontSize: '1rem', fontWeight: 800 } }}
                              />
                            );
                          }
                          return (
                            <Box onClick={() => { setDraft(value === 0 ? '' : String(value)); setEditing(true); }}
                              sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, cursor: 'pointer', flexShrink: 0, '&:hover .loan-edit-icon': { opacity: 1 } }}>
                              <Typography sx={{ fontSize: '1.05rem', fontWeight: 800, color }}>{fmt(value)}</Typography>
                              <EditIcon className="loan-edit-icon" sx={{ fontSize: 16, color, opacity: 0.6, transition: 'opacity 120ms' }} />
                            </Box>
                          );
                        };

                        // Working shown when the (i) beside each "Available" figure is tapped.
                        const cashAvailDialog: CalcDialogInfo = {
                          title: 'Cash Available for Down Payment',
                          rows: [
                            { label: 'Additional Cash (savings)', value: fmt(inputs.additionalCash) },
                            ...(cashProceedFromSelling > 0 ? [{ label: '+ Sale Proceeds', value: fmt(cashProceedFromSelling) }] : []),
                            ...(propertyTab === 'ec' && resaleLevy > 0 ? [{ label: '− EC Resale Levy', value: fmt(resaleLevy) }] : []),
                            { isSeparator: true, label: '', value: '' },
                            { label: '= Total Cash', value: fmt(totalCashIn) },
                            ...((bsdByCpf ? bsdCash : bsd) > 0 ? [{ label: '− BSD (cash portion)', value: fmt(bsdByCpf ? bsdCash : bsd) }] : []),
                            ...((absdByCpf ? absdCash : absdAmount) > 0 ? [{ label: '− ABSD (cash portion)', value: fmt(absdByCpf ? absdCash : absdAmount) }] : []),
                            { label: `− Mandatory ${minCashPct}% Min Cash`, value: fmt(minCash) },
                            { isSeparator: true, label: '', value: '' },
                            { label: '= Available for Down Payment', value: fmt(netCashForDown), isResult: true },
                          ],
                        };
                        const cpfAvailDialog: CalcDialogInfo = {
                          title: 'CPF Available for Down Payment',
                          rows: [
                            { label: 'CPF (OA)', value: fmt(inputs.cpf) },
                            ...(inputs.additionalCpf > 0 ? [{ label: '+ Additional CPF', value: fmt(inputs.additionalCpf) }] : []),
                            ...(propertyTab === 'ec' && ecHousingGrant > 0 ? [{ label: '+ EC Housing Grant', value: fmt(ecHousingGrant) }] : []),
                            { isSeparator: true, label: '', value: '' },
                            { label: '= Total CPF', value: fmt(totalCpfIn) },
                            ...(bsdByCpf && bsdCpf > 0 ? [{ label: '− BSD', value: fmt(bsdCpf) }] : []),
                            ...(absdByCpf && absdCpf > 0 ? [{ label: '− ABSD', value: fmt(absdCpf) }] : []),
                            ...(legalCpfCost > 0 ? [{ label: '− Legal Fee', value: fmt(legalCpfCost) }] : []),
                            { isSeparator: true, label: '', value: '' },
                            { label: '= Available for Down Payment', value: fmt(cpfForDown), isResult: true },
                          ],
                        };
                        // Working for the mandatory-cash Shortfall figure (tap to view).
                        const cashShortfallDialog: CalcDialogInfo = {
                          title: 'Mandatory Cash Shortfall',
                          rows: [
                            { label: 'Additional Cash (savings)', value: fmt(inputs.additionalCash) },
                            ...(cashProceedFromSelling > 0 ? [{ label: '+ Sale Proceeds', value: fmt(cashProceedFromSelling) }] : []),
                            ...(propertyTab === 'ec' && resaleLevy > 0 ? [{ label: '− EC Resale Levy', value: fmt(resaleLevy) }] : []),
                            { isSeparator: true, label: '', value: '' },
                            { label: '= Total Cash', value: fmt(totalCashIn) },
                            ...(stampCashCost > 0 ? [{ label: '− Stamp Duty (cash portion)', value: fmt(stampCashCost) }] : []),
                            { isSeparator: true, label: '', value: '' },
                            { label: '= Cash on Hand', value: fmt(cashOnHand) },
                            { label: `− Mandatory ${minCashPct}% Min Cash`, value: fmt(minCash) },
                            { isSeparator: true, label: '', value: '' },
                            { label: 'Shortfall', value: fmt(minCash - cashOnHand), isResult: true },
                            { note: true, label: `The ${minCashPct}% minimum must be paid in cash (CPF cannot cover it). Add cash to your Available Funds to clear the shortfall.`, value: '' },
                          ],
                        };

                        return (
                          <Box sx={{ mb: 2 }}>
                            <Typography variant="subtitle2" fontWeight={800} sx={{ mb: 0.25, fontSize: '0.9rem', letterSpacing: '0.01em' }}>Payment Structure</Typography>
                            <Typography sx={{ ...psHintSx, display: 'block', mb: 1.5 }}>
                              Allocate your cash / CPF toward the down payment. Loan covers the rest.
                            </Typography>

                            {/* ── Down Payment block ── */}
                            <Typography sx={{ ...psSubLabelSx, mb: 1 }}>Down Payment</Typography>

                            {/* Cash minimum — locked & mandatory (5% normally, 10% when tenure-stretched).
                                Rendered as a highlighted callout so it never reads as optional. */}
                            <Box sx={{
                              display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1,
                              mb: 1.25, p: 1.25, borderRadius: 2,
                              bgcolor: minCashShort ? 'rgba(211,47,47,0.06)' : 'rgba(217,119,6,0.07)',
                              border: '1px solid', borderColor: minCashShort ? 'error.main' : 'rgba(217,119,6,0.30)',
                            }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                                <LockOutlinedIcon sx={{ fontSize: 20, color: minCashShort ? 'error.main' : highlightColor, flexShrink: 0 }} />
                                <Box sx={{ minWidth: 0 }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                                    <Typography sx={psRowLabelSx}>Cash Down Payment</Typography>
                                    <Chip label={`${minCashPct}% required`} size="small"
                                      sx={{ height: 18, fontSize: '0.62rem', fontWeight: 700, letterSpacing: '0.03em',
                                        bgcolor: minCashShort ? 'error.main' : highlightColor, color: '#fff', '& .MuiChip-label': { px: 0.75 } }} />
                                  </Box>
                                  {minCashShort ? (
                                    <Typography component="span" onClick={() => setCalcDialog(cashShortfallDialog)}
                                      sx={{ ...psHintSx, display: 'inline-flex', alignItems: 'center', gap: 0.4, color: 'error.main', cursor: 'pointer', mt: 0.25 }}>
                                      <Box component="span" sx={{ textDecoration: 'underline', textUnderlineOffset: '2px', fontWeight: 600 }}>Shortfall {fmt(minCash - cashOnHand)}</Box>
                                      <InfoOutlinedIcon sx={{ fontSize: PS_INFO_ICON }} />
                                    </Typography>
                                  ) : (
                                    <Typography sx={{ ...psHintSx, display: 'block', mt: 0.25 }}>Minimum you must pay in cash upfront</Typography>
                                  )}
                                </Box>
                              </Box>
                              <Typography sx={{ ...psValueSx, color: minCashShort ? 'error.main' : highlightColor, flexShrink: 0 }}>{fmt(minCash)}</Typography>
                            </Box>

                            {/* Additional Cash */}
                            <Box sx={{ mb: 1, px: 0.5 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 0.25 }}>
                                <Box sx={{ minWidth: 0 }}>
                                  <Typography sx={psRowLabelSx}>Additional Cash</Typography>
                                  <AvailableNote cap={netCashForDown} exceeds={cashExceeds}
                                    onFill={() => setCashDownAlloc(netCashForDown)} info={cashAvailDialog} />
                                </Box>
                                <FRow label="" cap={netCashForDown} allocated={cashAllocated} exceeds={cashExceeds}
                                  onCommit={(v) => setCashDownAlloc(snapDownAlloc('cash', v, netCashForDown))}
                                />
                              </Box>
                            </Box>

                            {/* CPF */}
                            <Box sx={{ mb: 1, px: 0.5 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 0.25 }}>
                                <Box sx={{ minWidth: 0 }}>
                                  <Typography sx={psRowLabelSx}>CPF</Typography>
                                  <AvailableNote cap={cpfForDown} exceeds={cpfExceeds}
                                    onFill={() => setCpfDownAlloc(cpfForDown)} info={cpfAvailDialog} />
                                </Box>
                                <FRow label="" cap={cpfForDown} allocated={cpfAllocated} exceeds={cpfExceeds}
                                  onCommit={(v) => setCpfDownAlloc(snapDownAlloc('cpf', v, cpfForDown))}
                                />
                              </Box>
                            </Box>

                            <Divider sx={{ my: 1.25 }} />

                            {/* ── Special Bridging Loan block (EC DPS, existing HDB owner) ──
                                Read-only: the amount is derived in the Special Bridging Loan
                                section (90% × valuation − outstanding loan − levy). */}
                            {bridgingApplies && sblHasHdb && (() => {
                              const sblCapped = bridgingForLoan < sblMax;
                              const sblDialog: CalcDialogInfo = {
                                title: 'Special Bridging Loan',
                                rows: [
                                  { label: 'HDB Valuation', value: fmt(sblValuation) },
                                  { label: '× 90%', value: fmt(Math.round(sblValuation * SBL_VALUATION_PCT)) },
                                  { label: '− Outstanding HDB Loan', value: fmt(sblOutstandingLoan) },
                                  { label: '− Resale Levy', value: fmt(sblLevy) },
                                  { isSeparator: true, label: '', value: '' },
                                  { label: '= Max Special Bridging Loan', value: fmt(sblMax) },
                                  ...(sblCapped ? [{ label: 'Capped at TOP tranche / loan balance', value: fmt(sblTrancheCap) }] : []),
                                  { label: 'Applied at TOP', value: fmt(bridgingForLoan), isResult: true },
                                  { label: `Bank loan = ${fmt(price)} − ${fmt(totalDown)} − ${fmt(bridgingForLoan)}`, value: fmt(derivedLoan) },
                                  { note: true, label: 'Disbursed at TOP towards the deferred tranche and repaid from your HDB sale proceeds / CPF refund (typically within 6 months). It reduces the bank loan; it cannot fund the 20% down payment.', value: '' },
                                ],
                              };
                              return (
                                <>
                                  <Typography sx={{ ...psSubLabelSx, mb: 1 }}>Special Bridging Loan</Typography>
                                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, mb: 1, px: 0.5 }}>
                                    <Box sx={{ minWidth: 0 }}>
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <Typography sx={{ ...psRowLabelSx, fontWeight: 700, color: '#7c3aed' }}>Bridging Loan</Typography>
                                        <InfoOutlinedIcon onClick={() => setCalcDialog(sblDialog)}
                                          sx={{ fontSize: PS_INFO_ICON, color: 'primary.main', cursor: 'pointer' }} />
                                      </Box>
                                      <Typography sx={psHintSx}>
                                        {sblMax > 0 ? (sblCapped ? 'Capped at the TOP tranche' : 'From the Special Bridging Loan section') : 'Enter your HDB valuation above'}
                                      </Typography>
                                    </Box>
                                    <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: '#7c3aed', whiteSpace: 'nowrap' }}>
                                      {fmt(bridgingForLoan)}
                                    </Typography>
                                  </Box>
                                  <Divider sx={{ my: 1.25 }} />
                                </>
                              );
                            })()}

                            {/* ── Loan block ── */}
                            <Typography sx={{ ...psSubLabelSx, mb: 1 }}>Bank Loan</Typography>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, mb: 0.5, px: 0.5 }}>
                              <Box sx={{ minWidth: 0 }}>
                                <Typography sx={{ ...psRowLabelSx, fontWeight: 700, color: derivedLtvPct > maxLtvPct + 0.05 ? 'error.main' : highlightColor }}>
                                  {/* EC loans are sized by MSR/TDSR, so the LTV % is shown for private only. */}
                                  {propertyTab === 'ec' ? 'Loan' : `Loan (LTV ${derivedLtvPct.toFixed(1)}%)`}
                                </Typography>
                                <Typography sx={psHintSx}>
                                  {!inputs.hasBankLoan
                                    ? 'No bank loan'
                                    : ecQuantumBinds
                                      ? `Max loan quantum (${msrBinds ? 'MSR 30%' : 'TDSR 55%'}): ${fmt(maxLoan)}`
                                      : 'Tap to edit — CPF first, then Cash'}
                                </Typography>
                              </Box>
                              {inputs.hasBankLoan ? (
                                // EC shows what the bank will lend (capped at the MSR/TDSR loan
                                // quantum); the excess is listed below as the Loan Shortfall.
                                <LoanRow value={propertyTab === 'ec' ? Math.min(derivedLoan, maxLoan) : derivedLoan}
                                  error={derivedLtvPct > maxLtvPct + 0.05}
                                  onCommit={(v) => setPendingLoanChange(v)} />
                              ) : (
                                <Typography sx={{ fontSize: '1.05rem', fontWeight: 800, color: highlightColor, flexShrink: 0 }}>
                                  {fmt(derivedLoan)}
                                </Typography>
                              )}
                            </Box>
                            {propertyTab === 'ec' && inputs.hasBankLoan && loanShortfall > 0 && (
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, mt: 0.75, mb: 0.5, px: 0.5 }}>
                                <Box sx={{ minWidth: 0 }}>
                                  <Typography sx={{ ...psRowLabelSx, fontWeight: 700, color: 'error.main' }}>Loan Shortfall</Typography>
                                  <Typography sx={psHintSx}>Above the loan quantum — pay by cash or fund pledging</Typography>
                                </Box>
                                <Typography sx={{ ...psValueSx, color: 'error.main', flexShrink: 0 }}>{fmt(loanShortfall)}</Typography>
                              </Box>
                            )}

                            <Divider sx={{ my: 1 }} />

                            {/* Total */}
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 0.5 }}>
                              <Typography sx={{ ...psRowLabelSx, fontWeight: 700 }}>Total</Typography>
                              <Typography sx={psValueSx}>{fmt(inputs.purchasePrice)}</Typography>
                            </Box>

                            {/* Alerts */}
                            {shortfallToCap > 0 && (
                              <Box sx={{ mt: 1.5, p: 1.25, borderRadius: 1.5, bgcolor: 'rgba(211,47,47,0.07)', border: '1px solid', borderColor: 'error.main', display: 'flex', alignItems: 'flex-start', gap: 0.75 }}>
                                <WarningAmberIcon sx={{ color: 'error.main', fontSize: 18, flexShrink: 0, mt: 0.15 }} />
                                <Box>
                                  <Typography variant="caption" sx={{ color: 'error.main', fontWeight: 800, display: 'block' }}>
                                    Short {fmt(shortfallToCap)} for {maxLtvPct}% LTV
                                  </Typography>
                                  <Typography variant="caption" sx={{ color: 'error.main' }}>
                                    Add more cash/CPF in Available Funds, or accept the higher LTV.
                                  </Typography>
                                </Box>
                              </Box>
                            )}
                            {derivedLtvPct > maxLtvPct + 0.05 && (
                              <Box sx={{ mt: 1, p: 1.25, borderRadius: 1.5, bgcolor: 'rgba(211,47,47,0.07)', border: '1px solid', borderColor: 'error.main', display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                <WarningAmberIcon sx={{ color: 'error.main', fontSize: 18, flexShrink: 0 }} />
                                <Typography variant="caption" sx={{ color: 'error.main', fontWeight: 800 }}>
                                  Exceeds Max LTV ({maxLtvPct}%) — reduce loan or increase down payment
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        );
                      })()}

                      {(propertyTab === 'hdb' || inputs.hasBankLoan) && <>
                      {/* ── Loan Result (see the loan shortfall before deciding how to bridge it) ── */}
                      {renderLoanResult()}

                      {/* ── Fund Pledging (only when there is a shortfall) ── */}
                      {renderLoanPledging()}
                      </>}

                      </Box>
                    </Card>
                  </>
                )}

                {/* ── HDB Grants ── */}
                {propertyTab === 'hdb' && (
                  <Card data-tutorial="hdb-grants" variant="outlined" sx={sectionCardSx}>
                    {renderSectionHeader('HDB Grants', { step: 6 })}
                    <Box sx={sectionBodySx}>
                    {[
                      {
                        label: 'CPF Housing Grant',
                        key: 'cpfResaleGrant' as const,
                        info: {
                          title: 'CPF Housing Grant',
                          body: 'A grant for first-timer families buying a resale HDB flat, credited to your CPF Ordinary Account. Up to $80,000 for eligible Singapore Citizen families, or up to $40,000 for eligible singles aged 35 and above — the amount depends on the flat size. Household income ceiling: $16,000/month ($24,000 for an extended or multi-generation family).',
                          url: 'https://www.hdb.gov.sg/buying-a-flat/flat-grant-and-loan-eligibility/couples-and-families/cpf-housing-grants',
                        },
                      },
                      {
                        label: 'Proximity Housing Grant (PHG)',
                        key: 'phgGrant' as const,
                        info: {
                          title: 'Proximity Housing Grant (PHG)',
                          body: 'A grant to encourage families to live with or near their parents or married child (within 4km) when buying a resale flat. Up to $30,000 for families, or up to $15,000 for eligible singles. No income ceiling applies.',
                          url: 'https://www.hdb.gov.sg/buying-a-flat/flat-grant-and-loan-eligibility/couples-and-families/proximity-housing-grant',
                        },
                      },
                      {
                        label: 'Enhanced CPF Housing Grant (EHG)',
                        key: 'housingGrant' as const,
                        info: {
                          title: 'Enhanced CPF Housing Grant (EHG)',
                          body: 'A means-tested grant for first-timer families buying a new or resale flat, on top of the other grants. Up to $120,000 for families, or up to $60,000 for eligible singles — the amount tapers as household income rises. Income ceiling: $9,000/month for families ($4,500/month for singles).',
                          url: 'https://www.hdb.gov.sg/buying-a-flat/flat-grant-and-loan-eligibility/couples-and-families/enhanced-cpf-housing-grant',
                        },
                      },
                    ].map((g, i, arr) => (
                      <Box key={g.key} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: i < arr.length - 1 ? 1 : 0 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                          <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.82rem' }}>{g.label}</Typography>
                          {renderFieldInfoIcon(g.info)}
                        </Box>
                        <Box sx={{ width: 180 }}>
                          <CurrencyTextField value={hdbInputs[g.key]} onChange={(v) => updateHdb(g.key, v)} highlightColor={highlightColor} />
                        </Box>
                      </Box>
                    ))}
                    </Box>
                  </Card>
                )}

                {/* HDB Loan (HDB concessionary) specific section */}
                {propertyTab === 'hdb' && hdbInputs.loanSource === 'hdb' && (
                  <Card data-tutorial="hdb-loan" variant="outlined" sx={sectionCardSx}>
                    {renderSectionHeader('Loan', { step: 7 })}
                    <Box sx={sectionBodySx}>

                    {/* ── Loan Info (IWAA + affordability) ── */}
                    {renderLoanInfo()}

                    {/* ── Loan Parameter ── */}
                    {renderLoanSubLabel('Loan Parameter')}

                    {/* Max Loan Tenure — HDB concessionary loan: up to 25 yrs / age 65, no
                        extension. Defaults to the eligible maximum; pencil to key in less. */}
                    <Box sx={{ mb: 2 }}>
                      <TenureInlineEditor
                        label={(
                          <>
                            <Typography variant="body2" fontWeight={700} sx={{ fontSize: '0.82rem', color: 'text.primary' }}>Loan Tenure</Typography>
                            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', lineHeight: 1.35 }}>
                              {inputs.tenure === standardTenureCap ? 'Max you are eligible for' : `Max eligible: ${standardTenureCap} yrs`}
                            </Typography>
                          </>
                        )}
                        value={inputs.tenure}
                        min={1}
                        max={Math.max(1, standardTenureCap)}
                        color={highlightColor}
                        maxMessage={`Max ${Math.max(1, standardTenureCap)} yrs (HDB loan: 25 yrs or age 65)`}
                        onCommit={(v) => updateInput('tenure', v)}
                      />
                    </Box>

                    {/* HDB Interest Rate — fixed at 2.6% */}
                    <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Typography variant="body2" fontWeight={700}>HDB Interest Rate</Typography>
                      <Typography variant="body2" fontWeight={700} sx={{ color: highlightColor }}>2.60%</Typography>
                    </Box>

                    {/* HDB Concessionary Loan Payment Structure */}
                    {inputs.purchasePrice > 0 && (() => {
                      const valBase = hdbInputs.valuationPrice > 0 ? Math.min(inputs.purchasePrice, hdbInputs.valuationPrice) : inputs.purchasePrice;
                      const cov = Math.max(0, inputs.purchasePrice - (hdbInputs.valuationPrice > 0 ? hdbInputs.valuationPrice : inputs.purchasePrice));
                      const maxLoan75 = Math.round(valBase * 0.75);
                      const otpDeposits = otpDeposit;
                      // HDB loan: no strict 5% cash rule, COV + OTP must be cash
                      const secondLoanSetAside = hdbInputs.isSecondHdbLoan ? hdbInputs.secondHdbLoanCashSetAside : 0;
                      // Cash bears only the cash portion of the expenses — the CPF-paid
                      // BSD/ABSD/legal/HPS come out of the CPF pool instead.
                      const hdbExpenses = bsd + absdAmount + inputs.legalFee + hdbInputs.caveatFee + hdbInputs.valuationFee + hdbInputs.hdbAdminFee + hdbInputs.hpsAmount + hdbAgencyFee - hdbCpfExpenses;
                      const totalCashIn = inputs.additionalCash + cashProceedFromSelling;
                      const maxCashForDown = Math.max(0, totalCashIn - hdbExpenses - otpDeposits - cov - secondLoanSetAside);
                      const availCpfGrants = Math.max(0, (inputs.cpf + inputs.additionalCpf) + hdbInputs.housingGrant + hdbInputs.cpfResaleGrant + hdbInputs.phgGrant - hdbCpfExpenses);
                      const hdbLoan = Math.max(0, inputs.purchasePrice - otpDeposits - cov - hdbCashAlloc - hdbCpfAlloc);
                      const hdbLtvPct = valBase > 0 ? (hdbLoan / valBase) * 100 : 0;
                      const overLtv = hdbLtvPct > 75.05;
                      // Any allocation beyond what's available lights up red — including the
                      // $0 Available Funds state, so a missing fund declaration is visible.
                      const cashExceeds = hdbCashAlloc > maxCashForDown;
                      const cpfExceeds = hdbCpfAlloc > availCpfGrants;
                      // Workings shown when the (i) beside each available figure is tapped
                      // (mirrors the Private payment structure's AvailableNote).
                      const hdbGrantsTotal = hdbInputs.housingGrant + hdbInputs.cpfResaleGrant + hdbInputs.phgGrant;
                      const hdbCashAvailDialog: CalcDialogInfo = {
                        title: 'Cash Available for Down Payment',
                        rows: [
                          { label: 'Additional Cash (savings)', value: fmt(inputs.additionalCash) },
                          ...(cashProceedFromSelling > 0 ? [{ label: '+ Sale Proceeds', value: fmt(cashProceedFromSelling) }] : []),
                          { isSeparator: true, label: '', value: '' },
                          { label: '= Total Cash', value: fmt(totalCashIn) },
                          { label: '− Expenses (cash portion)', value: fmt(hdbExpenses) },
                          { label: '− OTP Deposits (cash)', value: fmt(otpDeposits) },
                          ...(cov > 0 ? [{ label: '− COV (cash only)', value: fmt(cov) }] : []),
                          ...(secondLoanSetAside > 0 ? [{ label: '− 2nd HDB Loan set-aside', value: fmt(secondLoanSetAside) }] : []),
                          { isSeparator: true, label: '', value: '' },
                          { label: '= Available for Down Payment', value: fmt(maxCashForDown), isResult: true },
                        ],
                      };
                      const hdbCpfAvailDialog: CalcDialogInfo = {
                        title: 'CPF Available for Down Payment',
                        rows: [
                          { label: 'CPF (OA)', value: fmt(inputs.cpf) },
                          ...(inputs.additionalCpf > 0 ? [{ label: '+ Additional CPF', value: fmt(inputs.additionalCpf) }] : []),
                          ...(hdbGrantsTotal > 0 ? [{ label: '+ Grants (Housing / Resale / PHG)', value: fmt(hdbGrantsTotal) }] : []),
                          { isSeparator: true, label: '', value: '' },
                          { label: '= Total CPF + Grants', value: fmt(inputs.cpf + inputs.additionalCpf + hdbGrantsTotal) },
                          ...(hdbCpfExpenses > 0 ? [{ label: '− CPF-paid expenses (BSD/ABSD/Legal/HPS)', value: fmt(hdbCpfExpenses) }] : []),
                          { isSeparator: true, label: '', value: '' },
                          { label: '= Available for Down Payment', value: fmt(availCpfGrants), isResult: true },
                        ],
                      };
                      // Editable HDB loan — tap to type an amount; the down payment is re-derived
                      // (CPF used up first, then cash) via the confirmation dialog.
                      const HdbLoanRow = ({ value, onCommit, error }: { value: number; onCommit: (v: number) => void; error?: boolean }) => {
                        const [editing, setEditing] = React.useState(false);
                        const [draft, setDraft] = React.useState('');
                        const commit = () => {
                          setEditing(false);
                          const n = parseInt(draft.replace(/[^0-9]/g, ''), 10);
                          if (!isNaN(n) && n !== value) onCommit(n);
                        };
                        const color = error ? 'error.main' : highlightColor;
                        if (editing) {
                          return (
                            <TextField
                              size="small" autoFocus
                              value={draft === '' ? '' : Number(draft).toLocaleString('en-SG')}
                              placeholder="0"
                              inputProps={{ inputMode: 'numeric' }}
                              onChange={(e) => setDraft(e.target.value.replace(/[^0-9]/g, ''))}
                              onBlur={commit}
                              onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); if (e.key === 'Escape') setEditing(false); }}
                              InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                              sx={{ width: 150, '& input': { textAlign: 'right', py: '4px', fontSize: '0.85rem', fontWeight: 800 } }}
                            />
                          );
                        }
                        return (
                          <Box onClick={() => { setDraft(value === 0 ? '' : String(value)); setEditing(true); }}
                            sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, cursor: 'pointer', flexShrink: 0, '&:hover .hdb-loan-edit-icon': { opacity: 1 } }}>
                            <Typography variant="body2" fontWeight={700} sx={{ fontSize: '0.8rem', color }}>{fmt(value)}</Typography>
                            <EditIcon className="hdb-loan-edit-icon" sx={{ fontSize: 14, color, opacity: 0.6, transition: 'opacity 120ms' }} />
                          </Box>
                        );
                      };
                      return (
                        <Box sx={{ mt: 1 }}>
                          <Typography variant="body2" fontWeight={700} sx={{ mb: 1, fontSize: '0.82rem' }}>Payment Structure</Typography>
                          <Box sx={{ p: 1.5, borderRadius: 1, bgcolor: cardBg, border: '1px solid', borderColor: cardBorder, mb: 1 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                              <Typography variant="caption" sx={{ color: 'text.secondary' }}>Valuation Base</Typography>
                              <Typography variant="caption" fontWeight={600}>{fmt(valBase)}</Typography>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                              <Typography variant="caption" sx={{ color: 'text.secondary' }}>Max HDB Loan (75%)</Typography>
                              <Typography variant="caption" fontWeight={600} sx={{ color: '#0ea5e9' }}>{fmt(maxLoan75)}</Typography>
                            </Box>
                            {cov > 0 && <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                              <Typography variant="caption" sx={{ color: 'text.secondary' }}>COV (cash only)</Typography>
                              <Typography variant="caption" fontWeight={600} sx={{ color: 'error.main' }}>{fmt(cov)}</Typography>
                            </Box>}
                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                              <Typography variant="caption" sx={{ color: 'text.secondary' }}>OTP Deposits (cash)</Typography>
                              <Typography variant="caption" fontWeight={600}>{fmt(otpDeposits)}</Typography>
                            </Box>
                            {secondLoanSetAside > 0 && (
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                                <Typography variant="caption" sx={{ color: 'text.secondary' }}>2nd HDB Loan set-aside (cash)</Typography>
                                <Typography variant="caption" fontWeight={600} sx={{ color: 'error.main' }}>−{fmt(secondLoanSetAside)}</Typography>
                              </Box>
                            )}
                          </Box>

                          {/* Cash input */}
                          <Box sx={{ mb: 0.5 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                              <Box sx={{ flex: 1 }}>
                                <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.8rem' }}>Cash (down payment)</Typography>
                                <Typography variant="caption" sx={{ color: cashExceeds ? 'error.main' : 'text.secondary' }}>
                                  After expenses, max:{' '}
                                  <Box component="span" onClick={() => setHdbCashAlloc(maxCashForDown)}
                                    sx={{ color: 'primary.main', fontWeight: 700, cursor: 'pointer', textDecoration: 'underline', textUnderlineOffset: '2px' }}>
                                    {fmt(maxCashForDown)}
                                  </Box>
                                  <InfoOutlinedIcon onClick={() => setCalcDialog(hdbCashAvailDialog)}
                                    sx={{ fontSize: 15, color: 'primary.main', cursor: 'pointer', verticalAlign: 'text-bottom', ml: 0.4 }} />
                                </Typography>
                              </Box>
                              <HdbAllocField value={hdbCashAlloc} cap={maxCashForDown} base={inputs.purchasePrice} onChange={setHdbCashAlloc} highlightColor={highlightColor} exceeds={cashExceeds} />
                            </Box>
                          </Box>

                          {/* CPF + Grants input */}
                          <Box sx={{ mb: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                              <Box sx={{ flex: 1 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.8rem' }}>CPF + Grants</Typography>
                                  <IconButton size="small" sx={{ p: 0.25 }} onClick={() => setCpfInfoOpen(true)} aria-label="CPF usage info">
                                    <InfoOutlinedIcon fontSize="small" color="primary" />
                                  </IconButton>
                                </Box>
                                <Typography variant="caption" sx={{ color: cpfExceeds ? 'error.main' : 'text.secondary' }}>
                                  Available:{' '}
                                  <Box component="span" onClick={() => setHdbCpfAlloc(availCpfGrants)}
                                    sx={{ color: 'primary.main', fontWeight: 700, cursor: 'pointer', textDecoration: 'underline', textUnderlineOffset: '2px' }}>
                                    {fmt(availCpfGrants)}
                                  </Box>
                                  <InfoOutlinedIcon onClick={() => setCalcDialog(hdbCpfAvailDialog)}
                                    sx={{ fontSize: 15, color: 'primary.main', cursor: 'pointer', verticalAlign: 'text-bottom', ml: 0.4 }} />
                                </Typography>
                              </Box>
                              <HdbAllocField value={hdbCpfAlloc} cap={availCpfGrants} base={inputs.purchasePrice} onChange={setHdbCpfAlloc} highlightColor={highlightColor} exceeds={cpfExceeds} />
                            </Box>
                          </Box>

                          {(() => {
                            const retained = availCpfGrants - hdbCpfAlloc;
                            const maxRetain = inputs.includeAdditionalBorrower ? 40000 : 20000;
                            if (retained <= maxRetain) return null;
                            return (
                              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, mb: 1 }}>
                                <WarningAmberIcon sx={{ color: 'error.main', fontSize: 16, mt: '1px', flexShrink: 0 }} />
                                <Typography variant="caption" sx={{ color: 'error.main' }}>
                                  You can keep aside at most {fmt(maxRetain)} in your CPF OA{inputs.includeAdditionalBorrower ? ' (2 borrowers)' : ''} — currently keeping {fmt(retained)}.
                                </Typography>
                              </Box>
                            );
                          })()}
                          <Divider sx={{ my: 0.75 }} />
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, mb: 0.25 }}>
                            <Box sx={{ minWidth: 0 }}>
                              <Typography variant="body2" fontWeight={700} sx={{ fontSize: '0.8rem', color: overLtv ? 'error.main' : highlightColor }}>
                                HDB Loan (LTV {hdbLtvPct.toFixed(1)}%)
                              </Typography>
                              <Typography variant="caption" sx={{ color: 'text.secondary' }}>Tap to edit — CPF first, then Cash</Typography>
                            </Box>
                            <HdbLoanRow value={hdbLoan} error={overLtv} onCommit={(v) => setPendingHdbLoanChange(v)} />
                          </Box>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                            <Typography variant="body2" fontWeight={700} sx={{ fontSize: '0.8rem' }}>Total</Typography>
                            <Typography variant="body2" fontWeight={700} sx={{ fontSize: '0.8rem' }}>{fmt(inputs.purchasePrice)}</Typography>
                          </Box>
                          {overLtv && (
                            <Box sx={{ mt: 1, p: 1, borderRadius: 1, bgcolor: 'rgba(211,47,47,0.08)', border: '1px solid', borderColor: 'error.main', display: 'flex', alignItems: 'flex-start', gap: 0.75 }}>
                              <WarningAmberIcon sx={{ color: 'error.main', fontSize: 18, flexShrink: 0 }} />
                              <Box>
                                <Typography variant="caption" fontWeight={800} sx={{ color: 'error.main', display: 'block' }}>
                                  Short {fmt(Math.round(hdbLoan - maxLoan75))} — exceeds 75% LTV
                                </Typography>
                                <Typography variant="caption" sx={{ color: 'error.main' }}>Add more cash/CPF or negotiate a lower price.</Typography>
                              </Box>
                            </Box>
                          )}
                        </Box>
                      );
                    })()}

                    {/* HDB concessionary loans cannot be topped up by pledging assets,
                        so there is no Fund Pledging step — a shortfall must be closed with
                        more cash/CPF, a longer tenure, or a lower price. */}

                    {/* ── Loan Result ── */}
                    {renderLoanResult({
                      showShortfall: false,
                      loanAmount: derivedLoan,
                      ltvPctLabel: derivedLtvPct,
                      monthly: calcMonthlyInstallment(derivedLoan, HDB_CONCESSIONARY_INTEREST_RATE, inputs.tenure),
                    })}

                    {loanShortfall > 0 && (
                      <Box sx={{ mt: 1.5, p: 1.5, borderRadius: 1, bgcolor: 'rgba(211,47,47,0.10)', border: '2px solid', borderColor: 'error.main', display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                        <WarningAmberIcon sx={{ color: 'error.main', fontSize: 22, flexShrink: 0, mt: '1px' }} />
                        <Box>
                          <Typography variant="body2" fontWeight={800} sx={{ color: 'error.main', display: 'block' }}>
                            Loan shortfall {fmt(loanShortfall)}
                          </Typography>
                          <Typography variant="caption" sx={{ color: 'error.main', display: 'block', mt: 0.25, lineHeight: 1.5 }}>
                            Your income supports only {fmt(maxLoan)} but {fmt(derivedLoan)} is needed. Adding extra funds helps by reducing the loan amount needed.
                          </Typography>
                          <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: 'text.secondary', lineHeight: 1.6 }}>
                            <Box
                              component="span"
                              onClick={() => setHdbCashAlloc(hdbCashAlloc + loanShortfall)}
                              sx={{ cursor: 'pointer', color: highlightColor, fontWeight: 700, textDecoration: 'underline', textUnderlineOffset: '2px' }}
                            >
                              Pay by cash
                            </Box>
                            {' '}— adds {fmt(loanShortfall)} to Cash (down payment), reducing the loan needed.
                          </Typography>
                        </Box>
                      </Box>
                    )}
                    </Box>
                  </Card>
                )}


                {/* ── S&P Date ── */}
                <Card data-tutorial="schedule-date" variant="outlined" sx={sectionCardSx}>
                  {renderSectionHeader('Schedule', { step: propertyTab === 'hdb' ? 8 : 6 })}
                  <Box sx={sectionBodySx}>

                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" sx={{ mb: 0.75, color: 'text.secondary', fontWeight: 500, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <CalendarTodayIcon sx={{ fontSize: 15 }} />
                      {propertyTab === 'hdb' ? 'OTP Date' : 'OTP / S&P Date'}
                    </Typography>
                    <TextField
                      size="small"
                      fullWidth
                      type="date"
                      value={inputs.spDate}
                      onChange={(e) => updateInput('spDate', e.target.value)}
                      helperText={propertyTab === 'hdb' ? 'HDB resale timeline calculated from this date' : 'Progressive schedule dates calculated from this date'}
                    />
                  </Box>
                  </Box>
                </Card>
                  </>
                )}
              </Box>
            </Grid>

            {/* ── RIGHT: Results + Schedule ── */}
            {/* Loan Affordability & Income-support (pledging) now live inside the
                left-hand Loan section — see renderLoanInfo / renderLoanResult /
                renderLoanPledging. */}
            <Grid item xs={12} md={7}>
              <Box sx={columnScrollSx}>
              {calculatorMode === 'simple' ? (() => {
                // Simple mode honours the New Launch / Resale choice: resale uses a flat
                // 25% cash down + the resale (OTP 1% → exercise 4% → balance 95%) timeline,
                // HDB uses its resale schedule, and new-launch/EC uses the progressive schedule.
                const simpleResale = propertyTab === 'private' && privateSubTab === 'resale';
                const simpleHdb = propertyTab === 'hdb';
                const simpleHasLoan = !simpleHdb || hdbInputs.loanSource !== 'none';
                // Use the active regulatory cap directly so the label remains correct
                // even before a purchase price has been entered (when derived LTV is 0).
                const simpleLtv = simpleHdb ? ltvDecimal : maxLtvPct / 100;
                const simpleInstallmentRate = simpleHdb && hdbInputs.loanSource === 'hdb'
                  ? HDB_CONCESSIONARY_INTEREST_RATE
                  : inputs.interestRate;
                return (
                <>
                <ResultsSummary
                  inputs={{ ...simpleOutputInputs, hasBankLoan: simpleHasLoan }}
                  ltvDecimal={simpleLtv}
                  fmt={fmt}
                  cardBg={cardBg}
                  cardBorder={cardBorder}
                  onExplain={setCalcDialog}
                  computedStages={(propertyTab === 'hdb' || simpleResale) ? [] : simpleComputedStages}
                  stagedDownPayment={propertyTab !== 'hdb' && !simpleResale}
                  cashAvailable={0}
                  cpfAvailable={0}
                  monthlyCpfOa={0}
                  downCashAlloc={simpleResale ? Math.round(inputs.purchasePrice * 0.25) : undefined}
                  downCpfAlloc={simpleResale ? 0 : undefined}
                  minCashPct={minCashPct}
                  hdbOutlay={propertyTab === 'hdb' ? hdbOutlay : null}
                  hdbLoanSource={simpleHdb ? hdbInputs.loanSource : undefined}
                  hdbMandatoryCash={simpleHdb ? hdbResaleFinancing.mandatoryCash : 0}
                  ltvPass
                  loanTypeLabel={simpleHdb && hdbInputs.loanSource === 'hdb' ? 'HDB Loan' : 'Bank Loan'}
                  ltvBase={simpleHdb ? hdbResaleFinancing.valuationBase : undefined}
                  ltvBaseLabel={simpleHdb && hdbResaleFinancing.valuationBase < inputs.purchasePrice ? 'Valuation Price' : undefined}
                  installmentRate={simpleInstallmentRate}
                  absdRateOverride={simpleAbsdRatePct}
                  hideFundBalance
                  simpleMode
                  simpleResale={simpleResale}
                />
                {propertyTab === 'hdb' ? renderHdbSchedule() : (
                  <Card data-tutorial="payment-schedule" variant="outlined" sx={{ p: { xs: 2, sm: 3 }, bgcolor: 'background.paper', borderColor: cardBorder }}>
                    {simpleResale ? (
                    <ResaleSchedule
                      purchasePrice={inputs.purchasePrice}
                      derivedLoan={derivedLoan}
                      interestRate={inputs.interestRate}
                      tenure={inputs.tenure}
                      hasBankLoan
                      absdAmount={simpleAbsdAmount}
                      absdRatePct={simpleAbsdRatePct}
                      dates={resaleDates}
                      onDateChange={commitResaleDate}
                      fmt={fmt}
                      cardBg={cardBg}
                      cardBorder={cardBorder}
                      onExplain={setCalcDialog}
                      showDates={showTimelineDates}
                      onShowDatesChange={setShowTimelineDates}
                    />
                  ) : (
                    <ProgressiveSchedule
                      onStageDateChange={commitProgressiveDate}
                      stageDateOverrides={stageDateOverrides}
                      computedStages={simpleComputedStages}
                      onGroupsChange={setScheduleGroups}
                      fmt={fmt}
                      spDateStr={inputs.spDate}
                      purchasePrice={inputs.purchasePrice}
                      interestRate={inputs.interestRate}
                      tenure={inputs.tenure}
                      absdAmount={simpleAbsdAmount}
                      absdRatePct={simpleAbsdRatePct}
                      onExplain={setCalcDialog}
                      onSpDateChange={(d) => { updateInput('spDate', d); setStageDateOverrides({}); }}
                      showDates={showTimelineDates}
                      onShowDatesChange={setShowTimelineDates}
                      simpleMode
                    />
                    )}
                  </Card>
                )}
                </>
                );
              })() : (
                <>
                {/* Results Summary. Loan Eligibility is temporarily omitted for every
                    role so admins see the same Bank Loan summary as other users. */}
                <ResultsSummary
                  inputs={{
                    ...inputs,
                    hasBankLoan: propertyTab === 'hdb'
                      ? hdbInputs.loanSource !== 'none'
                      : inputs.hasBankLoan,
                  }}
                  ltvDecimal={ltvDecimal}
                  fmt={fmt}
                  cardBg={cardBg}
                  cardBorder={cardBorder}
                  onExplain={setCalcDialog}
                  bsdByCpf={bsdByCpf}
                  absdByCpf={absdByCpf}
                  legalFeeByCpf={legalFeeByCpf}
                  effectivePledged={effectivePledged}
                  effectiveUnpledged={effectiveUnpledged}
                  computedStages={computedStages}
                  stagedDownPayment={(propertyTab === 'private' && privateSubTab === 'new-launch') || propertyTab === 'ec'}
                  cashAvailable={inputs.additionalCash + cashProceedFromSelling - (propertyTab === 'ec' ? resaleLevy : 0) - (propertyTab === 'hdb' && hdbInputs.loanSource === 'hdb' && hdbInputs.isSecondHdbLoan ? hdbInputs.secondHdbLoanCashSetAside : 0)}
                  cpfAvailable={inputs.cpf + inputs.additionalCpf + (propertyTab === 'hdb' ? hdbInputs.housingGrant + hdbInputs.cpfResaleGrant + hdbInputs.phgGrant : 0) + (propertyTab === 'ec' ? ecHousingGrant : 0)}
                  hdbCpfUsed={propertyTab === 'hdb' ? hdbCpfAlloc : 0}
                  downCashAlloc={propertyTab !== 'hdb' ? minCash + cashDownAlloc : undefined}
                  downCpfAlloc={propertyTab !== 'hdb' ? cpfDownAlloc : undefined}
                  minCashPct={minCashPct}
                  monthlyCpfOa={getMonthlyCpfOa(inputs.primary) + (inputs.includeAdditionalBorrower ? getMonthlyCpfOa(inputs.additional) : 0)}
                  bridgingAmount={bridgingForLoan}
                  hdbOutlay={hdbOutlay}
                  ltvPass={ltvPass}
                  loanTypeLabel={propertyTab === 'hdb' && hdbInputs.loanSource === 'hdb' ? 'HDB Loan' : 'Bank Loan'}
                  ltvBase={propertyTab === 'hdb' ? hdbResaleFinancing.valuationBase : undefined}
                  ltvBaseLabel={propertyTab === 'hdb' && hdbResaleFinancing.valuationBase < inputs.purchasePrice ? 'Valuation Price' : undefined}
                  installmentRate={effectiveInterestRate}
                  absdRateOverride={absdRatePct}
                  detailedResale={propertyTab === 'private' && privateSubTab === 'resale'}
                />

                {/* Progressive Schedule — private new-launch & EC */}
                {((propertyTab === 'private' && privateSubTab === 'new-launch') || propertyTab === 'ec') && (
                  <Card data-tutorial="payment-schedule" variant="outlined" sx={{ p: { xs: 2, sm: 3 }, bgcolor: 'background.paper', borderColor: cardBorder }}>
                    <ProgressiveSchedule
                      onStageDateChange={commitProgressiveDate}
                      stageDateOverrides={stageDateOverrides}
                      computedStages={computedStages}
                      onGroupsChange={setScheduleGroups}
                      fmt={fmt}
                      spDateStr={inputs.spDate}
                      purchasePrice={inputs.purchasePrice}
                      interestRate={inputs.interestRate}
                      tenure={inputs.tenure}
                      absdAmount={absdAmount}
                      absdRatePct={absdRatePct}
                      onExplain={setCalcDialog}
                      onSpDateChange={(d) => {
                        updateInput('spDate', d);
                        setStageDateOverrides({});
                        // OTP anchors the progressive timeline. Clear pinned S&P dates
                        // so every later milestone is re-derived after the anchor moves.
                        setExerciseDateOverride(null);
                        setSp15DateOverride(null);
                      }}
                      onExerciseDateChange={setExerciseDateOverride}
                      onSp15DateChange={setSp15DateOverride}
                      exerciseOverridden={exerciseDateOverride !== null}
                      sp15Overridden={sp15DateOverride !== null}
                      showDates={showTimelineDates}
                      onShowDatesChange={setShowTimelineDates}
                    />
                  </Card>
                )}

                {/* Resale Schedule — private resale */}
                {propertyTab === 'private' && privateSubTab === 'resale' && (
                  <Card data-tutorial="payment-schedule" variant="outlined" sx={{ p: { xs: 2, sm: 3 }, bgcolor: 'background.paper', borderColor: cardBorder }}>
                    <ResaleSchedule
                      purchasePrice={inputs.purchasePrice}
                      derivedLoan={derivedLoan}
                      interestRate={inputs.interestRate}
                      tenure={inputs.tenure}
                      hasBankLoan={inputs.hasBankLoan}
                      absdAmount={absdAmount}
                      absdRatePct={absdRatePct}
                      dates={resaleDates}
                      onDateChange={commitResaleDate}
                      fmt={fmt}
                      cardBg={cardBg}
                      cardBorder={cardBorder}
                      onExplain={setCalcDialog}
                      showDates={showTimelineDates}
                      onShowDatesChange={setShowTimelineDates}
                    />
                  </Card>
                )}

                {/* HDB Schedule */}
                {propertyTab === 'hdb' && renderHdbSchedule()}
                </>
              )}
              </Box>
            </Grid>
          </Grid>
        </>
      )}
      {/* ── EC Tab (old, removed — handled by generalized block above) ── */}
      {false && (
        <>
          <Grid container spacing={3}>
            <Grid item xs={12} md={5}>
              <Card variant="outlined" sx={{ p: { xs: 2, sm: 3 }, bgcolor: 'background.paper', borderColor: cardBorder }}>
                <Box sx={{ mx: -3, mt: -3, mb: 2.5, px: 3, py: 1.5, bgcolor: '#0ea5e9', borderRadius: '4px 4px 0 0' }}>
                  <Typography variant="subtitle1" fontWeight={800} sx={{ color: '#fff', textAlign: 'center', letterSpacing: '0.1em' }}>EC PAYMENT BREAKDOWN</Typography>
                </Box>

                {/* Reuse same inputs as private — purchase price, loan, CPF, etc. */}
                <Box sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                    <Box sx={{ flex: 1, height: 1, bgcolor: 'divider' }} />
                    <Typography variant="subtitle2" fontWeight={700} sx={{ px: 2 }}>Property</Typography>
                    <Box sx={{ flex: 1, height: 1, bgcolor: 'divider' }} />
                  </Box>
                  <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 1 }}>Purchase Price</Typography>
                  <TextField fullWidth size="small" type="number" value={inputs.purchasePrice || ''}
                    onChange={e => updateInput('purchasePrice', Number(e.target.value))}
                    InputProps={{ startAdornment: <InputAdornment position="start">S$</InputAdornment> }} sx={{ mb: 1.5 }} />

                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                    <Box sx={{ flex: 1, height: 1, bgcolor: 'divider' }} />
                    <Typography variant="subtitle2" fontWeight={700} sx={{ px: 2 }}>Loan</Typography>
                    <Box sx={{ flex: 1, height: 1, bgcolor: 'divider' }} />
                  </Box>
                  <FormControlLabel control={<Checkbox checked={inputs.hasBankLoan} onChange={e => updateInput('hasBankLoan', e.target.checked)} size="small" />}
                    label={<Typography variant="body2">Bank Loan (Max 75% LTV)</Typography>} sx={{ mb: 1 }} />
                  {inputs.hasBankLoan && (
                    <>
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>Bank Rate (%)</Typography>
                      <TextField fullWidth size="small" type="number" value={inputs.interestRate}
                        onChange={e => updateInput('interestRate', Number(e.target.value))}
                        InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }} sx={{ mb: 1.5, mt: 0.5 }} />
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>Tenure (years)</Typography>
                      <TextField fullWidth size="small" type="number" value={inputs.tenure}
                        onChange={e => updateInput('tenure', Number(e.target.value))} sx={{ mb: 1.5, mt: 0.5 }} />
                    </>
                  )}

                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                    <Box sx={{ flex: 1, height: 1, bgcolor: 'divider' }} />
                    <Typography variant="subtitle2" fontWeight={700} sx={{ px: 2 }}>CPF</Typography>
                    <Box sx={{ flex: 1, height: 1, bgcolor: 'divider' }} />
                  </Box>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>CPF OA</Typography>
                  <TextField fullWidth size="small" type="number" value={inputs.cpf || ''}
                    onChange={e => updateInput('cpf', Number(e.target.value))}
                    InputProps={{ startAdornment: <InputAdornment position="start">S$</InputAdornment> }} sx={{ mb: 1, mt: 0.5 }} />
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>Additional CPF</Typography>
                  <TextField fullWidth size="small" type="number" value={inputs.additionalCpf || ''}
                    onChange={e => updateInput('additionalCpf', Number(e.target.value))}
                    InputProps={{ startAdornment: <InputAdornment position="start">S$</InputAdornment> }} sx={{ mb: 1, mt: 0.5 }} />
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>Additional Cash</Typography>
                  <TextField fullWidth size="small" type="number" value={inputs.additionalCash || ''}
                    onChange={e => updateInput('additionalCash', Number(e.target.value))}
                    InputProps={{ startAdornment: <InputAdornment position="start">S$</InputAdornment> }} sx={{ mb: 1, mt: 0.5 }} />
                </Box>
              </Card>
            </Grid>

            <Grid item xs={12} md={7}>
              <ResultsSummary inputs={inputs} ltvDecimal={ltvDecimal} fmt={fmt} cardBg={cardBg} cardBorder={cardBorder} onExplain={setCalcDialog} />
              <Card variant="outlined" sx={{ p: { xs: 2, sm: 3 }, bgcolor: 'background.paper', borderColor: cardBorder }}>
                <ProgressiveSchedule
                      onStageDateChange={commitProgressiveDate}
                      stageDateOverrides={stageDateOverrides}
                  computedStages={computedStages}
                  fmt={fmt}
                  spDateStr={inputs.spDate}
                  purchasePrice={inputs.purchasePrice}
                  interestRate={inputs.interestRate}
                  tenure={inputs.tenure}
                  absdAmount={absdAmount}
                  absdRatePct={absdRatePct}
                  onExplain={setCalcDialog}
                  onSpDateChange={(d) => { updateInput('spDate', d); setStageDateOverrides({}); }}
                  showDates={showTimelineDates}
                  onShowDatesChange={setShowTimelineDates}
                />
              </Card>
            </Grid>
          </Grid>
        </>
      )}

      {/* ── Tutorial ── */}
      <CalculatorTutorial
        controller={tutorial}
        title="Buying Calculator Tutorial"
        manualName="Super Buying Calculator"
        steps={BUYING_TUTORIAL_STEPS}
        manualExtraSections={BUYING_MANUAL_SECTIONS}
      />

      {/* ── What's New ── */}
      <WhatsNewDialog controller={whatsNew} />

      {/* ── Clear Calculator Confirmation Dialog ── */}
      <Dialog open={clearDialogOpen} onClose={() => setClearDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, fontSize: '1rem', display: 'flex', alignItems: 'center', gap: 1 }}>
          <RestartAltIcon color="error" />
          Reset Buying Calculator?
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            This will reset all Buying Calculator values to their original defaults and clear the selected example or history entry.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Your saved history entries will not be deleted.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 2, py: 1 }}>
          <Button onClick={() => setClearDialogOpen(false)} size="small">Cancel</Button>
          <Button onClick={clearCalculator} size="small" variant="contained" color="error">
            Clear &amp; Reset
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Calc Explanation Dialog (shared across all tabs) ── */}
      {calcDialog && (
        <Dialog open onClose={() => setCalcDialog(null)} maxWidth="xs" fullWidth>
          <DialogTitle sx={{ fontWeight: 700, fontSize: '1rem', pb: 1 }}>{calcDialog.title}</DialogTitle>
          <DialogContent dividers sx={{ p: 0 }}>
            <Box sx={{ p: 2 }}>
              {calcDialog.rows.map((row, i) =>
                row.isSeparator ? (
                  <Divider key={i} sx={{ my: 1 }} />
                ) : row.note ? (
                  <Typography key={i} variant="caption" sx={{ display: 'block', color: 'text.secondary', lineHeight: 1.5, mb: 1 }}>
                    {row.label}
                  </Typography>
                ) : (
                  <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 0.5, gap: 1 }}>
                    <Typography variant="body2" sx={{ color: row.isResult ? 'text.primary' : 'text.secondary', fontWeight: row.isResult ? 700 : 400, flex: 1, fontFamily: row.label.startsWith('=') || row.label.startsWith('Formula') ? 'monospace' : undefined }}>
                      {row.label}
                    </Typography>
                    {row.value !== '' && (
                      <Typography variant="body2" fontWeight={row.isResult ? 700 : 600}
                        sx={{ color: row.isResult ? '#D97706' : 'text.primary', flexShrink: 0, fontFamily: 'monospace' }}>
                        {row.value}
                      </Typography>
                    )}
                  </Box>
                )
              )}
            </Box>
          </DialogContent>
          <DialogActions sx={{ px: 2, py: 1 }}>
            <Typography variant="caption" sx={{ color: 'text.disabled', flex: 1 }}>Tap any value for its working</Typography>
            <Button onClick={() => setCalcDialog(null)} size="small" sx={{ color: '#D97706' }}>Close</Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Field explainer dialog (HPS / grants) — description + reference link. */}
      {fieldInfo && (
        <Dialog open onClose={() => setFieldInfo(null)} maxWidth={fieldInfo.table ? 'sm' : 'xs'} fullWidth>
          <DialogTitle sx={{ fontWeight: 700, fontSize: '1rem', pb: 1 }}>{fieldInfo.title}</DialogTitle>
          <DialogContent dividers>
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1.5, whiteSpace: 'pre-line' }}>{fieldInfo.body}</Typography>
            {fieldInfo.table && (
              <Box sx={{ overflowX: 'auto', mb: 1.5 }}>
                <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem', '& th, & td': { border: `1px solid ${cardBorder}`, px: 1, py: 0.75, verticalAlign: 'top', textAlign: 'left' } }}>
                  <thead>
                    <tr>
                      {fieldInfo.table.headers.map((h, i) => (
                        <Box component="th" key={i} sx={{ fontWeight: 700, bgcolor: isDark ? 'rgba(255,255,255,0.06)' : theme.palette.grey[100], color: i === 2 ? highlightColor : 'text.primary' }}>{h}</Box>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {fieldInfo.table.rows.map((row, r) => (
                      <tr key={r}>
                        {row.map((cell, c) => (
                          <Box component="td" key={c} sx={{ fontWeight: c === 0 ? 700 : 400, color: c === 0 ? 'text.primary' : 'text.secondary', width: c === 0 ? '28%' : undefined }}>{cell}</Box>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </Box>
              </Box>
            )}
            {fieldInfo.footnote && (
              <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1.5 }}>{fieldInfo.footnote}</Typography>
            )}
            {fieldInfo.embed ? (
              <Link href={fieldInfo.url} target="_blank" rel="noopener noreferrer" variant="body2" sx={{ color: '#D97706', fontWeight: 700 }}>
                Open the HPS premium calculator ↗
              </Link>
            ) : (
              <Link href={fieldInfo.url} target="_blank" rel="noopener noreferrer" variant="body2" sx={{ color: '#D97706', fontWeight: 600, wordBreak: 'break-all' }}>
                {fieldInfo.url}
              </Link>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 2, py: 1 }}>
            <Button onClick={() => setFieldInfo(null)} size="small" sx={{ color: '#D97706' }}>Close</Button>
          </DialogActions>
        </Dialog>
      )}

      {/* ── CPF OA Retention Info Dialog (tap-friendly) ── */}
      <Dialog open={cpfInfoOpen} onClose={() => setCpfInfoOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, fontSize: '1rem', pb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
          <InfoOutlinedIcon fontSize="small" color="primary" />
          Using CPF for your home
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            For a housing loan, you can choose to retain up to <strong>$20,000</strong> in your CPF Ordinary Account
            instead of using it all toward the flat. The amount set aside continues to earn CPF interest and acts as
            a buffer for future monthly instalments.
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Read CPF&apos;s official guidance:{' '}
            <Link
              href="https://www.cpf.gov.sg/member/home-ownership/using-your-cpf-to-buy-a-home/retain-20000-in-your-oa-if-you-are-taking-a-housing-loan"
              target="_blank"
              rel="noopener noreferrer"
              sx={{ color: '#D97706', fontWeight: 600 }}
            >
              Retain $20,000 in your OA
            </Link>
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 2, py: 1 }}>
          <Button onClick={() => setCpfInfoOpen(false)} size="small" sx={{ color: '#D97706' }}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* ── Stretch Loan Tenure Confirmation Dialog ── */}
      <Dialog
        open={stretchDialogOpen}
        onClose={() => { setStretchDialogOpen(false); setPendingTenure(null); }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700, fontSize: '1rem', pb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningAmberIcon sx={{ color: '#D97706' }} />
          Stretch Loan Tenure to {pendingTenure ?? ''} yrs?
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            Stretching the loan tenure beyond {standardTenureCap} years triggers the MAS tighter LTV rules:
          </Typography>
          <Box component="ul" sx={{ pl: 2.5, mb: 1.5, '& li': { mb: 0.5 } }}>
            <Typography component="li" variant="body2">
              Minimum cash downpayment increases to <b>10%</b> (from 5%)
            </Typography>
            <Typography component="li" variant="body2">
              Maximum LTV is capped at <b>55%</b>
            </Typography>
          </Box>
          <Typography variant="caption" color="text.secondary">
            {ecFixedTenure
              ? `To revert, untick Stretch or key in ${standardTenureCap} years or less.`
              : `To revert, drag the tenure back to ${standardTenureCap} years or less.`}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 2, py: 1 }}>
          <Button
            onClick={() => { setStretchDialogOpen(false); setPendingTenure(null); }}
            size="small"
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              if (pendingTenure !== null) updateInput('tenure', pendingTenure);
              setPendingTenure(null);
              setStretchDialogOpen(false);
            }}
            size="small"
            variant="contained"
            sx={{ bgcolor: '#D97706', '&:hover': { bgcolor: '#b45309' } }}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Edit Bank Loan Confirmation Dialog ── */}
      <Dialog open={pendingLoanChange !== null} onClose={() => setPendingLoanChange(null)} maxWidth="xs" fullWidth>
        {pendingLoanChange !== null && (() => {
          const pending = pendingLoanChange;
          const price = inputs.purchasePrice;
          const { clampedLoan, cpfAllocation, cashAllocation } = getLoanChangeAllocation(pending);
          const newLtv = price > 0 ? (clampedLoan / price) * 100 : 0;
          return (
            <>
              <DialogTitle sx={{ fontWeight: 700, fontSize: '1rem', pb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                <WarningAmberIcon sx={{ color: '#D97706' }} />
                Set loan to {fmt(clampedLoan)}?
              </DialogTitle>
              <DialogContent dividers>
                <Typography variant="body2" sx={{ mb: 1.5 }}>
                  Editing the loan recalculates your Payment Structure. The down payment needed is re-allocated:
                </Typography>
                <Box component="ul" sx={{ pl: 2.5, mb: 1.5, '& li': { mb: 0.5 } }}>
                  <Typography component="li" variant="body2">New LTV: <b>{newLtv.toFixed(1)}%</b></Typography>
                  <Typography component="li" variant="body2">CPF: <b>{fmt(cpfAllocation)}</b> (used first)</Typography>
                  <Typography component="li" variant="body2">Additional Cash: <b>{fmt(cashAllocation)}</b> (remaining balance)</Typography>
                </Box>
                <Typography variant="caption" color="text.secondary">
                  The mandatory {minCashPct}% cash ({fmt(minCash)}) stays unchanged. CPF reserved for stamp duty or legal fees is excluded.
                </Typography>
              </DialogContent>
              <DialogActions sx={{ px: 2, py: 1 }}>
                <Button onClick={() => setPendingLoanChange(null)} size="small">Cancel</Button>
                <Button
                  onClick={() => { applyLoanChange(pending); setPendingLoanChange(null); }}
                  size="small" variant="contained"
                  sx={{ bgcolor: '#D97706', '&:hover': { bgcolor: '#b45309' } }}
                >
                  Confirm
                </Button>
              </DialogActions>
            </>
          );
        })()}
      </Dialog>

      {/* ── HDB Loan Change Confirmation Dialog ── */}
      <Dialog open={pendingHdbLoanChange !== null} onClose={() => setPendingHdbLoanChange(null)} maxWidth="xs" fullWidth>
        {pendingHdbLoanChange !== null && (() => {
          const pending = pendingHdbLoanChange;
          const { clampedLoan, cpfAllocation, cashAllocation, newLtv } = getHdbLoanChangeAllocation(pending);
          return (
            <>
              <DialogTitle sx={{ fontWeight: 700, fontSize: '1rem', pb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                <WarningAmberIcon sx={{ color: '#D97706' }} />
                Set {hdbInputs.loanSource === 'bank' ? 'bank' : 'HDB'} loan to {fmt(clampedLoan)}?
              </DialogTitle>
              <DialogContent dividers>
                <Typography variant="body2" sx={{ mb: 1.5 }}>
                  Editing the loan recalculates your Payment Structure. The down payment is re-allocated:
                </Typography>
                <Box component="ul" sx={{ pl: 2.5, mb: 1.5, '& li': { mb: 0.5 } }}>
                  <Typography component="li" variant="body2">New LTV: <b>{newLtv.toFixed(1)}%</b></Typography>
                  <Typography component="li" variant="body2">CPF + Grants: <b>{fmt(cpfAllocation)}</b> (used first)</Typography>
                  <Typography component="li" variant="body2">Cash (down payment): <b>{fmt(cashAllocation)}</b> (remaining balance)</Typography>
                </Box>
                <Typography variant="caption" color="text.secondary">
                  {hdbInputs.loanSource === 'bank'
                    ? `The OTP deposit (${fmt(otpDeposit)}) counts toward the 5% minimum cash down payment; any remaining minimum cash and COV stay payable in cash.`
                    : `The OTP deposit (${fmt(otpDeposit)}) and any COV stay payable in cash.`}
                </Typography>
              </DialogContent>
              <DialogActions sx={{ px: 2, py: 1 }}>
                <Button onClick={() => setPendingHdbLoanChange(null)} size="small">Cancel</Button>
                <Button
                  onClick={() => { applyHdbLoanChange(pending); setPendingHdbLoanChange(null); }}
                  size="small" variant="contained"
                  sx={{ bgcolor: '#D97706', '&:hover': { bgcolor: '#b45309' } }}
                >
                  Confirm
                </Button>
              </DialogActions>
            </>
          );
        })()}
      </Dialog>

      {/* ── Remove Additional Borrower Confirmation Dialog ── */}
      <Dialog
        open={removeBorrowerDialogOpen}
        onClose={() => setRemoveBorrowerDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700, fontSize: '1rem', pb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningAmberIcon sx={{ color: '#D97706' }} />
          Remove Additional Borrower?
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ mb: 1 }}>
            All inputs for the additional borrower (age, income, debt, citizenship) will be cleared.
          </Typography>
          <Typography variant="caption" color="text.secondary">
            You can re-add them later, but the data won't be restored.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 2, py: 1 }}>
          <Button onClick={() => setRemoveBorrowerDialogOpen(false)} size="small">Cancel</Button>
          <Button
            onClick={() => {
              updateInput('includeAdditionalBorrower', false);
              setRemoveBorrowerDialogOpen(false);
            }}
            size="small"
            variant="contained"
            color="error"
          >
            Remove
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Remove Primary Borrower Confirmation Dialog ── */}
      <Dialog
        open={removePrimaryDialogOpen}
        onClose={() => setRemovePrimaryDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700, fontSize: '1rem', pb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningAmberIcon sx={{ color: '#D97706' }} />
          Remove Primary Borrower?
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ mb: 1 }}>
            The <b>Additional Borrower</b> will be promoted to Primary, and their inputs will replace the current primary's.
          </Typography>
          <Typography variant="caption" color="text.secondary">
            The current primary borrower's inputs will be cleared.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 2, py: 1 }}>
          <Button onClick={() => setRemovePrimaryDialogOpen(false)} size="small">Cancel</Button>
          <Button
            onClick={() => {
              promoteAdditionalToPrimary();
              setRemovePrimaryDialogOpen(false);
            }}
            size="small"
            variant="contained"
            color="error"
          >
            Remove & Promote
          </Button>
        </DialogActions>
      </Dialog>


      {/* ── HDB Tab (old, removed — handled by generalized block above) ── */}
      {false && (
        <Grid container spacing={3}>
          {/* Left: HDB Inputs */}
          <Grid item xs={12} md={5}>
            <Card variant="outlined" sx={{ p: { xs: 2, sm: 3 }, bgcolor: 'background.paper', borderColor: cardBorder }}>
              <Box sx={{ mx: -3, mt: -3, mb: 2.5, px: 3, py: 1.5, bgcolor: '#D97706', borderRadius: '4px 4px 0 0' }}>
                <Typography variant="subtitle1" fontWeight={800} sx={{ color: '#fff', textAlign: 'center', letterSpacing: '0.1em' }}>HDB PAYMENT BREAKDOWN</Typography>
              </Box>

              {/* Property */}
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                <Box sx={{ flex: 1, height: 1, bgcolor: 'divider' }} />
                <Typography variant="subtitle2" fontWeight={700} sx={{ px: 2 }}>Property</Typography>
                <Box sx={{ flex: 1, height: 1, bgcolor: 'divider' }} />
              </Box>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>Purchase Price</Typography>
              <TextField fullWidth size="small" type="number" value={hdbInputs.purchasePrice || ''}
                onChange={e => updateHdb('purchasePrice', Number(e.target.value))}
                InputProps={{ startAdornment: <InputAdornment position="start">S$</InputAdornment> }} sx={{ mb: 1.5, mt: 0.5 }} />
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>Valuation Price</Typography>
              <TextField fullWidth size="small" type="number" value={hdbInputs.valuationPrice || ''}
                onChange={e => updateHdb('valuationPrice', Number(e.target.value))}
                InputProps={{ startAdornment: <InputAdornment position="start">S$</InputAdornment> }} sx={{ mb: 1, mt: 0.5 }} />
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>Cash Over Value (COV)</Typography>
              <TextField fullWidth size="small" disabled value={Math.max(0, hdbInputs.purchasePrice - hdbInputs.valuationPrice).toLocaleString('en-SG')}
                InputProps={{ startAdornment: <InputAdornment position="start">S$</InputAdornment> }} sx={{ mb: 1.5, mt: 0.5 }} />

              {/* Loan */}
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                <Box sx={{ flex: 1, height: 1, bgcolor: 'divider' }} />
                <Typography variant="subtitle2" fontWeight={700} sx={{ px: 2 }}>Loan</Typography>
                <Box sx={{ flex: 1, height: 1, bgcolor: 'divider' }} />
              </Box>
              <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 0.5 }}>Loan Source</Typography>
              <RadioGroup row value={hdbInputs.loanSource} onChange={e => updateHdb('loanSource', e.target.value as HdbInputs['loanSource'])} sx={{ mb: 1.5 }}>
                <FormControlLabel value="hdb" control={<Radio size="small" />} label={<Typography variant="body2">HDB</Typography>} />
                <FormControlLabel value="bank" control={<Radio size="small" />} label={<Typography variant="body2">Bank</Typography>} />
                <FormControlLabel value="none" control={<Radio size="small" />} label={<Typography variant="body2">No Loan</Typography>} />
              </RadioGroup>

              {hdbInputs.loanSource !== 'none' && (
                <>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>Maximum Loan Tenure</Typography>
                  <TextField fullWidth size="small" type="number" value={hdbInputs.tenure}
                    onChange={e => updateHdb('tenure', Number(e.target.value))}
                    inputProps={{ max: hdbInputs.loanSource === 'hdb' ? 25 : 30 }} sx={{ mb: 1.5, mt: 0.5 }} />
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    {hdbInputs.loanSource === 'hdb' ? 'HDB Interest Rate' : 'Bank Interest Rate'}
                  </Typography>
                  <TextField fullWidth size="small" type="number"
                    value={hdbInputs.loanSource === 'hdb' ? hdbInputs.hdbInterestRate : hdbInputs.bankInterestRate}
                    onChange={e => hdbInputs.loanSource === 'hdb' ? updateHdb('hdbInterestRate', Number(e.target.value)) : updateHdb('bankInterestRate', Number(e.target.value))}
                    InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }} sx={{ mb: 1.5, mt: 0.5 }} />
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    {hdbInputs.loanSource === 'hdb' ? 'HDB Loan (HFE Approved)' : 'Bank Loan Amount'}
                  </Typography>
                  <TextField fullWidth size="small" type="number" value={hdbInputs.hdbLoanAmount || ''}
                    onChange={e => updateHdb('hdbLoanAmount', Number(e.target.value))}
                    InputProps={{ startAdornment: <InputAdornment position="start">S$</InputAdornment> }} sx={{ mb: 1, mt: 0.5 }} />
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>Monthly Installment</Typography>
                  <TextField fullWidth size="small" disabled
                    value={calcMonthlyInstallment(hdbInputs.hdbLoanAmount,
                      hdbInputs.loanSource === 'hdb' ? hdbInputs.hdbInterestRate : hdbInputs.bankInterestRate,
                      hdbInputs.tenure).toLocaleString('en-SG')}
                    InputProps={{ startAdornment: <InputAdornment position="start">S$</InputAdornment> }} sx={{ mb: 1.5, mt: 0.5 }} />
                </>
              )}

              {/* CPF */}
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                <Box sx={{ flex: 1, height: 1, bgcolor: 'divider' }} />
                <Typography variant="subtitle2" fontWeight={700} sx={{ px: 2 }}>CPF</Typography>
                <Box sx={{ flex: 1, height: 1, bgcolor: 'divider' }} />
              </Box>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>Ordinary Account (OA)</Typography>
              <TextField fullWidth size="small" type="number" value={hdbInputs.cpfOa || ''}
                onChange={e => updateHdb('cpfOa', Number(e.target.value))}
                InputProps={{ startAdornment: <InputAdornment position="start">S$</InputAdornment> }} sx={{ mb: 1, mt: 0.5 }} />
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>Eligible Housing Grant</Typography>
              <TextField fullWidth size="small" type="number" value={hdbInputs.housingGrant || ''}
                onChange={e => updateHdb('housingGrant', Number(e.target.value))}
                InputProps={{ startAdornment: <InputAdornment position="start">S$</InputAdornment> }} sx={{ mb: 1, mt: 0.5 }} />
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>Buyer Stamp Duty (BSD)</Typography>
              <TextField fullWidth size="small" disabled value={calculateBsd(hdbInputs.purchasePrice).toLocaleString('en-SG')}
                InputProps={{ startAdornment: <InputAdornment position="start">S$</InputAdornment> }} sx={{ mb: 1, mt: 0.5 }} />
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>Legal Fee</Typography>
              <TextField fullWidth size="small" type="number" value={hdbInputs.legalFee}
                onChange={e => updateHdb('legalFee', Number(e.target.value))}
                InputProps={{ startAdornment: <InputAdornment position="start">S$</InputAdornment> }} sx={{ mb: 1, mt: 0.5 }} />
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>Home Protection Scheme (HPS)</Typography>
              <TextField fullWidth size="small" type="number" value={hdbInputs.hpsAmount || ''}
                onChange={e => updateHdb('hpsAmount', Number(e.target.value))}
                InputProps={{ startAdornment: <InputAdornment position="start">S$</InputAdornment> }} sx={{ mb: 1.5, mt: 0.5 }} />

              {/* Total Upfront */}
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                <Box sx={{ flex: 1, height: 1, bgcolor: 'divider' }} />
                <Typography variant="subtitle2" fontWeight={700} sx={{ px: 2 }}>Total Upfront Cash Needed</Typography>
                <Box sx={{ flex: 1, height: 1, bgcolor: 'divider' }} />
              </Box>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>Valuation Fee</Typography>
              <TextField fullWidth size="small" type="number" value={hdbInputs.valuationFee}
                onChange={e => updateHdb('valuationFee', Number(e.target.value))}
                InputProps={{ startAdornment: <InputAdornment position="start">S$</InputAdornment> }} sx={{ mb: 1, mt: 0.5 }} />
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>HDB Admin Fee</Typography>
              <TextField fullWidth size="small" type="number" value={hdbInputs.hdbAdminFee}
                onChange={e => updateHdb('hdbAdminFee', Number(e.target.value))}
                InputProps={{ startAdornment: <InputAdornment position="start">S$</InputAdornment> }} sx={{ mb: 1, mt: 0.5 }} />

              <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 1 }}>OTP Date</Typography>
              <TextField fullWidth size="small" type="date" value={hdbInputs.otpDate}
                onChange={e => updateHdb('otpDate', e.target.value)} sx={{ mb: 1, mt: 0.5 }} />
            </Card>
          </Grid>

          {/* Right: HDB Schedule */}
          <Grid item xs={12} md={7}>
            <HdbSchedule h={hdbInputs} fmt={fmt} cardBg={cardBg} cardBorder={cardBorder} onExplain={setCalcDialog} onOtpDateChange={(iso) => { updateHdb('otpDate', iso); clearHdbMilestonePins(); }} dateOverrides={hdbDateOverrides} onMilestoneDateChange={pinHdbMilestone} showDates={showTimelineDates} onShowDatesChange={setShowTimelineDates} />
          </Grid>
        </Grid>
      )}

    </Box>
  );
}
